import type { ChatProgress } from '../types/signlaRTypes';
import { ADDITIONAL_TRANSLATION_OVERRIDES } from './translationOverrides.additional';

/**
 * Translation overrides for terms where literal translation is incorrect.
 *
 * Remote translation files may contain literal/dictionary translations that
 * don't match the conventions used by major social platforms. This module
 * provides override maps keyed by i18next short language code. Overrides are
 * applied on top of loaded translations so the correct term always wins.
 *
 * To add a new override:
 *  1. Add the key + correct value under each language in TRANSLATION_OVERRIDES.
 *  2. The override is applied automatically when the language loads.
 */

// "Retired" is a survey/version lifecycle state, not a description of a
// person. Keep these translations explicitly object-oriented: no longer in use.
const RETIRED_SURVEY_STATUS_OVERRIDES: Record<string, string> = {
  cy: "Nid yw'n cael ei ddefnyddio mwyach",
  fr: "Hors d'usage",
  de: 'Nicht mehr in Gebrauch',
  ro: 'Nu mai este în uz',
  ka: 'აღარ გამოიყენება',
  nl: 'Niet meer in gebruik',
  it: 'Non più in uso',
  el: 'Δεν χρησιμοποιείται πλέον',
  es: 'Ya no se utiliza',
  pt: 'Já não está em uso',
  fi: 'Ei enää käytössä',
  nb: 'Ikke lenger i bruk',
  sv: 'Används inte längre',
  da: 'Ikke længere i brug',
  pl: 'Nie jest już używana',
  lv: 'Vairs netiek izmantota',
  lt: 'Nebenaudojama',
  cs: 'Již se nepoužívá',
  sk: 'Už sa nepoužíva',
  et: 'Pole enam kasutusel',
};

// The privacy statement and terms of use are legal texts maintained only in English.
export const LEGAL_ENGLISH_ONLY_NOTICES: Record<string, string> = {
  ...Object.fromEntries(Object.entries(ADDITIONAL_TRANSLATION_OVERRIDES).map(
    ([language, overrides]) => [language, overrides.legal_english_only_notice]
  )),
  cy: "Dim ond yn Saesneg y mae'r ddogfen hon ar gael.",
  fr: 'Ce document est disponible uniquement en anglais.',
  de: 'Dieses Dokument ist nur auf Englisch verfügbar.',
  ro: 'Acest document este disponibil doar în limba engleză.',
  ka: 'ეს დოკუმენტი ხელმისაწვდომია მხოლოდ ინგლისურ ენაზე.',
  nl: 'Dit document is alleen beschikbaar in het Engels.',
  it: 'Questo documento è disponibile solo in inglese.',
  el: 'Αυτό το έγγραφο είναι διαθέσιμο μόνο στα αγγλικά.',
  es: 'Este documento solo está disponible en inglés.',
  pt: 'Este documento está disponível apenas em inglês.',
  fi: 'Tämä asiakirja on saatavilla vain englanniksi.',
  nb: 'Dette dokumentet er bare tilgjengelig på engelsk.',
  sv: 'Detta dokument finns endast på engelska.',
  da: 'Dette dokument er kun tilgængeligt på engelsk.',
  pl: 'Ten dokument jest dostępny wyłącznie w języku angielskim.',
  lv: 'Šis dokuments ir pieejams tikai angļu valodā.',
  lt: 'Šis dokumentas prieinamas tik anglų kalba.',
  cs: 'Tento dokument je k dispozici pouze v angličtině.',
  sk: 'Tento dokument je k dispozícii iba v angličtine.',
  et: 'See dokument on saadaval ainult inglise keeles.',
};

export const LEGAL_TRANSLATION_NOTICES: Record<string, string> = {
  ...Object.fromEntries(Object.entries(ADDITIONAL_TRANSLATION_OVERRIDES).map(
    ([language, overrides]) => [language, overrides.legal_translation_notice]
  )),
  cy: "Darperir y cyfieithiad hwn er hwylustod. Os oes unrhyw wahaniaeth, y fersiwn Saesneg sy'n gymwys.",
  fr: 'Cette traduction est fournie à titre indicatif. En cas de divergence, la version anglaise prévaut.',
  de: 'Diese Übersetzung dient nur zur Information. Bei Abweichungen ist die englische Fassung maßgeblich.',
  ro: 'Această traducere este furnizată doar pentru comoditate. În caz de neconcordanță, prevalează versiunea în limba engleză.',
  ka: 'ეს თარგმანი მოწოდებულია მხოლოდ მოხერხებულობისთვის. ნებისმიერი შეუსაბამობის შემთხვევაში უპირატესობა ენიჭება ინგლისურ ვერსიას.',
  nl: 'Deze vertaling wordt uitsluitend voor het gemak verstrekt. Bij verschillen is de Engelse versie leidend.',
  it: 'Questa traduzione è fornita solo per comodità. In caso di discrepanze, prevale la versione inglese.',
  el: 'Η παρούσα μετάφραση παρέχεται για διευκόλυνση. Σε περίπτωση οποιασδήποτε απόκλισης, υπερισχύει η αγγλική έκδοση.',
  es: 'Esta traducción se proporciona únicamente para su comodidad. En caso de discrepancia, prevalece la versión en inglés.',
  pt: 'Esta tradução é fornecida apenas por conveniência. Em caso de divergência, prevalece a versão em inglês.',
  fi: 'Tämä käännös on tarkoitettu vain avuksi. Mahdollisissa ristiriitatilanteissa englanninkielinen versio on ratkaiseva.',
  nb: 'Denne oversettelsen er kun ment som en hjelp. Ved eventuelle avvik gjelder den engelske versjonen.',
  sv: 'Denna översättning tillhandahålls endast som en hjälp. Vid eventuella skillnader gäller den engelska versionen.',
  da: 'Denne oversættelse stilles kun til rådighed som en hjælp. I tilfælde af uoverensstemmelser gælder den engelske version.',
  pl: 'To tłumaczenie zostało udostępnione wyłącznie dla wygody. W przypadku jakichkolwiek rozbieżności obowiązuje wersja angielska.',
  lv: 'Šis tulkojums ir sniegts tikai ērtības labad. Neatbilstību gadījumā noteicošā ir angļu valodas versija.',
  lt: 'Šis vertimas pateikiamas tik patogumo sumetimais. Esant neatitikimų, viršenybė teikiama versijai anglų kalba.',
  cs: 'Tento překlad je poskytován pouze pro usnadnění. V případě jakýchkoli rozdílů má přednost anglická verze.',
  sk: 'Tento preklad sa poskytuje len pre uľahčenie. V prípade akýchkoľvek rozdielov má prednosť anglická verzia.',
  et: 'See tõlge on esitatud üksnes mugavuse huvides. Lahknevuste korral kehtib ingliskeelne versioon.',
};

const AI_CHAT_SOURCE_CITED_LABELS: Record<string, string> = {
  en: 'Citation available',
  cy: 'Cyfeiriad ar gael',
  fr: 'Référence disponible',
  de: 'Quellenangabe verfügbar',
  ro: 'Referință disponibilă',
  ka: 'ციტირება ხელმისაწვდომია',
  nl: 'Bronvermelding beschikbaar',
  it: 'Citazione disponibile',
  el: 'Διαθέσιμη παραπομπή',
  es: 'Cita disponible',
  pt: 'Citação disponível',
  fi: 'Lähdeviite saatavilla',
  nb: 'Kildehenvisning tilgjengelig',
  sv: 'Källhänvisning tillgänglig',
  da: 'Kildehenvisning tilgængelig',
  pl: 'Przypis źródłowy dostępny',
  lv: 'Atsauce pieejama',
  lt: 'Šaltinio nuoroda prieinama',
  cs: 'Citace k dispozici',
  sk: 'Citácia k dispozícii',
  et: 'Viide saadaval',
};

const AI_CHAT_CITATION_COUNT_LABELS: Record<string, string> = {
  en: 'Citations: {{found}} found, {{retained}} retained',
  cy: "Cyfeiriadau: {{found}} wedi'u canfod, {{retained}} wedi'u cadw",
  fr: 'Références : {{found}} trouvées, {{retained}} retenues',
  de: 'Quellenverweise: {{found}} gefunden, {{retained}} beibehalten',
  ro: 'Referințe: {{found}} găsite, {{retained}} păstrate',
  ka: 'წყაროები: ნაპოვნია {{found}}, შენარჩუნებულია {{retained}}',
  nl: 'Bronverwijzingen: {{found}} gevonden, {{retained}} behouden',
  it: 'Citazioni: {{found}} trovate, {{retained}} mantenute',
  el: 'Παραπομπές: {{found}} βρέθηκαν, {{retained}} διατηρήθηκαν',
  es: 'Citas: {{found}} encontradas, {{retained}} conservadas',
  pt: 'Referências: {{found}} encontradas, {{retained}} mantidas',
  fi: 'Lähdeviitteet: {{found}} löydetty, {{retained}} säilytetty',
  nb: 'Kildehenvisninger: {{found}} funnet, {{retained}} beholdt',
  sv: 'Källhänvisningar: {{found}} hittade, {{retained}} behållna',
  da: 'Kildehenvisninger: {{found}} fundet, {{retained}} beholdt',
  pl: 'Odwołania: {{found}} znalezionych, {{retained}} zachowanych',
  lv: 'Atsauces: {{found}} atrastas, {{retained}} paturētas',
  lt: 'Nuorodos: {{found}} rasta, {{retained}} palikta',
  cs: 'Citace: {{found}} nalezeno, {{retained}} ponecháno',
  sk: 'Citácie: {{found}} nájdených, {{retained}} ponechaných',
  et: 'Viited: {{found}} leitud, {{retained}} säilitatud',
};

const ROOM_SEARCH_SLIDER_KEYS = ['text', 'sources', 'budget', 'less', 'default', 'more', 'custom'] as const;
const ROOM_AI_BACKGROUND_LABELS: Record<string, readonly [string, string]> = {
  en: ['Show background pattern', 'Background visibility (%)'],
  cy: ['Dangos patrwm cefndir', 'Gwelededd y cefndir (%)'],
  fr: ['Afficher le motif de fond', 'Visibilité du fond (%)'],
  de: ['Hintergrundmuster anzeigen', 'Sichtbarkeit des Hintergrunds (%)'],
  ro: ['Afișează modelul de fundal', 'Vizibilitatea fundalului (%)'],
  ka: ['ფონის ნიმუშის ჩვენება', 'ფონის ხილვადობა (%)'],
  nl: ['Achtergrondpatroon tonen', 'Zichtbaarheid achtergrond (%)'],
  it: ['Mostra il motivo di sfondo', 'Visibilità dello sfondo (%)'],
  el: ['Εμφάνιση μοτίβου φόντου', 'Ορατότητα φόντου (%)'],
  es: ['Mostrar patrón de fondo', 'Visibilidad del fondo (%)'],
  pt: ['Mostrar padrão de fundo', 'Visibilidade do fundo (%)'],
  fi: ['Näytä taustakuvio', 'Taustan näkyvyys (%)'],
  nb: ['Vis bakgrunnsmønster', 'Bakgrunnens synlighet (%)'],
  sv: ['Visa bakgrundsmönster', 'Bakgrundens synlighet (%)'],
  da: ['Vis baggrundsmønster', 'Baggrundens synlighed (%)'],
  pl: ['Pokaż wzór tła', 'Widoczność tła (%)'],
  lv: ['Rādīt fona rakstu', 'Fona redzamība (%)'],
  lt: ['Rodyti fono raštą', 'Fono matomumas (%)'],
  cs: ['Zobrazit vzor pozadí', 'Viditelnost pozadí (%)'],
  sk: ['Zobraziť vzor pozadia', 'Viditeľnosť pozadia (%)'],
  et: ['Kuva taustamuster', 'Tausta nähtavus (%)'],
};
const ROOM_SEARCH_SLIDER_LABELS: Record<string, readonly [string, string, string, string, string, string, string]> = {
  en: ['Text amount', 'Source count', 'Evidence budget', 'Less', 'Default', 'More', 'Custom'],
  cy: ['Swm y testun', 'Nifer y ffynonellau', 'Terfyn tystiolaeth', 'Llai', 'Diofyn', 'Mwy', 'Personol'],
  fr: ['Quantité de texte', 'Nombre de sources', 'Limite des éléments probants', 'Moins', 'Par défaut', 'Plus', 'Personnalisé'],
  de: ['Textmenge', 'Anzahl der Quellen', 'Evidenzlimit', 'Weniger', 'Standard', 'Mehr', 'Benutzerdefiniert'],
  ro: ['Cantitate de text', 'Număr de surse', 'Limită de dovezi', 'Mai puțin', 'Implicit', 'Mai mult', 'Personalizat'],
  ka: ['ტექსტის მოცულობა', 'წყაროების რაოდენობა', 'მტკიცებულებების ლიმიტი', 'ნაკლები', 'ნაგულისხმევი', 'მეტი', 'მორგებული'],
  nl: ['Hoeveelheid tekst', 'Aantal bronnen', 'Bewijslimiet', 'Minder', 'Standaard', 'Meer', 'Aangepast'],
  it: ['Quantità di testo', 'Numero di fonti', 'Limite delle evidenze', 'Meno', 'Predefinito', 'Più', 'Personalizzato'],
  el: ['Ποσότητα κειμένου', 'Αριθμός πηγών', 'Όριο τεκμηρίων', 'Λιγότερα', 'Προεπιλογή', 'Περισσότερα', 'Προσαρμοσμένο'],
  es: ['Cantidad de texto', 'Número de fuentes', 'Límite de evidencias', 'Menos', 'Predeterminado', 'Más', 'Personalizado'],
  pt: ['Quantidade de texto', 'Número de fontes', 'Limite de evidências', 'Menos', 'Predefinido', 'Mais', 'Personalizado'],
  fi: ['Tekstin määrä', 'Lähteiden määrä', 'Todisteiden raja', 'Vähemmän', 'Oletus', 'Enemmän', 'Mukautettu'],
  nb: ['Tekstmengde', 'Antall kilder', 'Evidensgrense', 'Mindre', 'Standard', 'Mer', 'Egendefinert'],
  sv: ['Textmängd', 'Antal källor', 'Evidensgräns', 'Mindre', 'Standard', 'Mer', 'Anpassad'],
  da: ['Tekstmængde', 'Antal kilder', 'Evidensgrænse', 'Mindre', 'Standard', 'Mere', 'Tilpasset'],
  pl: ['Ilość tekstu', 'Liczba źródeł', 'Limit dowodów', 'Mniej', 'Domyślnie', 'Więcej', 'Niestandardowe'],
  lv: ['Teksta apjoms', 'Avotu skaits', 'Pierādījumu limits', 'Mazāk', 'Noklusējums', 'Vairāk', 'Pielāgots'],
  lt: ['Teksto kiekis', 'Šaltinių skaičius', 'Įrodymų riba', 'Mažiau', 'Numatyta', 'Daugiau', 'Pasirinktinis'],
  cs: ['Množství textu', 'Počet zdrojů', 'Limit důkazů', 'Méně', 'Výchozí', 'Více', 'Vlastní'],
  sk: ['Množstvo textu', 'Počet zdrojov', 'Limit dôkazov', 'Menej', 'Predvolené', 'Viac', 'Vlastné'],
  et: ['Teksti hulk', 'Allikate arv', 'Tõendite piir', 'Vähem', 'Vaikimisi', 'Rohkem', 'Kohandatud'],
};

const AI_CHAT_PROGRESS_OVERRIDES: Record<string,
  Record<`ai_chatbot_progress_${Exclude<ChatProgress['stage'], 'finished' | 'source_accepted'>}`, string>> = {
  cy: {
    ai_chatbot_progress_working: "Wrthi'n gweithio ar eich cais...",
    ai_chatbot_progress_searching: 'Chwilio ffynonellau...',
    ai_chatbot_progress_tool_running: 'Defnyddio offeryn...',
    ai_chatbot_progress_tool_returned: "Prosesu'r canlyniad...",
    ai_chatbot_progress_composing: "Paratoi'r ateb...",
    ai_chatbot_progress_validating: 'Gwirio cyfeiriadau ffynonellau...',
    ai_chatbot_progress_retrying: "Rhoi cynnig arall ar yr ateb...",
    ai_chatbot_progress_searching_documents: 'Chwilio dogfennau...',
    ai_chatbot_progress_searching_web: "Chwilio'r we...",
    ai_chatbot_progress_searching_documents_web: "Chwilio dogfennau a'r we...",
    ai_chatbot_progress_reviewing_sources: 'Adolygu ffynonellau...',
    ai_chatbot_progress_reading_documents: 'Darllen dogfennau perthnasol...',
    ai_chatbot_progress_reading_web_sources: 'Darllen tudalennau gwe...',
    ai_chatbot_progress_checking_source_safety: 'Gwirio cynnwys y ffynonellau...',
    ai_chatbot_progress_assessing_sources: 'Asesu gwybodaeth berthnasol...',
    ai_chatbot_progress_combining_sources: "Dod â'r ffynonellau ynghyd...",
    ai_chatbot_progress_summarizing_document: "Crynhoi'r ddogfen...",
    ai_chatbot_progress_summarizing_discussion: "Crynhoi'r drafodaeth...",
    ai_chatbot_progress_reviewing_health: 'Adolygu gwybodaeth iechyd...',
    ai_chatbot_progress_updating_symptoms: 'Diweddaru gwybodaeth am symptomau...',
    ai_chatbot_progress_updating_medications: 'Diweddaru gwybodaeth am feddyginiaethau...',
    ai_chatbot_progress_updating_treatments: 'Diweddaru gwybodaeth am driniaethau...',
    ai_chatbot_progress_updating_diagnoses: 'Diweddaru gwybodaeth am ddiagnosis...',
    ai_chatbot_progress_updating_journal: "Diweddaru'r dyddiadur...",
    ai_chatbot_progress_updating_health: 'Diweddaru gwybodaeth iechyd...',
    ai_chatbot_progress_preparing_email: "Paratoi'r e-bost...",
    ai_chatbot_progress_checking_members: "Gwirio'r rhestr aelodau...",
    ai_chatbot_progress_creating_community: "Sefydlu'r gymuned...",
    ai_chatbot_progress_updating_community: "Diweddaru gosodiadau'r gymuned...",
    ai_chatbot_progress_removing_community: "Prosesu dileu'r gymuned...",
    ai_chatbot_progress_updating_members: 'Diweddaru aelodaeth y gymuned...',
    ai_chatbot_progress_updating_permissions: 'Diweddaru caniatadau aelodau...',
    ai_chatbot_progress_checking_notifications: 'Gwirio hysbysiadau...',
    ai_chatbot_progress_checking_profile: 'Gwirio manylion y proffil...',
    ai_chatbot_progress_updating_profile: 'Diweddaru manylion y proffil...',
    ai_chatbot_progress_updating_credentials: 'Diweddaru manylion cefndir...',
    ai_chatbot_progress_updating_journey: "Diweddaru'r daith iechyd...",
    ai_chatbot_progress_updating_preferences: 'Diweddaru dewisiadau...',
    ai_chatbot_progress_checking_onboarding: 'Gwirio cynnydd y camau sefydlu...',
    ai_chatbot_progress_updating_onboarding: 'Diweddaru cynnydd y camau sefydlu...',
    ai_chatbot_progress_preparing_survey: 'Gweithio ar yr arolwg...',
    ai_chatbot_progress_recording_survey: 'Prosesu ateb yr arolwg...',
    ai_chatbot_progress_reviewing_needs: 'Adolygu anghenion cymorth...',
    ai_chatbot_progress_updating_needs: 'Diweddaru anghenion cymorth...',
    ai_chatbot_progress_finding_sites: 'Gwirio gwefannau sydd ar gael...',
    ai_chatbot_progress_preparing_response: "Paratoi'r ymateb...",
  },
  fr: {
    ai_chatbot_progress_working: 'Traitement de votre demande...',
    ai_chatbot_progress_searching: 'Recherche de sources...',
    ai_chatbot_progress_tool_running: "Utilisation d'un outil...",
    ai_chatbot_progress_tool_returned: 'Traitement du résultat...',
    ai_chatbot_progress_composing: 'Préparation de la réponse...',
    ai_chatbot_progress_validating: 'Vérification des références...',
    ai_chatbot_progress_retrying: 'Nouvelle tentative de réponse...',
    ai_chatbot_progress_searching_documents: 'Recherche dans les documents...',
    ai_chatbot_progress_searching_web: 'Recherche sur le Web...',
    ai_chatbot_progress_searching_documents_web: 'Recherche dans les documents et sur le Web...',
    ai_chatbot_progress_reviewing_sources: 'Examen des sources...',
    ai_chatbot_progress_reading_documents: 'Lecture des documents pertinents...',
    ai_chatbot_progress_reading_web_sources: 'Lecture des pages Web...',
    ai_chatbot_progress_checking_source_safety: 'Vérification du contenu des sources...',
    ai_chatbot_progress_assessing_sources: 'Évaluation des informations pertinentes...',
    ai_chatbot_progress_combining_sources: 'Regroupement des sources...',
    ai_chatbot_progress_summarizing_document: 'Résumé du document en cours...',
    ai_chatbot_progress_summarizing_discussion: 'Résumé de la discussion en cours...',
    ai_chatbot_progress_reviewing_health: 'Examen des informations de santé...',
    ai_chatbot_progress_updating_symptoms: 'Mise à jour des symptômes...',
    ai_chatbot_progress_updating_medications: 'Mise à jour des médicaments...',
    ai_chatbot_progress_updating_treatments: 'Mise à jour des traitements...',
    ai_chatbot_progress_updating_diagnoses: 'Mise à jour des diagnostics...',
    ai_chatbot_progress_updating_journal: 'Mise à jour du journal...',
    ai_chatbot_progress_updating_health: 'Mise à jour des informations de santé...',
    ai_chatbot_progress_preparing_email: "Préparation de l’e-mail...",
    ai_chatbot_progress_checking_members: 'Vérification de la liste des membres...',
    ai_chatbot_progress_creating_community: 'Création de la communauté...',
    ai_chatbot_progress_updating_community: 'Mise à jour des paramètres de la communauté...',
    ai_chatbot_progress_removing_community: 'Traitement de la suppression de la communauté...',
    ai_chatbot_progress_updating_members: 'Mise à jour des adhésions à la communauté...',
    ai_chatbot_progress_updating_permissions: 'Mise à jour des autorisations des membres...',
    ai_chatbot_progress_checking_notifications: 'Vérification des notifications...',
    ai_chatbot_progress_checking_profile: 'Vérification des détails du profil...',
    ai_chatbot_progress_updating_profile: 'Mise à jour des détails du profil...',
    ai_chatbot_progress_updating_credentials: 'Mise à jour des informations de parcours...',
    ai_chatbot_progress_updating_journey: 'Mise à jour du parcours de santé...',
    ai_chatbot_progress_updating_preferences: 'Mise à jour des préférences...',
    ai_chatbot_progress_checking_onboarding: 'Vérification des étapes de démarrage...',
    ai_chatbot_progress_updating_onboarding: 'Mise à jour des étapes de démarrage...',
    ai_chatbot_progress_preparing_survey: 'Traitement du questionnaire...',
    ai_chatbot_progress_recording_survey: 'Traitement de la réponse au questionnaire...',
    ai_chatbot_progress_reviewing_needs: 'Examen des besoins de soutien...',
    ai_chatbot_progress_updating_needs: 'Mise à jour des besoins de soutien...',
    ai_chatbot_progress_finding_sites: 'Vérification des sites Web disponibles...',
    ai_chatbot_progress_preparing_response: 'Mise en forme de la réponse...',
  },
  de: {
    ai_chatbot_progress_working: 'Ihre Anfrage wird bearbeitet...',
    ai_chatbot_progress_searching: 'Quellen werden gesucht...',
    ai_chatbot_progress_tool_running: 'Eine Funktion wird ausgeführt...',
    ai_chatbot_progress_tool_returned: 'Das Ergebnis wird verarbeitet...',
    ai_chatbot_progress_composing: 'Die Antwort wird vorbereitet...',
    ai_chatbot_progress_validating: 'Quellenangaben werden geprüft...',
    ai_chatbot_progress_retrying: 'Die Antwort wird erneut erstellt...',
    ai_chatbot_progress_searching_documents: 'Dokumente werden durchsucht...',
    ai_chatbot_progress_searching_web: 'Das Web wird durchsucht...',
    ai_chatbot_progress_searching_documents_web: 'Dokumente und das Web werden durchsucht...',
    ai_chatbot_progress_reviewing_sources: 'Quellen werden gesichtet...',
    ai_chatbot_progress_reading_documents: 'Relevante Dokumente werden gelesen...',
    ai_chatbot_progress_reading_web_sources: 'Webseiten werden gelesen...',
    ai_chatbot_progress_checking_source_safety: 'Quelleninhalte werden geprüft...',
    ai_chatbot_progress_assessing_sources: 'Relevante Informationen werden ausgewertet...',
    ai_chatbot_progress_combining_sources: 'Quellen werden zusammengeführt...',
    ai_chatbot_progress_summarizing_document: 'Das Dokument wird zusammengefasst...',
    ai_chatbot_progress_summarizing_discussion: 'Die Diskussion wird zusammengefasst...',
    ai_chatbot_progress_reviewing_health: 'Gesundheitsinformationen werden gesichtet...',
    ai_chatbot_progress_updating_symptoms: 'Symptominformationen werden aktualisiert...',
    ai_chatbot_progress_updating_medications: 'Medikamenteninformationen werden aktualisiert...',
    ai_chatbot_progress_updating_treatments: 'Behandlungsinformationen werden aktualisiert...',
    ai_chatbot_progress_updating_diagnoses: 'Diagnoseinformationen werden aktualisiert...',
    ai_chatbot_progress_updating_journal: 'Das Tagebuch wird aktualisiert...',
    ai_chatbot_progress_updating_health: 'Gesundheitsinformationen werden aktualisiert...',
    ai_chatbot_progress_preparing_email: 'Die E-Mail wird vorbereitet...',
    ai_chatbot_progress_checking_members: 'Die Mitgliederliste wird geprüft...',
    ai_chatbot_progress_creating_community: 'Die Community wird eingerichtet...',
    ai_chatbot_progress_updating_community: 'Community-Einstellungen werden aktualisiert...',
    ai_chatbot_progress_removing_community: 'Die Löschung der Community wird bearbeitet...',
    ai_chatbot_progress_updating_members: 'Community-Mitgliedschaften werden aktualisiert...',
    ai_chatbot_progress_updating_permissions: 'Mitgliederberechtigungen werden aktualisiert...',
    ai_chatbot_progress_checking_notifications: 'Benachrichtigungen werden geprüft...',
    ai_chatbot_progress_checking_profile: 'Profildetails werden geprüft...',
    ai_chatbot_progress_updating_profile: 'Profildetails werden aktualisiert...',
    ai_chatbot_progress_updating_credentials: 'Hintergrundangaben werden aktualisiert...',
    ai_chatbot_progress_updating_journey: 'Der Gesundheitsverlauf wird aktualisiert...',
    ai_chatbot_progress_updating_preferences: 'Präferenzen werden aktualisiert...',
    ai_chatbot_progress_checking_onboarding: 'Einrichtungsschritte werden geprüft...',
    ai_chatbot_progress_updating_onboarding: 'Der Einrichtungsfortschritt wird aktualisiert...',
    ai_chatbot_progress_preparing_survey: 'Die Umfrage wird bearbeitet...',
    ai_chatbot_progress_recording_survey: 'Die Umfrageantwort wird verarbeitet...',
    ai_chatbot_progress_reviewing_needs: 'Unterstützungsbedarf wird gesichtet...',
    ai_chatbot_progress_updating_needs: 'Unterstützungsbedarf wird aktualisiert...',
    ai_chatbot_progress_finding_sites: 'Verfügbare Websites werden geprüft...',
    ai_chatbot_progress_preparing_response: 'Die Rückmeldung wird vorbereitet...',
  },
  ro: {
    ai_chatbot_progress_working: 'Procesăm solicitarea...',
    ai_chatbot_progress_searching: 'Căutăm surse...',
    ai_chatbot_progress_tool_running: 'Utilizăm un instrument...',
    ai_chatbot_progress_tool_returned: 'Procesăm rezultatul...',
    ai_chatbot_progress_composing: 'Pregătim răspunsul...',
    ai_chatbot_progress_validating: 'Verificăm referințele surselor...',
    ai_chatbot_progress_retrying: 'Încercăm din nou să formulăm răspunsul...',
    ai_chatbot_progress_searching_documents: 'Căutăm în documente...',
    ai_chatbot_progress_searching_web: 'Căutăm pe web...',
    ai_chatbot_progress_searching_documents_web: 'Căutăm în documente și pe web...',
    ai_chatbot_progress_reviewing_sources: 'Analizăm sursele...',
    ai_chatbot_progress_reading_documents: 'Citim documentele relevante...',
    ai_chatbot_progress_reading_web_sources: 'Citim paginile web...',
    ai_chatbot_progress_checking_source_safety: 'Verificăm conținutul surselor...',
    ai_chatbot_progress_assessing_sources: 'Evaluăm informațiile relevante...',
    ai_chatbot_progress_combining_sources: 'Reunim informațiile din surse...',
    ai_chatbot_progress_summarizing_document: 'Rezumăm documentul...',
    ai_chatbot_progress_summarizing_discussion: 'Rezumăm discuția...',
    ai_chatbot_progress_reviewing_health: 'Analizăm informațiile de sănătate...',
    ai_chatbot_progress_updating_symptoms: 'Actualizăm informațiile despre simptome...',
    ai_chatbot_progress_updating_medications: 'Actualizăm informațiile despre medicamente...',
    ai_chatbot_progress_updating_treatments: 'Actualizăm informațiile despre tratamente...',
    ai_chatbot_progress_updating_diagnoses: 'Actualizăm informațiile despre diagnostice...',
    ai_chatbot_progress_updating_journal: 'Actualizăm jurnalul...',
    ai_chatbot_progress_updating_health: 'Actualizăm informațiile de sănătate...',
    ai_chatbot_progress_preparing_email: 'Pregătim e-mailul...',
    ai_chatbot_progress_checking_members: 'Verificăm lista membrilor...',
    ai_chatbot_progress_creating_community: 'Configurăm comunitatea...',
    ai_chatbot_progress_updating_community: 'Actualizăm setările comunității...',
    ai_chatbot_progress_removing_community: 'Procesăm eliminarea comunității...',
    ai_chatbot_progress_updating_members: 'Actualizăm apartenența la comunitate...',
    ai_chatbot_progress_updating_permissions: 'Actualizăm permisiunile membrilor...',
    ai_chatbot_progress_checking_notifications: 'Verificăm notificările...',
    ai_chatbot_progress_checking_profile: 'Verificăm detaliile profilului...',
    ai_chatbot_progress_updating_profile: 'Actualizăm detaliile profilului...',
    ai_chatbot_progress_updating_credentials: 'Actualizăm informațiile de fond...',
    ai_chatbot_progress_updating_journey: 'Actualizăm parcursul de sănătate...',
    ai_chatbot_progress_updating_preferences: 'Actualizăm preferințele...',
    ai_chatbot_progress_checking_onboarding: 'Verificăm progresul configurării...',
    ai_chatbot_progress_updating_onboarding: 'Actualizăm progresul configurării...',
    ai_chatbot_progress_preparing_survey: 'Lucrăm la chestionar...',
    ai_chatbot_progress_recording_survey: 'Procesăm răspunsul la chestionar...',
    ai_chatbot_progress_reviewing_needs: 'Analizăm nevoile de sprijin...',
    ai_chatbot_progress_updating_needs: 'Actualizăm nevoile de sprijin...',
    ai_chatbot_progress_finding_sites: 'Verificăm site-urile disponibile...',
    ai_chatbot_progress_preparing_response: 'Pregătim mesajul de răspuns...',
  },
  ka: {
    ai_chatbot_progress_working: 'მოთხოვნის დამუშავება...',
    ai_chatbot_progress_searching: 'წყაროების ძიება...',
    ai_chatbot_progress_tool_running: 'ინსტრუმენტის გამოყენება...',
    ai_chatbot_progress_tool_returned: 'შედეგის დამუშავება...',
    ai_chatbot_progress_composing: 'პასუხის მომზადება...',
    ai_chatbot_progress_validating: 'წყაროების მითითებების შემოწმება...',
    ai_chatbot_progress_retrying: 'პასუხის ხელახლა მომზადება...',
    ai_chatbot_progress_searching_documents: 'დოკუმენტებში ძიება...',
    ai_chatbot_progress_searching_web: 'ვებში ძიება...',
    ai_chatbot_progress_searching_documents_web: 'დოკუმენტებსა და ვებში ძიება...',
    ai_chatbot_progress_reviewing_sources: 'წყაროების განხილვა...',
    ai_chatbot_progress_reading_documents: 'შესაბამისი დოკუმენტების კითხვა...',
    ai_chatbot_progress_reading_web_sources: 'ვებგვერდების კითხვა...',
    ai_chatbot_progress_checking_source_safety: 'წყაროების შინაარსის შემოწმება...',
    ai_chatbot_progress_assessing_sources: 'შესაბამისი ინფორმაციის შეფასება...',
    ai_chatbot_progress_combining_sources: 'წყაროების გაერთიანება...',
    ai_chatbot_progress_summarizing_document: 'დოკუმენტის შეჯამება...',
    ai_chatbot_progress_summarizing_discussion: 'დისკუსიის შეჯამება...',
    ai_chatbot_progress_reviewing_health: 'ჯანმრთელობის ინფორმაციის განხილვა...',
    ai_chatbot_progress_updating_symptoms: 'სიმპტომების ინფორმაციის განახლება...',
    ai_chatbot_progress_updating_medications: 'მედიკამენტების ინფორმაციის განახლება...',
    ai_chatbot_progress_updating_treatments: 'მკურნალობის ინფორმაციის განახლება...',
    ai_chatbot_progress_updating_diagnoses: 'დიაგნოზების ინფორმაციის განახლება...',
    ai_chatbot_progress_updating_journal: 'დღიურის განახლება...',
    ai_chatbot_progress_updating_health: 'ჯანმრთელობის ინფორმაციის განახლება...',
    ai_chatbot_progress_preparing_email: 'ელფოსტის მომზადება...',
    ai_chatbot_progress_checking_members: 'წევრთა სიის შემოწმება...',
    ai_chatbot_progress_creating_community: 'საზოგადოების შექმნა...',
    ai_chatbot_progress_updating_community: 'საზოგადოების პარამეტრების განახლება...',
    ai_chatbot_progress_removing_community: 'საზოგადოების წაშლის დამუშავება...',
    ai_chatbot_progress_updating_members: 'საზოგადოების წევრობის განახლება...',
    ai_chatbot_progress_updating_permissions: 'წევრთა უფლებების განახლება...',
    ai_chatbot_progress_checking_notifications: 'შეტყობინებების შემოწმება...',
    ai_chatbot_progress_checking_profile: 'პროფილის დეტალების შემოწმება...',
    ai_chatbot_progress_updating_profile: 'პროფილის დეტალების განახლება...',
    ai_chatbot_progress_updating_credentials: 'ბიოგრაფიული დეტალების განახლება...',
    ai_chatbot_progress_updating_journey: 'ჯანმრთელობის გზის განახლება...',
    ai_chatbot_progress_updating_preferences: 'პრეფერენციების განახლება...',
    ai_chatbot_progress_checking_onboarding: 'საწყისი ნაბიჯების პროგრესის შემოწმება...',
    ai_chatbot_progress_updating_onboarding: 'საწყისი ნაბიჯების პროგრესის განახლება...',
    ai_chatbot_progress_preparing_survey: 'კითხვარზე მუშაობა...',
    ai_chatbot_progress_recording_survey: 'კითხვარის პასუხის დამუშავება...',
    ai_chatbot_progress_reviewing_needs: 'მხარდაჭერის საჭიროებების განხილვა...',
    ai_chatbot_progress_updating_needs: 'მხარდაჭერის საჭიროებების განახლება...',
    ai_chatbot_progress_finding_sites: 'ხელმისაწვდომი ვებსაიტების შემოწმება...',
    ai_chatbot_progress_preparing_response: 'საპასუხო შეტყობინების მომზადება...',
  },
  nl: {
    ai_chatbot_progress_working: 'Uw verzoek wordt verwerkt...',
    ai_chatbot_progress_searching: 'Bronnen zoeken...',
    ai_chatbot_progress_tool_running: 'Een hulpmiddel gebruiken...',
    ai_chatbot_progress_tool_returned: 'Het resultaat verwerken...',
    ai_chatbot_progress_composing: 'Het antwoord voorbereiden...',
    ai_chatbot_progress_validating: 'Bronverwijzingen controleren...',
    ai_chatbot_progress_retrying: 'Het antwoord opnieuw opstellen...',
    ai_chatbot_progress_searching_documents: 'Documenten doorzoeken...',
    ai_chatbot_progress_searching_web: 'Zoeken op het web...',
    ai_chatbot_progress_searching_documents_web: 'Documenten en het web doorzoeken...',
    ai_chatbot_progress_reviewing_sources: 'Bronnen bekijken...',
    ai_chatbot_progress_reading_documents: 'Relevante documenten lezen...',
    ai_chatbot_progress_reading_web_sources: "Webpagina's lezen...",
    ai_chatbot_progress_checking_source_safety: 'Broninhoud controleren...',
    ai_chatbot_progress_assessing_sources: 'Relevante informatie beoordelen...',
    ai_chatbot_progress_combining_sources: 'Bronnen samenbrengen...',
    ai_chatbot_progress_summarizing_document: 'Het document samenvatten...',
    ai_chatbot_progress_summarizing_discussion: 'Het gesprek samenvatten...',
    ai_chatbot_progress_reviewing_health: 'Gezondheidsinformatie bekijken...',
    ai_chatbot_progress_updating_symptoms: 'Symptoominformatie bijwerken...',
    ai_chatbot_progress_updating_medications: 'Medicatiegegevens bijwerken...',
    ai_chatbot_progress_updating_treatments: 'Behandelinformatie bijwerken...',
    ai_chatbot_progress_updating_diagnoses: 'Diagnosegegevens bijwerken...',
    ai_chatbot_progress_updating_journal: 'Het dagboek bijwerken...',
    ai_chatbot_progress_updating_health: 'Gezondheidsinformatie bijwerken...',
    ai_chatbot_progress_preparing_email: 'De e-mail voorbereiden...',
    ai_chatbot_progress_checking_members: 'De ledenlijst controleren...',
    ai_chatbot_progress_creating_community: 'De community instellen...',
    ai_chatbot_progress_updating_community: 'Community-instellingen bijwerken...',
    ai_chatbot_progress_removing_community: 'Het verwijderen van de community verwerken...',
    ai_chatbot_progress_updating_members: 'Communitylidmaatschappen bijwerken...',
    ai_chatbot_progress_updating_permissions: 'Ledenrechten bijwerken...',
    ai_chatbot_progress_checking_notifications: 'Meldingen controleren...',
    ai_chatbot_progress_checking_profile: 'Profielgegevens controleren...',
    ai_chatbot_progress_updating_profile: 'Profielgegevens bijwerken...',
    ai_chatbot_progress_updating_credentials: 'Achtergrondgegevens bijwerken...',
    ai_chatbot_progress_updating_journey: 'Het gezondheidstraject bijwerken...',
    ai_chatbot_progress_updating_preferences: 'Voorkeuren bijwerken...',
    ai_chatbot_progress_checking_onboarding: 'De voortgang van de startstappen controleren...',
    ai_chatbot_progress_updating_onboarding: 'De voortgang van de startstappen bijwerken...',
    ai_chatbot_progress_preparing_survey: 'De vragenlijst verwerken...',
    ai_chatbot_progress_recording_survey: 'Het antwoord op de vragenlijst verwerken...',
    ai_chatbot_progress_reviewing_needs: 'Ondersteuningsbehoeften bekijken...',
    ai_chatbot_progress_updating_needs: 'Ondersteuningsbehoeften bijwerken...',
    ai_chatbot_progress_finding_sites: 'Beschikbare websites controleren...',
    ai_chatbot_progress_preparing_response: 'De reactie voorbereiden...',
  },
  it: {
    ai_chatbot_progress_working: 'Elaborazione della richiesta...',
    ai_chatbot_progress_searching: 'Ricerca delle fonti...',
    ai_chatbot_progress_tool_running: 'Utilizzo di uno strumento...',
    ai_chatbot_progress_tool_returned: 'Elaborazione del risultato...',
    ai_chatbot_progress_composing: 'Preparazione della risposta...',
    ai_chatbot_progress_validating: 'Verifica dei riferimenti alle fonti...',
    ai_chatbot_progress_retrying: 'Nuovo tentativo di risposta...',
    ai_chatbot_progress_searching_documents: 'Ricerca nei documenti...',
    ai_chatbot_progress_searching_web: 'Ricerca sul web...',
    ai_chatbot_progress_searching_documents_web: 'Ricerca nei documenti e sul web...',
    ai_chatbot_progress_reviewing_sources: 'Esame delle fonti...',
    ai_chatbot_progress_reading_documents: 'Lettura dei documenti pertinenti...',
    ai_chatbot_progress_reading_web_sources: 'Lettura delle pagine web...',
    ai_chatbot_progress_checking_source_safety: 'Verifica del contenuto delle fonti...',
    ai_chatbot_progress_assessing_sources: 'Valutazione delle informazioni pertinenti...',
    ai_chatbot_progress_combining_sources: 'Riunione delle fonti...',
    ai_chatbot_progress_summarizing_document: 'Riepilogo del documento in corso...',
    ai_chatbot_progress_summarizing_discussion: 'Riepilogo della discussione in corso...',
    ai_chatbot_progress_reviewing_health: 'Esame delle informazioni sanitarie...',
    ai_chatbot_progress_updating_symptoms: 'Aggiornamento delle informazioni sui sintomi...',
    ai_chatbot_progress_updating_medications: 'Aggiornamento delle informazioni sui farmaci...',
    ai_chatbot_progress_updating_treatments: 'Aggiornamento delle informazioni sui trattamenti...',
    ai_chatbot_progress_updating_diagnoses: 'Aggiornamento delle informazioni sulle diagnosi...',
    ai_chatbot_progress_updating_journal: 'Aggiornamento del diario...',
    ai_chatbot_progress_updating_health: 'Aggiornamento delle informazioni sanitarie...',
    ai_chatbot_progress_preparing_email: "Preparazione dell'e-mail...",
    ai_chatbot_progress_checking_members: 'Verifica della lista dei membri...',
    ai_chatbot_progress_creating_community: 'Creazione della comunità...',
    ai_chatbot_progress_updating_community: 'Aggiornamento delle impostazioni della comunità...',
    ai_chatbot_progress_removing_community: 'Elaborazione della rimozione della comunità...',
    ai_chatbot_progress_updating_members: 'Aggiornamento delle adesioni alla comunità...',
    ai_chatbot_progress_updating_permissions: 'Aggiornamento delle autorizzazioni dei membri...',
    ai_chatbot_progress_checking_notifications: 'Verifica delle notifiche...',
    ai_chatbot_progress_checking_profile: 'Verifica dei dettagli del profilo...',
    ai_chatbot_progress_updating_profile: 'Aggiornamento dei dettagli del profilo...',
    ai_chatbot_progress_updating_credentials: 'Aggiornamento delle informazioni personali...',
    ai_chatbot_progress_updating_journey: 'Aggiornamento del percorso di salute...',
    ai_chatbot_progress_updating_preferences: 'Aggiornamento delle preferenze...',
    ai_chatbot_progress_checking_onboarding: 'Verifica dei passaggi iniziali...',
    ai_chatbot_progress_updating_onboarding: 'Aggiornamento dei passaggi iniziali...',
    ai_chatbot_progress_preparing_survey: 'Elaborazione del questionario...',
    ai_chatbot_progress_recording_survey: 'Elaborazione della risposta al questionario...',
    ai_chatbot_progress_reviewing_needs: 'Esame delle esigenze di supporto...',
    ai_chatbot_progress_updating_needs: 'Aggiornamento delle esigenze di supporto...',
    ai_chatbot_progress_finding_sites: 'Verifica dei siti web disponibili...',
    ai_chatbot_progress_preparing_response: 'Preparazione del messaggio di risposta...',
  },
  el: {
    ai_chatbot_progress_working: 'Επεξεργασία του αιτήματός σας...',
    ai_chatbot_progress_searching: 'Αναζήτηση πηγών...',
    ai_chatbot_progress_tool_running: 'Χρήση εργαλείου...',
    ai_chatbot_progress_tool_returned: 'Επεξεργασία αποτελέσματος...',
    ai_chatbot_progress_composing: 'Προετοιμασία απάντησης...',
    ai_chatbot_progress_validating: 'Έλεγχος παραπομπών στις πηγές...',
    ai_chatbot_progress_retrying: 'Νέα προσπάθεια απάντησης...',
    ai_chatbot_progress_searching_documents: 'Αναζήτηση σε έγγραφα...',
    ai_chatbot_progress_searching_web: 'Αναζήτηση στο διαδίκτυο...',
    ai_chatbot_progress_searching_documents_web: 'Αναζήτηση σε έγγραφα και στο διαδίκτυο...',
    ai_chatbot_progress_reviewing_sources: 'Εξέταση πηγών...',
    ai_chatbot_progress_reading_documents: 'Ανάγνωση σχετικών εγγράφων...',
    ai_chatbot_progress_reading_web_sources: 'Ανάγνωση ιστοσελίδων...',
    ai_chatbot_progress_checking_source_safety: 'Έλεγχος περιεχομένου πηγών...',
    ai_chatbot_progress_assessing_sources: 'Αξιολόγηση σχετικών πληροφοριών...',
    ai_chatbot_progress_combining_sources: 'Συνδυασμός πηγών...',
    ai_chatbot_progress_summarizing_document: 'Σύνοψη εγγράφου...',
    ai_chatbot_progress_summarizing_discussion: 'Σύνοψη συζήτησης...',
    ai_chatbot_progress_reviewing_health: 'Εξέταση πληροφοριών υγείας...',
    ai_chatbot_progress_updating_symptoms: 'Ενημέρωση πληροφοριών συμπτωμάτων...',
    ai_chatbot_progress_updating_medications: 'Ενημέρωση πληροφοριών φαρμάκων...',
    ai_chatbot_progress_updating_treatments: 'Ενημέρωση πληροφοριών θεραπειών...',
    ai_chatbot_progress_updating_diagnoses: 'Ενημέρωση πληροφοριών διαγνώσεων...',
    ai_chatbot_progress_updating_journal: 'Ενημέρωση ημερολογίου...',
    ai_chatbot_progress_updating_health: 'Ενημέρωση πληροφοριών υγείας...',
    ai_chatbot_progress_preparing_email: 'Προετοιμασία email...',
    ai_chatbot_progress_checking_members: 'Έλεγχος λίστας μελών...',
    ai_chatbot_progress_creating_community: 'Δημιουργία κοινότητας...',
    ai_chatbot_progress_updating_community: 'Ενημέρωση ρυθμίσεων κοινότητας...',
    ai_chatbot_progress_removing_community: 'Επεξεργασία διαγραφής κοινότητας...',
    ai_chatbot_progress_updating_members: 'Ενημέρωση συμμετοχών στην κοινότητα...',
    ai_chatbot_progress_updating_permissions: 'Ενημέρωση δικαιωμάτων μελών...',
    ai_chatbot_progress_checking_notifications: 'Έλεγχος ειδοποιήσεων...',
    ai_chatbot_progress_checking_profile: 'Έλεγχος στοιχείων προφίλ...',
    ai_chatbot_progress_updating_profile: 'Ενημέρωση στοιχείων προφίλ...',
    ai_chatbot_progress_updating_credentials: 'Ενημέρωση στοιχείων ιστορικού...',
    ai_chatbot_progress_updating_journey: 'Ενημέρωση πορείας υγείας...',
    ai_chatbot_progress_updating_preferences: 'Ενημέρωση προτιμήσεων...',
    ai_chatbot_progress_checking_onboarding: 'Έλεγχος προόδου αρχικών βημάτων...',
    ai_chatbot_progress_updating_onboarding: 'Ενημέρωση προόδου αρχικών βημάτων...',
    ai_chatbot_progress_preparing_survey: 'Επεξεργασία ερωτηματολογίου...',
    ai_chatbot_progress_recording_survey: 'Επεξεργασία απάντησης ερωτηματολογίου...',
    ai_chatbot_progress_reviewing_needs: 'Εξέταση αναγκών υποστήριξης...',
    ai_chatbot_progress_updating_needs: 'Ενημέρωση αναγκών υποστήριξης...',
    ai_chatbot_progress_finding_sites: 'Έλεγχος διαθέσιμων ιστοτόπων...',
    ai_chatbot_progress_preparing_response: 'Προετοιμασία μηνύματος απάντησης...',
  },
  es: {
    ai_chatbot_progress_working: 'Procesando tu solicitud...',
    ai_chatbot_progress_searching: 'Buscando fuentes...',
    ai_chatbot_progress_tool_running: 'Utilizando una herramienta...',
    ai_chatbot_progress_tool_returned: 'Procesando el resultado...',
    ai_chatbot_progress_composing: 'Preparando la respuesta...',
    ai_chatbot_progress_validating: 'Comprobando las referencias...',
    ai_chatbot_progress_retrying: 'Intentando responder de nuevo...',
    ai_chatbot_progress_searching_documents: 'Buscando en documentos...',
    ai_chatbot_progress_searching_web: 'Buscando en la web...',
    ai_chatbot_progress_searching_documents_web: 'Buscando en documentos y en la web...',
    ai_chatbot_progress_reviewing_sources: 'Revisando fuentes...',
    ai_chatbot_progress_reading_documents: 'Leyendo documentos relevantes...',
    ai_chatbot_progress_reading_web_sources: 'Leyendo páginas web...',
    ai_chatbot_progress_checking_source_safety: 'Comprobando el contenido de las fuentes...',
    ai_chatbot_progress_assessing_sources: 'Evaluando información relevante...',
    ai_chatbot_progress_combining_sources: 'Reuniendo las fuentes...',
    ai_chatbot_progress_summarizing_document: 'Resumiendo el documento...',
    ai_chatbot_progress_summarizing_discussion: 'Resumiendo la conversación...',
    ai_chatbot_progress_reviewing_health: 'Revisando información de salud...',
    ai_chatbot_progress_updating_symptoms: 'Actualizando información de síntomas...',
    ai_chatbot_progress_updating_medications: 'Actualizando información de medicamentos...',
    ai_chatbot_progress_updating_treatments: 'Actualizando información de tratamientos...',
    ai_chatbot_progress_updating_diagnoses: 'Actualizando información de diagnósticos...',
    ai_chatbot_progress_updating_journal: 'Actualizando el diario...',
    ai_chatbot_progress_updating_health: 'Actualizando información de salud...',
    ai_chatbot_progress_preparing_email: 'Preparando el correo...',
    ai_chatbot_progress_checking_members: 'Comprobando la lista de miembros...',
    ai_chatbot_progress_creating_community: 'Configurando la comunidad...',
    ai_chatbot_progress_updating_community: 'Actualizando ajustes de la comunidad...',
    ai_chatbot_progress_removing_community: 'Procesando la eliminación de la comunidad...',
    ai_chatbot_progress_updating_members: 'Actualizando membresías de la comunidad...',
    ai_chatbot_progress_updating_permissions: 'Actualizando permisos de miembros...',
    ai_chatbot_progress_checking_notifications: 'Comprobando notificaciones...',
    ai_chatbot_progress_checking_profile: 'Comprobando datos del perfil...',
    ai_chatbot_progress_updating_profile: 'Actualizando datos del perfil...',
    ai_chatbot_progress_updating_credentials: 'Actualizando datos de trayectoria...',
    ai_chatbot_progress_updating_journey: 'Actualizando el recorrido de salud...',
    ai_chatbot_progress_updating_preferences: 'Actualizando preferencias...',
    ai_chatbot_progress_checking_onboarding: 'Comprobando los pasos iniciales...',
    ai_chatbot_progress_updating_onboarding: 'Actualizando el progreso de los pasos iniciales...',
    ai_chatbot_progress_preparing_survey: 'Trabajando en la encuesta...',
    ai_chatbot_progress_recording_survey: 'Procesando la respuesta a la encuesta...',
    ai_chatbot_progress_reviewing_needs: 'Revisando necesidades de apoyo...',
    ai_chatbot_progress_updating_needs: 'Actualizando necesidades de apoyo...',
    ai_chatbot_progress_finding_sites: 'Comprobando sitios web disponibles...',
    ai_chatbot_progress_preparing_response: 'Preparando el mensaje de respuesta...',
  },
  pt: {
    ai_chatbot_progress_working: 'A processar o seu pedido...',
    ai_chatbot_progress_searching: 'A procurar fontes...',
    ai_chatbot_progress_tool_running: 'A utilizar uma ferramenta...',
    ai_chatbot_progress_tool_returned: 'A processar o resultado...',
    ai_chatbot_progress_composing: 'A preparar a resposta...',
    ai_chatbot_progress_validating: 'A verificar as referências...',
    ai_chatbot_progress_retrying: 'A tentar responder novamente...',
    ai_chatbot_progress_searching_documents: 'A pesquisar documentos...',
    ai_chatbot_progress_searching_web: 'A pesquisar na web...',
    ai_chatbot_progress_searching_documents_web: 'A pesquisar documentos e a web...',
    ai_chatbot_progress_reviewing_sources: 'A analisar fontes...',
    ai_chatbot_progress_reading_documents: 'A ler documentos relevantes...',
    ai_chatbot_progress_reading_web_sources: 'A ler páginas web...',
    ai_chatbot_progress_checking_source_safety: 'A verificar o conteúdo das fontes...',
    ai_chatbot_progress_assessing_sources: 'A avaliar informações relevantes...',
    ai_chatbot_progress_combining_sources: 'A reunir as fontes...',
    ai_chatbot_progress_summarizing_document: 'A resumir o documento...',
    ai_chatbot_progress_summarizing_discussion: 'A resumir a conversa...',
    ai_chatbot_progress_reviewing_health: 'A analisar informações de saúde...',
    ai_chatbot_progress_updating_symptoms: 'A atualizar informações sobre sintomas...',
    ai_chatbot_progress_updating_medications: 'A atualizar informações sobre medicamentos...',
    ai_chatbot_progress_updating_treatments: 'A atualizar informações sobre tratamentos...',
    ai_chatbot_progress_updating_diagnoses: 'A atualizar informações sobre diagnósticos...',
    ai_chatbot_progress_updating_journal: 'A atualizar o diário...',
    ai_chatbot_progress_updating_health: 'A atualizar informações de saúde...',
    ai_chatbot_progress_preparing_email: 'A preparar o email...',
    ai_chatbot_progress_checking_members: 'A verificar a lista de membros...',
    ai_chatbot_progress_creating_community: 'A configurar a comunidade...',
    ai_chatbot_progress_updating_community: 'A atualizar as definições da comunidade...',
    ai_chatbot_progress_removing_community: 'A processar a remoção da comunidade...',
    ai_chatbot_progress_updating_members: 'A atualizar as adesões à comunidade...',
    ai_chatbot_progress_updating_permissions: 'A atualizar as permissões dos membros...',
    ai_chatbot_progress_checking_notifications: 'A verificar notificações...',
    ai_chatbot_progress_checking_profile: 'A verificar os dados do perfil...',
    ai_chatbot_progress_updating_profile: 'A atualizar os dados do perfil...',
    ai_chatbot_progress_updating_credentials: 'A atualizar informações de percurso...',
    ai_chatbot_progress_updating_journey: 'A atualizar o percurso de saúde...',
    ai_chatbot_progress_updating_preferences: 'A atualizar preferências...',
    ai_chatbot_progress_checking_onboarding: 'A verificar o progresso dos passos iniciais...',
    ai_chatbot_progress_updating_onboarding: 'A atualizar o progresso dos passos iniciais...',
    ai_chatbot_progress_preparing_survey: 'A trabalhar no questionário...',
    ai_chatbot_progress_recording_survey: 'A processar a resposta ao questionário...',
    ai_chatbot_progress_reviewing_needs: 'A analisar necessidades de apoio...',
    ai_chatbot_progress_updating_needs: 'A atualizar necessidades de apoio...',
    ai_chatbot_progress_finding_sites: 'A verificar os sites disponíveis...',
    ai_chatbot_progress_preparing_response: 'A preparar a mensagem de resposta...',
  },
  fi: {
    ai_chatbot_progress_working: 'Käsitellään pyyntöäsi...',
    ai_chatbot_progress_searching: 'Etsitään lähteitä...',
    ai_chatbot_progress_tool_running: 'Käytetään työkalua...',
    ai_chatbot_progress_tool_returned: 'Käsitellään tulosta...',
    ai_chatbot_progress_composing: 'Valmistellaan vastausta...',
    ai_chatbot_progress_validating: 'Tarkistetaan lähdeviitteitä...',
    ai_chatbot_progress_retrying: 'Yritetään vastata uudelleen...',
    ai_chatbot_progress_searching_documents: 'Haetaan asiakirjoista...',
    ai_chatbot_progress_searching_web: 'Haetaan verkosta...',
    ai_chatbot_progress_searching_documents_web: 'Haetaan asiakirjoista ja verkosta...',
    ai_chatbot_progress_reviewing_sources: 'Käydään läpi lähteitä...',
    ai_chatbot_progress_reading_documents: 'Luetaan olennaisia asiakirjoja...',
    ai_chatbot_progress_reading_web_sources: 'Luetaan verkkosivuja...',
    ai_chatbot_progress_checking_source_safety: 'Tarkistetaan lähteiden sisältöä...',
    ai_chatbot_progress_assessing_sources: 'Arvioidaan olennaisia tietoja...',
    ai_chatbot_progress_combining_sources: 'Yhdistetään lähteiden tietoja...',
    ai_chatbot_progress_summarizing_document: 'Tiivistetään asiakirjaa...',
    ai_chatbot_progress_summarizing_discussion: 'Tiivistetään keskustelua...',
    ai_chatbot_progress_reviewing_health: 'Käydään läpi terveystietoja...',
    ai_chatbot_progress_updating_symptoms: 'Päivitetään oireita koskevia tietoja...',
    ai_chatbot_progress_updating_medications: 'Päivitetään lääkitystietoja...',
    ai_chatbot_progress_updating_treatments: 'Päivitetään hoitotietoja...',
    ai_chatbot_progress_updating_diagnoses: 'Päivitetään diagnoositietoja...',
    ai_chatbot_progress_updating_journal: 'Päivitetään päiväkirjaa...',
    ai_chatbot_progress_updating_health: 'Päivitetään terveystietoja...',
    ai_chatbot_progress_preparing_email: 'Valmistellaan sähköpostia...',
    ai_chatbot_progress_checking_members: 'Tarkistetaan jäsenluetteloa...',
    ai_chatbot_progress_creating_community: 'Luodaan yhteisöä...',
    ai_chatbot_progress_updating_community: 'Päivitetään yhteisön asetuksia...',
    ai_chatbot_progress_removing_community: 'Käsitellään yhteisön poistamista...',
    ai_chatbot_progress_updating_members: 'Päivitetään yhteisön jäsenyyksiä...',
    ai_chatbot_progress_updating_permissions: 'Päivitetään jäsenten oikeuksia...',
    ai_chatbot_progress_checking_notifications: 'Tarkistetaan ilmoituksia...',
    ai_chatbot_progress_checking_profile: 'Tarkistetaan profiilitietoja...',
    ai_chatbot_progress_updating_profile: 'Päivitetään profiilitietoja...',
    ai_chatbot_progress_updating_credentials: 'Päivitetään taustatietoja...',
    ai_chatbot_progress_updating_journey: 'Päivitetään terveyspolkua...',
    ai_chatbot_progress_updating_preferences: 'Päivitetään asetuksia...',
    ai_chatbot_progress_checking_onboarding: 'Tarkistetaan aloitusvaiheiden etenemistä...',
    ai_chatbot_progress_updating_onboarding: 'Päivitetään aloitusvaiheiden edistymistä...',
    ai_chatbot_progress_preparing_survey: 'Käsitellään kyselyä...',
    ai_chatbot_progress_recording_survey: 'Käsitellään kyselyvastausta...',
    ai_chatbot_progress_reviewing_needs: 'Käydään läpi tuen tarpeita...',
    ai_chatbot_progress_updating_needs: 'Päivitetään tuen tarpeita...',
    ai_chatbot_progress_finding_sites: 'Tarkistetaan käytettävissä olevia verkkosivustoja...',
    ai_chatbot_progress_preparing_response: 'Valmistellaan vastausviestiä...',
  },
  nb: {
    ai_chatbot_progress_working: 'Behandler forespørselen din...',
    ai_chatbot_progress_searching: 'Søker etter kilder...',
    ai_chatbot_progress_tool_running: 'Bruker et verktøy...',
    ai_chatbot_progress_tool_returned: 'Behandler resultatet...',
    ai_chatbot_progress_composing: 'Forbereder svaret...',
    ai_chatbot_progress_validating: 'Kontrollerer kildehenvisningene...',
    ai_chatbot_progress_retrying: 'Prøver å svare på nytt...',
    ai_chatbot_progress_searching_documents: 'Søker i dokumenter...',
    ai_chatbot_progress_searching_web: 'Søker på nettet...',
    ai_chatbot_progress_searching_documents_web: 'Søker i dokumenter og på nettet...',
    ai_chatbot_progress_reviewing_sources: 'Gjennomgår kilder...',
    ai_chatbot_progress_reading_documents: 'Leser relevante dokumenter...',
    ai_chatbot_progress_reading_web_sources: 'Leser nettsider...',
    ai_chatbot_progress_checking_source_safety: 'Kontrollerer kildeinnhold...',
    ai_chatbot_progress_assessing_sources: 'Vurderer relevant informasjon...',
    ai_chatbot_progress_combining_sources: 'Sammenstiller kildene...',
    ai_chatbot_progress_summarizing_document: 'Oppsummerer dokumentet...',
    ai_chatbot_progress_summarizing_discussion: 'Oppsummerer samtalen...',
    ai_chatbot_progress_reviewing_health: 'Gjennomgår helseopplysninger...',
    ai_chatbot_progress_updating_symptoms: 'Oppdaterer symptomopplysninger...',
    ai_chatbot_progress_updating_medications: 'Oppdaterer legemiddelopplysninger...',
    ai_chatbot_progress_updating_treatments: 'Oppdaterer behandlingsopplysninger...',
    ai_chatbot_progress_updating_diagnoses: 'Oppdaterer diagnoseopplysninger...',
    ai_chatbot_progress_updating_journal: 'Oppdaterer dagboken...',
    ai_chatbot_progress_updating_health: 'Oppdaterer helseopplysninger...',
    ai_chatbot_progress_preparing_email: 'Forbereder e-posten...',
    ai_chatbot_progress_checking_members: 'Sjekker medlemslisten...',
    ai_chatbot_progress_creating_community: 'Oppretter fellesskapet...',
    ai_chatbot_progress_updating_community: 'Oppdaterer fellesskapsinnstillinger...',
    ai_chatbot_progress_removing_community: 'Behandler slettingen av fellesskapet...',
    ai_chatbot_progress_updating_members: 'Oppdaterer medlemskap i fellesskapet...',
    ai_chatbot_progress_updating_permissions: 'Oppdaterer medlemstillatelser...',
    ai_chatbot_progress_checking_notifications: 'Sjekker varsler...',
    ai_chatbot_progress_checking_profile: 'Sjekker profilopplysninger...',
    ai_chatbot_progress_updating_profile: 'Oppdaterer profilopplysninger...',
    ai_chatbot_progress_updating_credentials: 'Oppdaterer bakgrunnsopplysninger...',
    ai_chatbot_progress_updating_journey: 'Oppdaterer helsereisen...',
    ai_chatbot_progress_updating_preferences: 'Oppdaterer preferanser...',
    ai_chatbot_progress_checking_onboarding: 'Sjekker fremdriften i oppstarten...',
    ai_chatbot_progress_updating_onboarding: 'Oppdaterer fremdriften i oppstarten...',
    ai_chatbot_progress_preparing_survey: 'Arbeider med spørreundersøkelsen...',
    ai_chatbot_progress_recording_survey: 'Behandler svaret på spørreundersøkelsen...',
    ai_chatbot_progress_reviewing_needs: 'Gjennomgår støttebehov...',
    ai_chatbot_progress_updating_needs: 'Oppdaterer støttebehov...',
    ai_chatbot_progress_finding_sites: 'Sjekker tilgjengelige nettsteder...',
    ai_chatbot_progress_preparing_response: 'Klargjør svarmeldingen...',
  },
  sv: {
    ai_chatbot_progress_working: 'Behandlar din förfrågan...',
    ai_chatbot_progress_searching: 'Söker efter källor...',
    ai_chatbot_progress_tool_running: 'Använder ett verktyg...',
    ai_chatbot_progress_tool_returned: 'Behandlar resultatet...',
    ai_chatbot_progress_composing: 'Förbereder svaret...',
    ai_chatbot_progress_validating: 'Kontrollerar källhänvisningarna...',
    ai_chatbot_progress_retrying: 'Försöker svara igen...',
    ai_chatbot_progress_searching_documents: 'Söker i dokument...',
    ai_chatbot_progress_searching_web: 'Söker på webben...',
    ai_chatbot_progress_searching_documents_web: 'Söker i dokument och på webben...',
    ai_chatbot_progress_reviewing_sources: 'Går igenom källor...',
    ai_chatbot_progress_reading_documents: 'Läser relevanta dokument...',
    ai_chatbot_progress_reading_web_sources: 'Läser webbsidor...',
    ai_chatbot_progress_checking_source_safety: 'Kontrollerar källornas innehåll...',
    ai_chatbot_progress_assessing_sources: 'Bedömer relevant information...',
    ai_chatbot_progress_combining_sources: 'Sammanställer källorna...',
    ai_chatbot_progress_summarizing_document: 'Sammanfattar dokumentet...',
    ai_chatbot_progress_summarizing_discussion: 'Sammanfattar samtalet...',
    ai_chatbot_progress_reviewing_health: 'Går igenom hälsoinformation...',
    ai_chatbot_progress_updating_symptoms: 'Uppdaterar symtominformation...',
    ai_chatbot_progress_updating_medications: 'Uppdaterar läkemedelsinformation...',
    ai_chatbot_progress_updating_treatments: 'Uppdaterar behandlingsinformation...',
    ai_chatbot_progress_updating_diagnoses: 'Uppdaterar diagnosinformation...',
    ai_chatbot_progress_updating_journal: 'Uppdaterar dagboken...',
    ai_chatbot_progress_updating_health: 'Uppdaterar hälsoinformation...',
    ai_chatbot_progress_preparing_email: 'Förbereder e-postmeddelandet...',
    ai_chatbot_progress_checking_members: 'Kontrollerar medlemslistan...',
    ai_chatbot_progress_creating_community: 'Skapar gemenskapen...',
    ai_chatbot_progress_updating_community: 'Uppdaterar gemenskapens inställningar...',
    ai_chatbot_progress_removing_community: 'Behandlar borttagningen av gemenskapen...',
    ai_chatbot_progress_updating_members: 'Uppdaterar medlemskap i gemenskapen...',
    ai_chatbot_progress_updating_permissions: 'Uppdaterar medlemsbehörigheter...',
    ai_chatbot_progress_checking_notifications: 'Kontrollerar aviseringar...',
    ai_chatbot_progress_checking_profile: 'Kontrollerar profiluppgifter...',
    ai_chatbot_progress_updating_profile: 'Uppdaterar profiluppgifter...',
    ai_chatbot_progress_updating_credentials: 'Uppdaterar bakgrundsuppgifter...',
    ai_chatbot_progress_updating_journey: 'Uppdaterar hälsoresan...',
    ai_chatbot_progress_updating_preferences: 'Uppdaterar preferenser...',
    ai_chatbot_progress_checking_onboarding: 'Kontrollerar framstegen i startstegen...',
    ai_chatbot_progress_updating_onboarding: 'Uppdaterar framstegen i startstegen...',
    ai_chatbot_progress_preparing_survey: 'Arbetar med enkäten...',
    ai_chatbot_progress_recording_survey: 'Behandlar enkätsvaret...',
    ai_chatbot_progress_reviewing_needs: 'Går igenom stödbehov...',
    ai_chatbot_progress_updating_needs: 'Uppdaterar stödbehov...',
    ai_chatbot_progress_finding_sites: 'Kontrollerar tillgängliga webbplatser...',
    ai_chatbot_progress_preparing_response: 'Förbereder svarsmeddelandet...',
  },
  da: {
    ai_chatbot_progress_working: 'Behandler din anmodning...',
    ai_chatbot_progress_searching: 'Søger efter kilder...',
    ai_chatbot_progress_tool_running: 'Bruger et værktøj...',
    ai_chatbot_progress_tool_returned: 'Behandler resultatet...',
    ai_chatbot_progress_composing: 'Forbereder svaret...',
    ai_chatbot_progress_validating: 'Kontrollerer kildehenvisningerne...',
    ai_chatbot_progress_retrying: 'Prøver at svare igen...',
    ai_chatbot_progress_searching_documents: 'Søger i dokumenter...',
    ai_chatbot_progress_searching_web: 'Søger på nettet...',
    ai_chatbot_progress_searching_documents_web: 'Søger i dokumenter og på nettet...',
    ai_chatbot_progress_reviewing_sources: 'Gennemgår kilder...',
    ai_chatbot_progress_reading_documents: 'Læser relevante dokumenter...',
    ai_chatbot_progress_reading_web_sources: 'Læser websider...',
    ai_chatbot_progress_checking_source_safety: 'Kontrollerer kildernes indhold...',
    ai_chatbot_progress_assessing_sources: 'Vurderer relevante oplysninger...',
    ai_chatbot_progress_combining_sources: 'Sammenstiller kilderne...',
    ai_chatbot_progress_summarizing_document: 'Opsummerer dokumentet...',
    ai_chatbot_progress_summarizing_discussion: 'Opsummerer samtalen...',
    ai_chatbot_progress_reviewing_health: 'Gennemgår helbredsoplysninger...',
    ai_chatbot_progress_updating_symptoms: 'Opdaterer symptomoplysninger...',
    ai_chatbot_progress_updating_medications: 'Opdaterer medicinoplysninger...',
    ai_chatbot_progress_updating_treatments: 'Opdaterer behandlingsoplysninger...',
    ai_chatbot_progress_updating_diagnoses: 'Opdaterer diagnoseoplysninger...',
    ai_chatbot_progress_updating_journal: 'Opdaterer dagbogen...',
    ai_chatbot_progress_updating_health: 'Opdaterer helbredsoplysninger...',
    ai_chatbot_progress_preparing_email: 'Forbereder e-mailen...',
    ai_chatbot_progress_checking_members: 'Tjekker medlemslisten...',
    ai_chatbot_progress_creating_community: 'Opretter fællesskabet...',
    ai_chatbot_progress_updating_community: 'Opdaterer fællesskabets indstillinger...',
    ai_chatbot_progress_removing_community: 'Behandler sletningen af fællesskabet...',
    ai_chatbot_progress_updating_members: 'Opdaterer medlemskab i fællesskabet...',
    ai_chatbot_progress_updating_permissions: 'Opdaterer medlemstilladelser...',
    ai_chatbot_progress_checking_notifications: 'Tjekker notifikationer...',
    ai_chatbot_progress_checking_profile: 'Tjekker profiloplysninger...',
    ai_chatbot_progress_updating_profile: 'Opdaterer profiloplysninger...',
    ai_chatbot_progress_updating_credentials: 'Opdaterer baggrundsoplysninger...',
    ai_chatbot_progress_updating_journey: 'Opdaterer sundhedsforløbet...',
    ai_chatbot_progress_updating_preferences: 'Opdaterer præferencer...',
    ai_chatbot_progress_checking_onboarding: 'Tjekker fremskridt i opstarten...',
    ai_chatbot_progress_updating_onboarding: 'Opdaterer fremskridt i opstarten...',
    ai_chatbot_progress_preparing_survey: 'Arbejder med spørgeskemaet...',
    ai_chatbot_progress_recording_survey: 'Behandler svaret på spørgeskemaet...',
    ai_chatbot_progress_reviewing_needs: 'Gennemgår støttebehov...',
    ai_chatbot_progress_updating_needs: 'Opdaterer støttebehov...',
    ai_chatbot_progress_finding_sites: 'Tjekker tilgængelige websteder...',
    ai_chatbot_progress_preparing_response: 'Gør svarbeskeden klar...',
  },
  pl: {
    ai_chatbot_progress_working: 'Przetwarzanie Twojej prośby...',
    ai_chatbot_progress_searching: 'Wyszukiwanie źródeł...',
    ai_chatbot_progress_tool_running: 'Korzystanie z narzędzia...',
    ai_chatbot_progress_tool_returned: 'Przetwarzanie wyniku...',
    ai_chatbot_progress_composing: 'Przygotowywanie odpowiedzi...',
    ai_chatbot_progress_validating: 'Sprawdzanie odwołań do źródeł...',
    ai_chatbot_progress_retrying: 'Ponowna próba odpowiedzi...',
    ai_chatbot_progress_searching_documents: 'Wyszukiwanie w dokumentach...',
    ai_chatbot_progress_searching_web: 'Wyszukiwanie w internecie...',
    ai_chatbot_progress_searching_documents_web: 'Wyszukiwanie w dokumentach i internecie...',
    ai_chatbot_progress_reviewing_sources: 'Przeglądanie źródeł...',
    ai_chatbot_progress_reading_documents: 'Czytanie odpowiednich dokumentów...',
    ai_chatbot_progress_reading_web_sources: 'Czytanie stron internetowych...',
    ai_chatbot_progress_checking_source_safety: 'Sprawdzanie treści źródeł...',
    ai_chatbot_progress_assessing_sources: 'Ocena istotnych informacji...',
    ai_chatbot_progress_combining_sources: 'Łączenie informacji ze źródeł...',
    ai_chatbot_progress_summarizing_document: 'Podsumowywanie dokumentu...',
    ai_chatbot_progress_summarizing_discussion: 'Podsumowywanie dyskusji...',
    ai_chatbot_progress_reviewing_health: 'Przeglądanie informacji zdrowotnych...',
    ai_chatbot_progress_updating_symptoms: 'Aktualizowanie informacji o objawach...',
    ai_chatbot_progress_updating_medications: 'Aktualizowanie informacji o lekach...',
    ai_chatbot_progress_updating_treatments: 'Aktualizowanie informacji o leczeniu...',
    ai_chatbot_progress_updating_diagnoses: 'Aktualizowanie informacji o diagnozach...',
    ai_chatbot_progress_updating_journal: 'Aktualizowanie dziennika...',
    ai_chatbot_progress_updating_health: 'Aktualizowanie informacji zdrowotnych...',
    ai_chatbot_progress_preparing_email: 'Przygotowywanie wiadomości e-mail...',
    ai_chatbot_progress_checking_members: 'Sprawdzanie listy członków...',
    ai_chatbot_progress_creating_community: 'Tworzenie społeczności...',
    ai_chatbot_progress_updating_community: 'Aktualizowanie ustawień społeczności...',
    ai_chatbot_progress_removing_community: 'Przetwarzanie usunięcia społeczności...',
    ai_chatbot_progress_updating_members: 'Aktualizowanie członkostwa w społeczności...',
    ai_chatbot_progress_updating_permissions: 'Aktualizowanie uprawnień członków...',
    ai_chatbot_progress_checking_notifications: 'Sprawdzanie powiadomień...',
    ai_chatbot_progress_checking_profile: 'Sprawdzanie danych profilu...',
    ai_chatbot_progress_updating_profile: 'Aktualizowanie danych profilu...',
    ai_chatbot_progress_updating_credentials: 'Aktualizowanie danych o doświadczeniu...',
    ai_chatbot_progress_updating_journey: 'Aktualizowanie ścieżki zdrowia...',
    ai_chatbot_progress_updating_preferences: 'Aktualizowanie preferencji...',
    ai_chatbot_progress_checking_onboarding: 'Sprawdzanie postępu pierwszych kroków...',
    ai_chatbot_progress_updating_onboarding: 'Aktualizowanie postępu pierwszych kroków...',
    ai_chatbot_progress_preparing_survey: 'Przetwarzanie ankiety...',
    ai_chatbot_progress_recording_survey: 'Przetwarzanie odpowiedzi na ankietę...',
    ai_chatbot_progress_reviewing_needs: 'Przeglądanie potrzeb wsparcia...',
    ai_chatbot_progress_updating_needs: 'Aktualizowanie potrzeb wsparcia...',
    ai_chatbot_progress_finding_sites: 'Sprawdzanie dostępnych stron internetowych...',
    ai_chatbot_progress_preparing_response: 'Przygotowywanie wiadomości zwrotnej...',
  },
  lv: {
    ai_chatbot_progress_working: 'Apstrādājam jūsu pieprasījumu...',
    ai_chatbot_progress_searching: 'Meklējam avotus...',
    ai_chatbot_progress_tool_running: 'Izmantojam rīku...',
    ai_chatbot_progress_tool_returned: 'Apstrādājam rezultātu...',
    ai_chatbot_progress_composing: 'Sagatavojam atbildi...',
    ai_chatbot_progress_validating: 'Pārbaudām atsauces uz avotiem...',
    ai_chatbot_progress_retrying: 'Mēģinām atbildēt vēlreiz...',
    ai_chatbot_progress_searching_documents: 'Meklējam dokumentos...',
    ai_chatbot_progress_searching_web: 'Meklējam tīmeklī...',
    ai_chatbot_progress_searching_documents_web: 'Meklējam dokumentos un tīmeklī...',
    ai_chatbot_progress_reviewing_sources: 'Pārskatām avotus...',
    ai_chatbot_progress_reading_documents: 'Lasām atbilstošos dokumentus...',
    ai_chatbot_progress_reading_web_sources: 'Lasām tīmekļa lapas...',
    ai_chatbot_progress_checking_source_safety: 'Pārbaudām avotu saturu...',
    ai_chatbot_progress_assessing_sources: 'Izvērtējam atbilstošo informāciju...',
    ai_chatbot_progress_combining_sources: 'Apkopojam informāciju no avotiem...',
    ai_chatbot_progress_summarizing_document: 'Apkopojam dokumentu...',
    ai_chatbot_progress_summarizing_discussion: 'Apkopojam diskusiju...',
    ai_chatbot_progress_reviewing_health: 'Pārskatām veselības informāciju...',
    ai_chatbot_progress_updating_symptoms: 'Atjauninām informāciju par simptomiem...',
    ai_chatbot_progress_updating_medications: 'Atjauninām informāciju par medikamentiem...',
    ai_chatbot_progress_updating_treatments: 'Atjauninām informāciju par ārstēšanu...',
    ai_chatbot_progress_updating_diagnoses: 'Atjauninām informāciju par diagnozēm...',
    ai_chatbot_progress_updating_journal: 'Atjauninām dienasgrāmatu...',
    ai_chatbot_progress_updating_health: 'Atjauninām veselības informāciju...',
    ai_chatbot_progress_preparing_email: 'Sagatavojam e-pastu...',
    ai_chatbot_progress_checking_members: 'Pārbaudām dalībnieku sarakstu...',
    ai_chatbot_progress_creating_community: 'Izveidojam kopienu...',
    ai_chatbot_progress_updating_community: 'Atjauninām kopienas iestatījumus...',
    ai_chatbot_progress_removing_community: 'Apstrādājam kopienas dzēšanu...',
    ai_chatbot_progress_updating_members: 'Atjauninām dalību kopienā...',
    ai_chatbot_progress_updating_permissions: 'Atjauninām dalībnieku atļaujas...',
    ai_chatbot_progress_checking_notifications: 'Pārbaudām paziņojumus...',
    ai_chatbot_progress_checking_profile: 'Pārbaudām profila datus...',
    ai_chatbot_progress_updating_profile: 'Atjauninām profila datus...',
    ai_chatbot_progress_updating_credentials: 'Atjauninām pamatinformāciju...',
    ai_chatbot_progress_updating_journey: 'Atjauninām veselības ceļu...',
    ai_chatbot_progress_updating_preferences: 'Atjauninām preferences...',
    ai_chatbot_progress_checking_onboarding: 'Pārbaudām sākotnējo soļu progresu...',
    ai_chatbot_progress_updating_onboarding: 'Atjauninām sākotnējo soļu progresu...',
    ai_chatbot_progress_preparing_survey: 'Apstrādājam aptauju...',
    ai_chatbot_progress_recording_survey: 'Apstrādājam aptaujas atbildi...',
    ai_chatbot_progress_reviewing_needs: 'Pārskatām atbalsta vajadzības...',
    ai_chatbot_progress_updating_needs: 'Atjauninām atbalsta vajadzības...',
    ai_chatbot_progress_finding_sites: 'Pārbaudām pieejamās vietnes...',
    ai_chatbot_progress_preparing_response: 'Sagatavojam atbildes ziņojumu...',
  },
  lt: {
    ai_chatbot_progress_working: 'Apdorojame jūsų užklausą...',
    ai_chatbot_progress_searching: 'Ieškome šaltinių...',
    ai_chatbot_progress_tool_running: 'Naudojame įrankį...',
    ai_chatbot_progress_tool_returned: 'Apdorojame rezultatą...',
    ai_chatbot_progress_composing: 'Ruošiame atsakymą...',
    ai_chatbot_progress_validating: 'Tikriname nuorodas į šaltinius...',
    ai_chatbot_progress_retrying: 'Bandome atsakyti dar kartą...',
    ai_chatbot_progress_searching_documents: 'Ieškome dokumentuose...',
    ai_chatbot_progress_searching_web: 'Ieškome internete...',
    ai_chatbot_progress_searching_documents_web: 'Ieškome dokumentuose ir internete...',
    ai_chatbot_progress_reviewing_sources: 'Peržiūrime šaltinius...',
    ai_chatbot_progress_reading_documents: 'Skaitome susijusius dokumentus...',
    ai_chatbot_progress_reading_web_sources: 'Skaitome tinklalapius...',
    ai_chatbot_progress_checking_source_safety: 'Tikriname šaltinių turinį...',
    ai_chatbot_progress_assessing_sources: 'Vertiname aktualią informaciją...',
    ai_chatbot_progress_combining_sources: 'Apibendriname šaltinių informaciją...',
    ai_chatbot_progress_summarizing_document: 'Apibendriname dokumentą...',
    ai_chatbot_progress_summarizing_discussion: 'Apibendriname diskusiją...',
    ai_chatbot_progress_reviewing_health: 'Peržiūrime sveikatos informaciją...',
    ai_chatbot_progress_updating_symptoms: 'Atnaujiname informaciją apie simptomus...',
    ai_chatbot_progress_updating_medications: 'Atnaujiname informaciją apie vaistus...',
    ai_chatbot_progress_updating_treatments: 'Atnaujiname informaciją apie gydymą...',
    ai_chatbot_progress_updating_diagnoses: 'Atnaujiname informaciją apie diagnozes...',
    ai_chatbot_progress_updating_journal: 'Atnaujiname dienoraštį...',
    ai_chatbot_progress_updating_health: 'Atnaujiname sveikatos informaciją...',
    ai_chatbot_progress_preparing_email: 'Ruošiame el. laišką...',
    ai_chatbot_progress_checking_members: 'Tikriname narių sąrašą...',
    ai_chatbot_progress_creating_community: 'Kuriame bendruomenę...',
    ai_chatbot_progress_updating_community: 'Atnaujiname bendruomenės nustatymus...',
    ai_chatbot_progress_removing_community: 'Apdorojame bendruomenės pašalinimą...',
    ai_chatbot_progress_updating_members: 'Atnaujiname narystę bendruomenėje...',
    ai_chatbot_progress_updating_permissions: 'Atnaujiname narių teises...',
    ai_chatbot_progress_checking_notifications: 'Tikriname pranešimus...',
    ai_chatbot_progress_checking_profile: 'Tikriname profilio duomenis...',
    ai_chatbot_progress_updating_profile: 'Atnaujiname profilio duomenis...',
    ai_chatbot_progress_updating_credentials: 'Atnaujiname biografinius duomenis...',
    ai_chatbot_progress_updating_journey: 'Atnaujiname sveikatos kelią...',
    ai_chatbot_progress_updating_preferences: 'Atnaujiname parinktis...',
    ai_chatbot_progress_checking_onboarding: 'Tikriname pradinių žingsnių pažangą...',
    ai_chatbot_progress_updating_onboarding: 'Atnaujiname pradinių žingsnių pažangą...',
    ai_chatbot_progress_preparing_survey: 'Apdorojame apklausą...',
    ai_chatbot_progress_recording_survey: 'Apdorojame apklausos atsakymą...',
    ai_chatbot_progress_reviewing_needs: 'Peržiūrime pagalbos poreikius...',
    ai_chatbot_progress_updating_needs: 'Atnaujiname pagalbos poreikius...',
    ai_chatbot_progress_finding_sites: 'Tikriname prieinamas svetaines...',
    ai_chatbot_progress_preparing_response: 'Ruošiame atsakymo pranešimą...',
  },
  cs: {
    ai_chatbot_progress_working: 'Zpracováváme váš požadavek...',
    ai_chatbot_progress_searching: 'Hledáme zdroje...',
    ai_chatbot_progress_tool_running: 'Používáme nástroj...',
    ai_chatbot_progress_tool_returned: 'Zpracováváme výsledek...',
    ai_chatbot_progress_composing: 'Připravujeme odpověď...',
    ai_chatbot_progress_validating: 'Kontrolujeme odkazy na zdroje...',
    ai_chatbot_progress_retrying: 'Zkoušíme odpovědět znovu...',
    ai_chatbot_progress_searching_documents: 'Hledáme v dokumentech...',
    ai_chatbot_progress_searching_web: 'Hledáme na webu...',
    ai_chatbot_progress_searching_documents_web: 'Hledáme v dokumentech a na webu...',
    ai_chatbot_progress_reviewing_sources: 'Procházíme zdroje...',
    ai_chatbot_progress_reading_documents: 'Čteme relevantní dokumenty...',
    ai_chatbot_progress_reading_web_sources: 'Čteme webové stránky...',
    ai_chatbot_progress_checking_source_safety: 'Kontrolujeme obsah zdrojů...',
    ai_chatbot_progress_assessing_sources: 'Posuzujeme relevantní informace...',
    ai_chatbot_progress_combining_sources: 'Spojujeme informace ze zdrojů...',
    ai_chatbot_progress_summarizing_document: 'Shrnujeme dokument...',
    ai_chatbot_progress_summarizing_discussion: 'Shrnujeme diskusi...',
    ai_chatbot_progress_reviewing_health: 'Procházíme zdravotní informace...',
    ai_chatbot_progress_updating_symptoms: 'Aktualizujeme informace o příznacích...',
    ai_chatbot_progress_updating_medications: 'Aktualizujeme informace o lécích...',
    ai_chatbot_progress_updating_treatments: 'Aktualizujeme informace o léčbě...',
    ai_chatbot_progress_updating_diagnoses: 'Aktualizujeme informace o diagnózách...',
    ai_chatbot_progress_updating_journal: 'Aktualizujeme deník...',
    ai_chatbot_progress_updating_health: 'Aktualizujeme zdravotní informace...',
    ai_chatbot_progress_preparing_email: 'Připravujeme e-mail...',
    ai_chatbot_progress_checking_members: 'Kontrolujeme seznam členů...',
    ai_chatbot_progress_creating_community: 'Vytváříme komunitu...',
    ai_chatbot_progress_updating_community: 'Aktualizujeme nastavení komunity...',
    ai_chatbot_progress_removing_community: 'Zpracováváme odstranění komunity...',
    ai_chatbot_progress_updating_members: 'Aktualizujeme členství v komunitě...',
    ai_chatbot_progress_updating_permissions: 'Aktualizujeme oprávnění členů...',
    ai_chatbot_progress_checking_notifications: 'Kontrolujeme oznámení...',
    ai_chatbot_progress_checking_profile: 'Kontrolujeme údaje profilu...',
    ai_chatbot_progress_updating_profile: 'Aktualizujeme údaje profilu...',
    ai_chatbot_progress_updating_credentials: 'Aktualizujeme základní údaje...',
    ai_chatbot_progress_updating_journey: 'Aktualizujeme cestu ke zdraví...',
    ai_chatbot_progress_updating_preferences: 'Aktualizujeme předvolby...',
    ai_chatbot_progress_checking_onboarding: 'Kontrolujeme průběh úvodních kroků...',
    ai_chatbot_progress_updating_onboarding: 'Aktualizujeme průběh úvodních kroků...',
    ai_chatbot_progress_preparing_survey: 'Zpracováváme dotazník...',
    ai_chatbot_progress_recording_survey: 'Zpracováváme odpověď na dotazník...',
    ai_chatbot_progress_reviewing_needs: 'Procházíme potřeby podpory...',
    ai_chatbot_progress_updating_needs: 'Aktualizujeme potřeby podpory...',
    ai_chatbot_progress_finding_sites: 'Kontrolujeme dostupné webové stránky...',
    ai_chatbot_progress_preparing_response: 'Připravujeme zprávu s odpovědí...',
  },
  sk: {
    ai_chatbot_progress_working: 'Spracúvame vašu požiadavku...',
    ai_chatbot_progress_searching: 'Hľadáme zdroje...',
    ai_chatbot_progress_tool_running: 'Používame nástroj...',
    ai_chatbot_progress_tool_returned: 'Spracúvame výsledok...',
    ai_chatbot_progress_composing: 'Pripravujeme odpoveď...',
    ai_chatbot_progress_validating: 'Kontrolujeme odkazy na zdroje...',
    ai_chatbot_progress_retrying: 'Skúšame odpovedať znova...',
    ai_chatbot_progress_searching_documents: 'Hľadáme v dokumentoch...',
    ai_chatbot_progress_searching_web: 'Hľadáme na webe...',
    ai_chatbot_progress_searching_documents_web: 'Hľadáme v dokumentoch a na webe...',
    ai_chatbot_progress_reviewing_sources: 'Prezeráme zdroje...',
    ai_chatbot_progress_reading_documents: 'Čítame relevantné dokumenty...',
    ai_chatbot_progress_reading_web_sources: 'Čítame webové stránky...',
    ai_chatbot_progress_checking_source_safety: 'Kontrolujeme obsah zdrojov...',
    ai_chatbot_progress_assessing_sources: 'Posudzujeme relevantné informácie...',
    ai_chatbot_progress_combining_sources: 'Spájame informácie zo zdrojov...',
    ai_chatbot_progress_summarizing_document: 'Zhrňujeme dokument...',
    ai_chatbot_progress_summarizing_discussion: 'Zhrňujeme diskusiu...',
    ai_chatbot_progress_reviewing_health: 'Prezeráme zdravotné informácie...',
    ai_chatbot_progress_updating_symptoms: 'Aktualizujeme informácie o príznakoch...',
    ai_chatbot_progress_updating_medications: 'Aktualizujeme informácie o liekoch...',
    ai_chatbot_progress_updating_treatments: 'Aktualizujeme informácie o liečbe...',
    ai_chatbot_progress_updating_diagnoses: 'Aktualizujeme informácie o diagnózach...',
    ai_chatbot_progress_updating_journal: 'Aktualizujeme denník...',
    ai_chatbot_progress_updating_health: 'Aktualizujeme zdravotné informácie...',
    ai_chatbot_progress_preparing_email: 'Pripravujeme e-mail...',
    ai_chatbot_progress_checking_members: 'Kontrolujeme zoznam členov...',
    ai_chatbot_progress_creating_community: 'Vytvárame komunitu...',
    ai_chatbot_progress_updating_community: 'Aktualizujeme nastavenia komunity...',
    ai_chatbot_progress_removing_community: 'Spracúvame odstránenie komunity...',
    ai_chatbot_progress_updating_members: 'Aktualizujeme členstvo v komunite...',
    ai_chatbot_progress_updating_permissions: 'Aktualizujeme oprávnenia členov...',
    ai_chatbot_progress_checking_notifications: 'Kontrolujeme oznámenia...',
    ai_chatbot_progress_checking_profile: 'Kontrolujeme údaje profilu...',
    ai_chatbot_progress_updating_profile: 'Aktualizujeme údaje profilu...',
    ai_chatbot_progress_updating_credentials: 'Aktualizujeme základné údaje...',
    ai_chatbot_progress_updating_journey: 'Aktualizujeme cestu za zdravím...',
    ai_chatbot_progress_updating_preferences: 'Aktualizujeme predvoľby...',
    ai_chatbot_progress_checking_onboarding: 'Kontrolujeme priebeh úvodných krokov...',
    ai_chatbot_progress_updating_onboarding: 'Aktualizujeme priebeh úvodných krokov...',
    ai_chatbot_progress_preparing_survey: 'Spracúvame dotazník...',
    ai_chatbot_progress_recording_survey: 'Spracúvame odpoveď na dotazník...',
    ai_chatbot_progress_reviewing_needs: 'Prezeráme potreby podpory...',
    ai_chatbot_progress_updating_needs: 'Aktualizujeme potreby podpory...',
    ai_chatbot_progress_finding_sites: 'Kontrolujeme dostupné webové stránky...',
    ai_chatbot_progress_preparing_response: 'Pripravujeme správu s odpoveďou...',
  },
  et: {
    ai_chatbot_progress_working: 'Töötleme teie päringut...',
    ai_chatbot_progress_searching: 'Otsime allikaid...',
    ai_chatbot_progress_tool_running: 'Kasutame tööriista...',
    ai_chatbot_progress_tool_returned: 'Töötleme tulemust...',
    ai_chatbot_progress_composing: 'Valmistame vastust ette...',
    ai_chatbot_progress_validating: 'Kontrollime allikaviiteid...',
    ai_chatbot_progress_retrying: 'Proovime uuesti vastata...',
    ai_chatbot_progress_searching_documents: 'Otsime dokumentidest...',
    ai_chatbot_progress_searching_web: 'Otsime veebist...',
    ai_chatbot_progress_searching_documents_web: 'Otsime dokumentidest ja veebist...',
    ai_chatbot_progress_reviewing_sources: 'Vaatame allikaid läbi...',
    ai_chatbot_progress_reading_documents: 'Loeme asjakohaseid dokumente...',
    ai_chatbot_progress_reading_web_sources: 'Loeme veebilehti...',
    ai_chatbot_progress_checking_source_safety: 'Kontrollime allikate sisu...',
    ai_chatbot_progress_assessing_sources: 'Hindame asjakohast teavet...',
    ai_chatbot_progress_combining_sources: 'Koondame allikate teavet...',
    ai_chatbot_progress_summarizing_document: 'Koostame dokumendi kokkuvõtet...',
    ai_chatbot_progress_summarizing_discussion: 'Koostame arutelu kokkuvõtet...',
    ai_chatbot_progress_reviewing_health: 'Vaatame terviseandmeid läbi...',
    ai_chatbot_progress_updating_symptoms: 'Uuendame sümptomite teavet...',
    ai_chatbot_progress_updating_medications: 'Uuendame ravimite teavet...',
    ai_chatbot_progress_updating_treatments: 'Uuendame raviteavet...',
    ai_chatbot_progress_updating_diagnoses: 'Uuendame diagnooside teavet...',
    ai_chatbot_progress_updating_journal: 'Uuendame päevikut...',
    ai_chatbot_progress_updating_health: 'Uuendame terviseandmeid...',
    ai_chatbot_progress_preparing_email: 'Valmistame e-kirja ette...',
    ai_chatbot_progress_checking_members: 'Kontrollime liikmete loendit...',
    ai_chatbot_progress_creating_community: 'Loome kogukonda...',
    ai_chatbot_progress_updating_community: 'Uuendame kogukonna seadeid...',
    ai_chatbot_progress_removing_community: 'Töötleme kogukonna eemaldamist...',
    ai_chatbot_progress_updating_members: 'Uuendame kogukonna liikmesust...',
    ai_chatbot_progress_updating_permissions: 'Uuendame liikmete õigusi...',
    ai_chatbot_progress_checking_notifications: 'Kontrollime teavitusi...',
    ai_chatbot_progress_checking_profile: 'Kontrollime profiiliandmeid...',
    ai_chatbot_progress_updating_profile: 'Uuendame profiiliandmeid...',
    ai_chatbot_progress_updating_credentials: 'Uuendame taustaandmeid...',
    ai_chatbot_progress_updating_journey: 'Uuendame terviseteekonda...',
    ai_chatbot_progress_updating_preferences: 'Uuendame eelistusi...',
    ai_chatbot_progress_checking_onboarding: 'Kontrollime alustamise edenemist...',
    ai_chatbot_progress_updating_onboarding: 'Uuendame alustamise edenemist...',
    ai_chatbot_progress_preparing_survey: 'Töötleme küsitlust...',
    ai_chatbot_progress_recording_survey: 'Töötleme küsitluse vastust...',
    ai_chatbot_progress_reviewing_needs: 'Vaatame toetusvajadusi läbi...',
    ai_chatbot_progress_updating_needs: 'Uuendame toetusvajadusi...',
    ai_chatbot_progress_finding_sites: 'Kontrollime saadaolevaid veebisaite...',
    ai_chatbot_progress_preparing_response: 'Valmistame vastussõnumit ette...',
  },
};

const SURVEY_BUTTON_OVERRIDES: Record<string, {
  back: string; submit: string; continue: string; help: string; quit: string; close: string;
}> = {
  en: { back: 'Back', submit: 'Submit', continue: 'Continue', help: 'Help', quit: 'Quit survey', close: 'Close' },
  cy: { back: 'Yn ôl', submit: 'Cyflwyno', continue: 'Parhau', help: 'Cymorth', quit: 'Gadael yr arolwg', close: 'Cau' },
  ro: { back: 'Înapoi', submit: 'Trimite', continue: 'Continuă', help: 'Ajutor', quit: 'Ieși din chestionar', close: 'Închide' },
  fr: { back: 'Retour', submit: 'Envoyer', continue: 'Continuer', help: 'Aide', quit: 'Quitter le questionnaire', close: 'Fermer' },
  de: { back: 'Zurück', submit: 'Absenden', continue: 'Weiter', help: 'Hilfe', quit: 'Umfrage verlassen', close: 'Schließen' },
  ka: { back: 'უკან', submit: 'გაგზავნა', continue: 'გაგრძელება', help: 'დახმარება', quit: 'გამოკითხვიდან გასვლა', close: 'დახურვა' },
  nl: { back: 'Terug', submit: 'Verzenden', continue: 'Verder', help: 'Help', quit: 'Enquête verlaten', close: 'Sluiten' },
  it: { back: 'Indietro', submit: 'Invia', continue: 'Continua', help: 'Aiuto', quit: 'Esci dal questionario', close: 'Chiudi' },
  el: { back: 'Πίσω', submit: 'Υποβολή', continue: 'Συνέχεια', help: 'Βοήθεια', quit: 'Έξοδος από το ερωτηματολόγιο', close: 'Κλείσιμο' },
  es: { back: 'Atrás', submit: 'Enviar', continue: 'Continuar', help: 'Ayuda', quit: 'Salir de la encuesta', close: 'Cerrar' },
  pt: { back: 'Voltar', submit: 'Enviar', continue: 'Continuar', help: 'Ajuda', quit: 'Sair do questionário', close: 'Fechar' },
  fi: { back: 'Takaisin', submit: 'Lähetä', continue: 'Jatka', help: 'Ohje', quit: 'Poistu kyselystä', close: 'Sulje' },
  nb: { back: 'Tilbake', submit: 'Send inn', continue: 'Fortsett', help: 'Hjelp', quit: 'Forlat undersøkelsen', close: 'Lukk' },
  sv: { back: 'Tillbaka', submit: 'Skicka', continue: 'Fortsätt', help: 'Hjälp', quit: 'Lämna enkäten', close: 'Stäng' },
  da: { back: 'Tilbage', submit: 'Send', continue: 'Fortsæt', help: 'Hjælp', quit: 'Forlad spørgeskemaet', close: 'Luk' },
  pl: { back: 'Wstecz', submit: 'Wyślij', continue: 'Dalej', help: 'Pomoc', quit: 'Opuść ankietę', close: 'Zamknij' },
  lv: { back: 'Atpakaļ', submit: 'Nosūtīt', continue: 'Turpināt', help: 'Palīdzība', quit: 'Iziet no aptaujas', close: 'Aizvērt' },
  lt: { back: 'Atgal', submit: 'Pateikti', continue: 'Tęsti', help: 'Pagalba', quit: 'Išeiti iš apklausos', close: 'Uždaryti' },
  cs: { back: 'Zpět', submit: 'Odeslat', continue: 'Pokračovat', help: 'Nápověda', quit: 'Opustit dotazník', close: 'Zavřít' },
  sk: { back: 'Späť', submit: 'Odoslať', continue: 'Pokračovať', help: 'Pomoc', quit: 'Opustiť dotazník', close: 'Zavrieť' },
  et: { back: 'Tagasi', submit: 'Saada', continue: 'Jätka', help: 'Abi', quit: 'Välju küsitlusest', close: 'Sulge' },
};

const NATIVE_SURVEY_DURATION_SENTENCES: Record<string, string> = {
  en: 'This survey should take about {{duration}}.',
  cy: "Dylai'r arolwg hwn gymryd tua {{duration}}.",
  fr: 'Ce questionnaire devrait prendre environ {{duration}}.',
  de: 'Diese Umfrage dauert voraussichtlich etwa {{duration}}.',
  ro: 'Completarea acestui chestionar ar trebui să dureze aproximativ {{duration}}.',
  ka: 'ამ გამოკითხვის შევსებას დაახლოებით {{duration}} დასჭირდება.',
  nl: 'Deze enquête duurt ongeveer {{duration}}.',
  it: 'Questo questionario dovrebbe richiedere circa {{duration}}.',
  el: 'Η συμπλήρωση αυτού του ερωτηματολογίου θα διαρκέσει περίπου {{duration}}.',
  es: 'Completar esta encuesta debería llevar unos {{duration}}.',
  pt: 'O preenchimento deste questionário deverá demorar cerca de {{duration}}.',
  fi: 'Tähän kyselyyn vastaaminen kestää noin {{duration}}.',
  nb: 'Denne spørreundersøkelsen tar omtrent {{duration}}.',
  sv: 'Den här enkäten tar ungefär {{duration}}.',
  da: 'Dette spørgeskema tager cirka {{duration}}.',
  pl: 'Wypełnienie tej ankiety powinno zająć około {{duration}}.',
  lv: 'Šīs aptaujas aizpildīšana aizņems aptuveni {{duration}}.',
  lt: 'Šios apklausos pildymas užtruks maždaug {{duration}}.',
  cs: 'Vyplnění tohoto dotazníku zabere přibližně {{duration}}.',
  sk: 'Vyplnenie tohto dotazníka potrvá približne {{duration}}.',
  et: 'Selle küsitluse täitmine võtab umbes {{duration}}.',
};

const NATIVE_SURVEY_UI_OVERRIDES: Record<string, Record<string, string>> = {
  ro: {
    native_survey_voice_controls: 'Comenzi vocale',
    native_survey_voice_free_text: 'Rostiți răspunsul. Când sunteți gata, rostiți o comandă disponibilă „{{callWord}}, …”.',
    native_survey_voice_choice: 'Rostiți numărul unei opțiuni sau textul exact. Când sunteți gata, rostiți o comandă disponibilă „{{callWord}}, …”.',
    native_survey_voice_quit_confirmation: 'Repetați „{{quit}}” în decurs de 10 secunde pentru confirmare sau rostiți „{{cancel}}”.',
    native_survey_question_number: 'Întrebarea {{number}}',
    native_survey_question_heading: 'Întrebarea {{number}} - {{title}}',
    native_survey_waiting: 'Îți evaluăm răspunsul',
    native_survey_clarification_context_hint: 'Adăugați detalii care leagă răspunsul de întrebare. Acest context permite ca {{name}} să înțeleagă ce vreți să spuneți și să vă evalueze răspunsul corect.',
    native_survey_clarification_placeholder: 'Adăugați context despre răspuns',
    ai_chatbot_expand_assistant: 'Extinde lansatorul {{name}}',
    ai_chatbot_collapse_assistant: 'Restrânge lansatorul {{name}}',
  },
  fr: {
    native_survey_voice_controls: 'Commandes vocales',
    native_survey_voice_free_text: 'Dictez votre réponse. Lorsque vous êtes prêt, dites une commande « {{callWord}}, … » disponible.',
    native_survey_voice_choice: 'Dites le numéro d’une option ou son texte exact. Lorsque vous êtes prêt, dites une commande « {{callWord}}, … » disponible.',
    native_survey_voice_quit_confirmation: 'Répétez « {{quit}} » dans les 10 secondes pour confirmer, ou dites « {{cancel}} ».',
    native_survey_submit: 'Envoyer',
    native_survey_back: 'Retour',
    native_survey_help: 'Aide',
    native_survey_skip: 'Quitter le questionnaire',
    native_survey_finish: 'Terminer',
    native_survey_question_number: 'Question {{number}}',
    native_survey_question_heading: 'Question {{number}} - {{title}}',
    native_survey_waiting: 'Évaluation de votre réponse',
    native_survey_clarification_context_hint: 'Ajoutez des détails qui relient votre réponse à la question. Ce contexte aide {{name}} à comprendre ce que vous voulez dire et à évaluer votre réponse avec précision.',
    native_survey_clarification_placeholder: 'Ajoutez du contexte à votre réponse',
    ai_chatbot_expand_assistant: 'Développer le lanceur {{name}}',
    ai_chatbot_collapse_assistant: 'Réduire le lanceur {{name}}',
  },
  de: {
    native_survey_voice_controls: 'Sprachsteuerung',
    native_survey_voice_free_text: 'Sprechen Sie Ihre Antwort. Sagen Sie anschließend einen verfügbaren „{{callWord}}, …“-Befehl.',
    native_survey_voice_choice: 'Sagen Sie die Nummer einer Option oder deren genauen Text. Sagen Sie anschließend einen verfügbaren „{{callWord}}, …“-Befehl.',
    native_survey_voice_quit_confirmation: 'Wiederholen Sie „{{quit}}“ innerhalb von 10 Sekunden zur Bestätigung oder sagen Sie „{{cancel}}“.',
    native_survey_question_number: 'Frage {{number}}',
    native_survey_question_heading: 'Frage {{number}} - {{title}}',
    native_survey_waiting: 'Ihre Antwort wird ausgewertet',
    native_survey_clarification_context_hint: 'Fügen Sie Details hinzu, die Ihre Antwort mit der Frage verbinden. Dieser Kontext hilft {{name}}, Ihre Aussage zu verstehen und Ihre Antwort genau zu bewerten.',
    native_survey_clarification_placeholder: 'Kontext zu Ihrer Antwort hinzufügen',
    ai_chatbot_expand_assistant: '{{name}}-Starter erweitern',
    ai_chatbot_collapse_assistant: '{{name}}-Starter reduzieren',
  },
  ka: {
    native_survey_voice_controls: 'ხმოვანი მართვა',
    native_survey_voice_free_text: 'წარმოთქვით თქვენი პასუხი. როცა მზად იქნებით, თქვით ხელმისაწვდომი ბრძანება „{{callWord}}, …“.',
    native_survey_voice_choice: 'თქვით ვარიანტის ნომერი ან მისი ზუსტი ტექსტი. როცა მზად იქნებით, თქვით ხელმისაწვდომი ბრძანება „{{callWord}}, …“.',
    native_survey_voice_quit_confirmation: 'დასადასტურებლად 10 წამში გაიმეორეთ „{{quit}}“, ან თქვით „{{cancel}}“.',
    native_survey_question_number: 'კითხვა {{number}}',
    native_survey_question_heading: 'კითხვა {{number}} - {{title}}',
    native_survey_waiting: 'თქვენს პასუხს ვაფასებთ',
    native_survey_clarification_context_hint: 'დაამატეთ დეტალები, რომლებიც თქვენს პასუხს კითხვასთან აკავშირებს. ამ კონტექსტით {{name}} შეძლებს გაიგოს თქვენი აზრი და ზუსტად შეაფასოს პასუხი.',
    native_survey_clarification_placeholder: 'დაამატეთ თქვენი პასუხის კონტექსტი',
    ai_chatbot_expand_assistant: '{{name}} გამშვების გაშლა',
    ai_chatbot_collapse_assistant: '{{name}} გამშვების აკეცვა',
  },
  cy: {
    native_survey_voice_controls: 'Rheolyddion llais',
    native_survey_voice_free_text: 'Dywedwch eich ateb. Dywedwch orchymyn ‘{{callWord}}, …’ sydd ar gael pan fyddwch yn barod.',
    native_survey_voice_choice: 'Dywedwch rif opsiwn neu ei union destun. Dywedwch orchymyn ‘{{callWord}}, …’ sydd ar gael pan fyddwch yn barod.',
    native_survey_voice_quit_confirmation: 'Ailadroddwch ‘{{quit}}’ o fewn 10 eiliad i gadarnhau, neu dywedwch ‘{{cancel}}’.',
    native_survey_question_number: 'Cwestiwn {{number}}',
    native_survey_question_heading: 'Cwestiwn {{number}} - {{title}}',
    native_survey_waiting: 'Yn gwerthuso eich ymateb',
    native_survey_clarification_context_hint: "Ychwanegwch fanylion sy'n cysylltu eich ateb â'r cwestiwn. Mae'r cyd-destun hwn yn helpu {{name}} i ddeall eich ystyr ac asesu eich ateb yn gywir.",
    native_survey_clarification_placeholder: 'Ychwanegwch gyd-destun am eich ateb',
    ai_chatbot_expand_assistant: 'Ehangu lansiwr {{name}}',
    ai_chatbot_collapse_assistant: 'Crebachu lansiwr {{name}}',
  },
  nl: {
    native_survey_voice_controls: 'Spraakbediening',
    native_survey_voice_free_text: 'Spreek uw antwoord uit. Zeg daarna een beschikbare opdracht ‘{{callWord}}, …’.',
    native_survey_voice_choice: 'Zeg het nummer van een optie of de exacte tekst. Zeg daarna een beschikbare opdracht ‘{{callWord}}, …’.',
    native_survey_voice_quit_confirmation: 'Herhaal ‘{{quit}}’ binnen 10 seconden om te bevestigen, of zeg ‘{{cancel}}’.',
    native_survey_question_number: 'Vraag {{number}}',
    native_survey_question_heading: 'Vraag {{number}} - {{title}}',
    native_survey_waiting: 'Uw antwoord wordt beoordeeld',
    native_survey_clarification_context_hint: 'Voeg details toe die uw antwoord met de vraag verbinden. Deze context helpt {{name}} te begrijpen wat u bedoelt en uw antwoord nauwkeurig te beoordelen.',
    native_survey_clarification_placeholder: 'Voeg context over uw antwoord toe',
    ai_chatbot_expand_assistant: '{{name}}-starter uitvouwen',
    ai_chatbot_collapse_assistant: '{{name}}-starter samenvouwen',
  },
  it: {
    native_survey_voice_controls: 'Comandi vocali',
    native_survey_voice_free_text: 'Pronuncia la risposta. Quando hai finito, pronuncia un comando disponibile “{{callWord}}, …”.',
    native_survey_voice_choice: 'Pronuncia il numero di un’opzione o il suo testo esatto. Quando hai finito, pronuncia un comando disponibile “{{callWord}}, …”.',
    native_survey_voice_quit_confirmation: 'Ripeti “{{quit}}” entro 10 secondi per confermare, oppure pronuncia “{{cancel}}”.',
    native_survey_question_number: 'Domanda {{number}}',
    native_survey_question_heading: 'Domanda {{number}} - {{title}}',
    native_survey_waiting: 'Stiamo valutando la tua risposta',
    native_survey_clarification_context_hint: 'Aggiungi dettagli che colleghino la tua risposta alla domanda. Questo contesto aiuta {{name}} a capire cosa intendi e a valutare accuratamente la risposta.',
    native_survey_clarification_placeholder: 'Aggiungi contesto alla tua risposta',
    ai_chatbot_expand_assistant: 'Espandi il pulsante {{name}}',
    ai_chatbot_collapse_assistant: 'Comprimi il pulsante {{name}}',
  },
  el: {
    native_survey_voice_controls: 'Φωνητικά στοιχεία ελέγχου',
    native_survey_voice_free_text: 'Πείτε την απάντησή σας. Όταν είστε έτοιμοι, πείτε μια διαθέσιμη εντολή «{{callWord}}, …».',
    native_survey_voice_choice: 'Πείτε τον αριθμό μιας επιλογής ή το ακριβές κείμενό της. Όταν είστε έτοιμοι, πείτε μια διαθέσιμη εντολή «{{callWord}}, …».',
    native_survey_voice_quit_confirmation: 'Επαναλάβετε «{{quit}}» μέσα σε 10 δευτερόλεπτα για επιβεβαίωση ή πείτε «{{cancel}}».',
    native_survey_question_number: 'Ερώτηση {{number}}',
    native_survey_question_heading: 'Ερώτηση {{number}} - {{title}}',
    native_survey_waiting: 'Αξιολογούμε την απάντησή σας',
    native_survey_clarification_context_hint: 'Προσθέστε λεπτομέρειες που συνδέουν την απάντησή σας με την ερώτηση. Με αυτό το πλαίσιο, {{name}} μπορεί να κατανοήσει τι εννοείτε και να αξιολογήσει με ακρίβεια την απάντησή σας.',
    native_survey_clarification_placeholder: 'Προσθέστε πλαίσιο για την απάντησή σας',
    ai_chatbot_expand_assistant: 'Ανάπτυξη εκκίνησης {{name}}',
    ai_chatbot_collapse_assistant: 'Σύμπτυξη εκκίνησης {{name}}',
  },
  es: {
    native_survey_voice_controls: 'Controles de voz',
    native_survey_voice_free_text: 'Di tu respuesta. Cuando termines, di un comando «{{callWord}}, …» disponible.',
    native_survey_voice_choice: 'Di el número de una opción o su texto exacto. Cuando termines, di un comando «{{callWord}}, …» disponible.',
    native_survey_voice_quit_confirmation: 'Repite «{{quit}}» en un plazo de 10 segundos para confirmar o di «{{cancel}}».',
    native_survey_question_number: 'Pregunta {{number}}',
    native_survey_question_heading: 'Pregunta {{number}} - {{title}}',
    native_survey_waiting: 'Estamos evaluando tu respuesta',
    native_survey_clarification_context_hint: 'Añade detalles que relacionen tu respuesta con la pregunta. Este contexto ayuda a {{name}} a entender lo que quieres decir y a evaluar tu respuesta con precisión.',
    native_survey_clarification_placeholder: 'Añade contexto sobre tu respuesta',
    ai_chatbot_expand_assistant: 'Expandir el iniciador de {{name}}',
    ai_chatbot_collapse_assistant: 'Contraer el iniciador de {{name}}',
  },
  pt: {
    native_survey_voice_controls: 'Controlos de voz',
    native_survey_voice_free_text: 'Diga a sua resposta. Quando terminar, diga um comando disponível “{{callWord}}, …”.',
    native_survey_voice_choice: 'Diga o número de uma opção ou o texto exato. Quando terminar, diga um comando disponível “{{callWord}}, …”.',
    native_survey_voice_quit_confirmation: 'Repita “{{quit}}” no prazo de 10 segundos para confirmar ou diga “{{cancel}}”.',
    native_survey_question_number: 'Pergunta {{number}}',
    native_survey_question_heading: 'Pergunta {{number}} - {{title}}',
    native_survey_waiting: 'Estamos a avaliar a sua resposta',
    native_survey_clarification_context_hint: 'Adicione detalhes que liguem a sua resposta à pergunta. Este contexto ajuda {{name}} a compreender o que quer dizer e a avaliar a sua resposta com precisão.',
    native_survey_clarification_placeholder: 'Adicione contexto à sua resposta',
    ai_chatbot_expand_assistant: 'Expandir o iniciador de {{name}}',
    ai_chatbot_collapse_assistant: 'Recolher o iniciador de {{name}}',
  },
  fi: {
    native_survey_voice_controls: 'Ääniohjaus',
    native_survey_voice_free_text: 'Sano vastauksesi. Kun olet valmis, sano jokin käytettävissä oleva ”{{callWord}}, …” -komento.',
    native_survey_voice_choice: 'Sano vaihtoehdon numero tai sen tarkka teksti. Kun olet valmis, sano jokin käytettävissä oleva ”{{callWord}}, …” -komento.',
    native_survey_voice_quit_confirmation: 'Vahvista toistamalla ”{{quit}}” 10 sekunnin kuluessa tai sano ”{{cancel}}”.',
    native_survey_question_number: 'Kysymys {{number}}',
    native_survey_question_heading: 'Kysymys {{number}} - {{title}}',
    native_survey_waiting: 'Vastaustasi arvioidaan',
    native_survey_clarification_context_hint: 'Lisää yksityiskohtia, jotka yhdistävät vastauksesi kysymykseen. Tämän taustan avulla {{name}} voi ymmärtää, mitä tarkoitat, ja arvioida vastauksesi tarkasti.',
    native_survey_clarification_placeholder: 'Lisää vastauksesi taustaa',
    ai_chatbot_expand_assistant: 'Laajenna {{name}}-käynnistin',
    ai_chatbot_collapse_assistant: 'Pienennä {{name}}-käynnistin',
  },
  nb: {
    native_survey_voice_controls: 'Talekontroller',
    native_survey_voice_free_text: 'Si svaret ditt. Når du er ferdig, sier du en tilgjengelig «{{callWord}}, …»-kommando.',
    native_survey_voice_choice: 'Si nummeret til et alternativ eller den nøyaktige teksten. Når du er ferdig, sier du en tilgjengelig «{{callWord}}, …»-kommando.',
    native_survey_voice_quit_confirmation: 'Gjenta «{{quit}}» innen 10 sekunder for å bekrefte, eller si «{{cancel}}».',
    native_survey_question_number: 'Spørsmål {{number}}',
    native_survey_question_heading: 'Spørsmål {{number}} - {{title}}',
    native_survey_waiting: 'Vi vurderer svaret ditt',
    native_survey_clarification_context_hint: 'Legg til detaljer som knytter svaret ditt til spørsmålet. Denne konteksten hjelper {{name}} med å forstå hva du mener og vurdere svaret ditt nøyaktig.',
    native_survey_clarification_placeholder: 'Legg til kontekst om svaret ditt',
    ai_chatbot_expand_assistant: 'Utvid {{name}}-starteren',
    ai_chatbot_collapse_assistant: 'Skjul {{name}}-starteren',
  },
  sv: {
    native_survey_voice_controls: 'Röststyrning',
    native_survey_voice_free_text: 'Säg ditt svar. När du är klar säger du ett tillgängligt ”{{callWord}}, …”-kommando.',
    native_survey_voice_choice: 'Säg numret på ett alternativ eller dess exakta text. När du är klar säger du ett tillgängligt ”{{callWord}}, …”-kommando.',
    native_survey_voice_quit_confirmation: 'Upprepa ”{{quit}}” inom 10 sekunder för att bekräfta eller säg ”{{cancel}}”.',
    native_survey_question_number: 'Fråga {{number}}',
    native_survey_question_heading: 'Fråga {{number}} - {{title}}',
    native_survey_waiting: 'Vi utvärderar ditt svar',
    native_survey_clarification_context_hint: 'Lägg till detaljer som kopplar ditt svar till frågan. Den här bakgrunden hjälper {{name}} att förstå vad du menar och bedöma ditt svar korrekt.',
    native_survey_clarification_placeholder: 'Lägg till bakgrund till ditt svar',
    ai_chatbot_expand_assistant: 'Expandera {{name}}-startaren',
    ai_chatbot_collapse_assistant: 'Minimera {{name}}-startaren',
  },
  da: {
    native_survey_voice_controls: 'Stemmestyring',
    native_survey_voice_free_text: 'Sig dit svar. Når du er færdig, skal du sige en tilgængelig “{{callWord}}, …”-kommando.',
    native_survey_voice_choice: 'Sig nummeret på en mulighed eller dens nøjagtige tekst. Når du er færdig, skal du sige en tilgængelig “{{callWord}}, …”-kommando.',
    native_survey_voice_quit_confirmation: 'Gentag “{{quit}}” inden for 10 sekunder for at bekræfte, eller sig “{{cancel}}”.',
    native_survey_question_number: 'Spørgsmål {{number}}',
    native_survey_question_heading: 'Spørgsmål {{number}} - {{title}}',
    native_survey_waiting: 'Vi vurderer dit svar',
    native_survey_clarification_context_hint: 'Tilføj detaljer, der forbinder dit svar med spørgsmålet. Denne kontekst hjælper {{name}} med at forstå, hvad du mener, og vurdere dit svar præcist.',
    native_survey_clarification_placeholder: 'Tilføj kontekst til dit svar',
    ai_chatbot_expand_assistant: 'Udvid {{name}}-starteren',
    ai_chatbot_collapse_assistant: 'Skjul {{name}}-starteren',
  },
  pl: {
    native_survey_voice_controls: 'Sterowanie głosowe',
    native_survey_voice_free_text: 'Wypowiedz odpowiedź. Gdy skończysz, wypowiedz dostępną komendę „{{callWord}}, …”.',
    native_survey_voice_choice: 'Wypowiedz numer opcji lub jej dokładny tekst. Gdy skończysz, wypowiedz dostępną komendę „{{callWord}}, …”.',
    native_survey_voice_quit_confirmation: 'Powtórz „{{quit}}” w ciągu 10 sekund, aby potwierdzić, albo powiedz „{{cancel}}”.',
    native_survey_question_number: 'Pytanie {{number}}',
    native_survey_question_heading: 'Pytanie {{number}} - {{title}}',
    native_survey_waiting: 'Oceniamy Twoją odpowiedź',
    native_survey_clarification_context_hint: 'Dodaj szczegóły, które łączą Twoją odpowiedź z pytaniem. Dzięki temu {{name}} może zrozumieć, co masz na myśli, i dokładnie ocenić odpowiedź.',
    native_survey_clarification_placeholder: 'Dodaj kontekst do swojej odpowiedzi',
    ai_chatbot_expand_assistant: 'Rozwiń przycisk {{name}}',
    ai_chatbot_collapse_assistant: 'Zwiń przycisk {{name}}',
  },
  lv: {
    native_survey_voice_controls: 'Balss vadība',
    native_survey_voice_free_text: 'Pasakiet savu atbildi. Kad esat pabeidzis, pasakiet pieejamu komandu “{{callWord}}, …”.',
    native_survey_voice_choice: 'Pasakiet opcijas numuru vai precīzu tās tekstu. Kad esat pabeidzis, pasakiet pieejamu komandu “{{callWord}}, …”.',
    native_survey_voice_quit_confirmation: 'Atkārtojiet “{{quit}}” 10 sekunžu laikā, lai apstiprinātu, vai sakiet “{{cancel}}”.',
    native_survey_question_number: 'Jautājums {{number}}',
    native_survey_question_heading: 'Jautājums {{number}} - {{title}}',
    native_survey_waiting: 'Izvērtējam jūsu atbildi',
    native_survey_clarification_context_hint: 'Pievienojiet informāciju, kas sasaista jūsu atbildi ar jautājumu. Izmantojot šo kontekstu, {{name}} var saprast jūsu domu un precīzi novērtēt atbildi.',
    native_survey_clarification_placeholder: 'Pievienojiet atbildes kontekstu',
    ai_chatbot_expand_assistant: 'Izvērst {{name}} palaidēju',
    ai_chatbot_collapse_assistant: 'Sakļaut {{name}} palaidēju',
  },
  lt: {
    native_survey_voice_controls: 'Valdymas balsu',
    native_survey_voice_free_text: 'Pasakykite savo atsakymą. Baigę pasakykite galimą komandą „{{callWord}}, …“.',
    native_survey_voice_choice: 'Pasakykite parinkties numerį arba tikslų jos tekstą. Baigę pasakykite galimą komandą „{{callWord}}, …“.',
    native_survey_voice_quit_confirmation: 'Per 10 sekundžių pakartokite „{{quit}}“, kad patvirtintumėte, arba sakykite „{{cancel}}“.',
    native_survey_question_number: 'Klausimas {{number}}',
    native_survey_question_heading: 'Klausimas {{number}} - {{title}}',
    native_survey_waiting: 'Vertiname jūsų atsakymą',
    native_survey_clarification_context_hint: 'Pridėkite informacijos, siejančios jūsų atsakymą su klausimu. Šio konteksto dėka {{name}} gali suprasti, ką turite omenyje, ir tiksliai įvertinti atsakymą.',
    native_survey_clarification_placeholder: 'Pridėkite atsakymo kontekstą',
    ai_chatbot_expand_assistant: 'Išskleisti {{name}} paleidiklį',
    ai_chatbot_collapse_assistant: 'Suskleisti {{name}} paleidiklį',
  },
  cs: {
    native_survey_voice_controls: 'Hlasové ovládání',
    native_survey_voice_free_text: 'Řekněte svou odpověď. Až budete hotovi, řekněte dostupný příkaz „{{callWord}}, …“.',
    native_survey_voice_choice: 'Řekněte číslo možnosti nebo její přesný text. Až budete hotovi, řekněte dostupný příkaz „{{callWord}}, …“.',
    native_survey_voice_quit_confirmation: 'Potvrďte zopakováním „{{quit}}“ do 10 sekund, nebo řekněte „{{cancel}}“.',
    native_survey_question_number: 'Otázka {{number}}',
    native_survey_question_heading: 'Otázka {{number}} - {{title}}',
    native_survey_waiting: 'Vyhodnocujeme vaši odpověď',
    native_survey_clarification_context_hint: 'Přidejte podrobnosti, které propojí vaši odpověď s otázkou. Díky tomuto kontextu může {{name}} pochopit, co máte na mysli, a přesně vyhodnotit vaši odpověď.',
    native_survey_clarification_placeholder: 'Přidejte kontext ke své odpovědi',
    ai_chatbot_expand_assistant: 'Rozbalit spouštěč {{name}}',
    ai_chatbot_collapse_assistant: 'Sbalit spouštěč {{name}}',
  },
  sk: {
    native_survey_voice_controls: 'Hlasové ovládanie',
    native_survey_voice_free_text: 'Povedzte svoju odpoveď. Keď skončíte, povedzte dostupný príkaz „{{callWord}}, …“.',
    native_survey_voice_choice: 'Povedzte číslo možnosti alebo jej presný text. Keď skončíte, povedzte dostupný príkaz „{{callWord}}, …“.',
    native_survey_voice_quit_confirmation: 'Potvrďte zopakovaním „{{quit}}“ do 10 sekúnd alebo povedzte „{{cancel}}“.',
    native_survey_question_number: 'Otázka {{number}}',
    native_survey_question_heading: 'Otázka {{number}} - {{title}}',
    native_survey_waiting: 'Vyhodnocujeme vašu odpoveď',
    native_survey_clarification_context_hint: 'Pridajte podrobnosti, ktoré prepoja vašu odpoveď s otázkou. Vďaka tomuto kontextu môže {{name}} pochopiť, čo máte na mysli, a presne vyhodnotiť vašu odpoveď.',
    native_survey_clarification_placeholder: 'Pridajte kontext k svojej odpovedi',
    ai_chatbot_expand_assistant: 'Rozbaliť spúšťač {{name}}',
    ai_chatbot_collapse_assistant: 'Zbaliť spúšťač {{name}}',
  },
  et: {
    native_survey_voice_controls: 'Hääljuhtimine',
    native_survey_voice_free_text: 'Öelge oma vastus. Kui olete valmis, öelge saadaolev käsk „{{callWord}}, …”.',
    native_survey_voice_choice: 'Öelge valiku number või selle täpne tekst. Kui olete valmis, öelge saadaolev käsk „{{callWord}}, …”.',
    native_survey_voice_quit_confirmation: 'Kinnitamiseks korrake 10 sekundi jooksul „{{quit}}” või öelge „{{cancel}}”.',
    native_survey_question_number: 'Küsimus {{number}}',
    native_survey_question_heading: 'Küsimus {{number}} - {{title}}',
    native_survey_waiting: 'Hindame teie vastust',
    native_survey_clarification_context_hint: 'Lisage üksikasju, mis seovad teie vastuse küsimusega. Selle konteksti abil saab {{name}} mõista, mida te mõtlete, ja teie vastust täpselt hinnata.',
    native_survey_clarification_placeholder: 'Lisage vastuse kohta konteksti',
    ai_chatbot_expand_assistant: 'Laienda {{name}} käivitit',
    ai_chatbot_collapse_assistant: 'Ahenda {{name}} käivitit',
  },
};

// Keyed by i18next short language code (same codes used in LANGUAGE_CODE_MAP).
// Only non-English languages need entries here; English is the source of truth.
const TRANSLATION_OVERRIDES: Record<string, Record<string, string>> = {
  // Welsh — already correct in cy-GB.json, included for completeness
  cy: {
    ai_context_mode_all_rooms: 'Awtomatig',
    like: 'Hoffi',
    likes: 'Hoffi',
    community_post_like: 'Hoffi',
    community_post_likes: 'Hoffi',
    reaction_like_label: 'Hoffi',
    story_allow_likes: 'Caniatáu hoff bethau',
    comment: 'Sylw',
    comments: 'Sylwadau',
    reaction_care_label: 'Gofalu',
    reaction_helpful_label: 'Defnyddiol',
    story_draft: 'Drafft',
    stories_drafts: 'Drafftiau',
    checker: 'Gwiriwr',
    health_picture_complete: 'Cwblhau',
    health_picture_diagnosis_type_nonmedical: 'Dan amheuaeth',
    preview: 'Rhagolwg',
    journal_name: 'Dyddlyfr',
    community: 'Ystafelloedd',
    stories: 'Straeon',
    profile_widget_journey: 'Teithiau',
    browser: 'Porwr',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Cysylltu',
    community_room_type_hub: 'Hyb',
    subgroups: 'Is-grwpiau',
    max: 'uchaf',
    companions_title: 'Cyfeillion',
    health_picture_ongoing: 'Parhaus',
    poll_period_unlimited: 'Diderfyn',
    joined_rooms_title: 'Ystafelloedd wedi ymuno yn yr Hwb hwn',
    // Draft sentences
    story_save_as_draft: 'Cadw fel drafft',
    story_saved_draft_snackbar: 'Drafft wedi ei gadw yn llwyddiannus',
    story_save_draft_success_dialog_content: 'Mae eich drafft wedi ei gadw yn llwyddiannus!',
    my_stories_empty_drafts_first_line: 'Nid oes gennych unrhyw straeon mewn Drafft.',
    // Journal sentences
    health_picture_add_journal: 'Ychwanegu cofnod dyddlyfr',
    health_picture_edit_journal: 'Golygu cofnod dyddlyfr',
    health_picture_journal: 'Fy Nyddlyfrau',
    health_journey_journal_title: 'Dyddlyfr',
    health_journey_journal_date: 'Dyddiad dyddlyfr',
    delete_journal_confirmation_title: 'Dileu dyddlyfr',
    delete_journal_confirmation_description: "Ydych chi'n siŵr eich bod am ddileu'r dyddlyfr hwn? Ni ellir dadwneud y weithred hon.",
    journal_deleted_successfully: 'Dyddlyfr wedi ei ddileu yn llwyddiannus.',
    error_deleting_journal: "Roedd problem wrth ddileu'r dyddlyfr. Rhowch gynnig arall arni.",
    // Stories sentences
    stories_first_tab: 'Pob stori',
    stories_third_tab: 'Fy straeon',
    stories_suggested: 'Straeon a awgrymir',
    stories_my_stories: 'Fy straeon',
    // Companions sentences
    companions_in_room_title: "Cyfeillion yn yr ystafell",
    // Ongoing sentences
    health_picture_snackbar_adding_ongoing_symptom: "Rydych eisoes wedi ychwanegu'r symptom hwn ac mae'n dal yn barhaus! Diweddarwch y cofnod hwnnw!",
    health_picture_snackbar_adding_ongoing_diagnosis: "Rydych eisoes wedi ychwanegu'r diagnosis hwn ac mae'n dal yn barhaus! Diweddarwch y cofnod hwnnw!",
    health_picture_snackbar_adding_ongoing_treatment: "Rydych eisoes wedi ychwanegu'r driniaeth hon ac mae'n dal yn barhaus! Diweddarwch y cofnod hwnnw!",
    health_picture_snackbar_adding_ongoing_medication: "Rydych eisoes wedi ychwanegu'r feddyginiaeth hon ac mae'n dal yn barhaus! Diweddarwch y cofnod hwnnw!",
    // Subgroups sentences
    your_subgroups: 'Eich is-grwpiau',
    other_subgroups: 'Is-grwpiau eraill',
    // Hub sentences
    community_room_type_hub_room: 'Ystafell Hyb',
    // Room consistency overrides
    rooms_title: 'Ystafelloedd',
    my_rooms_title: 'Fy Ystafelloedd a Hybiau',
    profile_widget_rooms: 'Ystafelloedd',
    bottom_nav_community: 'Ystafelloedd',
    search_rooms_title: 'Ystafelloedd',
    community_first_tab: 'Fy ystafelloedd',
    community_second_tab: 'Pob ystafell',
    community_room_type_private_room: 'Ystafell Breifat',
    community_room_type_public_room: 'Ystafell Gyhoeddus',
    related_rooms: 'Ystafelloedd Cysylltiedig',
    my_rooms_explore_other_rooms: 'Archwilio ystafelloedd eraill',
    rooms_autocomplete_caption: 'Archwilio ystafelloedd:',
    rooms_no_result_found: 'Ni chanfuwyd unrhyw ystafelloedd. Rhowch gynnig arall.',
    rooms_view_all_button: 'Gweld pob ystafell',
    rooms_view_rooms_button: 'Gweld ystafelloedd',
    rooms_leave_room: "Gadael yr ystafell",
    rooms_related_rooms_loading: 'Yn llwytho ystafelloedd cysylltiedig...',
    rooms_related_rooms_end: 'Dim mwy o ystafelloedd cysylltiedig',
    rooms_by_topic_view_more_button: 'Gweld mwy o ystafelloedd',
    share_story_to_rooms_no_rooms_button: 'Archwilio ystafelloedd',
    room_join_button: "Ymuno â'r ystafell",
    room_join_button_pending: 'Cais ystafell yn aros',
    room_leave_confirmation_title: "Gadael yr ystafell",
    no_related_rooms: 'Ni chanfuwyd ystafelloedd cysylltiedig.',
    poll_rooms_selection: "Rhannu pleidlais i'r ystafelloedd:",
    my_rooms_no_member_rooms: "Nid ydych yn aelod o unrhyw ystafell. Pam na wnewch chi <0>archwilio?<0>",
    suggest_new_rooms_title: "Methu dod o hyd i'r ystafell sydd ei hangen arnoch?",
    latest_posts_your_topics: 'Postiadau diweddaraf yn eich ystafelloedd',
    people_in_the_room: "Pobl yn yr ystafell",
    people_in_the_room_no_people: "Pam na fyddwch chi'r cyntaf i ymuno â'r ystafell a chreu post.",
    people_in_the_room_no_companions_and_people: "Llongyfarchiadau! Chi yw'r person cyntaf yn yr ystafell hon",
    no_moderators_in_the_room: "Dim cymedrolwyr yn yr ystafell hon",
    select_rooms_for_post_modal_title: "Dewiswch yr ystafelloedd rydych am rannu eich post iddynt:",
    rooms_create_post_dialog_placeholder_nonmember: "Mae angen i chi ymuno â'r ystafell er mwyn postio",
    rooms_blocked_member: "Yn anffodus mae eich mynediad i'r ystafell hon wedi'i ddiddymu",
    community_not_member_of_room: "Mae'n ddrwg gennym, ni allwch gael mynediad i'r ystafell hon",
    community_room_library_no_files: "Dim ffeiliau ynghlwm wrth yr ystafell hon.",
    community_room_library_details: 'Manylion',
    community_room_library_download: 'Lawrlwytho',
    community_room_library_summarise: 'Crynhoi',
    community_room_library_translate: 'Cyfieithu',
    community_room_library_delete: 'Dileu',
    community_room_library_update: 'Diweddaru',
    profile_settings_ai_settings_title: 'Gosodiadau AI',
    profile_settings_ai_tokens_allowed: 'Tocynnau a ganiateir y dydd:',
    profile_settings_ai_tokens_used: 'Tocynnau a ddefnyddiwyd heddiw:',
    featured_content_delete_file_warning_title: 'Rhybudd',
    featured_content_delete_file_warning_message: 'Pan fyddwch yn dileu ffeil yma, bydd hefyd yn cael ei dileu o\'r llyfrgell',
    featured_post_library_delete_warning_title: 'Rhybudd',
    featured_post_library_delete_warning_description: 'Bydd yr holl luniau a dogfennau sydd ynghlwm wrth y Post Nodedig hwn hefyd yn cael eu dileu o\'r llyfrgell',
    share_story_no_rooms_for_member: "Mae'n edrych fel nad ydych yn aelod o unrhyw ystafell. Pam na wnewch chi archwilio ac ymuno?",
    community_room_library_file_type: 'Math o ffeil',
    community_room_library_language: 'Iaith',
    community_room_library_valid_until: 'Yn ddilys tan',
    community_room_library_date_created: 'Dyddiad creu',
    snackbar_community_member_not_in_room: "Mae angen i chi ymuno ag ystafell {{roomName}} er mwyn ateb neu hoffi.",
    snackbar_community_member_not_in_room_poll: "Mae angen i chi ymuno ag ystafell {{roomName}} er mwyn creu pleidlais.",
    snackbar_community_moderator_remove_member_success: "Aelod wedi'i dynnu o'r ystafell!",
    snackbar_community_cannot_join_room: "Ni allwch ymuno â'r ystafell.",
    snackbar_community_cannot_join_room_blocked: "Rydych wedi'ch blocio o'r ystafell hon.",
    snackbar_community_cannot_leave_room: "Ni allwch adael yr ystafell.",
    snackbar_community_join_room_success: "Rydych wedi ymuno â'r ystafell!",
    snackbar_community_leave_room_success: "Rydych wedi gadael yr ystafell!",
    polls_widget_no_polls: "Nid oes unrhyw bleidleisiau yn yr ystafell hon eto.",
    poll_proposal_additional_info: "Bydd cymedrolwr yr ystafell yn cysylltu am ragor o wybodaeth am y bleidlais.",
    room_leave_confirmation_description: "Ydych chi'n siŵr eich bod am adael yr ystafell hon?",
    story_choose_rooms_subtitle: "Gallwch rannu'r stori hon yn yr ystafelloedd canlynol:",
    story_published_modal_title: "Stori wedi'i chyhoeddi'n llwyddiannus! Hoffech chi ei rhannu i'ch ystafelloedd?",
    story_published_modal_title_mobile: "Diolch am gyhoeddi eich stori. Hoffech chi ei rhannu i'ch ystafelloedd?",
    moderator_pin_post_dialog_description: "Ydych chi'n siŵr eich bod am binio'r post hwn i'r ystafell?",
    moderator_remove_member_dialog_description: "Ydych chi'n siŵr eich bod am dynnu'r aelod hwn o'r ystafell?",
    errors_posts_not_allowed_in_room: "Ni chaniateir postiadau yn yr ystafell hon.",
    errors_no_posts_found_room_member: "Byddwch y cyntaf i bostio yn yr ystafell hon!",
    errors_no_posts_found_room_not_member: "Ymunwch â'r ystafell a phostiwch rywbeth!",
    errors_no_posts_found_room: "Byddwch y cyntaf i bostio yn yr ystafell hon!",
    notifications_settings_someone_publishes_a_story_label: "Stori newydd yn Fy Ystafelloedd",
    share_story_to_rooms_second_title: "Hoffech chi ei rhannu i'ch ystafelloedd?",
    private_room_code_widget_title: 'Oes gennych chi god ar gyfer Ystafell neu Hyb preifat?',
    private_room_shim_name_title_dialog: "Croeso i'r ystafell breifat hon!",
    private_room_shim_name_subtitle_dialog: "Defnyddiwch yr enw mae pobl yn yr ystafell hon yn eich adnabod ag ef.",
    private_room_shim_name_subtitle_dialog_2: "Byddwch yn aros yn ddienw y tu allan i'r ystafell hon.",
    companions_description_no_companion_in_room: "Nid oes gennych unrhyw gyfeillion yn yr ystafell hon eto. Ymunwch â sgyrsiau a dewch o hyd i aelodau i gysylltu â nhw.",
    companions_description_no_companion_in_community: "Nid oes gennych unrhyw gyfeillion eto. Beth am archwilio ystafelloedd a dod o hyd i aelodau i gysylltu â nhw?",
    companions_no_companion: "Nid oes gennych unrhyw gyfeillion eto. Os ewch chi i'r ystafell aelodau newydd gallwch ddod o hyd i aelodau carecircle a chysylltu â nhw",
    personal_private_room_widget_button: "Creu ystafell breifat",
    personal_private_room_dialog_title: "Creu eich ystafell breifat",
    // Body part Level 1 overrides
    body_part_first_level_head: 'Pen',
    body_part_first_level_chest: 'Brest',
    body_part_first_level_abdomen: 'Abdomen',
    body_part_first_level_pelvis: 'Pelfis',
    body_part_first_level_arms: 'Breichiau',
    body_part_first_level_legs: 'Coesau',
    body_part_first_level_back: 'Cefn',
    body_part_first_level_buttocks: 'Pen-ôl',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Mae 'ystafelloedd' y gallwch ymweld â nhw'n ddienw ac ymuno mewn trafodaethau os dymunwch. Gallwch bostio sylwadau neu gwestiynau, neu ddod o hyd i gymdeithion a fydd yn eich cefnogi ar eich taith.",
    // Chats widget title
    conversations: 'Sgyrsiau',
    // Featured content overrides
    stories_carousel_title: 'Straeon dan sylw',
    content_carousel_title: 'Cynnwys dan sylw',
    content_carousel_add_new_post_button: 'Ychwanegu Cynnwys Dan Sylw newydd',
    content_carousel_no_content_moderator_subtitle: "Rhowch wybod i bawb yn yr ystafell hon am y newyddion, cyhoeddiadau ac adnoddau diweddaraf. Dim ond o'r ap gwe y gellir creu cynnwys dan sylw ar hyn o bryd.",
    room_content_type_featured_posts_and_library_option: 'Postiadau Dan Sylw a Llyfrgell',
    room_content_type_featured_posts_option: 'Postiadau Dan Sylw',
    room_create_content_success: 'Mae eitem cynnwys dan sylw wedi cael ei chreu yn llwyddiannus',
    room_update_content_success: 'Mae eitem cynnwys dan sylw wedi cael ei diweddaru yn llwyddiannus',
    room_delete_content_success: 'Mae eitem cynnwys dan sylw wedi cael ei dileu yn llwyddiannus',
    room_delete_content_error: 'Digwyddodd gwall wrth geisio dileu eitem cynnwys dan sylw',
    room_confirm_delete_featured_item_title: 'Dileu cynnwys dan sylw',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: "Sut ydych chi'n teimlo",
    wellness_tracker_morning: "bore 'ma",
    wellness_tracker_afternoon: "prynhawn 'ma",
    wellness_tracker_evening: 'heno',
    // Navigation "Previous" overrides
    previous: 'Blaenorol',
    health_picture_symptom_checker_previous: 'Blaenorol',
    errors_to_previous_link: 'tudalen flaenorol',
    errors_to_previous_mobile: "Ewch â fi i'r <0>sgrin flaenorol</0>.",
    errors_to_previous_link_mobile: 'sgrin flaenorol',
    stories_carousel_previous_story: 'Stori Flaenorol',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Teipiwch sylw...',
    chat_input_placeholder: 'Teipiwch neges...',
    ai_chatbot_widget_input_placeholder: 'Teipiwch rywbeth...',
    create_post_input_placeholder: 'Teipiwch neges...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Taith Iechyd',
    health_picture_name: 'Darlun Iechyd',
    ai_context_mode_health_journey: 'Taith Iechyd',
    health_journey_second_tab: 'Darlun Iechyd',
    health_journey_add_new: 'Creu taith iechyd',
    health_journey_rename_journey: 'Addasu Taith Iechyd',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Creu Taith Iechyd',
    hj_already_create_for_myself_4: 'Gweld Taith Iechyd',
    health_journey_privacy_dialog_health_journey_link: 'Taith Iechyd',
    health_picture_view_health_journey: 'Gweld taith iechyd',
    health_picture_symptom_checker_save_to_health_journey: 'Cadw i Daith Iechyd',
    health_journey_my_picture: 'Fy narlun iechyd',
    health_journey_picture_not_created_title: 'Dechreuwch olrhain eich taith iechyd',
    health_journey_start_health_picture_title: 'Cymerwch y cam cyntaf drwy greu darlun iechyd',
    verification_successful_subtitle: 'Cewch eich ailgyfeirio yn fuan.\nDechreuwch eich taith iechyd heddiw.',
    rooms_redirect_to_health_picture: 'llenwch eich darlun iechyd',
    health_picture_no_symptoms_info: 'Cofnodwch eich symptomau i gael darlun iechyd cyflawn',
    ai_context_label_health_journey: 'Y cyd-destun presennol yw Taith Iechyd:',
    wellness_tracker_no_journey_for_himself_subtitle: "Cr\u00ebwch eich Taith Iechyd eich hun nawr i gael trosolwg o'ch lles ac i archwilio nodweddion cyffrous eraill.",
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Rhowch enw i\'ch taith',
    new_health_journey_placeholder: 'Gallwch gael sawl taith i chi\'ch hun neu i rywun arall.',
    new_health_journey_validation_required_name: 'Rhowch enw ar gyfer eich taith os gwelwch yn dda',
    new_health_journey_validation_length_name: 'Ni ddylai enw eich taith iechyd fod \u00E2 mwy na 30 nod',
    hj_already_create_for_myself_1: 'Mae\'n edrych fel eich bod',
    hj_already_create_for_myself_2: 'eisoes wedi creu Taith Iechyd',
    hj_already_create_for_myself_3: 'i chi\'ch hun.',
    hj_multiple_dialog_title: 'Ar gyfer pwy ydych chi\'n creu taith iechyd?',
    hj_multiple_for_myself: 'Rydw i eisiau creu\'r daith hon i mi fy hun',
    hj_multiple_for_someone_else: 'Rwyf am greu\'r daith hon i rywun arall',
    // Conversation hide override
    conversation_hide: 'Cuddio',
    // Consultation form overrides
    consultation_name: 'Ymgynghoriad',
    health_picture_consultations: 'Fy Ymgynghoriadau',
    health_picture_add_consultation: 'Ychwanegu ymgynghoriad',
    health_picture_edit_consultation: 'Golygu ymgynghoriad',
    health_journey_consultation_type: 'Math o ymgynghoriad:',
    health_journey_timeline_consultation_type: 'Math:',
    mycare_consultation_type_label: 'Math o ymgynghoriad',
    mycare_consultation_provider_label: 'Enw\'r darparwr gofal iechyd',
    mycare_consultation_speciality_label: 'Arbenigedd',
    my_care_journal_notes: 'Nodiadau',
    my_care_journal_description_placeholder: 'Gwnewch nodiadau am unrhyw beth yr hoffech ei gofnodi...',
    content_carousel_no_content_moderator_title: 'Creu cynnwys ar gyfer eich ystafell',
    content_carousel_no_content_member_title: 'Cadwch mewn cysylltiad â\'ch ystafell',
    content_carousel_no_content_member_subtitle: 'Yma fe welwch y newyddion, cyhoeddiadau ac adnoddau diweddaraf.',
    ai_context_label_room: 'Y cyd-destun presennol yw\'r Ystafell:',
    companions_block_user: 'Blocio',
    companions_unblock_user: 'Dadflocio',
    companion_request_block: 'Blocio',
    companion_request_unblock: 'Dadflocio',
    companion_report: 'Adrodd',
    companion_block_message: 'Ydych chi\'n siŵr eich bod am flocio {{memberDisplayName}} a\'u tynnu fel cydymaith?',
    companion_unblock_message: 'Ydych chi\'n siŵr eich bod am ddadflocio {{memberDisplayName}}?',
    companion_report_message: 'Ydych chi\'n siŵr eich bod am adrodd am {{memberDisplayName}}?',
    companions_block_successfully: 'Rydych wedi blocio\'r cydymaith yn llwyddiannus!',
    companions_unblock_successfully: 'Rydych wedi dadflocio\'r cydymaith yn llwyddiannus!',
    companions_cannot_block_administrator: 'Ni allwch flocio gweinyddwr!',
    health_journey_select_date: 'Dewiswch ddyddiad',
  },
  fr: {
    ai_context_mode_all_rooms: 'Automatique',
    like: "J'aime",
    likes: "J'aime",
    community_post_like: "J'aime",
    community_post_likes: "J'aime",
    reaction_like_label: "J'aime",
    story_allow_likes: "Autoriser les j'aime",
    comment: 'Commentaire',
    comments: 'Commentaires',
    reaction_care_label: 'Solidaire',
    reaction_helpful_label: 'Utile',
    story_draft: 'Brouillon',
    stories_drafts: 'Brouillons',
    checker: 'Vérificateur',
    health_picture_complete: 'Terminer',
    health_picture_diagnosis_type_nonmedical: 'Suspecté',
    preview: 'Aperçu',
    journal_name: 'Journal',
    community: 'Salons',
    stories: 'Récits',
    profile_widget_journey: 'Parcours',
    browser: 'Navigateur',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Se connecter',
    community_room_type_hub: 'Hub',
    subgroups: 'Sous-groupes',
    max: 'max',
    companions_title: 'Compagnons',
    health_picture_ongoing: 'En cours',
    poll_period_unlimited: 'Illimité',
    joined_rooms_title: 'Salons rejoints dans ce Hub',
    story_save_as_draft: 'Enregistrer comme brouillon',
    story_saved_draft_snackbar: 'Brouillon enregistré avec succès',
    story_save_draft_success_dialog_content: 'Votre brouillon a été enregistré avec succès !',
    my_stories_empty_drafts_first_line: "Vous n'avez aucun récit en brouillon.",
    health_picture_add_journal: 'Ajouter une entrée de journal',
    health_picture_edit_journal: 'Modifier une entrée de journal',
    health_picture_journal: 'Mes journaux',
    health_journey_journal_title: 'Journal',
    health_journey_journal_date: 'Date du journal',
    delete_journal_confirmation_title: 'Supprimer le journal',
    delete_journal_confirmation_description: 'Êtes-vous sûr de vouloir supprimer ce journal ? Cette action est irréversible.',
    journal_deleted_successfully: 'Journal supprimé avec succès.',
    error_deleting_journal: 'Un problème est survenu lors de la suppression du journal. Veuillez réessayer.',
    stories_first_tab: 'Tous les récits',
    stories_third_tab: 'Mes récits',
    stories_suggested: 'Récits suggérés',
    stories_my_stories: 'Mes récits',
    companions_in_room_title: 'Compagnons dans le salon',
    health_picture_snackbar_adding_ongoing_symptom: 'Vous avez déjà ajouté ce symptôme et il est toujours en cours ! Veuillez mettre à jour cette entrée !',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Vous avez déjà ajouté ce diagnostic et il est toujours en cours ! Veuillez mettre à jour cette entrée !',
    health_picture_snackbar_adding_ongoing_treatment: 'Vous avez déjà ajouté ce traitement et il est toujours en cours ! Veuillez mettre à jour cette entrée !',
    health_picture_snackbar_adding_ongoing_medication: 'Vous avez déjà ajouté ce médicament et il est toujours en cours ! Veuillez mettre à jour cette entrée !',
    your_subgroups: 'Vos sous-groupes',
    other_subgroups: 'Autres sous-groupes',
    community_room_type_hub_room: 'Salon du Hub',
    // Room consistency overrides
    rooms_title: 'Salons',
    my_rooms_title: 'Mes Salons et Hubs',
    profile_widget_rooms: 'Salons',
    bottom_nav_community: 'Salons',
    search_rooms_title: 'Salons',
    community_first_tab: 'Mes salons',
    community_second_tab: 'Tous les salons',
    community_room_type_private_room: 'Salon privé',
    community_room_type_public_room: 'Salon public',
    related_rooms: 'Salons associés',
    my_rooms_explore_other_rooms: "Explorer d'autres salons",
    rooms_autocomplete_caption: 'Explorer les salons :',
    rooms_no_result_found: 'Aucun salon trouvé. Essayez une autre recherche.',
    rooms_view_all_button: 'Voir tous les salons',
    rooms_view_rooms_button: 'Voir les salons',
    rooms_leave_room: 'Quitter le salon',
    rooms_related_rooms_loading: 'Chargement des salons associés...',
    rooms_related_rooms_end: 'Plus de salons associés',
    rooms_by_topic_view_more_button: 'Voir plus de salons',
    share_story_to_rooms_no_rooms_button: 'Explorer les salons',
    room_join_button: 'Rejoindre le salon',
    room_join_button_pending: 'Demande de salon en attente',
    room_leave_confirmation_title: 'Quitter le salon',
    no_related_rooms: 'Aucun salon associé trouvé.',
    poll_rooms_selection: 'Partager le sondage dans les salons :',
    my_rooms_no_member_rooms: "Vous n'êtes membre d'aucun salon. Pourquoi ne pas <0>explorer ?<0>",
    suggest_new_rooms_title: 'Vous ne trouvez pas le salon dont vous avez besoin ?',
    latest_posts_your_topics: 'Dernières publications dans vos salons',
    people_in_the_room: 'Personnes dans le salon',
    people_in_the_room_no_people: 'Pourquoi ne pas être le premier à rejoindre le salon et créer une publication.',
    people_in_the_room_no_companions_and_people: 'Félicitations ! Vous êtes la première personne dans ce salon',
    no_moderators_in_the_room: 'Aucun modérateur dans ce salon',
    select_rooms_for_post_modal_title: 'Veuillez sélectionner les salons dans lesquels vous souhaitez partager votre publication :',
    rooms_create_post_dialog_placeholder_nonmember: 'Vous devez rejoindre le salon pour pouvoir publier',
    rooms_blocked_member: 'Malheureusement, votre accès à ce salon a été révoqué',
    community_not_member_of_room: 'Désolé, vous ne pouvez pas accéder à ce salon',
    community_room_library_no_files: 'Aucun fichier joint à ce salon.',
    community_room_library_details: 'Détails',
    community_room_library_download: 'Télécharger',
    community_room_library_summarise: 'Résumer',
    community_room_library_translate: 'Traduire',
    community_room_library_delete: 'Supprimer',
    community_room_library_update: 'Mettre à jour',
    profile_settings_ai_settings_title: 'Paramètres IA',
    profile_settings_ai_tokens_allowed: 'Jetons autorisés par jour :',
    profile_settings_ai_tokens_used: "Jetons utilisés aujourd'hui :",
    featured_content_delete_file_warning_title: 'Avertissement',
    featured_content_delete_file_warning_message: 'Lorsque vous supprimez un fichier ici, il sera également supprimé de la bibliothèque',
    featured_post_library_delete_warning_title: 'Avertissement',
    featured_post_library_delete_warning_description: 'Toutes les images et documents joints à cette Publication Vedette seront également supprimés de la bibliothèque',
    share_story_no_rooms_for_member: "Il semble que vous ne soyez membre d'aucun salon. Pourquoi ne pas explorer et rejoindre ?",
    community_room_library_file_type: 'Type de fichier',
    community_room_library_language: 'Langue',
    community_room_library_valid_until: "Valide jusqu'au",
    community_room_library_date_created: 'Date de création',
    snackbar_community_member_not_in_room: 'Vous devez rejoindre le salon {{roomName}} pour répondre ou aimer.',
    snackbar_community_member_not_in_room_poll: 'Vous devez rejoindre le salon {{roomName}} pour créer un sondage.',
    snackbar_community_moderator_remove_member_success: 'Membre retiré du salon !',
    snackbar_community_cannot_join_room: 'Vous ne pouvez pas rejoindre le salon.',
    snackbar_community_cannot_join_room_blocked: 'Vous avez été bloqué de ce salon.',
    snackbar_community_cannot_leave_room: 'Vous ne pouvez pas quitter le salon.',
    snackbar_community_join_room_success: 'Vous avez rejoint le salon !',
    snackbar_community_leave_room_success: 'Vous avez quitté le salon !',
    polls_widget_no_polls: "Il n'y a pas encore de sondages dans ce salon.",
    poll_proposal_additional_info: "Un modérateur du salon vous contactera pour plus d'informations concernant le sondage.",
    room_leave_confirmation_description: 'Êtes-vous sûr de vouloir quitter ce salon ?',
    story_choose_rooms_subtitle: 'Vous pouvez partager ce récit dans les salons suivants :',
    story_published_modal_title: 'Récit publié avec succès ! Souhaitez-vous le partager dans vos salons ?',
    story_published_modal_title_mobile: "Merci d'avoir publié votre récit. Souhaitez-vous le partager dans vos salons ?",
    moderator_pin_post_dialog_description: 'Êtes-vous sûr de vouloir épingler cette publication dans le salon ?',
    moderator_remove_member_dialog_description: 'Êtes-vous sûr de vouloir retirer ce membre du salon ?',
    errors_posts_not_allowed_in_room: 'Les publications ne sont pas autorisées dans ce salon.',
    errors_no_posts_found_room_member: 'Soyez le premier à publier dans ce salon !',
    errors_no_posts_found_room_not_member: 'Rejoignez le salon et publiez quelque chose !',
    errors_no_posts_found_room: 'Soyez le premier à publier dans ce salon !',
    notifications_settings_someone_publishes_a_story_label: 'Nouveau récit dans Mes Salons',
    share_story_to_rooms_second_title: 'Souhaitez-vous le partager dans vos salons ?',
    private_room_code_widget_title: 'Avez-vous un code pour un Salon ou un Hub privé ?',
    private_room_shim_name_title_dialog: 'Bienvenue dans ce salon privé !',
    private_room_shim_name_subtitle_dialog: 'Utilisez le nom sous lequel les personnes dans ce salon vous connaissent.',
    private_room_shim_name_subtitle_dialog_2: 'Vous resterez anonyme en dehors de ce salon.',
    companions_description_no_companion_in_room: "Vous n'avez pas encore de compagnons dans ce salon. Participez aux conversations et trouvez des membres avec qui vous connecter.",
    companions_description_no_companion_in_community: "Vous n'avez pas encore de compagnons. Pourquoi ne pas explorer les salons et trouver des membres avec qui vous connecter ?",
    companions_no_companion: "Vous n'avez pas encore de compagnons. Visitez le salon des nouveaux membres pour trouver des membres carecircle et vous connecter",
    personal_private_room_widget_button: 'Créer un salon privé',
    personal_private_room_dialog_title: 'Créer votre salon privé',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Tête',
    body_part_first_level_chest: 'Poitrine',
    body_part_first_level_abdomen: 'Abdomen',
    body_part_first_level_pelvis: 'Bassin',
    body_part_first_level_arms: 'Bras',
    body_part_first_level_legs: 'Jambes',
    body_part_first_level_back: 'Dos',
    body_part_first_level_buttocks: 'Fesses',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Il y a des 'salons' que vous pouvez visiter anonymement et rejoindre les discussions si vous le souhaitez. Vous pouvez poster des commentaires ou des questions, ou trouver des compagnons qui vous soutiendront dans votre parcours.",
    conversations: 'Conversations',
    // Featured content overrides
    stories_carousel_title: 'Histoires à la une',
    content_carousel_title: 'Contenu à la une',
    content_carousel_add_new_post_button: 'Ajouter un nouveau Contenu à la une',
    content_carousel_no_content_moderator_subtitle: "Informez tous les membres de ce salon des dernières nouvelles, annonces et ressources. Le contenu à la une ne peut être créé que depuis l'application web pour le moment.",
    room_content_type_featured_posts_and_library_option: 'Publications à la une et bibliothèque',
    room_content_type_featured_posts_option: 'Publications à la une',
    room_create_content_success: "L'élément de contenu à la une a été créé avec succès",
    room_update_content_success: "L'élément de contenu à la une a été mis à jour avec succès",
    room_delete_content_success: "L'élément de contenu à la une a été supprimé avec succès",
    room_delete_content_error: "Une erreur est survenue lors de la suppression de l'élément de contenu à la une",
    room_confirm_delete_featured_item_title: 'Supprimer le contenu à la une',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Comment vous sentez-vous',
    wellness_tracker_morning: 'ce matin',
    wellness_tracker_afternoon: 'cet après-midi',
    wellness_tracker_evening: 'ce soir',
    // Navigation "Previous" overrides
    previous: 'Précédent',
    health_picture_symptom_checker_previous: 'Précédent',
    errors_to_previous_link: 'page précédente',
    errors_to_previous_mobile: 'Emmenez-moi à l\'<0>écran précédent</0>.',
    errors_to_previous_link_mobile: 'écran précédent',
    stories_carousel_previous_story: 'Histoire précédente',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Écrivez un commentaire...',
    chat_input_placeholder: 'Écrivez un message...',
    ai_chatbot_widget_input_placeholder: 'Écrivez quelque chose...',
    create_post_input_placeholder: 'Écrivez un message...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Parcours Santé',
    health_picture_name: 'Bilan de Santé',
    ai_context_mode_health_journey: 'Parcours Santé',
    health_journey_second_tab: 'Bilan de Santé',
    health_journey_add_new: 'Créer un parcours santé',
    health_journey_rename_journey: 'Modifier le Parcours Santé',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Créer un Parcours Santé',
    hj_already_create_for_myself_4: 'Voir le Parcours Santé',
    health_journey_privacy_dialog_health_journey_link: 'Parcours Santé',
    health_picture_view_health_journey: 'Voir le parcours santé',
    health_picture_symptom_checker_save_to_health_journey: 'Sauvegarder dans le Parcours Santé',
    health_journey_my_picture: 'Mon bilan de santé',
    health_journey_picture_not_created_title: 'Commencez à suivre votre parcours santé',
    health_journey_start_health_picture_title: 'Faites le premier pas en créant un bilan de santé',
    verification_successful_subtitle: "Vous serez redirigé sous peu.\nCommencez votre parcours santé dès aujourd'hui.",
    rooms_redirect_to_health_picture: 'remplissez votre bilan de santé',
    health_picture_no_symptoms_info: 'Enregistrez vos symptômes pour obtenir un bilan de santé complet',
    ai_context_label_health_journey: 'Le contexte actuel est le Parcours Santé de :',
    wellness_tracker_no_journey_for_himself_subtitle: "Créez maintenant votre propre Parcours Santé pour avoir un aperçu de votre bien-être et découvrir d'autres fonctionnalités intéressantes.",
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Donnez un nom à votre parcours',
    new_health_journey_placeholder: 'Vous pouvez avoir plusieurs parcours pour vous-même ou pour quelqu\'un d\'autre.',
    new_health_journey_validation_required_name: 'Veuillez saisir un nom pour votre parcours',
    new_health_journey_validation_length_name: 'Le nom de votre parcours santé ne doit pas dépasser 30 caractères',
    hj_already_create_for_myself_1: 'Il semble que vous',
    hj_already_create_for_myself_2: 'ayez déjà créé un Parcours Santé',
    hj_already_create_for_myself_3: 'pour vous-même.',
    hj_multiple_dialog_title: 'Pour qui créez-vous un parcours santé ?',
    hj_multiple_for_myself: 'Je veux créer ce parcours pour moi-même',
    hj_multiple_for_someone_else: 'Je veux créer ce parcours pour quelqu\'un d\'autre',
    // Conversation hide override
    conversation_hide: 'Masquer',
    // Consultation form overrides
    consultation_name: 'Consultation',
    health_picture_consultations: 'Mes consultations',
    health_picture_add_consultation: 'Ajouter une consultation',
    health_picture_edit_consultation: 'Modifier la consultation',
    health_journey_consultation_type: 'Type de consultation :',
    health_journey_timeline_consultation_type: 'Type :',
    mycare_consultation_type_label: 'Type de consultation',
    mycare_consultation_provider_label: 'Nom du professionnel de santé',
    mycare_consultation_speciality_label: 'Spécialité',
    my_care_journal_notes: 'Notes',
    my_care_journal_description_placeholder: 'Prenez des notes sur tout ce que vous souhaitez...',
    content_carousel_no_content_moderator_title: 'Créez du contenu pour votre salon',
    content_carousel_no_content_member_title: 'Restez en contact avec votre salon',
    content_carousel_no_content_member_subtitle: 'Vous trouverez ici les dernières nouvelles, annonces et ressources.',
    ai_context_label_room: 'Le contexte actuel est le Salon :',
    companions_block_user: 'Bloquer',
    companions_unblock_user: 'Débloquer',
    companion_request_block: 'Bloquer',
    companion_request_unblock: 'Débloquer',
    companion_report: 'Signaler',
    companion_block_message: 'Êtes-vous sûr(e) de vouloir bloquer {{memberDisplayName}} et le/la retirer de vos compagnons ?',
    companion_unblock_message: 'Êtes-vous sûr(e) de vouloir débloquer {{memberDisplayName}} ?',
    companion_report_message: 'Êtes-vous sûr(e) de vouloir signaler {{memberDisplayName}} ?',
    companions_block_successfully: 'Vous avez bloqué le compagnon avec succès !',
    companions_unblock_successfully: 'Vous avez débloqué le compagnon avec succès !',
    companions_cannot_block_administrator: 'Vous ne pouvez pas bloquer un administrateur !',
    health_journey_select_date: 'Sélectionner la date',
  },
  de: {
    ai_context_mode_all_rooms: 'Automatisch',
    like: 'Gefällt mir',
    likes: 'Gefällt mir',
    community_post_like: 'Gefällt mir',
    community_post_likes: 'Gefällt mir',
    reaction_like_label: 'Gefällt mir',
    story_allow_likes: 'Gefällt-mir-Angaben erlauben',
    comment: 'Kommentar',
    comments: 'Kommentare',
    reaction_care_label: 'Mitgefühl',
    reaction_helpful_label: 'Hilfreich',
    story_draft: 'Entwurf',
    stories_drafts: 'Entwürfe',
    checker: 'Checker',
    health_picture_complete: 'Abschließen',
    health_picture_diagnosis_type_nonmedical: 'Vermutet',
    preview: 'Vorschau',
    journal_name: 'Tagebuch',
    community: 'Räume',
    stories: 'Geschichten',
    profile_widget_journey: 'Verläufe',
    browser: 'Browser',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Vernetzen',
    community_room_type_hub: 'Hub',
    subgroups: 'Untergruppen',
    max: 'max',
    companions_title: 'Begleiter',
    health_picture_ongoing: 'Laufend',
    poll_period_unlimited: 'Unbegrenzt',
    joined_rooms_title: 'Räume in diesem Hub beigetreten',
    story_save_as_draft: 'Als Entwurf speichern',
    story_saved_draft_snackbar: 'Entwurf erfolgreich gespeichert',
    story_save_draft_success_dialog_content: 'Ihr Entwurf wurde erfolgreich gespeichert!',
    my_stories_empty_drafts_first_line: 'Sie haben keine Geschichten im Entwurf.',
    health_picture_add_journal: 'Tagebucheintrag hinzufügen',
    health_picture_edit_journal: 'Tagebucheintrag bearbeiten',
    health_picture_journal: 'Meine Tagebücher',
    health_journey_journal_title: 'Tagebuch',
    health_journey_journal_date: 'Tagebuchdatum',
    delete_journal_confirmation_title: 'Tagebuch löschen',
    delete_journal_confirmation_description: 'Möchten Sie dieses Tagebuch wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    journal_deleted_successfully: 'Tagebuch erfolgreich gelöscht.',
    error_deleting_journal: 'Beim Löschen des Tagebuchs ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    stories_first_tab: 'Alle Geschichten',
    stories_third_tab: 'Meine Geschichten',
    stories_suggested: 'Vorgeschlagene Geschichten',
    stories_my_stories: 'Meine Geschichten',
    companions_in_room_title: 'Begleiter im Raum',
    health_picture_snackbar_adding_ongoing_symptom: 'Sie haben dieses Symptom bereits hinzugefügt und es ist noch laufend! Bitte aktualisieren Sie den Eintrag!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Sie haben diese Diagnose bereits hinzugefügt und sie ist noch laufend! Bitte aktualisieren Sie den Eintrag!',
    health_picture_snackbar_adding_ongoing_treatment: 'Sie haben diese Behandlung bereits hinzugefügt und sie ist noch laufend! Bitte aktualisieren Sie den Eintrag!',
    health_picture_snackbar_adding_ongoing_medication: 'Sie haben dieses Medikament bereits hinzugefügt und es ist noch laufend! Bitte aktualisieren Sie den Eintrag!',
    your_subgroups: 'Ihre Untergruppen',
    other_subgroups: 'Andere Untergruppen',
    community_room_type_hub_room: 'Hub-Raum',
    // Room consistency overrides
    rooms_title: 'Räume',
    my_rooms_title: 'Meine Räume & Hubs',
    profile_widget_rooms: 'Räume',
    bottom_nav_community: 'Räume',
    search_rooms_title: 'Räume',
    community_first_tab: 'Meine Räume',
    community_second_tab: 'Alle Räume',
    community_room_type_private_room: 'Privater Raum',
    community_room_type_public_room: 'Öffentlicher Raum',
    related_rooms: 'Ähnliche Räume',
    my_rooms_explore_other_rooms: 'Andere Räume entdecken',
    rooms_autocomplete_caption: 'Räume entdecken:',
    rooms_no_result_found: 'Keine Räume gefunden. Versuchen Sie eine andere Suche.',
    rooms_view_all_button: 'Alle Räume anzeigen',
    rooms_view_rooms_button: 'Räume anzeigen',
    rooms_leave_room: 'Den Raum verlassen',
    rooms_related_rooms_loading: 'Ähnliche Räume werden geladen...',
    rooms_related_rooms_end: 'Keine weiteren ähnlichen Räume',
    rooms_by_topic_view_more_button: 'Weitere Räume anzeigen',
    share_story_to_rooms_no_rooms_button: 'Räume entdecken',
    room_join_button: 'Dem Raum beitreten',
    room_join_button_pending: 'Raumanfrage ausstehend',
    room_leave_confirmation_title: 'Den Raum verlassen',
    no_related_rooms: 'Keine ähnlichen Räume gefunden.',
    poll_rooms_selection: 'Umfrage in den Räumen teilen:',
    my_rooms_no_member_rooms: 'Sie sind in keinem Raum Mitglied. Warum nicht <0>entdecken?<0>',
    suggest_new_rooms_title: 'Den Raum, den Sie suchen, nicht gefunden?',
    latest_posts_your_topics: 'Neueste Beiträge in Ihren Räumen',
    people_in_the_room: 'Personen im Raum',
    people_in_the_room_no_people: 'Warum nicht als Erster dem Raum beitreten und einen Beitrag erstellen.',
    people_in_the_room_no_companions_and_people: 'Herzlichen Glückwunsch! Sie sind die erste Person in diesem Raum',
    no_moderators_in_the_room: 'Keine Moderatoren in diesem Raum',
    select_rooms_for_post_modal_title: 'Bitte wählen Sie die Räume aus, in denen Sie Ihren Beitrag teilen möchten:',
    rooms_create_post_dialog_placeholder_nonmember: 'Sie müssen dem Raum beitreten, um posten zu können',
    rooms_blocked_member: 'Leider wurde Ihr Zugang zu diesem Raum widerrufen',
    community_not_member_of_room: 'Sie haben keinen Zugang zu diesem Raum',
    community_room_library_no_files: 'Keine Dateien an diesen Raum angehängt.',
    community_room_library_details: 'Details',
    community_room_library_download: 'Herunterladen',
    community_room_library_summarise: 'Zusammenfassen',
    community_room_library_translate: 'Übersetzen',
    community_room_library_delete: 'Löschen',
    community_room_library_update: 'Aktualisieren',
    profile_settings_ai_settings_title: 'KI-Einstellungen',
    profile_settings_ai_tokens_allowed: 'Erlaubte Token pro Tag:',
    profile_settings_ai_tokens_used: 'Heute verwendete Token:',
    featured_content_delete_file_warning_title: 'Warnung',
    featured_content_delete_file_warning_message: 'Wenn Sie eine Datei hier löschen, wird sie auch aus der Bibliothek gelöscht',
    featured_post_library_delete_warning_title: 'Warnung',
    featured_post_library_delete_warning_description: 'Alle angehängten Bilder und Dokumente dieses Hervorgehobenen Beitrags werden auch aus der Bibliothek gelöscht',
    share_story_no_rooms_for_member: 'Es sieht so aus, als wären Sie in keinem Raum Mitglied. Warum nicht entdecken und beitreten?',
    community_room_library_file_type: 'Dateityp',
    community_room_library_language: 'Sprache',
    community_room_library_valid_until: 'Gültig bis',
    community_room_library_date_created: 'Erstellungsdatum',
    snackbar_community_member_not_in_room: 'Sie müssen dem Raum {{roomName}} beitreten, um zu antworten oder zu liken.',
    snackbar_community_member_not_in_room_poll: 'Sie müssen dem Raum {{roomName}} beitreten, um eine Umfrage zu erstellen.',
    snackbar_community_moderator_remove_member_success: 'Mitglied aus dem Raum entfernt!',
    snackbar_community_cannot_join_room: 'Sie können dem Raum nicht beitreten.',
    snackbar_community_cannot_join_room_blocked: 'Sie wurden aus diesem Raum blockiert.',
    snackbar_community_cannot_leave_room: 'Sie können den Raum nicht verlassen.',
    snackbar_community_join_room_success: 'Sie sind dem Raum beigetreten!',
    snackbar_community_leave_room_success: 'Sie haben den Raum verlassen!',
    polls_widget_no_polls: 'Es gibt noch keine Umfragen in diesem Raum.',
    poll_proposal_additional_info: 'Ein Moderator des Raums wird Sie bezüglich der Umfrage kontaktieren.',
    room_leave_confirmation_description: 'Sind Sie sicher, dass Sie diesen Raum verlassen möchten?',
    story_choose_rooms_subtitle: 'Sie können diese Geschichte in folgenden Räumen teilen:',
    story_published_modal_title: 'Geschichte erfolgreich veröffentlicht! Möchten Sie sie in Ihren Räumen teilen?',
    story_published_modal_title_mobile: 'Vielen Dank für die Veröffentlichung Ihrer Geschichte. Möchten Sie sie in Ihren Räumen teilen?',
    moderator_pin_post_dialog_description: 'Sind Sie sicher, dass Sie diesen Beitrag im Raum anheften möchten?',
    moderator_remove_member_dialog_description: 'Sind Sie sicher, dass Sie dieses Mitglied aus dem Raum entfernen möchten?',
    errors_posts_not_allowed_in_room: 'Beiträge sind in diesem Raum nicht erlaubt.',
    errors_no_posts_found_room_member: 'Seien Sie der Erste, der in diesem Raum postet!',
    errors_no_posts_found_room_not_member: 'Treten Sie dem Raum bei und posten Sie etwas!',
    errors_no_posts_found_room: 'Seien Sie der Erste, der in diesem Raum postet!',
    notifications_settings_someone_publishes_a_story_label: 'Neue Geschichte in Meine Räume',
    share_story_to_rooms_second_title: 'Möchten Sie sie in Ihren Räumen teilen?',
    private_room_code_widget_title: 'Haben Sie einen Code für einen privaten Raum oder Hub?',
    private_room_shim_name_title_dialog: 'Willkommen in diesem privaten Raum!',
    private_room_shim_name_subtitle_dialog: 'Verwenden Sie den Namen, unter dem die Personen in diesem Raum Sie kennen.',
    private_room_shim_name_subtitle_dialog_2: 'Sie bleiben außerhalb dieses Raums anonym.',
    companions_description_no_companion_in_room: 'Sie haben noch keine Begleiter in diesem Raum. Nehmen Sie an Gesprächen teil und finden Sie Mitglieder zum Vernetzen.',
    companions_description_no_companion_in_community: 'Sie haben noch keine Begleiter. Warum nicht Räume entdecken und Mitglieder zum Vernetzen finden?',
    companions_no_companion: 'Sie haben noch keine Begleiter. Besuchen Sie den Raum für neue Mitglieder, um carecircle-Mitglieder zu finden und sich zu vernetzen',
    personal_private_room_widget_button: 'Einen privaten Raum erstellen',
    personal_private_room_dialog_title: 'Ihren privaten Raum erstellen',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Kopf',
    body_part_first_level_chest: 'Brust',
    body_part_first_level_abdomen: 'Bauch',
    body_part_first_level_pelvis: 'Becken',
    body_part_first_level_arms: 'Arme',
    body_part_first_level_legs: 'Beine',
    body_part_first_level_back: 'Rücken',
    body_part_first_level_buttocks: 'Gesäß',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Es gibt 'Räume', die Sie anonym besuchen und an Diskussionen teilnehmen können, wenn Sie möchten. Sie können Kommentare oder Fragen posten oder Begleiter finden, die Sie auf Ihrer Reise unterstützen.",
    conversations: 'Chats',
    // Featured content overrides
    stories_carousel_title: 'Geschichten im Fokus',
    content_carousel_title: 'Inhalte im Fokus',
    content_carousel_add_new_post_button: 'Neuen Fokus-Inhalt hinzufügen',
    content_carousel_no_content_moderator_subtitle: 'Informieren Sie alle in diesem Raum über die neuesten Nachrichten, Ankündigungen und Ressourcen. Fokus-Inhalte können derzeit nur über die Web-App erstellt werden.',
    room_content_type_featured_posts_and_library_option: 'Fokus-Beiträge & Bibliothek',
    room_content_type_featured_posts_option: 'Fokus-Beiträge',
    room_create_content_success: 'Fokus-Inhalt wurde erfolgreich erstellt',
    room_update_content_success: 'Fokus-Inhalt wurde erfolgreich aktualisiert',
    room_delete_content_success: 'Fokus-Inhalt wurde erfolgreich gelöscht',
    room_delete_content_error: 'Beim Löschen des Fokus-Inhalts ist ein Fehler aufgetreten',
    room_confirm_delete_featured_item_title: 'Fokus-Inhalt löschen',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Wie fühlen Sie sich',
    wellness_tracker_morning: 'heute Morgen',
    wellness_tracker_afternoon: 'heute Nachmittag',
    wellness_tracker_evening: 'heute Abend',
    // Navigation "Previous" overrides
    previous: 'Zurück',
    health_picture_symptom_checker_previous: 'Zurück',
    errors_to_previous_link: 'vorherige Seite',
    errors_to_previous_mobile: 'Bring mich zum <0>vorherigen Bildschirm</0>.',
    errors_to_previous_link_mobile: 'vorherigen Bildschirm',
    stories_carousel_previous_story: 'Vorherige Geschichte',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Kommentar eingeben...',
    chat_input_placeholder: 'Nachricht eingeben...',
    ai_chatbot_widget_input_placeholder: 'Etwas eingeben...',
    create_post_input_placeholder: 'Nachricht eingeben...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Gesundheitsverlauf',
    health_picture_name: 'Gesundheitsbild',
    ai_context_mode_health_journey: 'Gesundheitsverlauf',
    health_journey_second_tab: 'Gesundheitsbild',
    health_journey_add_new: 'Gesundheitsverlauf erstellen',
    health_journey_rename_journey: 'Gesundheitsverlauf bearbeiten',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Gesundheitsverlauf erstellen',
    hj_already_create_for_myself_4: 'Gesundheitsverlauf anzeigen',
    health_journey_privacy_dialog_health_journey_link: 'Gesundheitsverlauf',
    health_picture_view_health_journey: 'Gesundheitsverlauf anzeigen',
    health_picture_symptom_checker_save_to_health_journey: 'Im Gesundheitsverlauf speichern',
    health_journey_my_picture: 'Mein Gesundheitsbild',
    health_journey_picture_not_created_title: 'Beginnen Sie Ihren Gesundheitsverlauf zu verfolgen',
    health_journey_start_health_picture_title: 'Machen Sie den ersten Schritt und erstellen Sie ein Gesundheitsbild',
    verification_successful_subtitle: 'Sie werden in K\u00fcrze weitergeleitet.\nStarten Sie noch heute Ihren Gesundheitsverlauf.',
    rooms_redirect_to_health_picture: 'f\u00fcllen Sie Ihr Gesundheitsbild aus',
    health_picture_no_symptoms_info: 'Erfassen Sie Ihre Symptome f\u00fcr ein vollst\u00e4ndiges Gesundheitsbild',
    ai_context_label_health_journey: 'Der aktuelle Kontext ist der Gesundheitsverlauf von:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Erstellen Sie jetzt Ihren eigenen Gesundheitsverlauf, um einen \u00dcberblick \u00fcber Ihr Wohlbefinden zu erhalten und weitere spannende Funktionen zu entdecken.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Geben Sie Ihrem Verlauf einen Namen',
    new_health_journey_placeholder: 'Sie können mehrere Verläufe für sich selbst oder für jemand anderen erstellen.',
    new_health_journey_validation_required_name: 'Bitte geben Sie einen Namen für Ihren Verlauf ein',
    new_health_journey_validation_length_name: 'Der Name Ihres Gesundheitsverlaufs darf nicht mehr als 30 Zeichen enthalten',
    hj_already_create_for_myself_1: 'Es sieht so aus, als hätten Sie',
    hj_already_create_for_myself_2: 'bereits einen Gesundheitsverlauf erstellt',
    hj_already_create_for_myself_3: 'für sich selbst.',
    hj_multiple_dialog_title: 'Für wen erstellen Sie einen Gesundheitsverlauf?',
    hj_multiple_for_myself: 'Ich möchte diesen Verlauf für mich selbst erstellen',
    hj_multiple_for_someone_else: 'Ich möchte diesen Verlauf für jemand anderen erstellen',
    // Conversation hide override
    conversation_hide: 'Ausblenden',
    // Consultation form overrides
    consultation_name: 'Konsultation',
    health_picture_consultations: 'Meine Konsultationen',
    health_picture_add_consultation: 'Konsultation hinzufügen',
    health_picture_edit_consultation: 'Konsultation bearbeiten',
    health_journey_consultation_type: 'Art der Konsultation:',
    health_journey_timeline_consultation_type: 'Art:',
    mycare_consultation_type_label: 'Art der Konsultation',
    mycare_consultation_provider_label: 'Name des Gesundheitsdienstleisters',
    mycare_consultation_speciality_label: 'Fachgebiet',
    my_care_journal_notes: 'Notizen',
    my_care_journal_description_placeholder: 'Machen Sie sich Notizen zu allem, was Sie möchten...',
    content_carousel_no_content_moderator_title: 'Erstellen Sie Inhalte für Ihren Raum',
    content_carousel_no_content_member_title: 'Bleiben Sie mit Ihrem Raum in Kontakt',
    content_carousel_no_content_member_subtitle: 'Hier finden Sie die neuesten Nachrichten, Ankündigungen und Ressourcen.',
    ai_context_label_room: 'Der aktuelle Kontext ist der Raum:',
    companions_block_user: 'Blockieren',
    companions_unblock_user: 'Entblockieren',
    companion_request_block: 'Blockieren',
    companion_request_unblock: 'Entblockieren',
    companion_report: 'Melden',
    companion_block_message: 'Sind Sie sicher, dass Sie {{memberDisplayName}} blockieren und als Begleiter entfernen möchten?',
    companion_unblock_message: 'Sind Sie sicher, dass Sie {{memberDisplayName}} entblockieren möchten?',
    companion_report_message: 'Sind Sie sicher, dass Sie {{memberDisplayName}} melden möchten?',
    companions_block_successfully: 'Sie haben den Begleiter erfolgreich blockiert!',
    companions_unblock_successfully: 'Sie haben den Begleiter erfolgreich entblockiert!',
    companions_cannot_block_administrator: 'Sie können einen Administrator nicht blockieren!',
    health_journey_select_date: 'Datum auswählen',
  },
  ro: {
    ai_context_mode_all_rooms: 'Automat',
    like: 'Îmi place',
    likes: 'Aprecieri',
    community_post_like: 'Îmi place',
    community_post_likes: 'Aprecieri',
    reaction_like_label: 'Îmi place',
    story_allow_likes: 'Permite aprecieri',
    comment: 'Comentariu',
    comments: 'Comentarii',
    reaction_care_label: 'Îmi pasă',
    reaction_helpful_label: 'Util',
    story_draft: 'Ciornă',
    stories_drafts: 'Ciorne',
    checker: 'Verificator',
    health_picture_complete: 'Finalizare',
    health_picture_diagnosis_type_nonmedical: 'Suspectat',
    preview: 'Previzualizare',
    journal_name: 'Jurnal',
    community: 'Camere',
    stories: 'Povești',
    profile_widget_journey: 'Parcursuri',
    browser: 'Browser',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Conectare',
    community_room_type_hub: 'Hub',
    subgroups: 'Subgrupuri',
    max: 'max',
    companions_title: 'Însoțitori',
    health_picture_ongoing: 'În desfășurare',
    poll_period_unlimited: 'Nelimitat',
    joined_rooms_title: 'Camere la care v-ați alăturat în acest Hub',
    story_save_as_draft: 'Salvează ca ciornă',
    story_saved_draft_snackbar: 'Ciornă salvată cu succes',
    story_save_draft_success_dialog_content: 'Ciorna dumneavoastră a fost salvată cu succes!',
    my_stories_empty_drafts_first_line: 'Nu aveți nicio poveste în Ciornă.',
    health_picture_add_journal: 'Adăugați o intrare în jurnal',
    health_picture_edit_journal: 'Editați intrarea în jurnal',
    health_picture_journal: 'Jurnalele mele',
    health_journey_journal_title: 'Jurnal',
    health_journey_journal_date: 'Data jurnalului',
    delete_journal_confirmation_title: 'Ștergeți jurnalul',
    delete_journal_confirmation_description: 'Sunteți sigur că doriți să ștergeți acest jurnal? Această acțiune nu poate fi anulată.',
    journal_deleted_successfully: 'Jurnal șters cu succes.',
    error_deleting_journal: 'A apărut o problemă la ștergerea jurnalului. Vă rugăm să încercați din nou.',
    stories_first_tab: 'Toate poveștile',
    stories_third_tab: 'Poveștile mele',
    stories_suggested: 'Povești sugerate',
    stories_my_stories: 'Poveștile mele',
    companions_in_room_title: 'Însoțitori în cameră',
    health_picture_snackbar_adding_ongoing_symptom: 'Ați adăugat deja acest simptom și este încă în desfășurare! Vă rugăm să actualizați acea intrare!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Ați adăugat deja acest diagnostic și este încă în desfășurare! Vă rugăm să actualizați acea intrare!',
    health_picture_snackbar_adding_ongoing_treatment: 'Ați adăugat deja acest tratament și este încă în desfășurare! Vă rugăm să actualizați acea intrare!',
    health_picture_snackbar_adding_ongoing_medication: 'Ați adăugat deja acest medicament și este încă în desfășurare! Vă rugăm să actualizați acea intrare!',
    your_subgroups: 'Subgrupurile dumneavoastră',
    other_subgroups: 'Alte subgrupuri',
    community_room_type_hub_room: 'Cameră Hub',
    // Room consistency overrides
    rooms_title: 'Camere',
    my_rooms_title: 'Camerele și Hub-urile mele',
    profile_widget_rooms: 'Camere',
    bottom_nav_community: 'Camere',
    search_rooms_title: 'Camere',
    community_first_tab: 'Camerele mele',
    community_second_tab: 'Toate camerele',
    community_room_type_private_room: 'Cameră privată',
    community_room_type_public_room: 'Cameră publică',
    related_rooms: 'Camere asociate',
    my_rooms_explore_other_rooms: 'Explorați alte camere',
    rooms_autocomplete_caption: 'Explorați camere:',
    rooms_no_result_found: 'Nu au fost găsite camere. Încercați o altă căutare.',
    rooms_view_all_button: 'Vedeți toate camerele',
    rooms_view_rooms_button: 'Vedeți camere',
    rooms_leave_room: 'Părăsiți camera',
    rooms_related_rooms_loading: 'Se încarcă camerele asociate...',
    rooms_related_rooms_end: 'Nu mai sunt camere asociate',
    rooms_by_topic_view_more_button: 'Vedeți mai multe camere',
    share_story_to_rooms_no_rooms_button: 'Explorați camere',
    room_join_button: 'Alăturați-vă camerei',
    room_join_button_pending: 'Cerere de cameră în așteptare',
    room_leave_confirmation_title: 'Părăsiți camera',
    no_related_rooms: 'Nu au fost găsite camere asociate.',
    poll_rooms_selection: 'Partajați sondajul în camere:',
    my_rooms_no_member_rooms: 'Nu sunteți membru al niciunei camere. De ce nu <0>explorați?<0>',
    suggest_new_rooms_title: 'Nu găsiți camera de care aveți nevoie?',
    latest_posts_your_topics: 'Ultimele postări din camerele dumneavoastră',
    people_in_the_room: 'Persoane în cameră',
    people_in_the_room_no_people: 'De ce nu fiți primul care se alătură camerei și creați o postare.',
    people_in_the_room_no_companions_and_people: 'Felicitări! Sunteți prima persoană din această cameră',
    no_moderators_in_the_room: 'Niciun moderator în această cameră',
    select_rooms_for_post_modal_title: 'Vă rugăm să selectați camerele în care doriți să partajați postarea:',
    rooms_create_post_dialog_placeholder_nonmember: 'Trebuie să vă alăturați camerei pentru a posta',
    rooms_blocked_member: 'Din păcate, accesul dumneavoastră la această cameră a fost revocat',
    community_not_member_of_room: 'Ne pare rău, nu puteți accesa această cameră',
    community_room_library_no_files: 'Niciun fișier atașat acestei camere.',
    community_room_library_details: 'Detalii',
    community_room_library_download: 'Descarcă',
    community_room_library_summarise: 'Rezumă',
    community_room_library_translate: 'Traduce',
    community_room_library_delete: 'Șterge',
    community_room_library_update: 'Actualizează',
    profile_settings_ai_settings_title: 'Setări AI',
    profile_settings_ai_tokens_allowed: 'Jetoane permise pe zi:',
    profile_settings_ai_tokens_used: 'Jetoane utilizate astăzi:',
    featured_content_delete_file_warning_title: 'Avertisment',
    featured_content_delete_file_warning_message: 'Când ștergeți un fișier aici, acesta va fi șters și din bibliotecă',
    featured_post_library_delete_warning_title: 'Avertisment',
    featured_post_library_delete_warning_description: 'Toate imaginile și documentele atașate acestei Postări Evidențiate vor fi șterse și din bibliotecă',
    share_story_no_rooms_for_member: 'Se pare că nu sunteți membru al niciunei camere. De ce nu explorați și vă alăturați?',
    community_room_library_file_type: 'Tip fișier',
    community_room_library_language: 'Limbă',
    community_room_library_valid_until: 'Valabil până la',
    community_room_library_date_created: 'Data creării',
    snackbar_community_member_not_in_room: 'Trebuie să vă alăturați camerei {{roomName}} pentru a răspunde sau a aprecia.',
    snackbar_community_member_not_in_room_poll: 'Trebuie să vă alăturați camerei {{roomName}} pentru a crea un sondaj.',
    snackbar_community_moderator_remove_member_success: 'Membru eliminat din cameră!',
    snackbar_community_cannot_join_room: 'Nu vă puteți alătura camerei.',
    snackbar_community_cannot_join_room_blocked: 'Ați fost blocat din această cameră.',
    snackbar_community_cannot_leave_room: 'Nu puteți părăsi camera.',
    snackbar_community_join_room_success: 'V-ați alăturat camerei!',
    snackbar_community_leave_room_success: 'Ați părăsit camera!',
    polls_widget_no_polls: 'Nu există încă sondaje în această cameră.',
    poll_proposal_additional_info: 'Un moderator al camerei vă va contacta pentru mai multe informații despre sondaj.',
    room_leave_confirmation_description: 'Sunteți sigur că doriți să părăsiți această cameră?',
    story_choose_rooms_subtitle: 'Puteți partaja această poveste în următoarele camere:',
    story_published_modal_title: 'Poveste publicată cu succes! Doriți să o partajați în camerele dumneavoastră?',
    story_published_modal_title_mobile: 'Vă mulțumim pentru publicarea poveștii. Doriți să o partajați în camerele dumneavoastră?',
    moderator_pin_post_dialog_description: 'Sunteți sigur că doriți să fixați această postare în cameră?',
    moderator_remove_member_dialog_description: 'Sunteți sigur că doriți să eliminați acest membru din cameră?',
    errors_posts_not_allowed_in_room: 'Postările nu sunt permise în această cameră.',
    errors_no_posts_found_room_member: 'Fiți primul care postează în această cameră!',
    errors_no_posts_found_room_not_member: 'Alăturați-vă camerei și postați ceva!',
    errors_no_posts_found_room: 'Fiți primul care postează în această cameră!',
    notifications_settings_someone_publishes_a_story_label: 'Poveste nouă în Camerele mele',
    share_story_to_rooms_second_title: 'Doriți să o partajați în camerele dumneavoastră?',
    private_room_code_widget_title: 'Aveți un cod pentru o Cameră sau un Hub privat?',
    private_room_shim_name_title_dialog: 'Bine ați venit în această cameră privată!',
    private_room_shim_name_subtitle_dialog: 'Folosiți numele sub care persoanele din această cameră vă cunosc.',
    private_room_shim_name_subtitle_dialog_2: 'Veți rămâne anonim în afara acestei camere.',
    companions_description_no_companion_in_room: 'Nu aveți încă însoțitori în această cameră. Participați la conversații și găsiți membri cu care să vă conectați.',
    companions_description_no_companion_in_community: 'Nu aveți încă însoțitori. De ce nu explorați camere și găsiți membri cu care să vă conectați?',
    companions_no_companion: 'Nu aveți încă însoțitori. Vizitați camera membrilor noi pentru a găsi membri carecircle și a vă conecta',
    personal_private_room_widget_button: 'Creați o cameră privată',
    personal_private_room_dialog_title: 'Creați camera dumneavoastră privată',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Cap',
    body_part_first_level_chest: 'Piept',
    body_part_first_level_abdomen: 'Abdomen',
    body_part_first_level_pelvis: 'Pelvis',
    body_part_first_level_arms: 'Brațe',
    body_part_first_level_legs: 'Picioare',
    body_part_first_level_back: 'Spate',
    body_part_first_level_buttocks: 'Fese',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Există 'camere' pe care le puteți vizita anonim și vă puteți alătura discuțiilor dacă doriți. Puteți posta comentarii sau întrebări, sau puteți găsi compani care vă vor sprijini pe parcursul călătoriei dumneavoastră.",
    conversations: 'Conversații',
    // Featured content overrides
    stories_carousel_title: 'Povești recomandate',
    content_carousel_title: 'Conținut recomandat',
    content_carousel_add_new_post_button: 'Adaugă Conținut Recomandat nou',
    content_carousel_no_content_moderator_subtitle: 'Anunțați pe toți din această cameră despre cele mai recente știri, anunțuri și resurse. Conținutul recomandat poate fi creat doar din aplicația web în acest moment.',
    room_content_type_featured_posts_and_library_option: 'Postări Recomandate și Bibliotecă',
    room_content_type_featured_posts_option: 'Postări Recomandate',
    room_create_content_success: 'Elementul de conținut recomandat a fost creat cu succes',
    room_update_content_success: 'Elementul de conținut recomandat a fost actualizat cu succes',
    room_delete_content_success: 'Elementul de conținut recomandat a fost șters cu succes',
    room_delete_content_error: 'A apărut o eroare la încercarea de a șterge elementul de conținut recomandat',
    room_confirm_delete_featured_item_title: 'Ștergeți conținutul recomandat',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Cum te simți',
    wellness_tracker_morning: 'azi dimineață',
    wellness_tracker_afternoon: 'azi după-amiază',
    wellness_tracker_evening: 'azi seară',
    // Navigation "Previous" overrides
    previous: 'Anterior',
    health_picture_symptom_checker_previous: 'Anterior',
    errors_to_previous_link: 'pagina anterioară',
    errors_to_previous_mobile: 'Du-mă la <0>ecranul anterior</0>.',
    errors_to_previous_link_mobile: 'ecranul anterior',
    stories_carousel_previous_story: 'Povestea anterioară',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Scrieți un comentariu...',
    chat_input_placeholder: 'Scrieți un mesaj...',
    ai_chatbot_widget_input_placeholder: 'Scrieți ceva...',
    create_post_input_placeholder: 'Scrieți un mesaj...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Parcurs de Sănătate',
    health_picture_name: 'Profil de Sănătate',
    ai_context_mode_health_journey: 'Parcurs de Sănătate',
    health_journey_second_tab: 'Profil de Sănătate',
    health_journey_add_new: 'Creați un parcurs de sănătate',
    health_journey_rename_journey: 'Modificați Parcursul de Sănătate',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Creați Parcurs de Sănătate',
    hj_already_create_for_myself_4: 'Vizualizați Parcursul de Sănătate',
    health_journey_privacy_dialog_health_journey_link: 'Parcurs de Sănătate',
    health_picture_view_health_journey: 'Vizualizați parcursul de sănătate',
    health_picture_symptom_checker_save_to_health_journey: 'Salvați în Parcursul de Sănătate',
    health_journey_my_picture: 'Profilul meu de sănătate',
    health_journey_picture_not_created_title: 'Începeți să vă urmăriți parcursul de sănătate',
    health_journey_start_health_picture_title: 'Faceți primul pas creând un profil de sănătate',
    verification_successful_subtitle: 'Veți fi redirecționat în curând.\nÎncepeți-vă parcursul de sănătate astăzi.',
    rooms_redirect_to_health_picture: 'completați profilul de sănătate',
    health_picture_no_symptoms_info: 'Înregistrați simptomele pentru un profil de sănătate complet',
    ai_context_label_health_journey: 'Contextul actual este Parcursul de Sănătate al:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Creați-vă acum propriul Parcurs de Sănătate pentru a avea o imagine de ansamblu asupra bunăstării și pentru a explora alte funcționalități interesante.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Dați un nume parcursului dvs.',
    new_health_journey_placeholder: 'Puteți avea mai multe parcursuri pentru dvs. sau pentru altcineva.',
    new_health_journey_validation_required_name: 'Vă rugăm introduceți un nume pentru parcursul dvs.',
    new_health_journey_validation_length_name: 'Numele parcursului de sănătate nu trebuie să depășească 30 de caractere',
    hj_already_create_for_myself_1: 'Se pare că',
    hj_already_create_for_myself_2: 'ați creat deja un Parcurs de Sănătate',
    hj_already_create_for_myself_3: 'pentru dvs.',
    hj_multiple_dialog_title: 'Pentru cine creați un parcurs de sănătate?',
    hj_multiple_for_myself: 'Vreau să creez acest parcurs pentru mine',
    hj_multiple_for_someone_else: 'Vreau să creez acest parcurs pentru altcineva',
    // Conversation hide override
    conversation_hide: 'Ascunde',
    // Consultation form overrides
    consultation_name: 'Consultație',
    health_picture_consultations: 'Consultațiile mele',
    health_picture_add_consultation: 'Adăugați o consultație',
    health_picture_edit_consultation: 'Editați consultația',
    health_journey_consultation_type: 'Tipul consultației:',
    health_journey_timeline_consultation_type: 'Tip:',
    mycare_consultation_type_label: 'Tipul consultației',
    mycare_consultation_provider_label: 'Numele furnizorului de sănătate',
    mycare_consultation_speciality_label: 'Specialitate',
    my_care_journal_notes: 'Notițe',
    my_care_journal_description_placeholder: 'Faceți notițe despre orice doriți...',
    content_carousel_no_content_moderator_title: 'Creați conținut pentru camera dvs.',
    content_carousel_no_content_member_title: 'Rămâneți în legătură cu camera dvs.',
    content_carousel_no_content_member_subtitle: 'Aici veți găsi cele mai recente știri, anunțuri și resurse.',
    ai_context_label_room: 'Contextul curent este Camera:',
    companions_block_user: 'Blochează',
    companions_unblock_user: 'Deblochează',
    companion_request_block: 'Blochează',
    companion_request_unblock: 'Deblochează',
    companion_report: 'Raportează',
    companion_block_message: 'Sunteți sigur că doriți să blocați pe {{memberDisplayName}} și să îl eliminați ca însoțitor?',
    companion_unblock_message: 'Sunteți sigur că doriți să deblocați pe {{memberDisplayName}}?',
    companion_report_message: 'Sunteți sigur că doriți să raportați pe {{memberDisplayName}}?',
    companions_block_successfully: 'Ați blocat cu succes însoțitorul!',
    companions_unblock_successfully: 'Ați deblocat cu succes însoțitorul!',
    companions_cannot_block_administrator: 'Nu puteți bloca un administrator!',
    health_journey_select_date: 'Selectați data',
  },
  ka: {
    ai_context_mode_all_rooms: 'ავტომატური',
    like: 'მოწონება',
    likes: 'მოწონება',
    community_post_like: 'მოწონება',
    community_post_likes: 'მოწონება',
    reaction_like_label: 'მოწონება',
    story_allow_likes: 'მოწონებების დაშვება',
    comment: 'კომენტარი',
    comments: 'კომენტარები',
    reaction_care_label: 'მზრუნველობა',
    reaction_helpful_label: 'სასარგებლო',
    story_draft: 'მონახაზი',
    stories_drafts: 'მონახაზები',
    checker: 'შემოწმება',
    health_picture_complete: 'დასრულება',
    health_picture_diagnosis_type_nonmedical: 'სავარაუდო',
    preview: 'გადახედვა',
    journal_name: 'ჩანაწერი',
    community: 'ოთახები',
    stories: 'ამბები',
    profile_widget_journey: 'გზები',
    browser: 'ბრაუზერი',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'დაკავშირება',
    community_room_type_hub: 'Hub',
    subgroups: 'ქვეჯგუფები',
    max: 'მაქს',
    companions_title: 'თანამოაზრეები',
    health_picture_ongoing: 'მიმდინარე',
    poll_period_unlimited: 'შეუზღუდავი',
    joined_rooms_title: 'ოთახები ამ ჰაბში გაწევრიანებული',
    story_save_as_draft: 'მონახაზად შენახვა',
    story_saved_draft_snackbar: 'მონახაზი წარმატებით შეინახა',
    story_save_draft_success_dialog_content: 'თქვენი მონახაზი წარმატებით შეინახა!',
    my_stories_empty_drafts_first_line: 'თქვენ არ გაქვთ ამბები მონახაზში.',
    health_picture_add_journal: 'ჩანაწერის დამატება',
    health_picture_edit_journal: 'ჩანაწერის რედაქტირება',
    health_picture_journal: 'ჩემი ჩანაწერები',
    health_journey_journal_title: 'ჩანაწერი',
    health_journey_journal_date: 'ჩანაწერის თარიღი',
    delete_journal_confirmation_title: 'ჩანაწერის წაშლა',
    delete_journal_confirmation_description: 'დარწმუნებული ხართ, რომ გსურთ ამ ჩანაწერის წაშლა? ეს მოქმედება შეუქცევადია.',
    journal_deleted_successfully: 'ჩანაწერი წარმატებით წაიშალა.',
    error_deleting_journal: 'ჩანაწერის წაშლისას მოხდა შეცდომა. გთხოვთ, სცადოთ ხელახლა.',
    stories_first_tab: 'ყველა ამბავი',
    stories_third_tab: 'ჩემი ამბები',
    stories_suggested: 'შემოთავაზებული ამბები',
    stories_my_stories: 'ჩემი ამბები',
    companions_in_room_title: 'თანამოაზრეები ოთახში',
    health_picture_snackbar_adding_ongoing_symptom: 'თქვენ უკვე დაამატეთ ეს სიმპტომი და ის ჯერ კიდევ მიმდინარეა! გთხოვთ, განაახლოთ ის ჩანაწერი!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'თქვენ უკვე დაამატეთ ეს დიაგნოზი და ის ჯერ კიდევ მიმდინარეა! გთხოვთ, განაახლოთ ის ჩანაწერი!',
    health_picture_snackbar_adding_ongoing_treatment: 'თქვენ უკვე დაამატეთ ეს მკურნალობა და ის ჯერ კიდევ მიმდინარეა! გთხოვთ, განაახლოთ ის ჩანაწერი!',
    health_picture_snackbar_adding_ongoing_medication: 'თქვენ უკვე დაამატეთ ეს მედიკამენტი და ის ჯერ კიდევ მიმდინარეა! გთხოვთ, განაახლოთ ის ჩანაწერი!',
    your_subgroups: 'თქვენი ქვეჯგუფები',
    other_subgroups: 'სხვა ქვეჯგუფები',
    community_room_type_hub_room: 'Hub ოთახი',
    // Room consistency overrides
    rooms_title: 'ოთახები',
    my_rooms_title: 'ჩემი ოთახები და Hub-ები',
    profile_widget_rooms: 'ოთახები',
    bottom_nav_community: 'ოთახები',
    search_rooms_title: 'ოთახები',
    community_first_tab: 'ჩემი ოთახები',
    community_second_tab: 'ყველა ოთახი',
    community_room_type_private_room: 'პრივატული ოთახი',
    community_room_type_public_room: 'საჯარო ოთახი',
    related_rooms: 'დაკავშირებული ოთახები',
    my_rooms_explore_other_rooms: 'სხვა ოთახების შესწავლა',
    rooms_autocomplete_caption: 'ოთახების შესწავლა:',
    rooms_no_result_found: 'ოთახები ვერ მოიძებნა. სცადეთ სხვა ძიება.',
    rooms_view_all_button: 'ყველა ოთახის ნახვა',
    rooms_view_rooms_button: 'ოთახების ნახვა',
    rooms_leave_room: 'ოთახის დატოვება',
    rooms_related_rooms_loading: 'დაკავშირებული ოთახები იტვირთება...',
    rooms_related_rooms_end: 'მეტი დაკავშირებული ოთახი არ არის',
    rooms_by_topic_view_more_button: 'მეტი ოთახის ნახვა',
    share_story_to_rooms_no_rooms_button: 'ოთახების შესწავლა',
    room_join_button: 'ოთახში შესვლა',
    room_join_button_pending: 'ოთახის მოთხოვნა განხილვაშია',
    room_leave_confirmation_title: 'ოთახის დატოვება',
    no_related_rooms: 'დაკავშირებული ოთახები ვერ მოიძებნა.',
    poll_rooms_selection: 'გამოკითხვის გაზიარება ოთახებში:',
    my_rooms_no_member_rooms: 'თქვენ არ ხართ არცერთი ოთახის წევრი. რატომ არ <0>შეისწავლით?<0>',
    suggest_new_rooms_title: 'ვერ პოულობთ საჭირო ოთახს?',
    latest_posts_your_topics: 'უახლესი პოსტები თქვენს ოთახებში',
    people_in_the_room: 'ადამიანები ოთახში',
    people_in_the_room_no_people: 'რატომ არ იქნებით პირველი, ვინც შევა ოთახში და შექმნის პოსტს.',
    people_in_the_room_no_companions_and_people: 'გილოცავთ! თქვენ ხართ პირველი ადამიანი ამ ოთახში',
    no_moderators_in_the_room: 'ამ ოთახში მოდერატორები არ არიან',
    select_rooms_for_post_modal_title: 'გთხოვთ, აირჩიეთ ოთახები, რომლებშიც გსურთ თქვენი პოსტის გაზიარება:',
    rooms_create_post_dialog_placeholder_nonmember: 'პოსტის გამოსაქვეყნებლად უნდა შეხვიდეთ ოთახში',
    rooms_blocked_member: 'სამწუხაროდ, ამ ოთახზე თქვენი წვდომა გაუქმებულია',
    community_not_member_of_room: 'ბოდიში, თქვენ ვერ შეხვალთ ამ ოთახში',
    community_room_library_no_files: 'ამ ოთახს ფაილები არ აქვს მიმაგრებული.',
    community_room_library_details: 'დეტალები',
    community_room_library_download: 'ჩამოტვირთვა',
    community_room_library_summarise: 'შეჯამება',
    community_room_library_translate: 'თარგმნა',
    community_room_library_delete: 'წაშლა',
    community_room_library_update: 'განახლება',
    profile_settings_ai_settings_title: 'AI პარამეტრები',
    profile_settings_ai_tokens_allowed: 'დაშვებული ტოკენები დღეში:',
    profile_settings_ai_tokens_used: 'დღეს გამოყენებული ტოკენები:',
    featured_content_delete_file_warning_title: 'გაფრთხილება',
    featured_content_delete_file_warning_message: 'როდესაც აქ ფაილს წაშლით, ის ბიბლიოთეკიდანაც წაიშლება',
    featured_post_library_delete_warning_title: 'გაფრთხილება',
    featured_post_library_delete_warning_description: 'ამ გამორჩეულ პოსტზე მიმაგრებული ყველა სურათი და დოკუმენტი ასევე წაიშლება ბიბლიოთეკიდან',
    share_story_no_rooms_for_member: 'როგორც ჩანს, თქვენ არცერთი ოთახის წევრი არ ხართ. რატომ არ შეისწავლით და შეუერთდებით?',
    community_room_library_file_type: 'ფაილის ტიპი',
    community_room_library_language: 'ენა',
    community_room_library_valid_until: 'მოქმედებს -მდე',
    community_room_library_date_created: 'შექმნის თარიღი',
    snackbar_community_member_not_in_room: 'პასუხის ან მოწონებისთვის უნდა შეუერთდეთ ოთახს {{roomName}}.',
    snackbar_community_member_not_in_room_poll: 'გამოკითხვის შესაქმნელად უნდა შეუერთდეთ ოთახს {{roomName}}.',
    snackbar_community_moderator_remove_member_success: 'წევრი ოთახიდან წაიშალა!',
    snackbar_community_cannot_join_room: 'თქვენ ვერ შეხვალთ ოთახში.',
    snackbar_community_cannot_join_room_blocked: 'თქვენ დაბლოკილი ხართ ამ ოთახში.',
    snackbar_community_cannot_leave_room: 'თქვენ ვერ დატოვებთ ოთახს.',
    snackbar_community_join_room_success: 'თქვენ შეუერთდით ოთახს!',
    snackbar_community_leave_room_success: 'თქვენ დატოვეთ ოთახი!',
    polls_widget_no_polls: 'ამ ოთახში ჯერ არ არის გამოკითხვები.',
    poll_proposal_additional_info: 'ოთახის მოდერატორი დაგიკავშირდებათ გამოკითხვის შესახებ დამატებითი ინფორმაციისთვის.',
    room_leave_confirmation_description: 'დარწმუნებული ხართ, რომ გსურთ ამ ოთახის დატოვება?',
    story_choose_rooms_subtitle: 'შეგიძლიათ ეს ამბავი გააზიაროთ შემდეგ ოთახებში:',
    story_published_modal_title: 'ამბავი წარმატებით გამოქვეყნდა! გსურთ მისი გაზიარება თქვენს ოთახებში?',
    story_published_modal_title_mobile: 'გმადლობთ ამბის გამოქვეყნებისთვის. გსურთ მისი გაზიარება თქვენს ოთახებში?',
    moderator_pin_post_dialog_description: 'დარწმუნებული ხართ, რომ გსურთ ამ პოსტის მიმაგრება ოთახში?',
    moderator_remove_member_dialog_description: 'დარწმუნებული ხართ, რომ გსურთ ამ წევრის ოთახიდან წაშლა?',
    errors_posts_not_allowed_in_room: 'პოსტები ამ ოთახში ნებადართული არ არის.',
    errors_no_posts_found_room_member: 'იყავით პირველი, ვინც გამოაქვეყნებს პოსტს ამ ოთახში!',
    errors_no_posts_found_room_not_member: 'შეუერთდით ოთახს და გამოაქვეყნეთ რაიმე!',
    errors_no_posts_found_room: 'იყავით პირველი, ვინც გამოაქვეყნებს პოსტს ამ ოთახში!',
    notifications_settings_someone_publishes_a_story_label: 'ახალი ამბავი ჩემს ოთახებში',
    share_story_to_rooms_second_title: 'გსურთ მისი გაზიარება თქვენს ოთახებში?',
    private_room_code_widget_title: 'გაქვთ კოდი პრივატული ოთახისთვის ან Hub-ისთვის?',
    private_room_shim_name_title_dialog: 'კეთილი იყოს თქვენი მობრძანება ამ პრივატულ ოთახში!',
    private_room_shim_name_subtitle_dialog: 'გამოიყენეთ სახელი, რომლითაც ამ ოთახში ადამიანები გიცნობენ.',
    private_room_shim_name_subtitle_dialog_2: 'ამ ოთახის გარეთ ანონიმური დარჩებით.',
    companions_description_no_companion_in_room: 'ამ ოთახში ჯერ არ გყავთ თანამოაზრეები. შეუერთდით საუბრებს და იპოვეთ წევრები დასაკავშირებლად.',
    companions_description_no_companion_in_community: 'ჯერ არ გყავთ თანამოაზრეები. რატომ არ შეისწავლით ოთახებს და იპოვით წევრებს დასაკავშირებლად?',
    companions_no_companion: 'ჯერ არ გყავთ თანამოაზრეები. ეწვიეთ ახალი წევრების ოთახს carecircle-ის წევრების საპოვნელად და დასაკავშირებლად',
    personal_private_room_widget_button: 'პრივატული ოთახის შექმნა',
    personal_private_room_dialog_title: 'თქვენი პრივატული ოთახის შექმნა',
    // Body part Level 1 overrides
    body_part_first_level_head: 'თავი',
    body_part_first_level_chest: 'გულმკერდი',
    body_part_first_level_abdomen: 'მუცელი',
    body_part_first_level_pelvis: 'მენჯი',
    body_part_first_level_arms: 'ხელები',
    body_part_first_level_legs: 'ფეხები',
    body_part_first_level_back: 'ზურგი',
    body_part_first_level_buttocks: 'დუნდულები',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "არსებობს 'ოთახები', რომლებსაც შეგიძლიათ ანონიმურად ეწვიოთ და შეუერთდეთ დისკუსიებს, თუ გსურთ. შეგიძლიათ გამოაქვეყნოთ კომენტარები ან კითხვები, ან იპოვოთ თანამგზავრები, რომლებიც მხარს დაგიჭერენ თქვენს გზაზე.",
    conversations: 'ჩატები',
    // Featured content overrides
    stories_carousel_title: 'რჩეული ისტორიები',
    content_carousel_title: 'რჩეული კონტენტი',
    content_carousel_add_new_post_button: 'ახალი რჩეული კონტენტის დამატება',
    content_carousel_no_content_moderator_subtitle: 'აცნობეთ ამ ოთახში ყველას უახლესი სიახლეების, განცხადებებისა და რესურსების შესახებ. რჩეული კონტენტის შექმნა ამჟამად შესაძლებელია მხოლოდ ვებ-აპლიკაციიდან.',
    room_content_type_featured_posts_and_library_option: 'რჩეული პოსტები და ბიბლიოთეკა',
    room_content_type_featured_posts_option: 'რჩეული პოსტები',
    room_create_content_success: 'რჩეული კონტენტი წარმატებით შეიქმნა',
    room_update_content_success: 'რჩეული კონტენტი წარმატებით განახლდა',
    room_delete_content_success: 'რჩეული კონტენტი წარმატებით წაიშალა',
    room_delete_content_error: 'რჩეული კონტენტის წაშლისას შეცდომა დაფიქსირდა',
    room_confirm_delete_featured_item_title: 'რჩეული კონტენტის წაშლა',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'როგორ გრძნობთ თავს',
    wellness_tracker_morning: 'ამ დილით',
    wellness_tracker_afternoon: 'ამ შუადღისას',
    wellness_tracker_evening: 'ამ საღამოს',
    // Navigation "Previous" overrides
    previous: 'წინა',
    health_picture_symptom_checker_previous: 'წინა',
    errors_to_previous_link: 'წინა გვერდი',
    errors_to_previous_mobile: 'გადამიყვანე <0>წინა ეკრანზე</0>.',
    errors_to_previous_link_mobile: 'წინა ეკრანი',
    stories_carousel_previous_story: 'წინა ისტორია',
    // Input placeholder overrides
    story_comment_input_placeholder: 'დაწერეთ კომენტარი...',
    chat_input_placeholder: 'დაწერეთ შეტყობინება...',
    ai_chatbot_widget_input_placeholder: 'დაწერეთ რაღაც...',
    create_post_input_placeholder: 'დაწერეთ შეტყობინება...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'ჯანმრთელობის გზა',
    health_picture_name: 'ჯანმრთელობის სურათი',
    ai_context_mode_health_journey: 'ჯანმრთელობის გზა',
    health_journey_second_tab: 'ჯანმრთელობის სურათი',
    health_journey_add_new: 'ჯანმრთელობის გზის შექმნა',
    health_journey_rename_journey: 'ჯანმრთელობის გზის შეცვლა',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'ჯანმრთელობის გზის შექმნა',
    hj_already_create_for_myself_4: 'ჯანმრთელობის გზის ნახვა',
    health_journey_privacy_dialog_health_journey_link: 'ჯანმრთელობის გზა',
    health_picture_view_health_journey: 'ჯანმრთელობის გზის ნახვა',
    health_picture_symptom_checker_save_to_health_journey: 'შენახვა ჯანმრთელობის გზაში',
    health_journey_my_picture: 'ჩემი ჯანმრთელობის სურათი',
    health_journey_picture_not_created_title: 'დაიწყეთ თქვენი ჯანმრთელობის გზის თვალყურის დევნება',
    health_journey_start_health_picture_title: 'გადადგით პირველი ნაბიჯი ჯანმრთელობის სურათის შექმნით',
    verification_successful_subtitle: 'მალე გადამისამართდებით.\nდაიწყეთ თქვენი ჯანმრთელობის გზა დღესვე.',
    rooms_redirect_to_health_picture: 'შეავსეთ თქვენი ჯანმრთელობის სურათი',
    health_picture_no_symptoms_info: 'ჩაწერეთ თქვენი სიმპტომები სრული ჯანმრთელობის სურათისთვის',
    ai_context_label_health_journey: 'ამჟამინდელი კონტექსტი არის ჯანმრთელობის გზა:',
    wellness_tracker_no_journey_for_himself_subtitle: 'შექმენით თქვენი საკუთარი ჯანმრთელობის გზა კეთილდღეობის მიმოხილვისა და სხვა საინტერესო ფუნქციების შესასწავლად.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'მიეცით თქვენს გზას სახელი',
    new_health_journey_placeholder: 'შეგიძლიათ გქონდეთ რამდენიმე გზა საკუთარი თავისთვის ან სხვისთვის.',
    new_health_journey_validation_required_name: 'გთხოვთ შეიყვანოთ სახელი თქვენი გზისთვის',
    new_health_journey_validation_length_name: 'ჯანმრთელობის გზის სახელი არ უნდა აღემატებოდეს 30 სიმბოლოს',
    hj_already_create_for_myself_1: 'როგორც ჩანს, თქვენ',
    hj_already_create_for_myself_2: 'უკვე შექმენით ჯანმრთელობის გზა',
    hj_already_create_for_myself_3: 'საკუთარი თავისთვის.',
    hj_multiple_dialog_title: 'ვისთვის ქმნით ჯანმრთელობის გზას?',
    hj_multiple_for_myself: 'მსურს ეს გზა შევქმნა ჩემთვის',
    hj_multiple_for_someone_else: 'მსურს ეს გზა შევქმნა სხვისთვის',
    // Conversation hide override
    conversation_hide: 'დამალვა',
    // Consultation form overrides
    consultation_name: 'კონსულტაცია',
    health_picture_consultations: 'ჩემი კონსულტაციები',
    health_picture_add_consultation: 'კონსულტაციის დამატება',
    health_picture_edit_consultation: 'კონსულტაციის რედაქტირება',
    health_journey_consultation_type: 'კონსულტაციის ტიპი:',
    health_journey_timeline_consultation_type: 'ტიპი:',
    mycare_consultation_type_label: 'კონსულტაციის ტიპი',
    mycare_consultation_provider_label: 'ჯანდაცვის პროვაიდერის სახელი',
    mycare_consultation_speciality_label: 'სპეციალობა',
    my_care_journal_notes: 'ჩანაწერები',
    my_care_journal_description_placeholder: 'ჩაინიშნეთ ყველაფერი, რაც გსურთ...',
    content_carousel_no_content_moderator_title: 'შექმენით კონტენტი თქვენი ოთახისთვის',
    content_carousel_no_content_member_title: 'იყავით თქვენს ოთახთან კავშირში',
    content_carousel_no_content_member_subtitle: 'აქ იხილავთ უახლეს სიახლეებს, განცხადებებსა და რესურსებს.',
    ai_context_label_room: 'მიმდინარე კონტექსტი არის ოთახი:',
    companions_block_user: 'დაბლოკვა',
    companions_unblock_user: 'განბლოკვა',
    companion_request_block: 'დაბლოკვა',
    companion_request_unblock: 'განბლოკვა',
    companion_report: 'შეტყობინება',
    companion_block_message: 'დარწმუნებული ხართ, რომ გსურთ {{memberDisplayName}}-ის დაბლოკვა და თანამგზავრად წაშლა?',
    companion_unblock_message: 'დარწმუნებული ხართ, რომ გსურთ {{memberDisplayName}}-ის განბლოკვა?',
    companion_report_message: 'დარწმუნებული ხართ, რომ გსურთ {{memberDisplayName}}-ზე შეტყობინება?',
    companions_block_successfully: 'თქვენ წარმატებით დაბლოკეთ თანამგზავრი!',
    companions_unblock_successfully: 'თქვენ წარმატებით განბლოკეთ თანამგზავრი!',
    companions_cannot_block_administrator: 'ადმინისტრატორის დაბლოკვა შეუძლებელია!',
    health_journey_select_date: 'აირჩიეთ თარიღი',
  },
  nl: {
    ai_context_mode_all_rooms: 'Automatisch',
    like: 'Vind ik leuk',
    likes: 'Vind-ik-leuks',
    community_post_like: 'Vind ik leuk',
    community_post_likes: 'Vind-ik-leuks',
    reaction_like_label: 'Vind ik leuk',
    story_allow_likes: 'Vind-ik-leuks toestaan',
    comment: 'Reactie',
    comments: 'Reacties',
    reaction_care_label: 'Medeleven',
    reaction_helpful_label: 'Nuttig',
    story_draft: 'Concept',
    stories_drafts: 'Concepten',
    checker: 'Checker',
    health_picture_complete: 'Voltooien',
    health_picture_diagnosis_type_nonmedical: 'Vermoedelijk',
    preview: 'Voorvertoning',
    journal_name: 'Dagboek',
    community: 'Ruimtes',
    stories: 'Verhalen',
    profile_widget_journey: 'Trajecten',
    browser: 'Browser',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Verbinden',
    community_room_type_hub: 'Hub',
    subgroups: 'Subgroepen',
    max: 'max',
    companions_title: 'Metgezellen',
    health_picture_ongoing: 'Lopend',
    poll_period_unlimited: 'Onbeperkt',
    joined_rooms_title: 'Ruimtes in deze Hub',
    story_save_as_draft: 'Opslaan als concept',
    story_saved_draft_snackbar: 'Concept succesvol opgeslagen',
    story_save_draft_success_dialog_content: 'Uw concept is succesvol opgeslagen!',
    my_stories_empty_drafts_first_line: 'U heeft geen verhalen in Concept.',
    health_picture_add_journal: 'Dagboeknotitie toevoegen',
    health_picture_edit_journal: 'Dagboeknotitie bewerken',
    health_picture_journal: 'Mijn dagboeken',
    health_journey_journal_title: 'Dagboek',
    health_journey_journal_date: 'Dagboekdatum',
    delete_journal_confirmation_title: 'Dagboek verwijderen',
    delete_journal_confirmation_description: 'Weet u zeker dat u dit dagboek wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    journal_deleted_successfully: 'Dagboek succesvol verwijderd.',
    error_deleting_journal: 'Er is een probleem opgetreden bij het verwijderen van het dagboek. Probeer het opnieuw.',
    stories_first_tab: 'Alle verhalen',
    stories_third_tab: 'Mijn verhalen',
    stories_suggested: 'Voorgestelde verhalen',
    stories_my_stories: 'Mijn verhalen',
    companions_in_room_title: 'Metgezellen in de ruimte',
    health_picture_snackbar_adding_ongoing_symptom: 'U heeft dit symptoom al toegevoegd en het is nog lopend! Werk die vermelding bij!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'U heeft deze diagnose al toegevoegd en deze is nog lopend! Werk die vermelding bij!',
    health_picture_snackbar_adding_ongoing_treatment: 'U heeft deze behandeling al toegevoegd en deze is nog lopend! Werk die vermelding bij!',
    health_picture_snackbar_adding_ongoing_medication: 'U heeft dit medicijn al toegevoegd en het is nog lopend! Werk die vermelding bij!',
    your_subgroups: 'Uw subgroepen',
    other_subgroups: 'Andere subgroepen',
    community_room_type_hub_room: 'Hub-ruimte',
    // Room consistency overrides
    rooms_title: 'Ruimtes',
    my_rooms_title: 'Mijn Ruimtes & Hubs',
    profile_widget_rooms: 'Ruimtes',
    bottom_nav_community: 'Ruimtes',
    search_rooms_title: 'Ruimtes',
    community_first_tab: 'Mijn ruimtes',
    community_second_tab: 'Alle ruimtes',
    community_room_type_private_room: 'Privéruimte',
    community_room_type_public_room: 'Openbare Ruimte',
    related_rooms: 'Gerelateerde Ruimtes',
    my_rooms_explore_other_rooms: 'Andere ruimtes verkennen',
    rooms_autocomplete_caption: 'Ruimtes verkennen:',
    rooms_no_result_found: 'Geen ruimtes gevonden. Probeer een andere zoekopdracht.',
    rooms_view_all_button: 'Alle ruimtes bekijken',
    rooms_view_rooms_button: 'Ruimtes bekijken',
    rooms_leave_room: 'De ruimte verlaten',
    rooms_related_rooms_loading: 'Gerelateerde ruimtes laden...',
    rooms_related_rooms_end: 'Geen gerelateerde ruimtes meer',
    rooms_by_topic_view_more_button: 'Meer ruimtes bekijken',
    share_story_to_rooms_no_rooms_button: 'Ruimtes verkennen',
    room_join_button: 'Word lid van de ruimte',
    room_join_button_pending: 'Ruimteverzoek in behandeling',
    room_leave_confirmation_title: 'De ruimte verlaten',
    no_related_rooms: 'Geen gerelateerde ruimtes gevonden.',
    poll_rooms_selection: 'Deel peiling in de ruimtes:',
    my_rooms_no_member_rooms: 'U bent geen lid van een ruimte. Waarom niet <0>verkennen?<0>',
    suggest_new_rooms_title: 'Kunt u de ruimte die u nodig heeft niet vinden?',
    latest_posts_your_topics: 'Laatste berichten in uw ruimtes',
    people_in_the_room: 'Mensen in de ruimte',
    people_in_the_room_no_people: 'Waarom bent u niet de eerste om lid te worden van de ruimte en een bericht te plaatsen.',
    people_in_the_room_no_companions_and_people: 'Gefeliciteerd! U bent de eerste persoon in deze ruimte',
    no_moderators_in_the_room: 'Geen moderatoren in deze ruimte',
    select_rooms_for_post_modal_title: 'Selecteer de ruimtes waarnaar u uw bericht wilt delen:',
    rooms_create_post_dialog_placeholder_nonmember: 'U moet lid worden van de ruimte om te kunnen posten',
    rooms_blocked_member: 'Helaas is uw toegang tot deze ruimte ingetrokken',
    community_not_member_of_room: 'Sorry, u heeft geen toegang tot deze ruimte',
    community_room_library_no_files: 'Geen bestanden aan deze ruimte gekoppeld.',
    community_room_library_details: 'Details',
    community_room_library_download: 'Downloaden',
    community_room_library_summarise: 'Samenvatten',
    community_room_library_translate: 'Vertalen',
    community_room_library_delete: 'Verwijderen',
    community_room_library_update: 'Bijwerken',
    profile_settings_ai_settings_title: 'AI-instellingen',
    profile_settings_ai_tokens_allowed: 'Toegestane tokens per dag:',
    profile_settings_ai_tokens_used: 'Vandaag gebruikte tokens:',
    featured_content_delete_file_warning_title: 'Waarschuwing',
    featured_content_delete_file_warning_message: 'Wanneer u hier een bestand verwijdert, wordt het ook uit de bibliotheek verwijderd',
    featured_post_library_delete_warning_title: 'Waarschuwing',
    featured_post_library_delete_warning_description: 'Alle bijgevoegde afbeeldingen en documenten bij deze Uitgelichte Post worden ook uit de bibliotheek verwijderd',
    share_story_no_rooms_for_member: 'Het lijkt erop dat u geen lid bent van een ruimte. Waarom niet verkennen en lid worden?',
    community_room_library_file_type: 'Bestandstype',
    community_room_library_language: 'Taal',
    community_room_library_valid_until: 'Geldig tot',
    community_room_library_date_created: 'Aanmaakdatum',
    snackbar_community_member_not_in_room: 'U moet lid worden van ruimte {{roomName}} om te reageren of te liken.',
    snackbar_community_member_not_in_room_poll: 'U moet lid worden van ruimte {{roomName}} om een peiling te maken.',
    snackbar_community_moderator_remove_member_success: 'Lid verwijderd uit de ruimte!',
    snackbar_community_cannot_join_room: 'U kunt niet lid worden van de ruimte.',
    snackbar_community_cannot_join_room_blocked: 'U bent geblokkeerd in deze ruimte.',
    snackbar_community_cannot_leave_room: 'U kunt de ruimte niet verlaten.',
    snackbar_community_join_room_success: 'U bent lid geworden van de ruimte!',
    snackbar_community_leave_room_success: 'U heeft de ruimte verlaten!',
    polls_widget_no_polls: 'Er zijn nog geen peilingen in deze ruimte.',
    poll_proposal_additional_info: 'Een moderator van de ruimte neemt contact op voor meer informatie over de peiling.',
    room_leave_confirmation_description: 'Weet u zeker dat u deze ruimte wilt verlaten?',
    story_choose_rooms_subtitle: 'U kunt dit verhaal delen in de volgende ruimtes:',
    story_published_modal_title: 'Verhaal succesvol gepubliceerd! Wilt u het delen in uw ruimtes?',
    story_published_modal_title_mobile: 'Bedankt voor het publiceren van uw verhaal. Wilt u het delen in uw ruimtes?',
    moderator_pin_post_dialog_description: 'Weet u zeker dat u dit bericht wilt vastzetten in de ruimte?',
    moderator_remove_member_dialog_description: 'Weet u zeker dat u dit lid wilt verwijderen uit de ruimte?',
    errors_posts_not_allowed_in_room: 'Berichten zijn niet toegestaan in deze ruimte.',
    errors_no_posts_found_room_member: 'Wees de eerste om te posten in deze ruimte!',
    errors_no_posts_found_room_not_member: 'Word lid van de ruimte en post iets!',
    errors_no_posts_found_room: 'Wees de eerste om te posten in deze ruimte!',
    notifications_settings_someone_publishes_a_story_label: 'Nieuw verhaal in Mijn Ruimtes',
    share_story_to_rooms_second_title: 'Wilt u het delen in uw ruimtes?',
    private_room_code_widget_title: 'Heeft u een code voor een privéruimte of -Hub?',
    private_room_shim_name_title_dialog: 'Welkom in deze privéruimte!',
    private_room_shim_name_subtitle_dialog: 'Gebruik de naam waaronder mensen in deze ruimte u kennen.',
    private_room_shim_name_subtitle_dialog_2: 'U blijft anoniem buiten deze ruimte.',
    companions_description_no_companion_in_room: 'U heeft nog geen metgezellen in deze ruimte. Neem deel aan gesprekken en vind leden om mee te verbinden.',
    companions_description_no_companion_in_community: 'U heeft nog geen metgezellen. Waarom niet ruimtes verkennen en leden vinden om mee te verbinden?',
    companions_no_companion: 'U heeft nog geen metgezellen. Bezoek de ruimte voor nieuwe leden om carecircle-leden te vinden en contact te leggen',
    personal_private_room_widget_button: 'Een privéruimte aanmaken',
    personal_private_room_dialog_title: 'Uw privéruimte aanmaken',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Hoofd',
    body_part_first_level_chest: 'Borst',
    body_part_first_level_abdomen: 'Buik',
    body_part_first_level_pelvis: 'Bekken',
    body_part_first_level_arms: 'Armen',
    body_part_first_level_legs: 'Benen',
    body_part_first_level_back: 'Rug',
    body_part_first_level_buttocks: 'Billen',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Er zijn 'ruimtes' die je anoniem kunt bezoeken en waar je kunt deelnemen aan discussies als je dat wilt. Je kunt opmerkingen of vragen plaatsen, of metgezellen vinden die je ondersteunen op je reis.",
    conversations: 'Chats',
    // Featured content overrides
    stories_carousel_title: 'Uitgelichte verhalen',
    content_carousel_title: 'Uitgelichte content',
    content_carousel_add_new_post_button: 'Nieuwe Uitgelichte Content toevoegen',
    content_carousel_no_content_moderator_subtitle: 'Laat iedereen in deze ruimte weten over het laatste nieuws, aankondigingen en bronnen. Uitgelichte content kan momenteel alleen vanuit de web-app worden aangemaakt.',
    room_content_type_featured_posts_and_library_option: 'Uitgelichte berichten & bibliotheek',
    room_content_type_featured_posts_option: 'Uitgelichte berichten',
    room_create_content_success: 'Uitgelicht content-item is succesvol aangemaakt',
    room_update_content_success: 'Uitgelicht content-item is succesvol bijgewerkt',
    room_delete_content_success: 'Uitgelicht content-item is succesvol verwijderd',
    room_delete_content_error: 'Er is een fout opgetreden bij het verwijderen van het uitgelichte content-item',
    room_confirm_delete_featured_item_title: 'Uitgelichte content verwijderen',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Hoe voel je je deze',
    wellness_tracker_morning: 'ochtend',
    wellness_tracker_afternoon: 'middag',
    wellness_tracker_evening: 'avond',
    // Navigation "Previous" overrides (Eerder → Vorige)
    previous: 'Vorige',
    health_picture_symptom_checker_previous: 'Vorige',
    errors_to_previous_link: 'vorige pagina',
    errors_to_previous_mobile: 'Breng me naar het <0>vorige scherm</0>.',
    errors_to_previous_link_mobile: 'vorige scherm',
    stories_carousel_previous_story: 'Vorig verhaal',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Typ een reactie...',
    chat_input_placeholder: 'Typ een bericht...',
    ai_chatbot_widget_input_placeholder: 'Typ iets...',
    create_post_input_placeholder: 'Typ een bericht...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Gezondheidspad',
    health_picture_name: 'Gezondheidsprofiel',
    ai_context_mode_health_journey: 'Gezondheidspad',
    health_journey_second_tab: 'Gezondheidsprofiel',
    health_journey_add_new: 'Gezondheidspad aanmaken',
    health_journey_rename_journey: 'Gezondheidspad aanpassen',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Gezondheidspad aanmaken',
    hj_already_create_for_myself_4: 'Gezondheidspad bekijken',
    health_journey_privacy_dialog_health_journey_link: 'Gezondheidspad',
    health_picture_view_health_journey: 'Gezondheidspad bekijken',
    health_picture_symptom_checker_save_to_health_journey: 'Opslaan in Gezondheidspad',
    health_journey_my_picture: 'Mijn gezondheidsprofiel',
    health_journey_picture_not_created_title: 'Begin met het bijhouden van uw gezondheidspad',
    health_journey_start_health_picture_title: 'Zet de eerste stap door een gezondheidsprofiel aan te maken',
    verification_successful_subtitle: 'U wordt binnenkort doorgestuurd.\nBegin vandaag nog met uw gezondheidspad.',
    rooms_redirect_to_health_picture: 'vul uw gezondheidsprofiel in',
    health_picture_no_symptoms_info: 'Registreer uw symptomen voor een volledig gezondheidsprofiel',
    ai_context_label_health_journey: 'De huidige context is het Gezondheidspad van:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Maak nu uw eigen Gezondheidspad aan voor een overzicht van uw welzijn en om andere interessante functies te ontdekken.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Geef uw pad een naam',
    new_health_journey_placeholder: 'U kunt meerdere paden hebben voor uzelf of voor iemand anders.',
    new_health_journey_validation_required_name: 'Voer een naam in voor uw pad',
    new_health_journey_validation_length_name: 'De naam van uw gezondheidspad mag niet meer dan 30 tekens bevatten',
    hj_already_create_for_myself_1: 'Het lijkt erop dat u',
    hj_already_create_for_myself_2: 'al een Gezondheidspad heeft aangemaakt',
    hj_already_create_for_myself_3: 'voor uzelf.',
    hj_multiple_dialog_title: 'Voor wie maakt u een gezondheidspad aan?',
    hj_multiple_for_myself: 'Ik wil dit pad voor mezelf aanmaken',
    hj_multiple_for_someone_else: 'Ik wil dit pad voor iemand anders aanmaken',
    // Conversation hide override
    conversation_hide: 'Verbergen',
    // Consultation form overrides
    consultation_name: 'Consultatie',
    health_picture_consultations: 'Mijn consultaties',
    health_picture_add_consultation: 'Consultatie toevoegen',
    health_picture_edit_consultation: 'Consultatie bewerken',
    health_journey_consultation_type: 'Type consultatie:',
    health_journey_timeline_consultation_type: 'Type:',
    mycare_consultation_type_label: 'Type consultatie',
    mycare_consultation_provider_label: 'Naam zorgverlener',
    mycare_consultation_speciality_label: 'Specialisme',
    my_care_journal_notes: 'Notities',
    my_care_journal_description_placeholder: 'Maak notities over alles wat u wilt...',
    content_carousel_no_content_moderator_title: 'Maak content voor uw ruimte',
    content_carousel_no_content_member_title: 'Blijf in contact met uw ruimte',
    content_carousel_no_content_member_subtitle: 'Hier vindt u het laatste nieuws, aankondigingen en bronnen.',
    ai_context_label_room: 'De huidige context is de Ruimte:',
    companions_block_user: 'Blokkeren',
    companions_unblock_user: 'Deblokkeren',
    companion_request_block: 'Blokkeren',
    companion_request_unblock: 'Deblokkeren',
    companion_report: 'Melden',
    companion_block_message: 'Weet je zeker dat je {{memberDisplayName}} wilt blokkeren en als metgezel wilt verwijderen?',
    companion_unblock_message: 'Weet je zeker dat je {{memberDisplayName}} wilt deblokkeren?',
    companion_report_message: 'Weet je zeker dat je {{memberDisplayName}} wilt melden?',
    companions_block_successfully: 'Je hebt de metgezel succesvol geblokkeerd!',
    companions_unblock_successfully: 'Je hebt de metgezel succesvol gedeblokkeerd!',
    companions_cannot_block_administrator: 'Je kunt een beheerder niet blokkeren!',
    health_journey_select_date: 'Selecteer datum',
  },
  it: {
    ai_context_mode_all_rooms: 'Automatico',
    like: 'Mi piace',
    likes: 'Mi piace',
    community_post_like: 'Mi piace',
    community_post_likes: 'Mi piace',
    reaction_like_label: 'Mi piace',
    story_allow_likes: 'Consenti i mi piace',
    comment: 'Commento',
    comments: 'Commenti',
    reaction_care_label: 'Mi sta a cuore',
    reaction_helpful_label: 'Utile',
    story_draft: 'Bozza',
    stories_drafts: 'Bozze',
    checker: 'Checker',
    health_picture_complete: 'Completa',
    health_picture_diagnosis_type_nonmedical: 'Sospetto',
    preview: 'Anteprima',
    journal_name: 'Diario',
    community: 'Stanze',
    stories: 'Storie',
    profile_widget_journey: 'Percorsi',
    browser: 'Browser',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Connettiti',
    community_room_type_hub: 'Hub',
    subgroups: 'Sottogruppi',
    max: 'max',
    companions_title: 'Compagni',
    health_picture_ongoing: 'In corso',
    poll_period_unlimited: 'Illimitato',
    joined_rooms_title: 'Stanze in questo Hub',
    story_save_as_draft: 'Salva come bozza',
    story_saved_draft_snackbar: 'Bozza salvata con successo',
    story_save_draft_success_dialog_content: 'La tua bozza è stata salvata con successo!',
    my_stories_empty_drafts_first_line: 'Non hai storie in bozza.',
    health_picture_add_journal: 'Aggiungi voce del diario',
    health_picture_edit_journal: 'Modifica voce del diario',
    health_picture_journal: 'I miei diari',
    health_journey_journal_title: 'Diario',
    health_journey_journal_date: 'Data del diario',
    delete_journal_confirmation_title: 'Elimina diario',
    delete_journal_confirmation_description: 'Sei sicuro di voler eliminare questo diario? Questa azione non può essere annullata.',
    journal_deleted_successfully: 'Diario eliminato con successo.',
    error_deleting_journal: 'Si è verificato un problema durante l\'eliminazione del diario. Riprova.',
    stories_first_tab: 'Tutte le storie',
    stories_third_tab: 'Le mie storie',
    stories_suggested: 'Storie suggerite',
    stories_my_stories: 'Le mie storie',
    companions_in_room_title: 'Compagni nella stanza',
    health_picture_snackbar_adding_ongoing_symptom: 'Hai già aggiunto questo sintomo ed è ancora in corso! Aggiorna quella voce!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Hai già aggiunto questa diagnosi ed è ancora in corso! Aggiorna quella voce!',
    health_picture_snackbar_adding_ongoing_treatment: 'Hai già aggiunto questo trattamento ed è ancora in corso! Aggiorna quella voce!',
    health_picture_snackbar_adding_ongoing_medication: 'Hai già aggiunto questo farmaco ed è ancora in corso! Aggiorna quella voce!',
    your_subgroups: 'I tuoi sottogruppi',
    other_subgroups: 'Altri sottogruppi',
    community_room_type_hub_room: 'Stanza Hub',
    // Room consistency overrides
    rooms_title: 'Stanze',
    my_rooms_title: 'Le mie Stanze e Hub',
    profile_widget_rooms: 'Stanze',
    bottom_nav_community: 'Stanze',
    search_rooms_title: 'Stanze',
    community_first_tab: 'Le mie stanze',
    community_second_tab: 'Tutte le stanze',
    community_room_type_private_room: 'Stanza privata',
    community_room_type_public_room: 'Stanza pubblica',
    related_rooms: 'Stanze correlate',
    my_rooms_explore_other_rooms: 'Esplora altre stanze',
    rooms_autocomplete_caption: 'Esplora stanze:',
    rooms_no_result_found: 'Nessuna stanza trovata. Prova un\'altra ricerca.',
    rooms_view_all_button: 'Vedi tutte le stanze',
    rooms_view_rooms_button: 'Vedi stanze',
    rooms_leave_room: 'Lascia la stanza',
    rooms_related_rooms_loading: 'Caricamento stanze correlate...',
    rooms_related_rooms_end: 'Nessuna altra stanza correlata',
    rooms_by_topic_view_more_button: 'Vedi più stanze',
    share_story_to_rooms_no_rooms_button: 'Esplora stanze',
    room_join_button: 'Unisciti alla stanza',
    room_join_button_pending: 'Richiesta stanza in attesa',
    room_leave_confirmation_title: 'Lascia la stanza',
    no_related_rooms: 'Nessuna stanza correlata trovata.',
    poll_rooms_selection: 'Condividi sondaggio nelle stanze:',
    my_rooms_no_member_rooms: 'Non sei membro di nessuna stanza. Perché non <0>esplorare?<0>',
    suggest_new_rooms_title: 'Non riesci a trovare la stanza di cui hai bisogno?',
    latest_posts_your_topics: 'Ultimi post nelle tue stanze',
    people_in_the_room: 'Persone nella stanza',
    people_in_the_room_no_people: 'Perché non essere il primo a unirti alla stanza e creare un post.',
    people_in_the_room_no_companions_and_people: 'Congratulazioni! Sei la prima persona in questa stanza',
    no_moderators_in_the_room: 'Nessun moderatore in questa stanza',
    select_rooms_for_post_modal_title: 'Seleziona le stanze in cui desideri condividere il tuo post:',
    rooms_create_post_dialog_placeholder_nonmember: 'Devi unirti alla stanza per poter pubblicare',
    rooms_blocked_member: 'Purtroppo il tuo accesso a questa stanza è stato revocato',
    community_not_member_of_room: 'Spiacenti, non puoi accedere a questa stanza',
    community_room_library_no_files: 'Nessun file allegato a questa stanza.',
    community_room_library_details: 'Dettagli',
    community_room_library_download: 'Scarica',
    community_room_library_summarise: 'Riassumi',
    community_room_library_translate: 'Traduci',
    community_room_library_delete: 'Elimina',
    community_room_library_update: 'Aggiorna',
    profile_settings_ai_settings_title: 'Impostazioni AI',
    profile_settings_ai_tokens_allowed: 'Token consentiti al giorno:',
    profile_settings_ai_tokens_used: 'Token utilizzati oggi:',
    featured_content_delete_file_warning_title: 'Avviso',
    featured_content_delete_file_warning_message: 'Quando elimini un file qui, verrà eliminato anche dalla libreria',
    share_story_no_rooms_for_member: 'Sembra che tu non sia membro di nessuna stanza. Perché non esplorare e unirti?',
    community_room_library_file_type: 'Tipo di file',
    community_room_library_language: 'Lingua',
    community_room_library_valid_until: 'Valido fino al',
    community_room_library_date_created: 'Data di creazione',
    snackbar_community_member_not_in_room: 'Devi unirti alla stanza {{roomName}} per rispondere o mettere mi piace.',
    snackbar_community_member_not_in_room_poll: 'Devi unirti alla stanza {{roomName}} per creare un sondaggio.',
    snackbar_community_moderator_remove_member_success: 'Membro rimosso dalla stanza!',
    snackbar_community_cannot_join_room: 'Non puoi unirti alla stanza.',
    snackbar_community_cannot_join_room_blocked: 'Sei stato bloccato da questa stanza.',
    snackbar_community_cannot_leave_room: 'Non puoi lasciare la stanza.',
    snackbar_community_join_room_success: 'Ti sei unito alla stanza!',
    snackbar_community_leave_room_success: 'Hai lasciato la stanza!',
    polls_widget_no_polls: 'Non ci sono ancora sondaggi in questa stanza.',
    poll_proposal_additional_info: 'Un moderatore della stanza ti contatterà per ulteriori informazioni sul sondaggio.',
    room_leave_confirmation_description: 'Sei sicuro di voler lasciare questa stanza?',
    story_choose_rooms_subtitle: 'Puoi condividere questa storia nelle seguenti stanze:',
    story_published_modal_title: 'Storia pubblicata con successo! Vuoi condividerla nelle tue stanze?',
    story_published_modal_title_mobile: 'Grazie per aver pubblicato la tua storia. Vuoi condividerla nelle tue stanze?',
    moderator_pin_post_dialog_description: 'Sei sicuro di voler fissare questo post nella stanza?',
    moderator_remove_member_dialog_description: 'Sei sicuro di voler rimuovere questo membro dalla stanza?',
    errors_posts_not_allowed_in_room: 'I post non sono consentiti in questa stanza.',
    errors_no_posts_found_room_member: 'Sii il primo a pubblicare in questa stanza!',
    errors_no_posts_found_room_not_member: 'Unisciti alla stanza e pubblica qualcosa!',
    errors_no_posts_found_room: 'Sii il primo a pubblicare in questa stanza!',
    notifications_settings_someone_publishes_a_story_label: 'Nuova storia in Le mie Stanze',
    share_story_to_rooms_second_title: 'Vuoi condividerla nelle tue stanze?',
    private_room_code_widget_title: 'Hai un codice per una Stanza o un Hub privato?',
    private_room_shim_name_title_dialog: 'Benvenuto in questa stanza privata!',
    private_room_shim_name_subtitle_dialog: 'Usa il nome con cui le persone in questa stanza ti conoscono.',
    private_room_shim_name_subtitle_dialog_2: 'Rimarrai anonimo al di fuori di questa stanza.',
    companions_description_no_companion_in_room: 'Non hai ancora compagni in questa stanza. Partecipa alle conversazioni e trova membri con cui connetterti.',
    companions_description_no_companion_in_community: 'Non hai ancora compagni. Perché non esplorare le stanze e trovare membri con cui connetterti?',
    companions_no_companion: 'Non hai ancora compagni. Visita la stanza dei nuovi membri per trovare membri carecircle e connetterti',
    personal_private_room_widget_button: 'Crea una stanza privata',
    personal_private_room_dialog_title: 'Crea la tua stanza privata',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Testa',
    body_part_first_level_chest: 'Torace',
    body_part_first_level_abdomen: 'Addome',
    body_part_first_level_pelvis: 'Bacino',
    body_part_first_level_arms: 'Braccia',
    body_part_first_level_legs: 'Gambe',
    body_part_first_level_back: 'Schiena',
    body_part_first_level_buttocks: 'Glutei',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Ci sono 'stanze' che puoi visitare in modo anonimo e partecipare alle discussioni se lo desideri. Puoi pubblicare commenti o domande, oppure trovare compagni che ti sosterranno nel tuo percorso.",
    conversations: 'Chat',
    // Featured content overrides
    stories_carousel_title: 'Storie in evidenza',
    content_carousel_title: 'Contenuti in evidenza',
    content_carousel_add_new_post_button: 'Aggiungi nuovo Contenuto in evidenza',
    content_carousel_no_content_moderator_subtitle: "Informa tutti in questa stanza sulle ultime notizie, annunci e risorse. I contenuti in evidenza possono essere creati solo dall'app web al momento.",
    room_content_type_featured_posts_and_library_option: 'Post in evidenza e libreria',
    room_content_type_featured_posts_option: 'Post in evidenza',
    room_create_content_success: "L'elemento di contenuto in evidenza è stato creato con successo",
    room_update_content_success: "L'elemento di contenuto in evidenza è stato aggiornato con successo",
    room_delete_content_success: "L'elemento di contenuto in evidenza è stato eliminato con successo",
    room_delete_content_error: "Si è verificato un errore durante l'eliminazione dell'elemento di contenuto in evidenza",
    room_confirm_delete_featured_item_title: 'Elimina contenuto in evidenza',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Come ti senti',
    wellness_tracker_morning: 'stamattina',
    wellness_tracker_afternoon: 'questo pomeriggio',
    wellness_tracker_evening: 'stasera',
    // Navigation "Previous" overrides
    previous: 'Precedente',
    health_picture_symptom_checker_previous: 'Precedente',
    errors_to_previous_link: 'pagina precedente',
    errors_to_previous_mobile: 'Portami alla <0>schermata precedente</0>.',
    errors_to_previous_link_mobile: 'schermata precedente',
    stories_carousel_previous_story: 'Storia precedente',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Scrivi un commento...',
    chat_input_placeholder: 'Scrivi un messaggio...',
    ai_chatbot_widget_input_placeholder: 'Scrivi qualcosa...',
    create_post_input_placeholder: 'Scrivi un messaggio...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Percorso Salute',
    health_picture_name: 'Profilo Salute',
    ai_context_mode_health_journey: 'Percorso Salute',
    health_journey_second_tab: 'Profilo Salute',
    health_journey_add_new: 'Crea percorso salute',
    health_journey_rename_journey: 'Modifica Percorso Salute',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Crea Percorso Salute',
    hj_already_create_for_myself_4: 'Visualizza Percorso Salute',
    health_journey_privacy_dialog_health_journey_link: 'Percorso Salute',
    health_picture_view_health_journey: 'Visualizza percorso salute',
    health_picture_symptom_checker_save_to_health_journey: 'Salva nel Percorso Salute',
    health_journey_my_picture: 'Il mio profilo salute',
    health_journey_picture_not_created_title: 'Inizia a monitorare il tuo percorso salute',
    health_journey_start_health_picture_title: 'Fai il primo passo creando un profilo salute',
    verification_successful_subtitle: 'Sarai reindirizzato a breve.\nInizia il tuo percorso salute oggi.',
    rooms_redirect_to_health_picture: 'compila il tuo profilo salute',
    health_picture_no_symptoms_info: 'Registra i tuoi sintomi per un profilo salute completo',
    ai_context_label_health_journey: 'Il contesto attuale è il Percorso Salute di:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Crea ora il tuo Percorso Salute personale per avere una panoramica del tuo benessere e scoprire altre funzionalità interessanti.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Dai un nome al tuo percorso',
    new_health_journey_placeholder: 'Puoi avere più percorsi per te stesso o per qualcun altro.',
    new_health_journey_validation_required_name: 'Inserisci un nome per il tuo percorso',
    new_health_journey_validation_length_name: 'Il nome del percorso salute non deve superare i 30 caratteri',
    hj_already_create_for_myself_1: 'Sembra che tu',
    hj_already_create_for_myself_2: 'abbia già creato un Percorso Salute',
    hj_already_create_for_myself_3: 'per te stesso.',
    hj_multiple_dialog_title: 'Per chi stai creando un percorso salute?',
    hj_multiple_for_myself: 'Voglio creare questo percorso per me stesso',
    hj_multiple_for_someone_else: 'Voglio creare questo percorso per qualcun altro',
    // Conversation hide override
    conversation_hide: 'Nascondi',
    // Consultation form overrides
    consultation_name: 'Consulto medico',
    health_picture_consultations: 'I miei consulti medici',
    health_picture_add_consultation: 'Aggiungi consulto medico',
    health_picture_edit_consultation: 'Modifica consulto medico',
    health_journey_consultation_type: 'Tipo di consulto:',
    health_journey_timeline_consultation_type: 'Tipo:',
    mycare_consultation_type_label: 'Tipo di consulto',
    mycare_consultation_provider_label: 'Nome del professionista sanitario',
    mycare_consultation_speciality_label: 'Specialità',
    my_care_journal_notes: 'Note',
    my_care_journal_description_placeholder: 'Prendi nota di tutto ciò che desideri...',
    content_carousel_no_content_moderator_title: 'Crea contenuti per la tua stanza',
    content_carousel_no_content_member_title: 'Resta in contatto con la tua stanza',
    content_carousel_no_content_member_subtitle: 'Qui troverai le ultime notizie, annunci e risorse.',
    ai_context_label_room: 'Il contesto attuale è la Stanza:',
    companions_block_user: 'Blocca',
    companions_unblock_user: 'Sblocca',
    companion_request_block: 'Blocca',
    companion_request_unblock: 'Sblocca',
    companion_report: 'Segnala',
    companion_block_message: 'Sei sicuro/a di voler bloccare {{memberDisplayName}} e rimuoverlo/a come compagno?',
    companion_unblock_message: 'Sei sicuro/a di voler sbloccare {{memberDisplayName}}?',
    companion_report_message: 'Sei sicuro/a di voler segnalare {{memberDisplayName}}?',
    companions_block_successfully: 'Hai bloccato il compagno con successo!',
    companions_unblock_successfully: 'Hai sbloccato il compagno con successo!',
    companions_cannot_block_administrator: 'Non puoi bloccare un amministratore!',
    health_journey_select_date: 'Seleziona data',
  },
  el: {
    ai_context_mode_all_rooms: 'Αυτόματο',
    like: 'Μου αρέσει',
    likes: 'Μου αρέσει',
    community_post_like: 'Μου αρέσει',
    community_post_likes: 'Μου αρέσει',
    reaction_like_label: 'Μου αρέσει',
    story_allow_likes: 'Να επιτρέπονται τα μου αρέσει',
    comment: 'Σχόλιο',
    comments: 'Σχόλια',
    reaction_care_label: 'Νοιάζομαι',
    reaction_helpful_label: 'Χρήσιμο',
    story_draft: 'Πρόχειρο',
    stories_drafts: 'Πρόχειρα',
    checker: 'Έλεγχος',
    health_picture_complete: 'Ολοκλήρωση',
    health_picture_diagnosis_type_nonmedical: 'Πιθανή',
    preview: 'Προεπισκόπηση',
    journal_name: 'Ημερολόγιο',
    community: 'Δωμάτια',
    stories: 'Ιστορίες',
    profile_widget_journey: 'Διαδρομές',
    browser: 'Πρόγραμμα περιήγησης',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Σύνδεση',
    community_room_type_hub: 'Hub',
    subgroups: 'Υποομάδες',
    max: 'μέγ',
    companions_title: 'Συνοδοί',
    health_picture_ongoing: 'Σε εξέλιξη',
    poll_period_unlimited: 'Απεριόριστο',
    joined_rooms_title: 'Δωμάτια σε αυτό το Hub',
    story_save_as_draft: 'Αποθήκευση ως πρόχειρο',
    story_saved_draft_snackbar: 'Το πρόχειρο αποθηκεύτηκε με επιτυχία',
    story_save_draft_success_dialog_content: 'Το πρόχειρό σας αποθηκεύτηκε με επιτυχία!',
    my_stories_empty_drafts_first_line: 'Δεν έχετε ιστορίες σε Πρόχειρο.',
    health_picture_add_journal: 'Προσθήκη καταχώρησης ημερολογίου',
    health_picture_edit_journal: 'Επεξεργασία καταχώρησης ημερολογίου',
    health_picture_journal: 'Τα ημερολόγιά μου',
    health_journey_journal_title: 'Ημερολόγιο',
    health_journey_journal_date: 'Ημερομηνία ημερολογίου',
    delete_journal_confirmation_title: 'Διαγραφή ημερολογίου',
    delete_journal_confirmation_description: 'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το ημερολόγιο; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
    journal_deleted_successfully: 'Το ημερολόγιο διαγράφηκε με επιτυχία.',
    error_deleting_journal: 'Παρουσιάστηκε πρόβλημα κατά τη διαγραφή του ημερολογίου. Δοκιμάστε ξανά.',
    stories_first_tab: 'Όλες οι ιστορίες',
    stories_third_tab: 'Οι ιστορίες μου',
    stories_suggested: 'Προτεινόμενες ιστορίες',
    stories_my_stories: 'Οι ιστορίες μου',
    companions_in_room_title: 'Συνοδοί στο δωμάτιο',
    health_picture_snackbar_adding_ongoing_symptom: 'Έχετε ήδη προσθέσει αυτό το σύμπτωμα και είναι ακόμα σε εξέλιξη! Ενημερώστε εκείνη την καταχώρηση!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Έχετε ήδη προσθέσει αυτή τη διάγνωση και είναι ακόμα σε εξέλιξη! Ενημερώστε εκείνη την καταχώρηση!',
    health_picture_snackbar_adding_ongoing_treatment: 'Έχετε ήδη προσθέσει αυτή τη θεραπεία και είναι ακόμα σε εξέλιξη! Ενημερώστε εκείνη την καταχώρηση!',
    health_picture_snackbar_adding_ongoing_medication: 'Έχετε ήδη προσθέσει αυτό το φάρμακο και είναι ακόμα σε εξέλιξη! Ενημερώστε εκείνη την καταχώρηση!',
    your_subgroups: 'Οι υποομάδες σας',
    other_subgroups: 'Άλλες υποομάδες',
    community_room_type_hub_room: 'Δωμάτιο Hub',
    // Room consistency overrides
    rooms_title: 'Δωμάτια',
    my_rooms_title: 'Τα Δωμάτια & Hubs μου',
    profile_widget_rooms: 'Δωμάτια',
    bottom_nav_community: 'Δωμάτια',
    search_rooms_title: 'Δωμάτια',
    community_first_tab: 'Τα δωμάτιά μου',
    community_second_tab: 'Όλα τα δωμάτια',
    community_room_type_private_room: 'Ιδιωτικό Δωμάτιο',
    community_room_type_public_room: 'Δημόσιο Δωμάτιο',
    related_rooms: 'Σχετικά Δωμάτια',
    my_rooms_explore_other_rooms: 'Εξερεύνηση άλλων δωματίων',
    rooms_autocomplete_caption: 'Εξερεύνηση δωματίων:',
    rooms_no_result_found: 'Δεν βρέθηκαν δωμάτια. Δοκιμάστε άλλη αναζήτηση.',
    rooms_view_all_button: 'Εμφάνιση όλων των δωματίων',
    rooms_view_rooms_button: 'Εμφάνιση δωματίων',
    rooms_leave_room: 'Αποχώρηση από το δωμάτιο',
    rooms_related_rooms_loading: 'Φόρτωση σχετικών δωματίων...',
    rooms_related_rooms_end: 'Δεν υπάρχουν άλλα σχετικά δωμάτια',
    rooms_by_topic_view_more_button: 'Εμφάνιση περισσότερων δωματίων',
    share_story_to_rooms_no_rooms_button: 'Εξερεύνηση δωματίων',
    room_join_button: 'Εγγραφή στο δωμάτιο',
    room_join_button_pending: 'Αίτημα δωματίου σε εκκρεμότητα',
    room_leave_confirmation_title: 'Αποχώρηση από το δωμάτιο',
    no_related_rooms: 'Δεν βρέθηκαν σχετικά δωμάτια.',
    poll_rooms_selection: 'Κοινοποίηση δημοσκόπησης στα δωμάτια:',
    my_rooms_no_member_rooms: 'Δεν είστε μέλος κανενός δωματίου. Γιατί να μην <0>εξερευνήσετε;<0>',
    suggest_new_rooms_title: 'Δεν μπορείτε να βρείτε το δωμάτιο που χρειάζεστε;',
    latest_posts_your_topics: 'Τελευταίες δημοσιεύσεις στα δωμάτιά σας',
    people_in_the_room: 'Άτομα στο δωμάτιο',
    people_in_the_room_no_people: 'Γιατί να μην είστε ο πρώτος που θα εγγραφεί στο δωμάτιο και θα δημιουργήσει μια δημοσίευση.',
    people_in_the_room_no_companions_and_people: 'Συγχαρητήρια! Είστε το πρώτο άτομο σε αυτό το δωμάτιο',
    no_moderators_in_the_room: 'Δεν υπάρχουν συντονιστές σε αυτό το δωμάτιο',
    select_rooms_for_post_modal_title: 'Επιλέξτε τα δωμάτια στα οποία θέλετε να μοιραστείτε τη δημοσίευσή σας:',
    rooms_create_post_dialog_placeholder_nonmember: 'Πρέπει να εγγραφείτε στο δωμάτιο για να δημοσιεύσετε',
    rooms_blocked_member: 'Δυστυχώς η πρόσβασή σας σε αυτό το δωμάτιο έχει ανακληθεί',
    community_not_member_of_room: 'Λυπούμαστε, δεν μπορείτε να αποκτήσετε πρόσβαση σε αυτό το δωμάτιο',
    community_room_library_no_files: 'Δεν υπάρχουν αρχεία σε αυτό το δωμάτιο.',
    community_room_library_details: 'Λεπτομέρειες',
    community_room_library_download: 'Λήψη',
    community_room_library_summarise: 'Σύνοψη',
    community_room_library_translate: 'Μετάφραση',
    community_room_library_delete: 'Διαγραφή',
    community_room_library_update: 'Ενημέρωση',
    profile_settings_ai_settings_title: 'Ρυθμίσεις AI',
    profile_settings_ai_tokens_allowed: 'Επιτρεπόμενα token ανά ημέρα:',
    profile_settings_ai_tokens_used: 'Token που χρησιμοποιήθηκαν σήμερα:',
    featured_content_delete_file_warning_title: 'Προειδοποίηση',
    featured_content_delete_file_warning_message: 'Όταν διαγράφετε ένα αρχείο εδώ, θα διαγραφεί επίσης από τη βιβλιοθήκη',
    share_story_no_rooms_for_member: 'Φαίνεται ότι δεν είστε μέλος κανενός δωματίου. Γιατί να μην εξερευνήσετε και εγγραφείτε;',
    community_room_library_file_type: 'Τύπος αρχείου',
    community_room_library_language: 'Γλώσσα',
    community_room_library_valid_until: 'Ισχύει έως',
    community_room_library_date_created: 'Ημερομηνία δημιουργίας',
    snackbar_community_member_not_in_room: 'Πρέπει να εγγραφείτε στο δωμάτιο {{roomName}} για να απαντήσετε ή να κάνετε μου αρέσει.',
    snackbar_community_member_not_in_room_poll: 'Πρέπει να εγγραφείτε στο δωμάτιο {{roomName}} για να δημιουργήσετε δημοσκόπηση.',
    snackbar_community_moderator_remove_member_success: 'Το μέλος αφαιρέθηκε από το δωμάτιο!',
    snackbar_community_cannot_join_room: 'Δεν μπορείτε να εγγραφείτε στο δωμάτιο.',
    snackbar_community_cannot_join_room_blocked: 'Έχετε αποκλειστεί από αυτό το δωμάτιο.',
    snackbar_community_cannot_leave_room: 'Δεν μπορείτε να αποχωρήσετε από το δωμάτιο.',
    snackbar_community_join_room_success: 'Εγγραφήκατε στο δωμάτιο!',
    snackbar_community_leave_room_success: 'Αποχωρήσατε από το δωμάτιο!',
    polls_widget_no_polls: 'Δεν υπάρχουν ακόμα δημοσκοπήσεις σε αυτό το δωμάτιο.',
    poll_proposal_additional_info: 'Ένας συντονιστής του δωματίου θα επικοινωνήσει μαζί σας για περισσότερες πληροφορίες σχετικά με τη δημοσκόπηση.',
    room_leave_confirmation_description: 'Είστε σίγουροι ότι θέλετε να αποχωρήσετε από αυτό το δωμάτιο;',
    story_choose_rooms_subtitle: 'Μπορείτε να μοιραστείτε αυτή την ιστορία στα ακόλουθα δωμάτια:',
    story_published_modal_title: 'Η ιστορία δημοσιεύτηκε με επιτυχία! Θέλετε να τη μοιραστείτε στα δωμάτιά σας;',
    story_published_modal_title_mobile: 'Ευχαριστούμε που δημοσιεύσατε την ιστορία σας. Θέλετε να τη μοιραστείτε στα δωμάτιά σας;',
    moderator_pin_post_dialog_description: 'Είστε σίγουροι ότι θέλετε να καρφιτσώσετε αυτή τη δημοσίευση στο δωμάτιο;',
    moderator_remove_member_dialog_description: 'Είστε σίγουροι ότι θέλετε να αφαιρέσετε αυτό το μέλος από το δωμάτιο;',
    errors_posts_not_allowed_in_room: 'Οι δημοσιεύσεις δεν επιτρέπονται σε αυτό το δωμάτιο.',
    errors_no_posts_found_room_member: 'Γίνετε ο πρώτος που θα δημοσιεύσει σε αυτό το δωμάτιο!',
    errors_no_posts_found_room_not_member: 'Εγγραφείτε στο δωμάτιο και δημοσιεύστε κάτι!',
    errors_no_posts_found_room: 'Γίνετε ο πρώτος που θα δημοσιεύσει σε αυτό το δωμάτιο!',
    notifications_settings_someone_publishes_a_story_label: 'Νέα ιστορία στα Δωμάτιά μου',
    share_story_to_rooms_second_title: 'Θέλετε να τη μοιραστείτε στα δωμάτιά σας;',
    private_room_code_widget_title: 'Έχετε κωδικό για ιδιωτικό Δωμάτιο ή Hub;',
    private_room_shim_name_title_dialog: 'Καλώς ήρθατε σε αυτό το ιδιωτικό δωμάτιο!',
    private_room_shim_name_subtitle_dialog: 'Χρησιμοποιήστε το όνομα με το οποίο σας γνωρίζουν οι άνθρωποι σε αυτό το δωμάτιο.',
    private_room_shim_name_subtitle_dialog_2: 'Θα παραμείνετε ανώνυμοι εκτός αυτού του δωματίου.',
    companions_description_no_companion_in_room: 'Δεν έχετε ακόμα συνοδούς σε αυτό το δωμάτιο. Συμμετάσχετε σε συζητήσεις και βρείτε μέλη για να συνδεθείτε.',
    companions_description_no_companion_in_community: 'Δεν έχετε ακόμα συνοδούς. Γιατί να μην εξερευνήσετε δωμάτια και βρείτε μέλη για να συνδεθείτε;',
    companions_no_companion: 'Δεν έχετε ακόμα συνοδούς. Επισκεφθείτε το δωμάτιο νέων μελών για να βρείτε μέλη carecircle και να συνδεθείτε',
    personal_private_room_widget_button: 'Δημιουργία ιδιωτικού δωματίου',
    personal_private_room_dialog_title: 'Δημιουργήστε το ιδιωτικό σας δωμάτιο',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Κεφάλι',
    body_part_first_level_chest: 'Θώρακας',
    body_part_first_level_abdomen: 'Κοιλιά',
    body_part_first_level_pelvis: 'Λεκάνη',
    body_part_first_level_arms: 'Χέρια',
    body_part_first_level_legs: 'Πόδια',
    body_part_first_level_back: 'Πλάτη',
    body_part_first_level_buttocks: 'Γλουτοί',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Υπάρχουν 'δωμάτια' που μπορείτε να επισκεφθείτε ανώνυμα και να συμμετάσχετε σε συζητήσεις αν θέλετε. Μπορείτε να δημοσιεύσετε σχόλια ή ερωτήσεις, ή να βρείτε συντρόφους που θα σας υποστηρίξουν στο ταξίδι σας.",
    conversations: 'Συνομιλίες',
    // Featured content overrides
    stories_carousel_title: 'Προτεινόμενες ιστορίες',
    content_carousel_title: 'Προτεινόμενο περιεχόμενο',
    content_carousel_add_new_post_button: 'Προσθήκη νέου Προτεινόμενου Περιεχομένου',
    content_carousel_no_content_moderator_subtitle: 'Ενημερώστε όλους σε αυτό το δωμάτιο για τα τελευταία νέα, ανακοινώσεις και πόρους. Το προτεινόμενο περιεχόμενο μπορεί να δημιουργηθεί μόνο από την εφαρμογή ιστού προς το παρόν.',
    room_content_type_featured_posts_and_library_option: 'Προτεινόμενες δημοσιεύσεις & βιβλιοθήκη',
    room_content_type_featured_posts_option: 'Προτεινόμενες δημοσιεύσεις',
    room_create_content_success: 'Το στοιχείο προτεινόμενου περιεχομένου δημιουργήθηκε με επιτυχία',
    room_update_content_success: 'Το στοιχείο προτεινόμενου περιεχομένου ενημερώθηκε με επιτυχία',
    room_delete_content_success: 'Το στοιχείο προτεινόμενου περιεχομένου διαγράφηκε με επιτυχία',
    room_delete_content_error: 'Παρουσιάστηκε σφάλμα κατά τη διαγραφή του στοιχείου προτεινόμενου περιεχομένου',
    room_confirm_delete_featured_item_title: 'Διαγραφή προτεινόμενου περιεχομένου',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Πώς νιώθετε',
    wellness_tracker_morning: 'σήμερα το πρωί',
    wellness_tracker_afternoon: 'σήμερα το απόγευμα',
    wellness_tracker_evening: 'σήμερα το βράδυ',
    // Navigation "Previous" overrides
    previous: 'Προηγούμενο',
    health_picture_symptom_checker_previous: 'Προηγούμενο',
    errors_to_previous_link: 'προηγούμενη σελίδα',
    errors_to_previous_mobile: 'Πήγαινέ με στην <0>προηγούμενη οθόνη</0>.',
    errors_to_previous_link_mobile: 'προηγούμενη οθόνη',
    stories_carousel_previous_story: 'Προηγούμενη ιστορία',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Γράψτε ένα σχόλιο...',
    chat_input_placeholder: 'Γράψτε ένα μήνυμα...',
    ai_chatbot_widget_input_placeholder: 'Γράψτε κάτι...',
    create_post_input_placeholder: 'Γράψτε ένα μήνυμα...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Πορεία Υγείας',
    health_picture_name: 'Εικόνα Υγείας',
    ai_context_mode_health_journey: 'Πορεία Υγείας',
    health_journey_second_tab: 'Εικόνα Υγείας',
    health_journey_add_new: 'Δημιουργία πορείας υγείας',
    health_journey_rename_journey: 'Τροποποίηση Πορείας Υγείας',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Δημιουργία Πορείας Υγείας',
    hj_already_create_for_myself_4: 'Προβολή Πορείας Υγείας',
    health_journey_privacy_dialog_health_journey_link: 'Πορεία Υγείας',
    health_picture_view_health_journey: 'Προβολή πορείας υγείας',
    health_picture_symptom_checker_save_to_health_journey: 'Αποθήκευση στην Πορεία Υγείας',
    health_journey_my_picture: 'Η εικόνα υγείας μου',
    health_journey_picture_not_created_title: 'Ξεκινήστε να παρακολουθείτε την πορεία υγείας σας',
    health_journey_start_health_picture_title: 'Κάντε το πρώτο βήμα δημιουργώντας μια εικόνα υγείας',
    verification_successful_subtitle: 'Θα ανακατευθυνθείτε σύντομα.\nΞεκινήστε την πορεία υγείας σας σήμερα.',
    rooms_redirect_to_health_picture: 'συμπληρώστε την εικόνα υγείας σας',
    health_picture_no_symptoms_info: 'Καταγράψτε τα συμπτώματά σας για μια πλήρη εικόνα υγείας',
    ai_context_label_health_journey: 'Το τρέχον πλαίσιο είναι η Πορεία Υγείας:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Δημιουργήστε τώρα τη δική σας Πορεία Υγείας για να έχετε μια επισκόπηση της ευεξίας σας και να εξερευνήσετε άλλες συναρπαστικές λειτουργίες.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Δώστε ένα όνομα στην πορεία σας',
    new_health_journey_placeholder: 'Μπορείτε να έχετε πολλές πορείες για εσάς ή για κάποιον άλλο.',
    new_health_journey_validation_required_name: 'Εισαγάγετε ένα όνομα για την πορεία σας',
    new_health_journey_validation_length_name: 'Το όνομα της πορείας υγείας δεν πρέπει να ξεπερνά τους 30 χαρακτήρες',
    hj_already_create_for_myself_1: 'Φαίνεται ότι',
    hj_already_create_for_myself_2: 'έχετε ήδη δημιουργήσει μια Πορεία Υγείας',
    hj_already_create_for_myself_3: 'για τον εαυτό σας.',
    hj_multiple_dialog_title: 'Για ποιον δημιουργείτε μια πορεία υγείας;',
    hj_multiple_for_myself: 'Θέλω να δημιουργήσω αυτή την πορεία για τον εαυτό μου',
    hj_multiple_for_someone_else: 'Θέλω να δημιουργήσω αυτή την πορεία για κάποιον άλλο',
    // Conversation hide override
    conversation_hide: 'Απόκρυψη',
    // Consultation form overrides
    consultation_name: 'Ιατρική επίσκεψη',
    health_picture_consultations: 'Οι ιατρικές επισκέψεις μου',
    health_picture_add_consultation: 'Προσθήκη ιατρικής επίσκεψης',
    health_picture_edit_consultation: 'Επεξεργασία ιατρικής επίσκεψης',
    health_journey_consultation_type: 'Τύπος ιατρικής επίσκεψης:',
    health_journey_timeline_consultation_type: 'Τύπος:',
    mycare_consultation_type_label: 'Τύπος ιατρικής επίσκεψης',
    mycare_consultation_provider_label: 'Όνομα παρόχου υγείας',
    mycare_consultation_speciality_label: 'Ειδικότητα',
    my_care_journal_notes: 'Σημειώσεις',
    my_care_journal_description_placeholder: 'Κρατήστε σημειώσεις για ό,τι θέλετε...',
    content_carousel_no_content_moderator_title: 'Δημιουργήστε περιεχόμενο για το δωμάτιό σας',
    content_carousel_no_content_member_title: 'Μείνετε σε επαφή με το δωμάτιό σας',
    content_carousel_no_content_member_subtitle: 'Εδώ θα βρείτε τα τελευταία νέα, ανακοινώσεις και πόρους.',
    ai_context_label_room: 'Η τρέχουσα συνάφεια είναι το Δωμάτιο:',
    companions_block_user: 'Αποκλεισμός',
    companions_unblock_user: 'Άρση αποκλεισμού',
    companion_request_block: 'Αποκλεισμός',
    companion_request_unblock: 'Άρση αποκλεισμού',
    companion_report: 'Αναφορά',
    companion_block_message: 'Είστε σίγουροι ότι θέλετε να αποκλείσετε τον/την {{memberDisplayName}} και να τον/την αφαιρέσετε ως σύντροφο;',
    companion_unblock_message: 'Είστε σίγουροι ότι θέλετε να άρετε τον αποκλεισμό του/της {{memberDisplayName}};',
    companion_report_message: 'Είστε σίγουροι ότι θέλετε να αναφέρετε τον/την {{memberDisplayName}};',
    companions_block_successfully: 'Αποκλείσατε τον σύντροφο με επιτυχία!',
    companions_unblock_successfully: 'Άρατε τον αποκλεισμό του συντρόφου με επιτυχία!',
    companions_cannot_block_administrator: 'Δεν μπορείτε να αποκλείσετε έναν διαχειριστή!',
    health_journey_select_date: 'Επιλέξτε ημερομηνία',
  },
  es: {
    ai_context_mode_all_rooms: 'Automático',
    like: 'Me gusta',
    likes: 'Me gusta',
    community_post_like: 'Me gusta',
    community_post_likes: 'Me gusta',
    reaction_like_label: 'Me gusta',
    story_allow_likes: 'Permitir me gusta',
    comment: 'Comentario',
    comments: 'Comentarios',
    reaction_care_label: 'Me importa',
    reaction_helpful_label: 'Útil',
    story_draft: 'Borrador',
    stories_drafts: 'Borradores',
    checker: 'Verificador',
    health_picture_complete: 'Completar',
    health_picture_diagnosis_type_nonmedical: 'Sospechado',
    preview: 'Vista previa',
    journal_name: 'Diario',
    community: 'Salas',
    stories: 'Historias',
    profile_widget_journey: 'Recorridos',
    browser: 'Navegador',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Conectar',
    community_room_type_hub: 'Hub',
    subgroups: 'Subgrupos',
    max: 'máx',
    companions_title: 'Compañeros',
    health_picture_ongoing: 'En curso',
    poll_period_unlimited: 'Sin límite',
    joined_rooms_title: 'Salas en este Hub',
    story_save_as_draft: 'Guardar como borrador',
    story_saved_draft_snackbar: 'Borrador guardado con éxito',
    story_save_draft_success_dialog_content: '¡Tu borrador se ha guardado con éxito!',
    my_stories_empty_drafts_first_line: 'No tienes historias en Borrador.',
    health_picture_add_journal: 'Añadir entrada de diario',
    health_picture_edit_journal: 'Editar entrada de diario',
    health_picture_journal: 'Mis diarios',
    health_journey_journal_title: 'Diario',
    health_journey_journal_date: 'Fecha del diario',
    delete_journal_confirmation_title: 'Eliminar diario',
    delete_journal_confirmation_description: '¿Estás seguro de que quieres eliminar este diario? Esta acción no se puede deshacer.',
    journal_deleted_successfully: 'Diario eliminado con éxito.',
    error_deleting_journal: 'Se produjo un problema al eliminar el diario. Inténtalo de nuevo.',
    stories_first_tab: 'Todas las historias',
    stories_third_tab: 'Mis historias',
    stories_suggested: 'Historias sugeridas',
    stories_my_stories: 'Mis historias',
    companions_in_room_title: 'Compañeros en la sala',
    health_picture_snackbar_adding_ongoing_symptom: '¡Ya has añadido este síntoma y todavía está en curso! ¡Actualiza esa entrada!',
    health_picture_snackbar_adding_ongoing_diagnosis: '¡Ya has añadido este diagnóstico y todavía está en curso! ¡Actualiza esa entrada!',
    health_picture_snackbar_adding_ongoing_treatment: '¡Ya has añadido este tratamiento y todavía está en curso! ¡Actualiza esa entrada!',
    health_picture_snackbar_adding_ongoing_medication: '¡Ya has añadido este medicamento y todavía está en curso! ¡Actualiza esa entrada!',
    your_subgroups: 'Tus subgrupos',
    other_subgroups: 'Otros subgrupos',
    community_room_type_hub_room: 'Sala Hub',
    // Room consistency overrides
    rooms_title: 'Salas',
    my_rooms_title: 'Mis Salas y Hubs',
    profile_widget_rooms: 'Salas',
    bottom_nav_community: 'Salas',
    search_rooms_title: 'Salas',
    community_first_tab: 'Mis salas',
    community_second_tab: 'Todas las salas',
    community_room_type_private_room: 'Sala privada',
    community_room_type_public_room: 'Sala pública',
    related_rooms: 'Salas relacionadas',
    my_rooms_explore_other_rooms: 'Explorar otras salas',
    rooms_autocomplete_caption: 'Explorar salas:',
    rooms_no_result_found: 'No se encontraron salas. Intenta otra búsqueda.',
    rooms_view_all_button: 'Ver todas las salas',
    rooms_view_rooms_button: 'Ver salas',
    rooms_leave_room: 'Abandonar la sala',
    rooms_related_rooms_loading: 'Cargando salas relacionadas...',
    rooms_related_rooms_end: 'No hay más salas relacionadas',
    rooms_by_topic_view_more_button: 'Ver más salas',
    share_story_to_rooms_no_rooms_button: 'Explorar salas',
    room_join_button: 'Unirse a la sala',
    room_join_button_pending: 'Solicitud de sala pendiente',
    room_leave_confirmation_title: 'Abandonar la sala',
    no_related_rooms: 'No se encontraron salas relacionadas.',
    poll_rooms_selection: 'Compartir encuesta en las salas:',
    my_rooms_no_member_rooms: 'No eres miembro de ninguna sala. ¿Por qué no <0>explorar?<0>',
    suggest_new_rooms_title: '¿No encuentras la sala que necesitas?',
    latest_posts_your_topics: 'Últimas publicaciones en tus salas',
    people_in_the_room: 'Personas en la sala',
    people_in_the_room_no_people: '¿Por qué no ser el primero en unirse a la sala y crear una publicación?',
    people_in_the_room_no_companions_and_people: '¡Felicidades! Eres la primera persona en esta sala',
    no_moderators_in_the_room: 'No hay moderadores en esta sala',
    select_rooms_for_post_modal_title: 'Selecciona las salas en las que deseas compartir tu publicación:',
    rooms_create_post_dialog_placeholder_nonmember: 'Debes unirte a la sala para poder publicar',
    rooms_blocked_member: 'Lamentablemente, tu acceso a esta sala ha sido revocado',
    community_not_member_of_room: 'Lo sentimos, no puedes acceder a esta sala',
    community_room_library_no_files: 'No hay archivos adjuntos a esta sala.',
    community_room_library_details: 'Detalles',
    community_room_library_download: 'Descargar',
    community_room_library_summarise: 'Resumir',
    community_room_library_translate: 'Traducir',
    community_room_library_delete: 'Eliminar',
    community_room_library_update: 'Actualizar',
    profile_settings_ai_settings_title: 'Configuración de IA',
    profile_settings_ai_tokens_allowed: 'Tokens permitidos por día:',
    profile_settings_ai_tokens_used: 'Tokens utilizados hoy:',
    featured_content_delete_file_warning_title: 'Advertencia',
    featured_content_delete_file_warning_message: 'Cuando eliminas un archivo aquí, también se eliminará de la biblioteca',
    share_story_no_rooms_for_member: 'Parece que no eres miembro de ninguna sala. ¿Por qué no explorar y unirte?',
    community_room_library_file_type: 'Tipo de archivo',
    community_room_library_language: 'Idioma',
    community_room_library_valid_until: 'Válido hasta',
    community_room_library_date_created: 'Fecha de creación',
    snackbar_community_member_not_in_room: 'Debes unirte a la sala {{roomName}} para responder o dar me gusta.',
    snackbar_community_member_not_in_room_poll: 'Debes unirte a la sala {{roomName}} para crear una encuesta.',
    snackbar_community_moderator_remove_member_success: '¡Miembro eliminado de la sala!',
    snackbar_community_cannot_join_room: 'No puedes unirte a la sala.',
    snackbar_community_cannot_join_room_blocked: 'Has sido bloqueado de esta sala.',
    snackbar_community_cannot_leave_room: 'No puedes abandonar la sala.',
    snackbar_community_join_room_success: '¡Te has unido a la sala!',
    snackbar_community_leave_room_success: '¡Has abandonado la sala!',
    polls_widget_no_polls: 'Aún no hay encuestas en esta sala.',
    poll_proposal_additional_info: 'Un moderador de la sala se pondrá en contacto para más información sobre la encuesta.',
    room_leave_confirmation_description: '¿Estás seguro de que quieres abandonar esta sala?',
    story_choose_rooms_subtitle: 'Puedes compartir esta historia en las siguientes salas:',
    story_published_modal_title: '¡Historia publicada con éxito! ¿Deseas compartirla en tus salas?',
    story_published_modal_title_mobile: 'Gracias por publicar tu historia. ¿Deseas compartirla en tus salas?',
    moderator_pin_post_dialog_description: '¿Estás seguro de que quieres fijar esta publicación en la sala?',
    moderator_remove_member_dialog_description: '¿Estás seguro de que quieres eliminar a este miembro de la sala?',
    errors_posts_not_allowed_in_room: 'Las publicaciones no están permitidas en esta sala.',
    errors_no_posts_found_room_member: '¡Sé el primero en publicar en esta sala!',
    errors_no_posts_found_room_not_member: '¡Únete a la sala y publica algo!',
    errors_no_posts_found_room: '¡Sé el primero en publicar en esta sala!',
    notifications_settings_someone_publishes_a_story_label: 'Nueva historia en Mis Salas',
    share_story_to_rooms_second_title: '¿Deseas compartirla en tus salas?',
    private_room_code_widget_title: '¿Tienes un código para una Sala o un Hub privado?',
    private_room_shim_name_title_dialog: '¡Bienvenido a esta sala privada!',
    private_room_shim_name_subtitle_dialog: 'Usa el nombre por el que te conocen las personas en esta sala.',
    private_room_shim_name_subtitle_dialog_2: 'Permanecerás anónimo fuera de esta sala.',
    companions_description_no_companion_in_room: 'Aún no tienes compañeros en esta sala. Participa en conversaciones y encuentra miembros con quienes conectarte.',
    companions_description_no_companion_in_community: 'Aún no tienes compañeros. ¿Por qué no explorar salas y encontrar miembros con quienes conectarte?',
    companions_no_companion: 'Aún no tienes compañeros. Visita la sala de nuevos miembros para encontrar miembros de carecircle y conectarte',
    personal_private_room_widget_button: 'Crear una sala privada',
    personal_private_room_dialog_title: 'Crear tu sala privada',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Cabeza',
    body_part_first_level_chest: 'Pecho',
    body_part_first_level_abdomen: 'Abdomen',
    body_part_first_level_pelvis: 'Pelvis',
    body_part_first_level_arms: 'Brazos',
    body_part_first_level_legs: 'Piernas',
    body_part_first_level_back: 'Espalda',
    body_part_first_level_buttocks: 'Glúteos',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Hay 'salas' que puedes visitar de forma anónima y unirte a las discusiones si lo deseas. Puedes publicar comentarios o preguntas, o encontrar compañeros que te apoyarán en tu camino.",
    conversations: 'Chats',
    // Featured content overrides
    stories_carousel_title: 'Historias destacadas',
    content_carousel_title: 'Contenido destacado',
    content_carousel_add_new_post_button: 'Añadir nuevo Contenido Destacado',
    content_carousel_no_content_moderator_subtitle: 'Informa a todos en esta sala sobre las últimas noticias, anuncios y recursos. El contenido destacado solo se puede crear desde la aplicación web por el momento.',
    room_content_type_featured_posts_and_library_option: 'Publicaciones destacadas y biblioteca',
    room_content_type_featured_posts_option: 'Publicaciones destacadas',
    room_create_content_success: 'El elemento de contenido destacado se ha creado correctamente',
    room_update_content_success: 'El elemento de contenido destacado se ha actualizado correctamente',
    room_delete_content_success: 'El elemento de contenido destacado se ha eliminado correctamente',
    room_delete_content_error: 'Se ha producido un error al eliminar el elemento de contenido destacado',
    room_confirm_delete_featured_item_title: 'Eliminar contenido destacado',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: '¿Cómo te sientes',
    wellness_tracker_morning: 'esta mañana',
    wellness_tracker_afternoon: 'esta tarde',
    wellness_tracker_evening: 'esta noche',
    // Navigation "Previous" overrides
    previous: 'Anterior',
    health_picture_symptom_checker_previous: 'Anterior',
    errors_to_previous_link: 'página anterior',
    errors_to_previous_mobile: 'Llévame a la <0>pantalla anterior</0>.',
    errors_to_previous_link_mobile: 'pantalla anterior',
    stories_carousel_previous_story: 'Historia anterior',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Escribe un comentario...',
    chat_input_placeholder: 'Escribe un mensaje...',
    ai_chatbot_widget_input_placeholder: 'Escribe algo...',
    create_post_input_placeholder: 'Escribe un mensaje...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Recorrido de Salud',
    health_picture_name: 'Perfil de Salud',
    ai_context_mode_health_journey: 'Recorrido de Salud',
    health_journey_second_tab: 'Perfil de Salud',
    health_journey_add_new: 'Crear recorrido de salud',
    health_journey_rename_journey: 'Modificar Recorrido de Salud',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Crear Recorrido de Salud',
    hj_already_create_for_myself_4: 'Ver Recorrido de Salud',
    health_journey_privacy_dialog_health_journey_link: 'Recorrido de Salud',
    health_picture_view_health_journey: 'Ver recorrido de salud',
    health_picture_symptom_checker_save_to_health_journey: 'Guardar en el Recorrido de Salud',
    health_journey_my_picture: 'Mi perfil de salud',
    health_journey_picture_not_created_title: 'Empiece a seguir su recorrido de salud',
    health_journey_start_health_picture_title: 'Dé el primer paso creando un perfil de salud',
    verification_successful_subtitle: 'Será redirigido en breve.\nComience su recorrido de salud hoy.',
    rooms_redirect_to_health_picture: 'complete su perfil de salud',
    health_picture_no_symptoms_info: 'Registre sus síntomas para obtener un perfil de salud completo',
    ai_context_label_health_journey: 'El contexto actual es el Recorrido de Salud de:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Cree ahora su propio Recorrido de Salud para tener una visión general de su bienestar y explorar otras funcionalidades interesantes.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Dé un nombre a su recorrido',
    new_health_journey_placeholder: 'Puede tener varios recorridos para usted mismo o para otra persona.',
    new_health_journey_validation_required_name: 'Introduzca un nombre para su recorrido',
    new_health_journey_validation_length_name: 'El nombre de su recorrido de salud no debe tener más de 30 caracteres',
    hj_already_create_for_myself_1: 'Parece que usted',
    hj_already_create_for_myself_2: 'ya ha creado un Recorrido de Salud',
    hj_already_create_for_myself_3: 'para usted mismo.',
    hj_multiple_dialog_title: '¿Para quién está creando un recorrido de salud?',
    hj_multiple_for_myself: 'Quiero crear este recorrido para mí mismo',
    hj_multiple_for_someone_else: 'Quiero crear este recorrido para otra persona',
    // Conversation hide override
    conversation_hide: 'Ocultar',
    // Consultation form overrides
    consultation_name: 'Consulta médica',
    health_picture_consultations: 'Mis consultas médicas',
    health_picture_add_consultation: 'Añadir consulta médica',
    health_picture_edit_consultation: 'Editar consulta médica',
    health_journey_consultation_type: 'Tipo de consulta:',
    health_journey_timeline_consultation_type: 'Tipo:',
    mycare_consultation_type_label: 'Tipo de consulta',
    mycare_consultation_provider_label: 'Nombre del profesional sanitario',
    mycare_consultation_speciality_label: 'Especialidad',
    my_care_journal_notes: 'Notas',
    my_care_journal_description_placeholder: 'Haz notas de cualquier cosa que quieras...',
    content_carousel_no_content_moderator_title: 'Crea contenido para tu sala',
    content_carousel_no_content_member_title: 'Mantén el contacto con tu sala',
    content_carousel_no_content_member_subtitle: 'Aquí encontrarás las últimas noticias, anuncios y recursos.',
    ai_context_label_room: 'El contexto actual es la Sala:',
    companions_block_user: 'Bloquear',
    companions_unblock_user: 'Desbloquear',
    companion_request_block: 'Bloquear',
    companion_request_unblock: 'Desbloquear',
    companion_report: 'Denunciar',
    companion_block_message: '¿Estás seguro/a de que quieres bloquear a {{memberDisplayName}} y eliminarlo/a como compañero/a?',
    companion_unblock_message: '¿Estás seguro/a de que quieres desbloquear a {{memberDisplayName}}?',
    companion_report_message: '¿Estás seguro/a de que quieres denunciar a {{memberDisplayName}}?',
    companions_block_successfully: '¡Has bloqueado al compañero/a con éxito!',
    companions_unblock_successfully: '¡Has desbloqueado al compañero/a con éxito!',
    companions_cannot_block_administrator: '¡No puedes bloquear a un administrador!',
    health_journey_select_date: 'Seleccionar fecha',
  },
  pt: {
    ai_context_mode_all_rooms: 'Automático',
    like: 'Gosto',
    likes: 'Gostos',
    community_post_like: 'Gosto',
    community_post_likes: 'Gostos',
    reaction_like_label: 'Gosto',
    story_allow_likes: 'Permitir gostos',
    comment: 'Comentário',
    comments: 'Comentários',
    reaction_care_label: 'Solidariedade',
    reaction_helpful_label: 'Útil',
    story_draft: 'Rascunho',
    stories_drafts: 'Rascunhos',
    checker: 'Verificador',
    health_picture_complete: 'Concluir',
    health_picture_diagnosis_type_nonmedical: 'Suspeito',
    preview: 'Pré-visualização',
    journal_name: 'Diário',
    community: 'Salas',
    stories: 'Histórias',
    profile_widget_journey: 'Percursos',
    browser: 'Navegador',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Conectar',
    community_room_type_hub: 'Hub',
    subgroups: 'Subgrupos',
    max: 'máx',
    companions_title: 'Companheiros',
    health_picture_ongoing: 'Em curso',
    poll_period_unlimited: 'Ilimitado',
    joined_rooms_title: 'Salas neste Hub',
    story_save_as_draft: 'Guardar como rascunho',
    story_saved_draft_snackbar: 'Rascunho guardado com sucesso',
    story_save_draft_success_dialog_content: 'O seu rascunho foi guardado com sucesso!',
    my_stories_empty_drafts_first_line: 'Não tem histórias em Rascunho.',
    health_picture_add_journal: 'Adicionar entrada no diário',
    health_picture_edit_journal: 'Editar entrada no diário',
    health_picture_journal: 'Os meus diários',
    health_journey_journal_title: 'Diário',
    health_journey_journal_date: 'Data do diário',
    delete_journal_confirmation_title: 'Eliminar diário',
    delete_journal_confirmation_description: 'Tem a certeza de que pretende eliminar este diário? Esta ação não pode ser revertida.',
    journal_deleted_successfully: 'Diário eliminado com sucesso.',
    error_deleting_journal: 'Ocorreu um problema ao eliminar o diário. Tente novamente.',
    stories_first_tab: 'Todas as histórias',
    stories_third_tab: 'As minhas histórias',
    stories_suggested: 'Histórias sugeridas',
    stories_my_stories: 'As minhas histórias',
    companions_in_room_title: 'Companheiros na sala',
    health_picture_snackbar_adding_ongoing_symptom: 'Já adicionou este sintoma e ainda está em curso! Atualize essa entrada!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Já adicionou este diagnóstico e ainda está em curso! Atualize essa entrada!',
    health_picture_snackbar_adding_ongoing_treatment: 'Já adicionou este tratamento e ainda está em curso! Atualize essa entrada!',
    health_picture_snackbar_adding_ongoing_medication: 'Já adicionou este medicamento e ainda está em curso! Atualize essa entrada!',
    your_subgroups: 'Os seus subgrupos',
    other_subgroups: 'Outros subgrupos',
    community_room_type_hub_room: 'Sala Hub',
    // Room consistency overrides
    rooms_title: 'Salas',
    my_rooms_title: 'As minhas Salas e Hubs',
    profile_widget_rooms: 'Salas',
    bottom_nav_community: 'Salas',
    search_rooms_title: 'Salas',
    community_first_tab: 'As minhas salas',
    community_second_tab: 'Todas as salas',
    community_room_type_private_room: 'Sala privada',
    community_room_type_public_room: 'Sala pública',
    related_rooms: 'Salas relacionadas',
    my_rooms_explore_other_rooms: 'Explorar outras salas',
    rooms_autocomplete_caption: 'Explorar salas:',
    rooms_no_result_found: 'Não foram encontradas salas. Tente outra pesquisa.',
    rooms_view_all_button: 'Ver todas as salas',
    rooms_view_rooms_button: 'Ver salas',
    rooms_leave_room: 'Sair da sala',
    rooms_related_rooms_loading: 'A carregar salas relacionadas...',
    rooms_related_rooms_end: 'Não há mais salas relacionadas',
    rooms_by_topic_view_more_button: 'Ver mais salas',
    share_story_to_rooms_no_rooms_button: 'Explorar salas',
    room_join_button: 'Aderir à sala',
    room_join_button_pending: 'Pedido de sala pendente',
    room_leave_confirmation_title: 'Sair da sala',
    no_related_rooms: 'Não foram encontradas salas relacionadas.',
    poll_rooms_selection: 'Partilhar sondagem nas salas:',
    my_rooms_no_member_rooms: 'Não é membro de nenhuma sala. Porque não <0>explorar?<0>',
    suggest_new_rooms_title: 'Não encontra a sala de que precisa?',
    latest_posts_your_topics: 'Últimas publicações nas suas salas',
    people_in_the_room: 'Pessoas na sala',
    people_in_the_room_no_people: 'Porque não ser o primeiro a aderir à sala e criar uma publicação.',
    people_in_the_room_no_companions_and_people: 'Parabéns! É a primeira pessoa nesta sala',
    no_moderators_in_the_room: 'Sem moderadores nesta sala',
    select_rooms_for_post_modal_title: 'Selecione as salas onde pretende partilhar a sua publicação:',
    rooms_create_post_dialog_placeholder_nonmember: 'Precisa de aderir à sala para poder publicar',
    rooms_blocked_member: 'Infelizmente, o seu acesso a esta sala foi revogado',
    community_not_member_of_room: 'Desculpe, não pode aceder a esta sala',
    community_room_library_no_files: 'Sem ficheiros anexados a esta sala.',
    community_room_library_details: 'Detalhes',
    community_room_library_download: 'Transferir',
    community_room_library_summarise: 'Resumir',
    community_room_library_translate: 'Traduzir',
    community_room_library_delete: 'Eliminar',
    community_room_library_update: 'Atualizar',
    profile_settings_ai_settings_title: 'Definições de IA',
    profile_settings_ai_tokens_allowed: 'Tokens permitidos por dia:',
    profile_settings_ai_tokens_used: 'Tokens utilizados hoje:',
    featured_content_delete_file_warning_title: 'Aviso',
    featured_content_delete_file_warning_message: 'Quando elimina um ficheiro aqui, ele também será eliminado da biblioteca',
    share_story_no_rooms_for_member: 'Parece que não é membro de nenhuma sala. Porque não explorar e aderir?',
    community_room_library_file_type: 'Tipo de ficheiro',
    community_room_library_language: 'Idioma',
    community_room_library_valid_until: 'Válido até',
    community_room_library_date_created: 'Data de criação',
    snackbar_community_member_not_in_room: 'Precisa de aderir à sala {{roomName}} para responder ou gostar.',
    snackbar_community_member_not_in_room_poll: 'Precisa de aderir à sala {{roomName}} para criar uma sondagem.',
    snackbar_community_moderator_remove_member_success: 'Membro removido da sala!',
    snackbar_community_cannot_join_room: 'Não pode aderir à sala.',
    snackbar_community_cannot_join_room_blocked: 'Foi bloqueado desta sala.',
    snackbar_community_cannot_leave_room: 'Não pode sair da sala.',
    snackbar_community_join_room_success: 'Aderiu à sala!',
    snackbar_community_leave_room_success: 'Saiu da sala!',
    polls_widget_no_polls: 'Ainda não existem sondagens nesta sala.',
    poll_proposal_additional_info: 'Um moderador da sala irá contactá-lo para mais informações sobre a sondagem.',
    room_leave_confirmation_description: 'Tem a certeza de que pretende sair desta sala?',
    story_choose_rooms_subtitle: 'Pode partilhar esta história nas seguintes salas:',
    story_published_modal_title: 'História publicada com sucesso! Deseja partilhá-la nas suas salas?',
    story_published_modal_title_mobile: 'Obrigado por publicar a sua história. Deseja partilhá-la nas suas salas?',
    moderator_pin_post_dialog_description: 'Tem a certeza de que pretende fixar esta publicação na sala?',
    moderator_remove_member_dialog_description: 'Tem a certeza de que pretende remover este membro da sala?',
    errors_posts_not_allowed_in_room: 'Publicações não permitidas nesta sala.',
    errors_no_posts_found_room_member: 'Seja o primeiro a publicar nesta sala!',
    errors_no_posts_found_room_not_member: 'Adira à sala e publique algo!',
    errors_no_posts_found_room: 'Seja o primeiro a publicar nesta sala!',
    notifications_settings_someone_publishes_a_story_label: 'Nova história em As minhas Salas',
    share_story_to_rooms_second_title: 'Deseja partilhá-la nas suas salas?',
    private_room_code_widget_title: 'Tem um código para uma Sala ou Hub privado?',
    private_room_shim_name_title_dialog: 'Bem-vindo a esta sala privada!',
    private_room_shim_name_subtitle_dialog: 'Use o nome pelo qual as pessoas nesta sala o conhecem.',
    private_room_shim_name_subtitle_dialog_2: 'Permanecerá anónimo fora desta sala.',
    companions_description_no_companion_in_room: 'Ainda não tem companheiros nesta sala. Participe em conversas e encontre membros para se conectar.',
    companions_description_no_companion_in_community: 'Ainda não tem companheiros. Porque não explorar salas e encontrar membros para se conectar?',
    companions_no_companion: 'Ainda não tem companheiros. Visite a sala de novos membros para encontrar membros carecircle e conectar-se',
    personal_private_room_widget_button: 'Criar uma sala privada',
    personal_private_room_dialog_title: 'Criar a sua sala privada',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Cabeça',
    body_part_first_level_chest: 'Peito',
    body_part_first_level_abdomen: 'Abdómen',
    body_part_first_level_pelvis: 'Pélvis',
    body_part_first_level_arms: 'Braços',
    body_part_first_level_legs: 'Pernas',
    body_part_first_level_back: 'Costas',
    body_part_first_level_buttocks: 'Glúteos',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Existem 'salas' que pode visitar anonimamente e participar em discussões se desejar. Pode publicar comentários ou perguntas, ou encontrar companheiros que o apoiarão na sua jornada.",
    conversations: 'Conversas',
    // Featured content overrides
    stories_carousel_title: 'Histórias em destaque',
    content_carousel_title: 'Conteúdo em destaque',
    content_carousel_add_new_post_button: 'Adicionar novo Conteúdo em Destaque',
    content_carousel_no_content_moderator_subtitle: 'Informe todos nesta sala sobre as últimas notícias, anúncios e recursos. O conteúdo em destaque só pode ser criado a partir da aplicação web de momento.',
    room_content_type_featured_posts_and_library_option: 'Publicações em destaque e biblioteca',
    room_content_type_featured_posts_option: 'Publicações em destaque',
    room_create_content_success: 'O item de conteúdo em destaque foi criado com sucesso',
    room_update_content_success: 'O item de conteúdo em destaque foi atualizado com sucesso',
    room_delete_content_success: 'O item de conteúdo em destaque foi eliminado com sucesso',
    room_delete_content_error: 'Ocorreu um erro ao tentar eliminar o item de conteúdo em destaque',
    room_confirm_delete_featured_item_title: 'Eliminar conteúdo em destaque',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Como se sente',
    wellness_tracker_morning: 'esta manhã',
    wellness_tracker_afternoon: 'esta tarde',
    wellness_tracker_evening: 'esta noite',
    // Navigation "Previous" overrides
    previous: 'Anterior',
    health_picture_symptom_checker_previous: 'Anterior',
    errors_to_previous_link: 'página anterior',
    errors_to_previous_mobile: 'Leve-me para o <0>ecrã anterior</0>.',
    errors_to_previous_link_mobile: 'ecrã anterior',
    stories_carousel_previous_story: 'História anterior',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Escreva um comentário...',
    chat_input_placeholder: 'Escreva uma mensagem...',
    ai_chatbot_widget_input_placeholder: 'Escreva algo...',
    create_post_input_placeholder: 'Escreva uma mensagem...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Percurso de Saúde',
    health_picture_name: 'Perfil de Saúde',
    ai_context_mode_health_journey: 'Percurso de Saúde',
    health_journey_second_tab: 'Perfil de Saúde',
    health_journey_add_new: 'Criar percurso de saúde',
    health_journey_rename_journey: 'Modificar Percurso de Saúde',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Criar Percurso de Saúde',
    hj_already_create_for_myself_4: 'Ver Percurso de Saúde',
    health_journey_privacy_dialog_health_journey_link: 'Percurso de Saúde',
    health_picture_view_health_journey: 'Ver percurso de saúde',
    health_picture_symptom_checker_save_to_health_journey: 'Guardar no Percurso de Saúde',
    health_journey_my_picture: 'O meu perfil de saúde',
    health_journey_picture_not_created_title: 'Comece a acompanhar o seu percurso de saúde',
    health_journey_start_health_picture_title: 'Dê o primeiro passo criando um perfil de saúde',
    verification_successful_subtitle: 'Será redirecionado em breve.\nComece o seu percurso de saúde hoje.',
    rooms_redirect_to_health_picture: 'preencha o seu perfil de saúde',
    health_picture_no_symptoms_info: 'Registe os seus sintomas para ter um perfil de saúde completo',
    ai_context_label_health_journey: 'O contexto atual é o Percurso de Saúde de:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Crie agora o seu próprio Percurso de Saúde para ter uma visão geral do seu bem-estar e explorar outras funcionalidades interessantes.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Dê um nome ao seu percurso',
    new_health_journey_placeholder: 'Pode ter vários percursos para si ou para outra pessoa.',
    new_health_journey_validation_required_name: 'Introduza um nome para o seu percurso',
    new_health_journey_validation_length_name: 'O nome do seu percurso de saúde não deve ter mais de 30 caracteres',
    hj_already_create_for_myself_1: 'Parece que',
    hj_already_create_for_myself_2: 'já criou um Percurso de Saúde',
    hj_already_create_for_myself_3: 'para si.',
    hj_multiple_dialog_title: 'Para quem está a criar um percurso de saúde?',
    hj_multiple_for_myself: 'Quero criar este percurso para mim',
    hj_multiple_for_someone_else: 'Quero criar este percurso para outra pessoa',
    // Conversation hide override
    conversation_hide: 'Ocultar',
    // Consultation form overrides
    consultation_name: 'Consulta médica',
    health_picture_consultations: 'As minhas consultas médicas',
    health_picture_add_consultation: 'Adicionar consulta médica',
    health_picture_edit_consultation: 'Editar consulta médica',
    health_journey_consultation_type: 'Tipo de consulta:',
    health_journey_timeline_consultation_type: 'Tipo:',
    mycare_consultation_type_label: 'Tipo de consulta',
    mycare_consultation_provider_label: 'Nome do profissional de saúde',
    mycare_consultation_speciality_label: 'Especialidade',
    my_care_journal_notes: 'Notas',
    my_care_journal_description_placeholder: 'Faça notas de tudo o que quiser...',
    content_carousel_no_content_moderator_title: 'Crie conteúdo para a sua sala',
    content_carousel_no_content_member_title: 'Mantenha-se em contacto com a sua sala',
    content_carousel_no_content_member_subtitle: 'Aqui encontrará as últimas notícias, anúncios e recursos.',
    ai_context_label_room: 'O contexto atual é a Sala:',
    companions_block_user: 'Bloquear',
    companions_unblock_user: 'Desbloquear',
    companion_request_block: 'Bloquear',
    companion_request_unblock: 'Desbloquear',
    companion_report: 'Denunciar',
    companion_block_message: 'Tem a certeza de que pretende bloquear {{memberDisplayName}} e removê-lo/a como companheiro/a?',
    companion_unblock_message: 'Tem a certeza de que pretende desbloquear {{memberDisplayName}}?',
    companion_report_message: 'Tem a certeza de que pretende denunciar {{memberDisplayName}}?',
    companions_block_successfully: 'Bloqueou o companheiro com sucesso!',
    companions_unblock_successfully: 'Desbloqueou o companheiro com sucesso!',
    companions_cannot_block_administrator: 'Não pode bloquear um administrador!',
    health_journey_select_date: 'Selecionar data',
  },
  fi: {
    ai_context_mode_all_rooms: 'Automaattinen',
    like: 'Tykkää',
    likes: 'Tykkäykset',
    community_post_like: 'Tykkää',
    community_post_likes: 'Tykkäykset',
    reaction_like_label: 'Tykkää',
    story_allow_likes: 'Salli tykkäykset',
    comment: 'Kommentti',
    comments: 'Kommentit',
    reaction_care_label: 'Välitän',
    reaction_helpful_label: 'Hyödyllinen',
    story_draft: 'Luonnos',
    stories_drafts: 'Luonnokset',
    checker: 'Tarkistus',
    health_picture_complete: 'Valmis',
    health_picture_diagnosis_type_nonmedical: 'Epäilty',
    preview: 'Esikatselu',
    journal_name: 'Päiväkirja',
    community: 'Huoneet',
    stories: 'Tarinat',
    profile_widget_journey: 'Polut',
    browser: 'Selain',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Yhdistä',
    community_room_type_hub: 'Hub',
    subgroups: 'Alaryhmät',
    max: 'maks',
    companions_title: 'Kumppanit',
    health_picture_ongoing: 'Käynnissä',
    poll_period_unlimited: 'Rajoittamaton',
    joined_rooms_title: 'Huoneet tässä Hubissa',
    story_save_as_draft: 'Tallenna luonnoksena',
    story_saved_draft_snackbar: 'Luonnos tallennettu onnistuneesti',
    story_save_draft_success_dialog_content: 'Luonnoksesi on tallennettu onnistuneesti!',
    my_stories_empty_drafts_first_line: 'Sinulla ei ole tarinoita luonnoksissa.',
    health_picture_add_journal: 'Lisää päiväkirjamerkintä',
    health_picture_edit_journal: 'Muokkaa päiväkirjamerkintää',
    health_picture_journal: 'Omat päiväkirjat',
    health_journey_journal_title: 'Päiväkirja',
    health_journey_journal_date: 'Päiväkirjan päivämäärä',
    delete_journal_confirmation_title: 'Poista päiväkirja',
    delete_journal_confirmation_description: 'Haluatko varmasti poistaa tämän päiväkirjan? Tätä toimintoa ei voi kumota.',
    journal_deleted_successfully: 'Päiväkirja poistettu onnistuneesti.',
    error_deleting_journal: 'Päiväkirjan poistamisessa tapahtui virhe. Yritä uudelleen.',
    stories_first_tab: 'Kaikki tarinat',
    stories_third_tab: 'Omat tarinat',
    stories_suggested: 'Ehdotetut tarinat',
    stories_my_stories: 'Omat tarinat',
    companions_in_room_title: 'Kumppanit huoneessa',
    health_picture_snackbar_adding_ongoing_symptom: 'Olet jo lisännyt tämän oireen ja se on edelleen käynnissä! Päivitä kyseinen merkintä!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Olet jo lisännyt tämän diagnoosin ja se on edelleen käynnissä! Päivitä kyseinen merkintä!',
    health_picture_snackbar_adding_ongoing_treatment: 'Olet jo lisännyt tämän hoidon ja se on edelleen käynnissä! Päivitä kyseinen merkintä!',
    health_picture_snackbar_adding_ongoing_medication: 'Olet jo lisännyt tämän lääkkeen ja se on edelleen käynnissä! Päivitä kyseinen merkintä!',
    your_subgroups: 'Omat alaryhmät',
    other_subgroups: 'Muut alaryhmät',
    community_room_type_hub_room: 'Hub-huone',
    // Room consistency overrides
    rooms_title: 'Huoneet',
    my_rooms_title: 'Omat Huoneet ja Hubit',
    profile_widget_rooms: 'Huoneet',
    bottom_nav_community: 'Huoneet',
    search_rooms_title: 'Huoneet',
    community_first_tab: 'Omat huoneet',
    community_second_tab: 'Kaikki huoneet',
    community_room_type_private_room: 'Yksityinen huone',
    community_room_type_public_room: 'Julkinen huone',
    related_rooms: 'Liittyvät huoneet',
    my_rooms_explore_other_rooms: 'Tutustu muihin huoneisiin',
    rooms_autocomplete_caption: 'Tutustu huoneisiin:',
    rooms_no_result_found: 'Huoneita ei löytynyt. Kokeile toista hakua.',
    rooms_view_all_button: 'Näytä kaikki huoneet',
    rooms_view_rooms_button: 'Näytä huoneet',
    rooms_leave_room: 'Poistu huoneesta',
    rooms_related_rooms_loading: 'Ladataan liittyviä huoneita...',
    rooms_related_rooms_end: 'Ei enempää liittyviä huoneita',
    rooms_by_topic_view_more_button: 'Näytä lisää huoneita',
    share_story_to_rooms_no_rooms_button: 'Tutustu huoneisiin',
    room_join_button: 'Liity huoneeseen',
    room_join_button_pending: 'Huonepyyntö odottaa',
    room_leave_confirmation_title: 'Poistu huoneesta',
    no_related_rooms: 'Liittyviä huoneita ei löytynyt.',
    poll_rooms_selection: 'Jaa kysely huoneisiin:',
    my_rooms_no_member_rooms: 'Et ole minkään huoneen jäsen. Mikset <0>tutustu?<0>',
    suggest_new_rooms_title: 'Etkö löydä tarvitsemaasi huonetta?',
    latest_posts_your_topics: 'Viimeisimmät julkaisut huoneissasi',
    people_in_the_room: 'Henkilöt huoneessa',
    people_in_the_room_no_people: 'Mikset liity ensimmäisenä huoneeseen ja luo julkaisu.',
    people_in_the_room_no_companions_and_people: 'Onnittelut! Olet ensimmäinen henkilö tässä huoneessa',
    no_moderators_in_the_room: 'Ei moderaattoreita tässä huoneessa',
    select_rooms_for_post_modal_title: 'Valitse huoneet, joihin haluat jakaa julkaisusi:',
    rooms_create_post_dialog_placeholder_nonmember: 'Sinun täytyy liittyä huoneeseen voidaksesi julkaista',
    rooms_blocked_member: 'Valitettavasti pääsysi tähän huoneeseen on peruutettu',
    community_not_member_of_room: 'Valitettavasti et voi käyttää tätä huonetta',
    community_room_library_no_files: 'Ei tiedostoja liitettynä tähän huoneeseen.',
    community_room_library_details: 'Tiedot',
    community_room_library_download: 'Lataa',
    community_room_library_summarise: 'Tiivistä',
    community_room_library_translate: 'Käännä',
    community_room_library_delete: 'Poista',
    community_room_library_update: 'Päivitä',
    profile_settings_ai_settings_title: 'Tekoälyasetukset',
    profile_settings_ai_tokens_allowed: 'Sallitut tokenit päivässä:',
    profile_settings_ai_tokens_used: 'Tänään käytetyt tokenit:',
    featured_content_delete_file_warning_title: 'Varoitus',
    featured_content_delete_file_warning_message: 'Kun poistat tiedoston täältä, se poistetaan myös kirjastosta',
    share_story_no_rooms_for_member: 'Näyttää siltä, ettet ole minkään huoneen jäsen. Mikset tutustu ja liity?',
    community_room_library_file_type: 'Tiedostotyyppi',
    community_room_library_language: 'Kieli',
    community_room_library_valid_until: 'Voimassa asti',
    community_room_library_date_created: 'Luontipäivä',
    snackbar_community_member_not_in_room: 'Sinun täytyy liittyä huoneeseen {{roomName}} vastataksesi tai tykätäksesi.',
    snackbar_community_member_not_in_room_poll: 'Sinun täytyy liittyä huoneeseen {{roomName}} luodaksesi kyselyn.',
    snackbar_community_moderator_remove_member_success: 'Jäsen poistettu huoneesta!',
    snackbar_community_cannot_join_room: 'Et voi liittyä huoneeseen.',
    snackbar_community_cannot_join_room_blocked: 'Sinut on estetty tästä huoneesta.',
    snackbar_community_cannot_leave_room: 'Et voi poistua huoneesta.',
    snackbar_community_join_room_success: 'Olet liittynyt huoneeseen!',
    snackbar_community_leave_room_success: 'Olet poistunut huoneesta!',
    polls_widget_no_polls: 'Tässä huoneessa ei ole vielä kyselyjä.',
    poll_proposal_additional_info: 'Huoneen moderaattori ottaa yhteyttä lisätietojen saamiseksi kyselystä.',
    room_leave_confirmation_description: 'Haluatko varmasti poistua tästä huoneesta?',
    story_choose_rooms_subtitle: 'Voit jakaa tämän tarinan seuraavissa huoneissa:',
    story_published_modal_title: 'Tarina julkaistu onnistuneesti! Haluatko jakaa sen huoneisiisi?',
    story_published_modal_title_mobile: 'Kiitos tarinasi julkaisemisesta. Haluatko jakaa sen huoneisiisi?',
    moderator_pin_post_dialog_description: 'Haluatko varmasti kiinnittää tämän julkaisun huoneeseen?',
    moderator_remove_member_dialog_description: 'Haluatko varmasti poistaa tämän jäsenen huoneesta?',
    errors_posts_not_allowed_in_room: 'Julkaisut eivät ole sallittuja tässä huoneessa.',
    errors_no_posts_found_room_member: 'Ole ensimmäinen, joka julkaisee tässä huoneessa!',
    errors_no_posts_found_room_not_member: 'Liity huoneeseen ja julkaise jotain!',
    errors_no_posts_found_room: 'Ole ensimmäinen, joka julkaisee tässä huoneessa!',
    notifications_settings_someone_publishes_a_story_label: 'Uusi tarina Omissa Huoneissa',
    share_story_to_rooms_second_title: 'Haluatko jakaa sen huoneisiisi?',
    private_room_code_widget_title: 'Onko sinulla koodi yksityiseen Huoneeseen tai Hubiin?',
    private_room_shim_name_title_dialog: 'Tervetuloa tähän yksityiseen huoneeseen!',
    private_room_shim_name_subtitle_dialog: 'Käytä nimeä, jolla ihmiset tässä huoneessa sinut tuntevat.',
    private_room_shim_name_subtitle_dialog_2: 'Pysyt anonyyminä tämän huoneen ulkopuolella.',
    companions_description_no_companion_in_room: 'Sinulla ei ole vielä kumppaneita tässä huoneessa. Osallistu keskusteluihin ja löydä jäseniä, joihin yhdistyä.',
    companions_description_no_companion_in_community: 'Sinulla ei ole vielä kumppaneita. Mikset tutustu huoneisiin ja löydä jäseniä, joihin yhdistyä?',
    companions_no_companion: 'Sinulla ei ole vielä kumppaneita. Vieraile uusien jäsenten huoneessa löytääksesi carecircle-jäseniä ja yhdistyäksesi',
    personal_private_room_widget_button: 'Luo yksityinen huone',
    personal_private_room_dialog_title: 'Luo yksityinen huoneesi',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Pää',
    body_part_first_level_chest: 'Rintakehä',
    body_part_first_level_abdomen: 'Vatsa',
    body_part_first_level_pelvis: 'Lantio',
    body_part_first_level_arms: 'Käsivarret',
    body_part_first_level_legs: 'Jalat',
    body_part_first_level_back: 'Selkä',
    body_part_first_level_buttocks: 'Pakarat',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "On 'huoneita', joissa voit vierailla nimettömänä ja osallistua keskusteluihin halutessasi. Voit julkaista kommentteja tai kysymyksiä, tai löytää kumppaneita, jotka tukevat sinua matkallasi.",
    conversations: 'Keskustelut',
    // Featured content overrides
    stories_carousel_title: 'Suositellut tarinat',
    content_carousel_title: 'Suositeltu sisältö',
    content_carousel_add_new_post_button: 'Lisää uusi Suositeltu sisältö',
    content_carousel_no_content_moderator_subtitle: 'Kerro kaikille tässä huoneessa viimeisimmistä uutisista, ilmoituksista ja resursseista. Suositeltua sisältöä voi tällä hetkellä luoda vain verkkosovelluksesta.',
    room_content_type_featured_posts_and_library_option: 'Suositellut julkaisut ja kirjasto',
    room_content_type_featured_posts_option: 'Suositellut julkaisut',
    room_create_content_success: 'Suositeltu sisältökohde luotiin onnistuneesti',
    room_update_content_success: 'Suositeltu sisältökohde päivitettiin onnistuneesti',
    room_delete_content_success: 'Suositeltu sisältökohde poistettiin onnistuneesti',
    room_delete_content_error: 'Suositellun sisältökohteen poistamisessa tapahtui virhe',
    room_confirm_delete_featured_item_title: 'Poista suositeltu sisältö',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Miltä sinusta tuntuu',
    wellness_tracker_morning: 'tänä aamuna',
    wellness_tracker_afternoon: 'tänä iltapäivänä',
    wellness_tracker_evening: 'tänä iltana',
    // Navigation "Previous" overrides
    previous: 'Edellinen',
    health_picture_symptom_checker_previous: 'Edellinen',
    errors_to_previous_link: 'edellinen sivu',
    errors_to_previous_mobile: 'Vie minut <0>edelliselle näytölle</0>.',
    errors_to_previous_link_mobile: 'edellinen näyttö',
    stories_carousel_previous_story: 'Edellinen tarina',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Kirjoita kommentti...',
    chat_input_placeholder: 'Kirjoita viesti...',
    ai_chatbot_widget_input_placeholder: 'Kirjoita jotain...',
    create_post_input_placeholder: 'Kirjoita viesti...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Terveyspolku',
    health_picture_name: 'Terveyskuva',
    ai_context_mode_health_journey: 'Terveyspolku',
    health_journey_second_tab: 'Terveyskuva',
    health_journey_add_new: 'Luo terveyspolku',
    health_journey_rename_journey: 'Muokkaa terveyspolkua',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Luo terveyspolku',
    hj_already_create_for_myself_4: 'Näytä terveyspolku',
    health_journey_privacy_dialog_health_journey_link: 'Terveyspolku',
    health_picture_view_health_journey: 'Näytä terveyspolku',
    health_picture_symptom_checker_save_to_health_journey: 'Tallenna terveyspolkuun',
    health_journey_my_picture: 'Oma terveyskuva',
    health_journey_picture_not_created_title: 'Aloita terveyspolkusi seuranta',
    health_journey_start_health_picture_title: 'Ota ensimmäinen askel luomalla terveyskuva',
    verification_successful_subtitle: 'Sinut ohjataan pian eteenpäin.\nAloita terveyspolkusi tänään.',
    rooms_redirect_to_health_picture: 'täytä terveyskuvasi',
    health_picture_no_symptoms_info: 'Kirjaa oireesi saadaksesi kattavan terveyskuvan',
    ai_context_label_health_journey: 'Nykyinen konteksti on terveyspolku:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Luo nyt oma terveyspolkusi saadaksesi yleiskuvan hyvinvoinnistasi ja tutustuaksesi muihin jännittäviin ominaisuuksiin.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Anna polullesi nimi',
    new_health_journey_placeholder: 'Sinulla voi olla useita polkuja itsellesi tai jollekin toiselle.',
    new_health_journey_validation_required_name: 'Anna polullesi nimi',
    new_health_journey_validation_length_name: 'Terveyspolun nimi ei saa olla yli 30 merkkiä',
    hj_already_create_for_myself_1: 'Näyttää siltä, että olet',
    hj_already_create_for_myself_2: 'jo luonut terveyspolun',
    hj_already_create_for_myself_3: 'itsellesi.',
    hj_multiple_dialog_title: 'Kenelle luot terveyspolun?',
    hj_multiple_for_myself: 'Haluan luoda tämän polun itselleni',
    hj_multiple_for_someone_else: 'Haluan luoda tämän polun jollekulle toiselle',
    // Conversation hide override
    conversation_hide: 'Piilota',
    // Consultation form overrides
    consultation_name: 'Lääkärikäynti',
    health_picture_consultations: 'Lääkärikäyntini',
    health_picture_add_consultation: 'Lisää lääkärikäynti',
    health_picture_edit_consultation: 'Muokkaa lääkärikäyntiä',
    health_journey_consultation_type: 'Käynnin tyyppi:',
    health_journey_timeline_consultation_type: 'Tyyppi:',
    mycare_consultation_type_label: 'Käynnin tyyppi',
    mycare_consultation_provider_label: 'Terveydenhuollon tarjoajan nimi',
    mycare_consultation_speciality_label: 'Erikoisala',
    my_care_journal_notes: 'Muistiinpanot',
    my_care_journal_description_placeholder: 'Kirjoita muistiinpanoja mistä tahansa haluat...',
    content_carousel_no_content_moderator_title: 'Luo sisältöä huoneellesi',
    content_carousel_no_content_member_title: 'Pysy yhteydessä huoneeseesi',
    content_carousel_no_content_member_subtitle: 'Täältä löydät uusimmat uutiset, ilmoitukset ja resurssit.',
    ai_context_label_room: 'Nykyinen konteksti on Huone:',
    companions_block_user: 'Estä',
    companions_unblock_user: 'Poista esto',
    companion_request_block: 'Estä',
    companion_request_unblock: 'Poista esto',
    companion_report: 'Ilmianna',
    companion_block_message: 'Haluatko varmasti estää käyttäjän {{memberDisplayName}} ja poistaa hänet kumppanina?',
    companion_unblock_message: 'Haluatko varmasti poistaa käyttäjän {{memberDisplayName}} eston?',
    companion_report_message: 'Haluatko varmasti ilmiantaa käyttäjän {{memberDisplayName}}?',
    companions_block_successfully: 'Olet estänyt kumppanin onnistuneesti!',
    companions_unblock_successfully: 'Olet poistanut kumppanin eston onnistuneesti!',
    companions_cannot_block_administrator: 'Et voi estää ylläpitäjää!',
    health_journey_select_date: 'Valitse päivämäärä',
  },
  nb: {
    ai_context_mode_all_rooms: 'Automatisk',
    like: 'Liker',
    likes: 'Liker',
    community_post_like: 'Liker',
    community_post_likes: 'Liker',
    reaction_like_label: 'Liker',
    story_allow_likes: 'Tillat liker',
    comment: 'Kommentar',
    comments: 'Kommentarer',
    reaction_care_label: 'Bryr meg',
    reaction_helpful_label: 'Nyttig',
    story_draft: 'Utkast',
    stories_drafts: 'Utkast',
    checker: 'Sjekker',
    health_picture_complete: 'Fullfør',
    health_picture_diagnosis_type_nonmedical: 'Antatt',
    preview: 'Forhåndsvisning',
    journal_name: 'Dagbok',
    community: 'Rum',
    stories: 'Historier',
    profile_widget_journey: 'Forløp',
    browser: 'Nettleser',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Koble til',
    community_room_type_hub: 'Hub',
    subgroups: 'Undergrupper',
    max: 'maks',
    companions_title: 'Følgesvenner',
    health_picture_ongoing: 'Pågående',
    poll_period_unlimited: 'Ubegrenset',
    joined_rooms_title: 'Rom i denne Huben',
    story_save_as_draft: 'Lagre som utkast',
    story_saved_draft_snackbar: 'Utkast lagret',
    story_save_draft_success_dialog_content: 'Utkastet ditt er lagret!',
    my_stories_empty_drafts_first_line: 'Du har ingen historier i Utkast.',
    health_picture_add_journal: 'Legg til dagbokoppføring',
    health_picture_edit_journal: 'Rediger dagbokoppføring',
    health_picture_journal: 'Mine dagbøker',
    health_journey_journal_title: 'Dagbok',
    health_journey_journal_date: 'Dagbokdato',
    delete_journal_confirmation_title: 'Slett dagbok',
    delete_journal_confirmation_description: 'Er du sikker på at du vil slette denne dagboken? Denne handlingen kan ikke angres.',
    journal_deleted_successfully: 'Dagbok slettet.',
    error_deleting_journal: 'Det oppstod et problem med å slette dagboken. Prøv igjen.',
    stories_first_tab: 'Alle historier',
    stories_third_tab: 'Mine historier',
    stories_suggested: 'Foreslåtte historier',
    stories_my_stories: 'Mine historier',
    companions_in_room_title: 'Følgesvenner i rommet',
    health_picture_snackbar_adding_ongoing_symptom: 'Du har allerede lagt til dette symptomet og det er fortsatt pågående! Oppdater den oppføringen!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Du har allerede lagt til denne diagnosen og den er fortsatt pågående! Oppdater den oppføringen!',
    health_picture_snackbar_adding_ongoing_treatment: 'Du har allerede lagt til denne behandlingen og den er fortsatt pågående! Oppdater den oppføringen!',
    health_picture_snackbar_adding_ongoing_medication: 'Du har allerede lagt til dette medikamentet og det er fortsatt pågående! Oppdater den oppføringen!',
    your_subgroups: 'Dine undergrupper',
    other_subgroups: 'Andre undergrupper',
    community_room_type_hub_room: 'Hub-rom',
    // Room consistency overrides
    rooms_title: 'Rom',
    my_rooms_title: 'Mine Rom og Huber',
    profile_widget_rooms: 'Rom',
    bottom_nav_community: 'Rom',
    search_rooms_title: 'Rom',
    community_first_tab: 'Mine rom',
    community_second_tab: 'Alle rom',
    community_room_type_private_room: 'Privat rom',
    community_room_type_public_room: 'Offentlig rom',
    related_rooms: 'Relaterte rom',
    my_rooms_explore_other_rooms: 'Utforsk andre rom',
    rooms_autocomplete_caption: 'Utforsk rom:',
    rooms_no_result_found: 'Ingen rom funnet. Prøv et annet søk.',
    rooms_view_all_button: 'Vis alle rom',
    rooms_view_rooms_button: 'Vis rom',
    rooms_leave_room: 'Forlat rommet',
    rooms_related_rooms_loading: 'Laster relaterte rom...',
    rooms_related_rooms_end: 'Ingen flere relaterte rom',
    rooms_by_topic_view_more_button: 'Vis flere rom',
    share_story_to_rooms_no_rooms_button: 'Utforsk rom',
    room_join_button: 'Bli med i rommet',
    room_join_button_pending: 'Romforespørsel venter',
    room_leave_confirmation_title: 'Forlat rommet',
    no_related_rooms: 'Ingen relaterte rom funnet.',
    poll_rooms_selection: 'Del avstemning i rommene:',
    my_rooms_no_member_rooms: 'Du er ikke medlem av noe rom. Hvorfor ikke <0>utforske?<0>',
    suggest_new_rooms_title: 'Finner du ikke rommet du trenger?',
    latest_posts_your_topics: 'Siste innlegg i dine rom',
    people_in_the_room: 'Personer i rommet',
    people_in_the_room_no_people: 'Hvorfor ikke være den første til å bli med i rommet og lage et innlegg.',
    people_in_the_room_no_companions_and_people: 'Gratulerer! Du er den første personen i dette rommet',
    no_moderators_in_the_room: 'Ingen moderatorer i dette rommet',
    select_rooms_for_post_modal_title: 'Velg rommene du vil dele innlegget ditt i:',
    rooms_create_post_dialog_placeholder_nonmember: 'Du må bli med i rommet for å poste',
    rooms_blocked_member: 'Dessverre er tilgangen din til dette rommet trukket tilbake',
    community_not_member_of_room: 'Beklager, du har ikke tilgang til dette rommet',
    community_room_library_no_files: 'Ingen filer knyttet til dette rommet.',
    community_room_library_details: 'Detaljer',
    community_room_library_download: 'Last ned',
    community_room_library_summarise: 'Oppsummer',
    community_room_library_translate: 'Oversett',
    community_room_library_delete: 'Slett',
    community_room_library_update: 'Oppdater',
    profile_settings_ai_settings_title: 'AI-innstillinger',
    profile_settings_ai_tokens_allowed: 'Tillatte tokens per dag:',
    profile_settings_ai_tokens_used: 'Tokens brukt i dag:',
    featured_content_delete_file_warning_title: 'Advarsel',
    featured_content_delete_file_warning_message: 'Når du sletter en fil her, vil den også bli slettet fra biblioteket',
    share_story_no_rooms_for_member: 'Det ser ut til at du ikke er medlem av noe rom. Hvorfor ikke utforske og bli med?',
    community_room_library_file_type: 'Filtype',
    community_room_library_language: 'Språk',
    community_room_library_valid_until: 'Gyldig til',
    community_room_library_date_created: 'Opprettelsesdato',
    snackbar_community_member_not_in_room: 'Du må bli med i rommet {{roomName}} for å svare eller like.',
    snackbar_community_member_not_in_room_poll: 'Du må bli med i rommet {{roomName}} for å opprette en avstemning.',
    snackbar_community_moderator_remove_member_success: 'Medlem fjernet fra rommet!',
    snackbar_community_cannot_join_room: 'Du kan ikke bli med i rommet.',
    snackbar_community_cannot_join_room_blocked: 'Du er blokkert fra dette rommet.',
    snackbar_community_cannot_leave_room: 'Du kan ikke forlate rommet.',
    snackbar_community_join_room_success: 'Du har blitt med i rommet!',
    snackbar_community_leave_room_success: 'Du har forlatt rommet!',
    polls_widget_no_polls: 'Det er ingen avstemninger i dette rommet ennå.',
    poll_proposal_additional_info: 'En moderator i rommet vil ta kontakt for mer informasjon om avstemningen.',
    room_leave_confirmation_description: 'Er du sikker på at du vil forlate dette rommet?',
    story_choose_rooms_subtitle: 'Du kan dele denne historien i følgende rom:',
    story_published_modal_title: 'Historien er publisert! Vil du dele den i rommene dine?',
    story_published_modal_title_mobile: 'Takk for at du publiserte historien din. Vil du dele den i rommene dine?',
    moderator_pin_post_dialog_description: 'Er du sikker på at du vil feste dette innlegget i rommet?',
    moderator_remove_member_dialog_description: 'Er du sikker på at du vil fjerne dette medlemmet fra rommet?',
    errors_posts_not_allowed_in_room: 'Innlegg er ikke tillatt i dette rommet.',
    errors_no_posts_found_room_member: 'Vær den første til å poste i dette rommet!',
    errors_no_posts_found_room_not_member: 'Bli med i rommet og post noe!',
    errors_no_posts_found_room: 'Vær den første til å poste i dette rommet!',
    notifications_settings_someone_publishes_a_story_label: 'Ny historie i Mine Rom',
    share_story_to_rooms_second_title: 'Vil du dele den i rommene dine?',
    private_room_code_widget_title: 'Har du en kode for et privat Rom eller Hub?',
    private_room_shim_name_title_dialog: 'Velkommen til dette private rommet!',
    private_room_shim_name_subtitle_dialog: 'Bruk navnet som folk i dette rommet kjenner deg som.',
    private_room_shim_name_subtitle_dialog_2: 'Du forblir anonym utenfor dette rommet.',
    companions_description_no_companion_in_room: 'Du har ingen følgesvenner i dette rommet ennå. Delta i samtaler og finn medlemmer å knytte kontakt med.',
    companions_description_no_companion_in_community: 'Du har ingen følgesvenner ennå. Hvorfor ikke utforske rom og finne medlemmer å knytte kontakt med?',
    companions_no_companion: 'Du har ingen følgesvenner ennå. Besøk rommet for nye medlemmer for å finne carecircle-medlemmer og knytte kontakt',
    personal_private_room_widget_button: 'Opprett et privat rom',
    personal_private_room_dialog_title: 'Opprett ditt private rom',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Hode',
    body_part_first_level_chest: 'Bryst',
    body_part_first_level_abdomen: 'Mage',
    body_part_first_level_pelvis: 'Bekken',
    body_part_first_level_arms: 'Armer',
    body_part_first_level_legs: 'Ben',
    body_part_first_level_back: 'Rygg',
    body_part_first_level_buttocks: 'Rumpe',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Det finnes 'rom' du kan besøke anonymt og delta i diskusjoner hvis du ønsker det. Du kan legge inn kommentarer eller spørsmål, eller finne følgesvenner som vil støtte deg på reisen din.",
    conversations: 'Chatter',
    // Featured content overrides
    stories_carousel_title: 'Utvalgte historier',
    content_carousel_title: 'Utvalgt innhold',
    content_carousel_add_new_post_button: 'Legg til nytt Utvalgt innhold',
    content_carousel_no_content_moderator_subtitle: 'Informer alle i dette rommet om de siste nyhetene, kunngjøringene og ressursene. Utvalgt innhold kan foreløpig bare opprettes fra nettappen.',
    room_content_type_featured_posts_and_library_option: 'Utvalgte innlegg og bibliotek',
    room_content_type_featured_posts_option: 'Utvalgte innlegg',
    room_create_content_success: 'Utvalgt innholdselement ble opprettet',
    room_update_content_success: 'Utvalgt innholdselement ble oppdatert',
    room_delete_content_success: 'Utvalgt innholdselement ble slettet',
    room_delete_content_error: 'Det oppstod en feil under sletting av utvalgt innholdselement',
    room_confirm_delete_featured_item_title: 'Slett utvalgt innhold',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Hvordan føler du deg',
    wellness_tracker_morning: 'denne morgenen',
    wellness_tracker_afternoon: 'denne ettermiddagen',
    wellness_tracker_evening: 'denne kvelden',
    // Navigation "Previous" overrides
    previous: 'Forrige',
    health_picture_symptom_checker_previous: 'Forrige',
    errors_to_previous_link: 'forrige side',
    errors_to_previous_mobile: 'Ta meg til <0>forrige skjerm</0>.',
    errors_to_previous_link_mobile: 'forrige skjerm',
    stories_carousel_previous_story: 'Forrige historie',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Skriv en kommentar...',
    chat_input_placeholder: 'Skriv en melding...',
    ai_chatbot_widget_input_placeholder: 'Skriv noe...',
    create_post_input_placeholder: 'Skriv en melding...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Helseforløp',
    health_picture_name: 'Helsebilde',
    ai_context_mode_health_journey: 'Helseforløp',
    health_journey_second_tab: 'Helsebilde',
    health_journey_add_new: 'Opprett helseforløp',
    health_journey_rename_journey: 'Endre helseforløp',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Opprett helseforløp',
    hj_already_create_for_myself_4: 'Se helseforløp',
    health_journey_privacy_dialog_health_journey_link: 'Helseforløp',
    health_picture_view_health_journey: 'Se helseforløp',
    health_picture_symptom_checker_save_to_health_journey: 'Lagre i helseforløpet',
    health_journey_my_picture: 'Mitt helsebilde',
    health_journey_picture_not_created_title: 'Begynn å følge helseforløpet ditt',
    health_journey_start_health_picture_title: 'Ta det første steget ved å opprette et helsebilde',
    verification_successful_subtitle: 'Du blir snart videresendt.\nStart helseforløpet ditt i dag.',
    rooms_redirect_to_health_picture: 'fyll ut helsebildet ditt',
    health_picture_no_symptoms_info: 'Registrer symptomene dine for et fullstendig helsebilde',
    ai_context_label_health_journey: 'Gjeldende kontekst er helseforløpet til:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Opprett nå ditt eget helseforløp for å få oversikt over velværet ditt og utforske andre spennende funksjoner.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Gi forløpet ditt et navn',
    new_health_journey_placeholder: 'Du kan ha flere forløp for deg selv eller for noen andre.',
    new_health_journey_validation_required_name: 'Vennligst skriv inn et navn for forløpet ditt',
    new_health_journey_validation_length_name: 'Navnet på helseforløpet ditt kan ikke ha mer enn 30 tegn',
    hj_already_create_for_myself_1: 'Det ser ut som du',
    hj_already_create_for_myself_2: 'allerede har opprettet et helseforløp',
    hj_already_create_for_myself_3: 'for deg selv.',
    hj_multiple_dialog_title: 'Hvem oppretter du et helseforløp for?',
    hj_multiple_for_myself: 'Jeg vil opprette dette forløpet for meg selv',
    hj_multiple_for_someone_else: 'Jeg vil opprette dette forløpet for noen andre',
    // Conversation hide override
    conversation_hide: 'Skjul',
    // Consultation form overrides
    consultation_name: 'Legekonsultasjon',
    health_picture_consultations: 'Mine legekonsultasjoner',
    health_picture_add_consultation: 'Legg til legekonsultasjon',
    health_picture_edit_consultation: 'Rediger legekonsultasjon',
    health_journey_consultation_type: 'Type konsultasjon:',
    health_journey_timeline_consultation_type: 'Type:',
    mycare_consultation_type_label: 'Type konsultasjon',
    mycare_consultation_provider_label: 'Navn på helsepersonell',
    mycare_consultation_speciality_label: 'Spesialitet',
    my_care_journal_notes: 'Notater',
    my_care_journal_description_placeholder: 'Skriv notater om hva du vil...',
    content_carousel_no_content_moderator_title: 'Lag innhold for rommet ditt',
    content_carousel_no_content_member_title: 'Hold kontakten med rommet ditt',
    content_carousel_no_content_member_subtitle: 'Her finner du de siste nyhetene, kunngjøringene og ressursene.',
    ai_context_label_room: 'Gjeldende kontekst er Rommet:',
    companions_block_user: 'Blokker',
    companions_unblock_user: 'Fjern blokkering',
    companion_request_block: 'Blokker',
    companion_request_unblock: 'Fjern blokkering',
    companion_report: 'Rapporter',
    companion_block_message: 'Er du sikker på at du vil blokkere {{memberDisplayName}} og fjerne som følgesvenn?',
    companion_unblock_message: 'Er du sikker på at du vil fjerne blokkeringen av {{memberDisplayName}}?',
    companion_report_message: 'Er du sikker på at du vil rapportere {{memberDisplayName}}?',
    companions_block_successfully: 'Du har blokkert følgesvennen!',
    companions_unblock_successfully: 'Du har fjernet blokkeringen av følgesvennen!',
    companions_cannot_block_administrator: 'Du kan ikke blokkere en administrator!',
    health_journey_select_date: 'Velg dato',
  },
  sv: {
    ai_context_mode_all_rooms: 'Automatisk',
    like: 'Gilla',
    likes: 'Gilla-markeringar',
    community_post_like: 'Gilla',
    community_post_likes: 'Gilla-markeringar',
    reaction_like_label: 'Gilla',
    story_allow_likes: 'Tillåt gilla-markeringar',
    comment: 'Kommentar',
    comments: 'Kommentarer',
    reaction_care_label: 'Omtanke',
    reaction_helpful_label: 'Användbart',
    story_draft: 'Utkast',
    stories_drafts: 'Utkast',
    checker: 'Kontroll',
    health_picture_complete: 'Slutför',
    health_picture_diagnosis_type_nonmedical: 'Förmodad',
    preview: 'Förhandsgranskning',
    journal_name: 'Dagbok',
    community: 'Rum',
    stories: 'Berättelser',
    profile_widget_journey: 'Förlopp',
    browser: 'Webbläsare',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Anslut',
    community_room_type_hub: 'Hub',
    subgroups: 'Undergrupper',
    max: 'max',
    companions_title: 'Följeslagare',
    health_picture_ongoing: 'Pågående',
    poll_period_unlimited: 'Obegränsad',
    joined_rooms_title: 'Rum i denna Hub',
    story_save_as_draft: 'Spara som utkast',
    story_saved_draft_snackbar: 'Utkast sparat',
    story_save_draft_success_dialog_content: 'Ditt utkast har sparats!',
    my_stories_empty_drafts_first_line: 'Du har inga berättelser i Utkast.',
    health_picture_add_journal: 'Lägg till dagboksinlägg',
    health_picture_edit_journal: 'Redigera dagboksinlägg',
    health_picture_journal: 'Mina dagböcker',
    health_journey_journal_title: 'Dagbok',
    health_journey_journal_date: 'Dagboksdatum',
    delete_journal_confirmation_title: 'Ta bort dagbok',
    delete_journal_confirmation_description: 'Är du säker på att du vill ta bort denna dagbok? Denna åtgärd kan inte ångras.',
    journal_deleted_successfully: 'Dagbok borttagen.',
    error_deleting_journal: 'Det uppstod ett problem vid borttagning av dagboken. Försök igen.',
    stories_first_tab: 'Alla berättelser',
    stories_third_tab: 'Mina berättelser',
    stories_suggested: 'Föreslagna berättelser',
    stories_my_stories: 'Mina berättelser',
    companions_in_room_title: 'Följeslagare i rummet',
    health_picture_snackbar_adding_ongoing_symptom: 'Du har redan lagt till detta symptom och det pågår fortfarande! Uppdatera den posten!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Du har redan lagt till denna diagnos och den pågår fortfarande! Uppdatera den posten!',
    health_picture_snackbar_adding_ongoing_treatment: 'Du har redan lagt till denna behandling och den pågår fortfarande! Uppdatera den posten!',
    health_picture_snackbar_adding_ongoing_medication: 'Du har redan lagt till detta läkemedel och det pågår fortfarande! Uppdatera den posten!',
    your_subgroups: 'Dina undergrupper',
    other_subgroups: 'Andra undergrupper',
    community_room_type_hub_room: 'Hub-rum',
    // Room consistency overrides
    rooms_title: 'Rum',
    my_rooms_title: 'Mina Rum och Hubbar',
    profile_widget_rooms: 'Rum',
    bottom_nav_community: 'Rum',
    search_rooms_title: 'Rum',
    community_first_tab: 'Mina rum',
    community_second_tab: 'Alla rum',
    community_room_type_private_room: 'Privat rum',
    community_room_type_public_room: 'Offentligt rum',
    related_rooms: 'Relaterade rum',
    my_rooms_explore_other_rooms: 'Utforska andra rum',
    rooms_autocomplete_caption: 'Utforska rum:',
    rooms_no_result_found: 'Inga rum hittades. Försök med en annan sökning.',
    rooms_view_all_button: 'Visa alla rum',
    rooms_view_rooms_button: 'Visa rum',
    rooms_leave_room: 'Lämna rummet',
    rooms_related_rooms_loading: 'Laddar relaterade rum...',
    rooms_related_rooms_end: 'Inga fler relaterade rum',
    rooms_by_topic_view_more_button: 'Visa fler rum',
    share_story_to_rooms_no_rooms_button: 'Utforska rum',
    room_join_button: 'Gå med i rummet',
    room_join_button_pending: 'Rumsförfrågan väntar',
    room_leave_confirmation_title: 'Lämna rummet',
    no_related_rooms: 'Inga relaterade rum hittades.',
    poll_rooms_selection: 'Dela omröstning i rummen:',
    my_rooms_no_member_rooms: 'Du är inte medlem i något rum. Varför inte <0>utforska?<0>',
    suggest_new_rooms_title: 'Hittar du inte det rum du behöver?',
    latest_posts_your_topics: 'Senaste inläggen i dina rum',
    people_in_the_room: 'Personer i rummet',
    people_in_the_room_no_people: 'Varför inte vara den första att gå med i rummet och skapa ett inlägg.',
    people_in_the_room_no_companions_and_people: 'Grattis! Du är den första personen i detta rum',
    no_moderators_in_the_room: 'Inga moderatorer i detta rum',
    select_rooms_for_post_modal_title: 'Välj de rum du vill dela ditt inlägg i:',
    rooms_create_post_dialog_placeholder_nonmember: 'Du måste gå med i rummet för att kunna publicera',
    rooms_blocked_member: 'Tyvärr har din åtkomst till detta rum återkallats',
    community_not_member_of_room: 'Tyvärr kan du inte komma åt detta rum',
    community_room_library_no_files: 'Inga filer bifogade till detta rum.',
    community_room_library_details: 'Detaljer',
    community_room_library_download: 'Ladda ner',
    community_room_library_summarise: 'Sammanfatta',
    community_room_library_translate: 'Översätt',
    community_room_library_delete: 'Radera',
    community_room_library_update: 'Uppdatera',
    profile_settings_ai_settings_title: 'AI-inställningar',
    profile_settings_ai_tokens_allowed: 'Tillåtna tokens per dag:',
    profile_settings_ai_tokens_used: 'Tokens använda idag:',
    featured_content_delete_file_warning_title: 'Varning',
    featured_content_delete_file_warning_message: 'När du tar bort en fil här kommer den också att tas bort från biblioteket',
    share_story_no_rooms_for_member: 'Det verkar som att du inte är medlem i något rum. Varför inte utforska och gå med?',
    community_room_library_file_type: 'Filtyp',
    community_room_library_language: 'Språk',
    community_room_library_valid_until: 'Giltig till',
    community_room_library_date_created: 'Skapelsedatum',
    snackbar_community_member_not_in_room: 'Du måste gå med i rummet {{roomName}} för att svara eller gilla.',
    snackbar_community_member_not_in_room_poll: 'Du måste gå med i rummet {{roomName}} för att skapa en omröstning.',
    snackbar_community_moderator_remove_member_success: 'Medlem borttagen från rummet!',
    snackbar_community_cannot_join_room: 'Du kan inte gå med i rummet.',
    snackbar_community_cannot_join_room_blocked: 'Du har blockerats från detta rum.',
    snackbar_community_cannot_leave_room: 'Du kan inte lämna rummet.',
    snackbar_community_join_room_success: 'Du har gått med i rummet!',
    snackbar_community_leave_room_success: 'Du har lämnat rummet!',
    polls_widget_no_polls: 'Det finns inga omröstningar i detta rum ännu.',
    poll_proposal_additional_info: 'En moderator i rummet kommer att kontakta dig för mer information om omröstningen.',
    room_leave_confirmation_description: 'Är du säker på att du vill lämna detta rum?',
    story_choose_rooms_subtitle: 'Du kan dela denna berättelse i följande rum:',
    story_published_modal_title: 'Berättelsen har publicerats! Vill du dela den i dina rum?',
    story_published_modal_title_mobile: 'Tack för att du publicerade din berättelse. Vill du dela den i dina rum?',
    moderator_pin_post_dialog_description: 'Är du säker på att du vill fästa detta inlägg i rummet?',
    moderator_remove_member_dialog_description: 'Är du säker på att du vill ta bort denna medlem från rummet?',
    errors_posts_not_allowed_in_room: 'Inlägg är inte tillåtna i detta rum.',
    errors_no_posts_found_room_member: 'Var den första att publicera i detta rum!',
    errors_no_posts_found_room_not_member: 'Gå med i rummet och publicera något!',
    errors_no_posts_found_room: 'Var den första att publicera i detta rum!',
    notifications_settings_someone_publishes_a_story_label: 'Ny berättelse i Mina Rum',
    share_story_to_rooms_second_title: 'Vill du dela den i dina rum?',
    private_room_code_widget_title: 'Har du en kod för ett privat Rum eller Hubb?',
    private_room_shim_name_title_dialog: 'Välkommen till detta privata rum!',
    private_room_shim_name_subtitle_dialog: 'Använd det namn som personerna i detta rum känner dig som.',
    private_room_shim_name_subtitle_dialog_2: 'Du förblir anonym utanför detta rum.',
    companions_description_no_companion_in_room: 'Du har inga följeslagare i detta rum ännu. Delta i samtal och hitta medlemmar att ansluta dig till.',
    companions_description_no_companion_in_community: 'Du har inga följeslagare ännu. Varför inte utforska rum och hitta medlemmar att ansluta dig till?',
    companions_no_companion: 'Du har inga följeslagare ännu. Besök rummet för nya medlemmar för att hitta carecircle-medlemmar och ansluta dig',
    personal_private_room_widget_button: 'Skapa ett privat rum',
    personal_private_room_dialog_title: 'Skapa ditt privata rum',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Huvud',
    body_part_first_level_chest: 'Bröst',
    body_part_first_level_abdomen: 'Mage',
    body_part_first_level_pelvis: 'Bäcken',
    body_part_first_level_arms: 'Armar',
    body_part_first_level_legs: 'Ben',
    body_part_first_level_back: 'Rygg',
    body_part_first_level_buttocks: 'Skinkor',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Det finns 'rum' som du kan besöka anonymt och delta i diskussioner om du vill. Du kan skriva kommentarer eller frågor, eller hitta följeslagare som stödjer dig på din resa.",
    conversations: 'Chattar',
    // Featured content overrides
    stories_carousel_title: 'Utvalda berättelser',
    content_carousel_title: 'Utvalt innehåll',
    content_carousel_add_new_post_button: 'Lägg till nytt Utvalt innehåll',
    content_carousel_no_content_moderator_subtitle: 'Informera alla i detta rum om de senaste nyheterna, meddelandena och resurserna. Utvalt innehåll kan för närvarande bara skapas från webbappen.',
    room_content_type_featured_posts_and_library_option: 'Utvalda inlägg och bibliotek',
    room_content_type_featured_posts_option: 'Utvalda inlägg',
    room_create_content_success: 'Utvalt innehållsobjekt skapades framgångsrikt',
    room_update_content_success: 'Utvalt innehållsobjekt uppdaterades framgångsrikt',
    room_delete_content_success: 'Utvalt innehållsobjekt raderades framgångsrikt',
    room_delete_content_error: 'Ett fel uppstod vid radering av utvalt innehållsobjekt',
    room_confirm_delete_featured_item_title: 'Radera utvalt innehåll',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Hur mår du',
    wellness_tracker_morning: 'denna morgon',
    wellness_tracker_afternoon: 'denna eftermiddag',
    wellness_tracker_evening: 'denna kväll',
    // Navigation "Previous" overrides
    previous: 'Föregående',
    health_picture_symptom_checker_previous: 'Föregående',
    errors_to_previous_link: 'föregående sida',
    errors_to_previous_mobile: 'Ta mig till <0>föregående skärm</0>.',
    errors_to_previous_link_mobile: 'föregående skärm',
    stories_carousel_previous_story: 'Föregående berättelse',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Skriv en kommentar...',
    chat_input_placeholder: 'Skriv ett meddelande...',
    ai_chatbot_widget_input_placeholder: 'Skriv något...',
    create_post_input_placeholder: 'Skriv ett meddelande...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Hälsoresa',
    health_picture_name: 'Hälsobild',
    ai_context_mode_health_journey: 'Hälsoresa',
    health_journey_second_tab: 'Hälsobild',
    health_journey_add_new: 'Skapa hälsoresa',
    health_journey_rename_journey: 'Ändra hälsoresa',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Skapa hälsoresa',
    hj_already_create_for_myself_4: 'Visa hälsoresa',
    health_journey_privacy_dialog_health_journey_link: 'Hälsoresa',
    health_picture_view_health_journey: 'Visa hälsoresa',
    health_picture_symptom_checker_save_to_health_journey: 'Spara i hälsoresan',
    health_journey_my_picture: 'Min hälsobild',
    health_journey_picture_not_created_title: 'Börja följa din hälsoresa',
    health_journey_start_health_picture_title: 'Ta det första steget genom att skapa en hälsobild',
    verification_successful_subtitle: 'Du omdirigeras snart.\nBörja din hälsoresa idag.',
    rooms_redirect_to_health_picture: 'fyll i din hälsobild',
    health_picture_no_symptoms_info: 'Registrera dina symtom för en komplett hälsobild',
    ai_context_label_health_journey: 'Nuvarande kontext är hälsoresan för:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Skapa nu din egen hälsoresa för att få en överblick av ditt välmående och utforska andra spännande funktioner.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Ge din resa ett namn',
    new_health_journey_placeholder: 'Du kan ha flera resor för dig själv eller för någon annan.',
    new_health_journey_validation_required_name: 'Ange ett namn för din resa',
    new_health_journey_validation_length_name: 'Namnet på din hälsoresa får inte ha mer än 30 tecken',
    hj_already_create_for_myself_1: 'Det verkar som att du',
    hj_already_create_for_myself_2: 'redan har skapat en hälsoresa',
    hj_already_create_for_myself_3: 'för dig själv.',
    hj_multiple_dialog_title: 'För vem skapar du en hälsoresa?',
    hj_multiple_for_myself: 'Jag vill skapa denna resa för mig själv',
    hj_multiple_for_someone_else: 'Jag vill skapa denna resa för någon annan',
    // Conversation hide override
    conversation_hide: 'Dölj',
    // Consultation form overrides
    consultation_name: 'Läkarbesök',
    health_picture_consultations: 'Mina läkarbesök',
    health_picture_add_consultation: 'Lägg till läkarbesök',
    health_picture_edit_consultation: 'Redigera läkarbesök',
    health_journey_consultation_type: 'Typ av besök:',
    health_journey_timeline_consultation_type: 'Typ:',
    mycare_consultation_type_label: 'Typ av besök',
    mycare_consultation_provider_label: 'Namn på vårdgivare',
    mycare_consultation_speciality_label: 'Specialitet',
    my_care_journal_notes: 'Anteckningar',
    my_care_journal_description_placeholder: 'Skriv anteckningar om vad du vill...',
    content_carousel_no_content_moderator_title: 'Skapa innehåll för ditt rum',
    content_carousel_no_content_member_title: 'Håll kontakten med ditt rum',
    content_carousel_no_content_member_subtitle: 'Här hittar du de senaste nyheterna, meddelandena och resurserna.',
    ai_context_label_room: 'Nuvarande kontext är Rummet:',
    companions_block_user: 'Blockera',
    companions_unblock_user: 'Avblockera',
    companion_request_block: 'Blockera',
    companion_request_unblock: 'Avblockera',
    companion_report: 'Rapportera',
    companion_block_message: 'Är du säker på att du vill blockera {{memberDisplayName}} och ta bort som följeslagare?',
    companion_unblock_message: 'Är du säker på att du vill avblockera {{memberDisplayName}}?',
    companion_report_message: 'Är du säker på att du vill rapportera {{memberDisplayName}}?',
    companions_block_successfully: 'Du har blockerat följeslagaren!',
    companions_unblock_successfully: 'Du har avblockerat följeslagaren!',
    companions_cannot_block_administrator: 'Du kan inte blockera en administratör!',
    health_journey_select_date: 'Välj datum',
  },
  da: {
    ai_context_mode_all_rooms: 'Automatisk',
    like: 'Synes godt om',
    likes: 'Synes godt om',
    community_post_like: 'Synes godt om',
    community_post_likes: 'Synes godt om',
    reaction_like_label: 'Synes godt om',
    story_allow_likes: 'Tillad synes godt om',
    comment: 'Kommentar',
    comments: 'Kommentarer',
    reaction_care_label: 'Omsorg',
    reaction_helpful_label: 'Nyttigt',
    story_draft: 'Kladde',
    stories_drafts: 'Kladder',
    checker: 'Tjekker',
    health_picture_complete: 'Fuldfør',
    health_picture_diagnosis_type_nonmedical: 'Formodet',
    preview: 'Forhåndsvisning',
    journal_name: 'Dagbog',
    community: 'Rum',
    stories: 'Historier',
    profile_widget_journey: 'Forløb',
    browser: 'Browser',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Forbind',
    community_room_type_hub: 'Hub',
    subgroups: 'Undergrupper',
    max: 'maks',
    companions_title: 'Ledsagere',
    health_picture_ongoing: 'Igangværende',
    poll_period_unlimited: 'Ubegrænset',
    joined_rooms_title: 'Rum i denne Hub',
    story_save_as_draft: 'Gem som kladde',
    story_saved_draft_snackbar: 'Kladde gemt',
    story_save_draft_success_dialog_content: 'Din kladde er blevet gemt!',
    my_stories_empty_drafts_first_line: 'Du har ingen historier i Kladde.',
    health_picture_add_journal: 'Tilføj dagbogsnotat',
    health_picture_edit_journal: 'Rediger dagbogsnotat',
    health_picture_journal: 'Mine dagbøger',
    health_journey_journal_title: 'Dagbog',
    health_journey_journal_date: 'Dagbogsdato',
    delete_journal_confirmation_title: 'Slet dagbog',
    delete_journal_confirmation_description: 'Er du sikker på, at du vil slette denne dagbog? Denne handling kan ikke fortrydes.',
    journal_deleted_successfully: 'Dagbog slettet.',
    error_deleting_journal: 'Der opstod et problem med at slette dagbogen. Prøv igen.',
    stories_first_tab: 'Alle historier',
    stories_third_tab: 'Mine historier',
    stories_suggested: 'Foreslåede historier',
    stories_my_stories: 'Mine historier',
    companions_in_room_title: 'Ledsagere i rummet',
    health_picture_snackbar_adding_ongoing_symptom: 'Du har allerede tilføjet dette symptom, og det er stadig igangværende! Opdater den post!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Du har allerede tilføjet denne diagnose, og den er stadig igangværende! Opdater den post!',
    health_picture_snackbar_adding_ongoing_treatment: 'Du har allerede tilføjet denne behandling, og den er stadig igangværende! Opdater den post!',
    health_picture_snackbar_adding_ongoing_medication: 'Du har allerede tilføjet dette lægemiddel, og det er stadig igangværende! Opdater den post!',
    your_subgroups: 'Dine undergrupper',
    other_subgroups: 'Andre undergrupper',
    community_room_type_hub_room: 'Hub-rum',
    // Room consistency overrides
    rooms_title: 'Rum',
    my_rooms_title: 'Mine Rum og Hubs',
    profile_widget_rooms: 'Rum',
    bottom_nav_community: 'Rum',
    search_rooms_title: 'Rum',
    community_first_tab: 'Mine rum',
    community_second_tab: 'Alle rum',
    community_room_type_private_room: 'Privat rum',
    community_room_type_public_room: 'Offentligt rum',
    related_rooms: 'Relaterede rum',
    my_rooms_explore_other_rooms: 'Udforsk andre rum',
    rooms_autocomplete_caption: 'Udforsk rum:',
    rooms_no_result_found: 'Ingen rum fundet. Prøv en anden søgning.',
    rooms_view_all_button: 'Vis alle rum',
    rooms_view_rooms_button: 'Vis rum',
    rooms_leave_room: 'Forlad rummet',
    rooms_related_rooms_loading: 'Indlæser relaterede rum...',
    rooms_related_rooms_end: 'Ingen flere relaterede rum',
    rooms_by_topic_view_more_button: 'Vis flere rum',
    share_story_to_rooms_no_rooms_button: 'Udforsk rum',
    room_join_button: 'Deltag i rummet',
    room_join_button_pending: 'Rumanmodning afventer',
    room_leave_confirmation_title: 'Forlad rummet',
    no_related_rooms: 'Ingen relaterede rum fundet.',
    poll_rooms_selection: 'Del afstemning i rummene:',
    my_rooms_no_member_rooms: 'Du er ikke medlem af noget rum. Hvorfor ikke <0>udforske?<0>',
    suggest_new_rooms_title: 'Kan du ikke finde det rum, du har brug for?',
    latest_posts_your_topics: 'Seneste opslag i dine rum',
    people_in_the_room: 'Personer i rummet',
    people_in_the_room_no_people: 'Hvorfor ikke være den første til at deltage i rummet og oprette et opslag.',
    people_in_the_room_no_companions_and_people: 'Tillykke! Du er den første person i dette rum',
    no_moderators_in_the_room: 'Ingen moderatorer i dette rum',
    select_rooms_for_post_modal_title: 'Vælg de rum, du vil dele dit opslag i:',
    rooms_create_post_dialog_placeholder_nonmember: 'Du skal deltage i rummet for at kunne poste',
    rooms_blocked_member: 'Desværre er din adgang til dette rum blevet tilbagekaldt',
    community_not_member_of_room: 'Beklager, du kan ikke få adgang til dette rum',
    community_room_library_no_files: 'Ingen filer vedhæftet dette rum.',
    community_room_library_details: 'Detaljer',
    community_room_library_download: 'Download',
    community_room_library_summarise: 'Opsummér',
    community_room_library_translate: 'Oversæt',
    community_room_library_delete: 'Slet',
    community_room_library_update: 'Opdatér',
    profile_settings_ai_settings_title: 'AI-indstillinger',
    profile_settings_ai_tokens_allowed: 'Tilladte tokens pr. dag:',
    profile_settings_ai_tokens_used: 'Tokens brugt i dag:',
    featured_content_delete_file_warning_title: 'Advarsel',
    featured_content_delete_file_warning_message: 'Når du sletter en fil her, vil den også blive slettet fra biblioteket',
    share_story_no_rooms_for_member: 'Det ser ud til, at du ikke er medlem af noget rum. Hvorfor ikke udforske og deltage?',
    community_room_library_file_type: 'Filtype',
    community_room_library_language: 'Sprog',
    community_room_library_valid_until: 'Gyldig til',
    community_room_library_date_created: 'Oprettelsesdato',
    snackbar_community_member_not_in_room: 'Du skal deltage i rummet {{roomName}} for at svare eller synes godt om.',
    snackbar_community_member_not_in_room_poll: 'Du skal deltage i rummet {{roomName}} for at oprette en afstemning.',
    snackbar_community_moderator_remove_member_success: 'Medlem fjernet fra rummet!',
    snackbar_community_cannot_join_room: 'Du kan ikke deltage i rummet.',
    snackbar_community_cannot_join_room_blocked: 'Du er blevet blokeret fra dette rum.',
    snackbar_community_cannot_leave_room: 'Du kan ikke forlade rummet.',
    snackbar_community_join_room_success: 'Du er nu med i rummet!',
    snackbar_community_leave_room_success: 'Du har forladt rummet!',
    polls_widget_no_polls: 'Der er ingen afstemninger i dette rum endnu.',
    poll_proposal_additional_info: 'En moderator i rummet vil kontakte dig for mere information om afstemningen.',
    room_leave_confirmation_description: 'Er du sikker på, at du vil forlade dette rum?',
    story_choose_rooms_subtitle: 'Du kan dele denne historie i følgende rum:',
    story_published_modal_title: 'Historien er publiceret! Vil du dele den i dine rum?',
    story_published_modal_title_mobile: 'Tak fordi du publicerede din historie. Vil du dele den i dine rum?',
    moderator_pin_post_dialog_description: 'Er du sikker på, at du vil fastgøre dette opslag i rummet?',
    moderator_remove_member_dialog_description: 'Er du sikker på, at du vil fjerne dette medlem fra rummet?',
    errors_posts_not_allowed_in_room: 'Opslag er ikke tilladt i dette rum.',
    errors_no_posts_found_room_member: 'Vær den første til at poste i dette rum!',
    errors_no_posts_found_room_not_member: 'Deltag i rummet og post noget!',
    errors_no_posts_found_room: 'Vær den første til at poste i dette rum!',
    notifications_settings_someone_publishes_a_story_label: 'Ny historie i Mine Rum',
    share_story_to_rooms_second_title: 'Vil du dele den i dine rum?',
    private_room_code_widget_title: 'Har du en kode til et privat Rum eller Hub?',
    private_room_shim_name_title_dialog: 'Velkommen til dette private rum!',
    private_room_shim_name_subtitle_dialog: 'Brug det navn, som folk i dette rum kender dig som.',
    private_room_shim_name_subtitle_dialog_2: 'Du forbliver anonym uden for dette rum.',
    companions_description_no_companion_in_room: 'Du har endnu ingen ledsagere i dette rum. Deltag i samtaler og find medlemmer at forbinde dig med.',
    companions_description_no_companion_in_community: 'Du har endnu ingen ledsagere. Hvorfor ikke udforske rum og finde medlemmer at forbinde dig med?',
    companions_no_companion: 'Du har endnu ingen ledsagere. Besøg rummet for nye medlemmer for at finde carecircle-medlemmer og forbinde dig',
    personal_private_room_widget_button: 'Opret et privat rum',
    personal_private_room_dialog_title: 'Opret dit private rum',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Hoved',
    body_part_first_level_chest: 'Bryst',
    body_part_first_level_abdomen: 'Mave',
    body_part_first_level_pelvis: 'Bækken',
    body_part_first_level_arms: 'Arme',
    body_part_first_level_legs: 'Ben',
    body_part_first_level_back: 'Ryg',
    body_part_first_level_buttocks: 'Baller',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Der er 'rum', du kan besøge anonymt og deltage i diskussioner, hvis du ønsker det. Du kan skrive kommentarer eller spørgsmål, eller finde ledsagere, der vil støtte dig på din rejse.",
    conversations: 'Chats',
    // Featured content overrides
    stories_carousel_title: 'Fremhævede historier',
    content_carousel_title: 'Fremhævet indhold',
    content_carousel_add_new_post_button: 'Tilføj nyt Fremhævet indhold',
    content_carousel_no_content_moderator_subtitle: 'Informer alle i dette rum om de seneste nyheder, meddelelser og ressourcer. Fremhævet indhold kan i øjeblikket kun oprettes fra webappen.',
    room_content_type_featured_posts_and_library_option: 'Fremhævede opslag og bibliotek',
    room_content_type_featured_posts_option: 'Fremhævede opslag',
    room_create_content_success: 'Fremhævet indholdselement blev oprettet',
    room_update_content_success: 'Fremhævet indholdselement blev opdateret',
    room_delete_content_success: 'Fremhævet indholdselement blev slettet',
    room_delete_content_error: 'Der opstod en fejl under sletning af fremhævet indholdselement',
    room_confirm_delete_featured_item_title: 'Slet fremhævet indhold',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Hvordan har du det',
    wellness_tracker_morning: 'denne morgen',
    wellness_tracker_afternoon: 'denne eftermiddag',
    wellness_tracker_evening: 'denne aften',
    // Navigation "Previous" overrides
    previous: 'Forrige',
    health_picture_symptom_checker_previous: 'Forrige',
    errors_to_previous_link: 'forrige side',
    errors_to_previous_mobile: 'Tag mig til <0>forrige skærm</0>.',
    errors_to_previous_link_mobile: 'forrige skærm',
    stories_carousel_previous_story: 'Forrige historie',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Skriv en kommentar...',
    chat_input_placeholder: 'Skriv en besked...',
    ai_chatbot_widget_input_placeholder: 'Skriv noget...',
    create_post_input_placeholder: 'Skriv en besked...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Sundhedsforløb',
    health_picture_name: 'Sundhedsbillede',
    ai_context_mode_health_journey: 'Sundhedsforløb',
    health_journey_second_tab: 'Sundhedsbillede',
    health_journey_add_new: 'Opret sundhedsforløb',
    health_journey_rename_journey: 'Rediger sundhedsforløb',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Opret sundhedsforløb',
    hj_already_create_for_myself_4: 'Se sundhedsforløb',
    health_journey_privacy_dialog_health_journey_link: 'Sundhedsforløb',
    health_picture_view_health_journey: 'Se sundhedsforløb',
    health_picture_symptom_checker_save_to_health_journey: 'Gem i sundhedsforløbet',
    health_journey_my_picture: 'Mit sundhedsbillede',
    health_journey_picture_not_created_title: 'Begynd at følge dit sundhedsforløb',
    health_journey_start_health_picture_title: 'Tag det første skridt ved at oprette et sundhedsbillede',
    verification_successful_subtitle: 'Du vil snart blive omdirigeret.\nBegynd dit sundhedsforløb i dag.',
    rooms_redirect_to_health_picture: 'udfyld dit sundhedsbillede',
    health_picture_no_symptoms_info: 'Registrer dine symptomer for et komplet sundhedsbillede',
    ai_context_label_health_journey: 'Den aktuelle kontekst er sundhedsforløbet for:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Opret nu dit eget sundhedsforløb for at få et overblik over dit velvære og udforske andre spændende funktioner.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Giv dit forløb et navn',
    new_health_journey_placeholder: 'Du kan have flere forløb for dig selv eller for en anden.',
    new_health_journey_validation_required_name: 'Indtast venligst et navn for dit forløb',
    new_health_journey_validation_length_name: 'Navnet på dit sundhedsforløb må ikke have mere end 30 tegn',
    hj_already_create_for_myself_1: 'Det ser ud til, at du',
    hj_already_create_for_myself_2: 'allerede har oprettet et sundhedsforløb',
    hj_already_create_for_myself_3: 'for dig selv.',
    hj_multiple_dialog_title: 'Hvem opretter du et sundhedsforløb for?',
    hj_multiple_for_myself: 'Jeg vil oprette dette forløb for mig selv',
    hj_multiple_for_someone_else: 'Jeg vil oprette dette forløb for en anden',
    // Conversation hide override
    conversation_hide: 'Skjul',
    // Consultation form overrides
    consultation_name: 'Lægekonsultation',
    health_picture_consultations: 'Mine lægekonsultationer',
    health_picture_add_consultation: 'Tilføj lægekonsultation',
    health_picture_edit_consultation: 'Rediger lægekonsultation',
    health_journey_consultation_type: 'Type konsultation:',
    health_journey_timeline_consultation_type: 'Type:',
    mycare_consultation_type_label: 'Type konsultation',
    mycare_consultation_provider_label: 'Navn på sundhedsudbyder',
    mycare_consultation_speciality_label: 'Speciale',
    my_care_journal_notes: 'Noter',
    my_care_journal_description_placeholder: 'Skriv noter om hvad du vil...',
    content_carousel_no_content_moderator_title: 'Opret indhold til dit rum',
    content_carousel_no_content_member_title: 'Hold kontakten med dit rum',
    content_carousel_no_content_member_subtitle: 'Her finder du de seneste nyheder, meddelelser og ressourcer.',
    ai_context_label_room: 'Den aktuelle kontekst er Rummet:',
    companions_block_user: 'Bloker',
    companions_unblock_user: 'Fjern blokering',
    companion_request_block: 'Bloker',
    companion_request_unblock: 'Fjern blokering',
    companion_report: 'Anmeld',
    companion_block_message: 'Er du sikker på, at du vil blokere {{memberDisplayName}} og fjerne som ledsager?',
    companion_unblock_message: 'Er du sikker på, at du vil fjerne blokeringen af {{memberDisplayName}}?',
    companion_report_message: 'Er du sikker på, at du vil anmelde {{memberDisplayName}}?',
    companions_block_successfully: 'Du har blokeret ledsageren!',
    companions_unblock_successfully: 'Du har fjernet blokeringen af ledsageren!',
    companions_cannot_block_administrator: 'Du kan ikke blokere en administrator!',
    health_journey_select_date: 'Vælg dato',
  },
  pl: {
    ai_context_mode_all_rooms: 'Automatycznie',
    like: 'Lubię to',
    likes: 'Lubię to',
    community_post_like: 'Lubię to',
    community_post_likes: 'Lubię to',
    reaction_like_label: 'Lubię to',
    story_allow_likes: 'Zezwól na lubię to',
    comment: 'Komentarz',
    comments: 'Komentarze',
    reaction_care_label: 'Wspieram',
    reaction_helpful_label: 'Pomocne',
    story_draft: 'Szkic',
    stories_drafts: 'Szkice',
    checker: 'Sprawdzacz',
    health_picture_complete: 'Zakończ',
    health_picture_diagnosis_type_nonmedical: 'Prawdopodobne',
    preview: 'Podgląd',
    journal_name: 'Dziennik',
    community: 'Pokoje',
    stories: 'Historie',
    profile_widget_journey: 'Ścieżki',
    browser: 'Przeglądarka',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Połącz się',
    community_room_type_hub: 'Hub',
    subgroups: 'Podgrupy',
    max: 'maks',
    companions_title: 'Towarzysze',
    health_picture_ongoing: 'W trakcie',
    poll_period_unlimited: 'Bez limitu',
    joined_rooms_title: 'Pokoje w tym Hubie',
    story_save_as_draft: 'Zapisz jako szkic',
    story_saved_draft_snackbar: 'Szkic zapisany pomyślnie',
    story_save_draft_success_dialog_content: 'Twój szkic został zapisany pomyślnie!',
    my_stories_empty_drafts_first_line: 'Nie masz żadnych historii w szkicach.',
    health_picture_add_journal: 'Dodaj wpis do dziennika',
    health_picture_edit_journal: 'Edytuj wpis w dzienniku',
    health_picture_journal: 'Moje dzienniki',
    health_journey_journal_title: 'Dziennik',
    health_journey_journal_date: 'Data dziennika',
    delete_journal_confirmation_title: 'Usuń dziennik',
    delete_journal_confirmation_description: 'Czy na pewno chcesz usunąć ten dziennik? Tej czynności nie można cofnąć.',
    journal_deleted_successfully: 'Dziennik usunięty pomyślnie.',
    error_deleting_journal: 'Wystąpił problem podczas usuwania dziennika. Spróbuj ponownie.',
    stories_first_tab: 'Wszystkie historie',
    stories_third_tab: 'Moje historie',
    stories_suggested: 'Sugerowane historie',
    stories_my_stories: 'Moje historie',
    companions_in_room_title: 'Towarzysze w pokoju',
    health_picture_snackbar_adding_ongoing_symptom: 'Już dodałeś ten objaw i jest nadal w trakcie! Zaktualizuj ten wpis!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Już dodałeś tę diagnozę i jest nadal w trakcie! Zaktualizuj ten wpis!',
    health_picture_snackbar_adding_ongoing_treatment: 'Już dodałeś to leczenie i jest nadal w trakcie! Zaktualizuj ten wpis!',
    health_picture_snackbar_adding_ongoing_medication: 'Już dodałeś ten lek i jest nadal w trakcie! Zaktualizuj ten wpis!',
    your_subgroups: 'Twoje podgrupy',
    other_subgroups: 'Inne podgrupy',
    community_room_type_hub_room: 'Pokój Hub',
    // Room consistency overrides
    rooms_title: 'Pokoje',
    my_rooms_title: 'Moje Pokoje i Huby',
    profile_widget_rooms: 'Pokoje',
    bottom_nav_community: 'Pokoje',
    search_rooms_title: 'Pokoje',
    community_first_tab: 'Moje pokoje',
    community_second_tab: 'Wszystkie pokoje',
    community_room_type_private_room: 'Prywatny pokój',
    community_room_type_public_room: 'Publiczny pokój',
    related_rooms: 'Powiązane pokoje',
    my_rooms_explore_other_rooms: 'Odkryj inne pokoje',
    rooms_autocomplete_caption: 'Odkryj pokoje:',
    rooms_no_result_found: 'Nie znaleziono pokojów. Spróbuj innego wyszukiwania.',
    rooms_view_all_button: 'Zobacz wszystkie pokoje',
    rooms_view_rooms_button: 'Zobacz pokoje',
    rooms_leave_room: 'Opuść pokój',
    rooms_related_rooms_loading: 'Ładowanie powiązanych pokojów...',
    rooms_related_rooms_end: 'Brak więcej powiązanych pokojów',
    rooms_by_topic_view_more_button: 'Zobacz więcej pokojów',
    share_story_to_rooms_no_rooms_button: 'Odkryj pokoje',
    room_join_button: 'Dołącz do pokoju',
    room_join_button_pending: 'Prośba o pokój oczekuje',
    room_leave_confirmation_title: 'Opuść pokój',
    no_related_rooms: 'Nie znaleziono powiązanych pokojów.',
    poll_rooms_selection: 'Udostępnij ankietę w pokojach:',
    my_rooms_no_member_rooms: 'Nie jesteś członkiem żadnego pokoju. Dlaczego nie <0>odkryjesz?<0>',
    suggest_new_rooms_title: 'Nie możesz znaleźć potrzebnego pokoju?',
    latest_posts_your_topics: 'Najnowsze posty w Twoich pokojach',
    people_in_the_room: 'Osoby w pokoju',
    people_in_the_room_no_people: 'Dlaczego nie być pierwszym, który dołączy do pokoju i utworzy post.',
    people_in_the_room_no_companions_and_people: 'Gratulacje! Jesteś pierwszą osobą w tym pokoju',
    no_moderators_in_the_room: 'Brak moderatorów w tym pokoju',
    select_rooms_for_post_modal_title: 'Wybierz pokoje, w których chcesz udostępnić swój post:',
    rooms_create_post_dialog_placeholder_nonmember: 'Musisz dołączyć do pokoju, aby móc publikować',
    rooms_blocked_member: 'Niestety Twój dostęp do tego pokoju został cofnięty',
    community_not_member_of_room: 'Przepraszamy, nie masz dostępu do tego pokoju',
    community_room_library_no_files: 'Brak plików dołączonych do tego pokoju.',
    community_room_library_details: 'Szczegóły',
    community_room_library_download: 'Pobierz',
    community_room_library_summarise: 'Podsumuj',
    community_room_library_translate: 'Przetłumacz',
    community_room_library_delete: 'Usuń',
    community_room_library_update: 'Aktualizuj',
    profile_settings_ai_settings_title: 'Ustawienia AI',
    profile_settings_ai_tokens_allowed: 'Dozwolone tokeny dziennie:',
    profile_settings_ai_tokens_used: 'Tokeny wykorzystane dzisiaj:',
    featured_content_delete_file_warning_title: 'Ostrzeżenie',
    featured_content_delete_file_warning_message: 'Gdy usuniesz plik tutaj, zostanie on również usunięty z biblioteki',
    share_story_no_rooms_for_member: 'Wygląda na to, że nie jesteś członkiem żadnego pokoju. Dlaczego nie odkryjesz i dołączysz?',
    community_room_library_file_type: 'Typ pliku',
    community_room_library_language: 'Język',
    community_room_library_valid_until: 'Ważny do',
    community_room_library_date_created: 'Data utworzenia',
    snackbar_community_member_not_in_room: 'Musisz dołączyć do pokoju {{roomName}}, aby odpowiedzieć lub polubić.',
    snackbar_community_member_not_in_room_poll: 'Musisz dołączyć do pokoju {{roomName}}, aby utworzyć ankietę.',
    snackbar_community_moderator_remove_member_success: 'Członek usunięty z pokoju!',
    snackbar_community_cannot_join_room: 'Nie możesz dołączyć do pokoju.',
    snackbar_community_cannot_join_room_blocked: 'Zostałeś zablokowany w tym pokoju.',
    snackbar_community_cannot_leave_room: 'Nie możesz opuścić pokoju.',
    snackbar_community_join_room_success: 'Dołączyłeś do pokoju!',
    snackbar_community_leave_room_success: 'Opuściłeś pokój!',
    polls_widget_no_polls: 'W tym pokoju nie ma jeszcze ankiet.',
    poll_proposal_additional_info: 'Moderator pokoju skontaktuje się w sprawie dodatkowych informacji o ankiecie.',
    room_leave_confirmation_description: 'Czy na pewno chcesz opuścić ten pokój?',
    story_choose_rooms_subtitle: 'Możesz udostępnić tę historię w następujących pokojach:',
    story_published_modal_title: 'Historia opublikowana pomyślnie! Czy chcesz ją udostępnić w swoich pokojach?',
    story_published_modal_title_mobile: 'Dziękujemy za opublikowanie historii. Czy chcesz ją udostępnić w swoich pokojach?',
    moderator_pin_post_dialog_description: 'Czy na pewno chcesz przypiąć ten post w pokoju?',
    moderator_remove_member_dialog_description: 'Czy na pewno chcesz usunąć tego członka z pokoju?',
    errors_posts_not_allowed_in_room: 'Posty nie są dozwolone w tym pokoju.',
    errors_no_posts_found_room_member: 'Bądź pierwszym, który opublikuje w tym pokoju!',
    errors_no_posts_found_room_not_member: 'Dołącz do pokoju i opublikuj coś!',
    errors_no_posts_found_room: 'Bądź pierwszym, który opublikuje w tym pokoju!',
    notifications_settings_someone_publishes_a_story_label: 'Nowa historia w Moich Pokojach',
    share_story_to_rooms_second_title: 'Czy chcesz ją udostępnić w swoich pokojach?',
    private_room_code_widget_title: 'Masz kod do prywatnego Pokoju lub Huba?',
    private_room_shim_name_title_dialog: 'Witamy w tym prywatnym pokoju!',
    private_room_shim_name_subtitle_dialog: 'Użyj imienia, pod którym znają Cię osoby w tym pokoju.',
    private_room_shim_name_subtitle_dialog_2: 'Pozostaniesz anonimowy poza tym pokojem.',
    companions_description_no_companion_in_room: 'Nie masz jeszcze towarzyszy w tym pokoju. Dołącz do rozmów i znajdź członków, z którymi możesz się połączyć.',
    companions_description_no_companion_in_community: 'Nie masz jeszcze towarzyszy. Dlaczego nie odkryjesz pokojów i znajdziesz członków, z którymi możesz się połączyć?',
    companions_no_companion: 'Nie masz jeszcze towarzyszy. Odwiedź pokój nowych członków, aby znaleźć członków carecircle i nawiązać kontakt',
    personal_private_room_widget_button: 'Utwórz prywatny pokój',
    personal_private_room_dialog_title: 'Utwórz swój prywatny pokój',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Głowa',
    body_part_first_level_chest: 'Klatka piersiowa',
    body_part_first_level_abdomen: 'Brzuch',
    body_part_first_level_pelvis: 'Miednica',
    body_part_first_level_arms: 'Ręce',
    body_part_first_level_legs: 'Nogi',
    body_part_first_level_back: 'Plecy',
    body_part_first_level_buttocks: 'Pośladki',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Są 'pokoje', które możesz odwiedzać anonimowo i dołączać do dyskusji, jeśli chcesz. Możesz publikować komentarze lub pytania, lub znaleźć towarzyszy, którzy wesprą cię w twojej podróży.",
    conversations: 'Czaty',
    // Featured content overrides
    stories_carousel_title: 'Wyróżnione historie',
    content_carousel_title: 'Wyróżniona treść',
    content_carousel_add_new_post_button: 'Dodaj nową Wyróżnioną treść',
    content_carousel_no_content_moderator_subtitle: 'Poinformuj wszystkich w tym pokoju o najnowszych wiadomościach, ogłoszeniach i zasobach. Wyróżnioną treść można obecnie tworzyć tylko z aplikacji webowej.',
    room_content_type_featured_posts_and_library_option: 'Wyróżnione posty i biblioteka',
    room_content_type_featured_posts_option: 'Wyróżnione posty',
    room_create_content_success: 'Element wyróżnionej treści został pomyślnie utworzony',
    room_update_content_success: 'Element wyróżnionej treści został pomyślnie zaktualizowany',
    room_delete_content_success: 'Element wyróżnionej treści został pomyślnie usunięty',
    room_delete_content_error: 'Wystąpił błąd podczas usuwania elementu wyróżnionej treści',
    room_confirm_delete_featured_item_title: 'Usuń wyróżnioną treść',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Jak się czujesz',
    wellness_tracker_morning: 'dziś rano',
    wellness_tracker_afternoon: 'dziś po południu',
    wellness_tracker_evening: 'dziś wieczorem',
    // Navigation "Previous" overrides
    previous: 'Poprzedni',
    health_picture_symptom_checker_previous: 'Poprzedni',
    errors_to_previous_link: 'poprzednia strona',
    errors_to_previous_mobile: 'Zabierz mnie do <0>poprzedniego ekranu</0>.',
    errors_to_previous_link_mobile: 'poprzedni ekran',
    stories_carousel_previous_story: 'Poprzednia historia',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Napisz komentarz...',
    chat_input_placeholder: 'Napisz wiadomość...',
    ai_chatbot_widget_input_placeholder: 'Napisz coś...',
    create_post_input_placeholder: 'Napisz wiadomość...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Ścieżka Zdrowia',
    health_picture_name: 'Obraz Zdrowia',
    ai_context_mode_health_journey: 'Ścieżka Zdrowia',
    health_journey_second_tab: 'Obraz Zdrowia',
    health_journey_add_new: 'Utwórz ścieżkę zdrowia',
    health_journey_rename_journey: 'Zmień Ścieżkę Zdrowia',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Utwórz Ścieżkę Zdrowia',
    hj_already_create_for_myself_4: 'Zobacz Ścieżkę Zdrowia',
    health_journey_privacy_dialog_health_journey_link: 'Ścieżka Zdrowia',
    health_picture_view_health_journey: 'Zobacz ścieżkę zdrowia',
    health_picture_symptom_checker_save_to_health_journey: 'Zapisz w Ścieżce Zdrowia',
    health_journey_my_picture: 'Mój obraz zdrowia',
    health_journey_picture_not_created_title: 'Zacznij śledzić swoją ścieżkę zdrowia',
    health_journey_start_health_picture_title: 'Zrób pierwszy krok tworząc obraz zdrowia',
    verification_successful_subtitle: 'Wkrótce zostaniesz przekierowany.\nRozpocznij swoją ścieżkę zdrowia już dziś.',
    rooms_redirect_to_health_picture: 'uzupełnij swój obraz zdrowia',
    health_picture_no_symptoms_info: 'Zarejestruj swoje objawy, aby mieć pełny obraz zdrowia',
    ai_context_label_health_journey: 'Bieżący kontekst to Ścieżka Zdrowia:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Utwórz teraz własną Ścieżkę Zdrowia, aby mieć przegląd swojego samopoczucia i odkryć inne ciekawe funkcje.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Nadaj swojej ścieżce nazwę',
    new_health_journey_placeholder: 'Możesz mieć wiele ścieżek dla siebie lub dla kogoś innego.',
    new_health_journey_validation_required_name: 'Wprowadź nazwę dla swojej ścieżki',
    new_health_journey_validation_length_name: 'Nazwa ścieżki zdrowia nie może mieć więcej niż 30 znaków',
    hj_already_create_for_myself_1: 'Wygląda na to, że',
    hj_already_create_for_myself_2: 'już utworzyłeś Ścieżkę Zdrowia',
    hj_already_create_for_myself_3: 'dla siebie.',
    hj_multiple_dialog_title: 'Dla kogo tworzysz ścieżkę zdrowia?',
    hj_multiple_for_myself: 'Chcę utworzyć tę ścieżkę dla siebie',
    hj_multiple_for_someone_else: 'Chcę utworzyć tę ścieżkę dla kogoś innego',
    // Conversation hide override
    conversation_hide: 'Ukryj',
    // Consultation form overrides
    consultation_name: 'Konsultacja lekarska',
    health_picture_consultations: 'Moje konsultacje lekarskie',
    health_picture_add_consultation: 'Dodaj konsultację lekarską',
    health_picture_edit_consultation: 'Edytuj konsultację lekarską',
    health_journey_consultation_type: 'Typ konsultacji:',
    health_journey_timeline_consultation_type: 'Typ:',
    mycare_consultation_type_label: 'Typ konsultacji',
    mycare_consultation_provider_label: 'Nazwa świadczeniodawcy',
    mycare_consultation_speciality_label: 'Specjalność',
    my_care_journal_notes: 'Notatki',
    my_care_journal_description_placeholder: 'Zrób notatki o czymkolwiek, co chcesz...',
    content_carousel_no_content_moderator_title: 'Utwórz treść dla swojego pokoju',
    content_carousel_no_content_member_title: 'Bądź w kontakcie ze swoim pokojem',
    content_carousel_no_content_member_subtitle: 'Tutaj znajdziesz najnowsze wiadomości, ogłoszenia i zasoby.',
    ai_context_label_room: 'Bieżący kontekst to Pokój:',
    companions_block_user: 'Zablokuj',
    companions_unblock_user: 'Odblokuj',
    companion_request_block: 'Zablokuj',
    companion_request_unblock: 'Odblokuj',
    companion_report: 'Zgłoś',
    companion_block_message: 'Czy na pewno chcesz zablokować użytkownika {{memberDisplayName}} i usunąć go jako towarzysza?',
    companion_unblock_message: 'Czy na pewno chcesz odblokować użytkownika {{memberDisplayName}}?',
    companion_report_message: 'Czy na pewno chcesz zgłosić użytkownika {{memberDisplayName}}?',
    companions_block_successfully: 'Pomyślnie zablokowano towarzysza!',
    companions_unblock_successfully: 'Pomyślnie odblokowano towarzysza!',
    companions_cannot_block_administrator: 'Nie możesz zablokować administratora!',
    health_journey_select_date: 'Wybierz datę',
  },
  lv: {
    ai_context_mode_all_rooms: 'Automātiski',
    like: 'Patīk',
    likes: 'Patīk',
    community_post_like: 'Patīk',
    community_post_likes: 'Patīk',
    reaction_like_label: 'Patīk',
    story_allow_likes: 'Atļaut patīk',
    comment: 'Komentārs',
    comments: 'Komentāri',
    reaction_care_label: 'Man rūp',
    reaction_helpful_label: 'Noderīgi',
    story_draft: 'Melnraksts',
    stories_drafts: 'Melnraksti',
    checker: 'Pārbaudītājs',
    health_picture_complete: 'Pabeigt',
    health_picture_diagnosis_type_nonmedical: 'Iespējama',
    preview: 'Priekšskatījums',
    journal_name: 'Dienasgrāmata',
    community: 'Istabas',
    stories: 'Stāsti',
    profile_widget_journey: 'Gaitas',
    browser: 'Pārlūks',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Savienoties',
    community_room_type_hub: 'Hub',
    subgroups: 'Apakšgrupas',
    max: 'maks',
    companions_title: 'Līdzgaitnieki',
    health_picture_ongoing: 'Turpinās',
    poll_period_unlimited: 'Neierobežots',
    joined_rooms_title: 'Istabas šajā Hub',
    story_save_as_draft: 'Saglabāt kā melnrakstu',
    story_saved_draft_snackbar: 'Melnraksts veiksmīgi saglabāts',
    story_save_draft_success_dialog_content: 'Jūsu melnraksts ir veiksmīgi saglabāts!',
    my_stories_empty_drafts_first_line: 'Jums nav stāstu melnrakstos.',
    health_picture_add_journal: 'Pievienot dienasgrāmatas ierakstu',
    health_picture_edit_journal: 'Rediģēt dienasgrāmatas ierakstu',
    health_picture_journal: 'Manas dienasgrāmatas',
    health_journey_journal_title: 'Dienasgrāmata',
    health_journey_journal_date: 'Dienasgrāmatas datums',
    delete_journal_confirmation_title: 'Dzēst dienasgrāmatu',
    delete_journal_confirmation_description: 'Vai tiešām vēlaties dzēst šo dienasgrāmatu? Šo darbību nevar atsaukt.',
    journal_deleted_successfully: 'Dienasgrāmata veiksmīgi dzēsta.',
    error_deleting_journal: 'Radās problēma, dzēšot dienasgrāmatu. Lūdzu, mēģiniet vēlreiz.',
    stories_first_tab: 'Visi stāsti',
    stories_third_tab: 'Mani stāsti',
    stories_suggested: 'Ieteiktie stāsti',
    stories_my_stories: 'Mani stāsti',
    companions_in_room_title: 'Līdzgaitnieki istabā',
    health_picture_snackbar_adding_ongoing_symptom: 'Jūs jau esat pievienojis šo simptomu, un tas joprojām turpinās! Lūdzu, atjauniniet šo ierakstu!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Jūs jau esat pievienojis šo diagnozi, un tā joprojām turpinās! Lūdzu, atjauniniet šo ierakstu!',
    health_picture_snackbar_adding_ongoing_treatment: 'Jūs jau esat pievienojis šo ārstēšanu, un tā joprojām turpinās! Lūdzu, atjauniniet šo ierakstu!',
    health_picture_snackbar_adding_ongoing_medication: 'Jūs jau esat pievienojis šo medikamentu, un tas joprojām turpinās! Lūdzu, atjauniniet šo ierakstu!',
    your_subgroups: 'Jūsu apakšgrupas',
    other_subgroups: 'Citas apakšgrupas',
    community_room_type_hub_room: 'Hub istaba',
    // Room consistency overrides
    rooms_title: 'Istabas',
    my_rooms_title: 'Manas Istabas un Hubi',
    profile_widget_rooms: 'Istabas',
    bottom_nav_community: 'Istabas',
    search_rooms_title: 'Istabas',
    community_first_tab: 'Manas istabas',
    community_second_tab: 'Visas istabas',
    community_room_type_private_room: 'Privāta istaba',
    community_room_type_public_room: 'Publiska istaba',
    related_rooms: 'Saistītās istabas',
    my_rooms_explore_other_rooms: 'Izpētīt citas istabas',
    rooms_autocomplete_caption: 'Izpētīt istabas:',
    rooms_no_result_found: 'Istabas netika atrastas. Mēģiniet citu meklēšanu.',
    rooms_view_all_button: 'Skatīt visas istabas',
    rooms_view_rooms_button: 'Skatīt istabas',
    rooms_leave_room: 'Pamest istabu',
    rooms_related_rooms_loading: 'Ielādē saistītās istabas...',
    rooms_related_rooms_end: 'Vairāk saistītu istabu nav',
    rooms_by_topic_view_more_button: 'Skatīt vairāk istabu',
    share_story_to_rooms_no_rooms_button: 'Izpētīt istabas',
    room_join_button: 'Pievienoties istabai',
    room_join_button_pending: 'Istabas pieprasījums gaida',
    room_leave_confirmation_title: 'Pamest istabu',
    no_related_rooms: 'Saistītās istabas netika atrastas.',
    poll_rooms_selection: 'Dalīties ar aptauju istabās:',
    my_rooms_no_member_rooms: 'Jūs neesat nevienas istabas dalībnieks. Kāpēc gan ne <0>izpētīt?<0>',
    suggest_new_rooms_title: 'Nevarat atrast vajadzīgo istabu?',
    latest_posts_your_topics: 'Jaunākie ieraksti jūsu istabās',
    people_in_the_room: 'Cilvēki istabā',
    people_in_the_room_no_people: 'Kāpēc gan nebūt pirmajam, kas pievienojas istabai un izveido ierakstu.',
    people_in_the_room_no_companions_and_people: 'Apsveicam! Jūs esat pirmā persona šajā istabā',
    no_moderators_in_the_room: 'Šajā istabā nav moderatoru',
    select_rooms_for_post_modal_title: 'Lūdzu, izvēlieties istabas, kurās vēlaties kopīgot savu ierakstu:',
    rooms_create_post_dialog_placeholder_nonmember: 'Jums jāpievienojas istabai, lai varētu publicēt',
    rooms_blocked_member: 'Diemžēl jūsu piekļuve šai istabai ir atsaukta',
    community_not_member_of_room: 'Atvainojiet, jūs nevarat piekļūt šai istabai',
    community_room_library_no_files: 'Šai istabai nav pievienotu failu.',
    community_room_library_details: 'Detaļas',
    community_room_library_download: 'Lejupielādēt',
    community_room_library_summarise: 'Apkopot',
    community_room_library_translate: 'Tulkot',
    community_room_library_delete: 'Dzēst',
    community_room_library_update: 'Atjaunināt',
    profile_settings_ai_settings_title: 'AI iestatījumi',
    profile_settings_ai_tokens_allowed: 'Atļautie žetoni dienā:',
    profile_settings_ai_tokens_used: 'Šodien izmantotie žetoni:',
    featured_content_delete_file_warning_title: 'Brīdinājums',
    featured_content_delete_file_warning_message: 'Kad dzēšat failu šeit, tas tiks dzēsts arī no bibliotēkas',
    share_story_no_rooms_for_member: 'Izskatās, ka jūs neesat nevienas istabas dalībnieks. Kāpēc gan neizpētīt un nepievienoties?',
    community_room_library_file_type: 'Faila tips',
    community_room_library_language: 'Valoda',
    community_room_library_valid_until: 'Derīgs līdz',
    community_room_library_date_created: 'Izveides datums',
    snackbar_community_member_not_in_room: 'Jums jāpievienojas istabai {{roomName}}, lai atbildētu vai izteiktu patīk.',
    snackbar_community_member_not_in_room_poll: 'Jums jāpievienojas istabai {{roomName}}, lai izveidotu aptauju.',
    snackbar_community_moderator_remove_member_success: 'Dalībnieks noņemts no istabas!',
    snackbar_community_cannot_join_room: 'Jūs nevarat pievienoties istabai.',
    snackbar_community_cannot_join_room_blocked: 'Jūs esat bloķēts šajā istabā.',
    snackbar_community_cannot_leave_room: 'Jūs nevarat pamest istabu.',
    snackbar_community_join_room_success: 'Jūs esat pievienojies istabai!',
    snackbar_community_leave_room_success: 'Jūs esat pametis istabu!',
    polls_widget_no_polls: 'Šajā istabā vēl nav aptauju.',
    poll_proposal_additional_info: 'Istabas moderators sazināsies ar jums, lai saņemtu papildu informāciju par aptauju.',
    room_leave_confirmation_description: 'Vai tiešām vēlaties pamest šo istabu?',
    story_choose_rooms_subtitle: 'Jūs varat kopīgot šo stāstu šādās istabās:',
    story_published_modal_title: 'Stāsts veiksmīgi publicēts! Vai vēlaties to kopīgot savās istabās?',
    story_published_modal_title_mobile: 'Paldies par stāsta publicēšanu. Vai vēlaties to kopīgot savās istabās?',
    moderator_pin_post_dialog_description: 'Vai tiešām vēlaties piespraust šo ierakstu istabā?',
    moderator_remove_member_dialog_description: 'Vai tiešām vēlaties noņemt šo dalībnieku no istabas?',
    errors_posts_not_allowed_in_room: 'Ieraksti šajā istabā nav atļauti.',
    errors_no_posts_found_room_member: 'Esi pirmais, kas publicē šajā istabā!',
    errors_no_posts_found_room_not_member: 'Pievienojies istabai un publicē kaut ko!',
    errors_no_posts_found_room: 'Esi pirmais, kas publicē šajā istabā!',
    notifications_settings_someone_publishes_a_story_label: 'Jauns stāsts Manās Istabās',
    share_story_to_rooms_second_title: 'Vai vēlaties to kopīgot savās istabās?',
    private_room_code_widget_title: 'Vai jums ir kods privātai Istabai vai Hubam?',
    private_room_shim_name_title_dialog: 'Laipni lūdzam šajā privātajā istabā!',
    private_room_shim_name_subtitle_dialog: 'Izmantojiet vārdu, ar kādu cilvēki šajā istabā jūs pazīst.',
    private_room_shim_name_subtitle_dialog_2: 'Jūs paliksiet anonīms ārpus šīs istabas.',
    companions_description_no_companion_in_room: 'Jums vēl nav līdzgaitnieku šajā istabā. Piedalieties sarunās un atrodiet dalībniekus, ar kuriem sazināties.',
    companions_description_no_companion_in_community: 'Jums vēl nav līdzgaitnieku. Kāpēc gan neizpētīt istabas un neatrast dalībniekus, ar kuriem sazināties?',
    companions_no_companion: 'Jums vēl nav līdzgaitnieku. Apmeklējiet jauno dalībnieku istabu, lai atrastu carecircle dalībniekus un sazinātos',
    personal_private_room_widget_button: 'Izveidot privātu istabu',
    personal_private_room_dialog_title: 'Izveidojiet savu privāto istabu',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Galva',
    body_part_first_level_chest: 'Krūtis',
    body_part_first_level_abdomen: 'Vēders',
    body_part_first_level_pelvis: 'Iegurnis',
    body_part_first_level_arms: 'Rokas',
    body_part_first_level_legs: 'Kājas',
    body_part_first_level_back: 'Mugura',
    body_part_first_level_buttocks: 'Sēžamvieta',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Ir 'istabas', kuras varat apmeklēt anonīmi un pievienoties diskusijām, ja vēlaties. Varat publicēt komentārus vai jautājumus, vai atrast pavadoņus, kas atbalstīs jūs jūsu ceļojumā.",
    conversations: 'Tērzēšana',
    // Featured content overrides
    stories_carousel_title: 'Ieteiktie stāsti',
    content_carousel_title: 'Ieteiktais saturs',
    content_carousel_add_new_post_button: 'Pievienot jaunu Ieteikto saturu',
    content_carousel_no_content_moderator_subtitle: 'Informējiet visus šajā istabā par jaunumiem, paziņojumiem un resursiem. Ieteikto saturu pašlaik var izveidot tikai no tīmekļa lietotnes.',
    room_content_type_featured_posts_and_library_option: 'Ieteiktie ieraksti un bibliotēka',
    room_content_type_featured_posts_option: 'Ieteiktie ieraksti',
    room_create_content_success: 'Ieteiktā satura elements tika veiksmīgi izveidots',
    room_update_content_success: 'Ieteiktā satura elements tika veiksmīgi atjaunināts',
    room_delete_content_success: 'Ieteiktā satura elements tika veiksmīgi izdzēsts',
    room_delete_content_error: 'Dzēšot ieteiktā satura elementu, radās kļūda',
    room_confirm_delete_featured_item_title: 'Dzēst ieteikto saturu',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Kā jūs jūtaties',
    wellness_tracker_morning: 'šorīt',
    wellness_tracker_afternoon: 'šopēcpusdien',
    wellness_tracker_evening: 'šovakar',
    // Navigation "Previous" overrides
    previous: 'Iepriekšējais',
    health_picture_symptom_checker_previous: 'Iepriekšējais',
    errors_to_previous_link: 'iepriekšējā lapa',
    errors_to_previous_mobile: 'Aizved mani uz <0>iepriekšējo ekrānu</0>.',
    errors_to_previous_link_mobile: 'iepriekšējo ekrānu',
    stories_carousel_previous_story: 'Iepriekšējais stāsts',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Ierakstiet komentāru...',
    chat_input_placeholder: 'Ierakstiet ziņojumu...',
    ai_chatbot_widget_input_placeholder: 'Ierakstiet kaut ko...',
    create_post_input_placeholder: 'Ierakstiet ziņojumu...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Veselības ceļš',
    health_picture_name: 'Veselības aina',
    ai_context_mode_health_journey: 'Veselības ceļš',
    health_journey_second_tab: 'Veselības aina',
    health_journey_add_new: 'Izveidot veselības ceļu',
    health_journey_rename_journey: 'Mainīt veselības ceļu',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Izveidot veselības ceļu',
    hj_already_create_for_myself_4: 'Skatīt veselības ceļu',
    health_journey_privacy_dialog_health_journey_link: 'Veselības ceļš',
    health_picture_view_health_journey: 'Skatīt veselības ceļu',
    health_picture_symptom_checker_save_to_health_journey: 'Saglabāt veselības ceļā',
    health_journey_my_picture: 'Mana veselības aina',
    health_journey_picture_not_created_title: 'Sāciet sekot savam veselības ceļam',
    health_journey_start_health_picture_title: 'Spēriet pirmo soli, izveidojot veselības ainu',
    verification_successful_subtitle: 'Jūs drīz tiksiet novirzīts.\nSāciet savu veselības ceļu jau šodien.',
    rooms_redirect_to_health_picture: 'aizpildiet savu veselības ainu',
    health_picture_no_symptoms_info: 'Reģistrējiet savus simptomus pilnīgai veselības ainai',
    ai_context_label_health_journey: 'Pašreizējais konteksts ir veselības ceļš:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Izveidojiet tagad savu veselības ceļu, lai iegūtu pārskatu par savu labsajūtu un izpētītu citas aizraujošas iespējas.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Piešķiriet savam ceļam nosaukumu',
    new_health_journey_placeholder: 'Jums var būt vairāki ceļi sev vai kādam citam.',
    new_health_journey_validation_required_name: 'Lūdzu, ievadiet nosaukumu savam ceļam',
    new_health_journey_validation_length_name: 'Veselības ceļa nosaukumā nedrīkst būt vairāk par 30 rakstzīmēm',
    hj_already_create_for_myself_1: 'Izskatās, ka jūs',
    hj_already_create_for_myself_2: 'jau esat izveidojis veselības ceļu',
    hj_already_create_for_myself_3: 'sev.',
    hj_multiple_dialog_title: 'Kam jūs veidojat veselības ceļu?',
    hj_multiple_for_myself: 'Es vēlos izveidot šo ceļu sev',
    hj_multiple_for_someone_else: 'Es vēlos izveidot šo ceļu kādam citam',
    conversation_hide: 'Paslēpt',
    consultation_name: 'Ārsta konsultācija',
    health_picture_consultations: 'Manas ārsta konsultācijas',
    health_picture_add_consultation: 'Pievienot ārsta konsultāciju',
    health_picture_edit_consultation: 'Rediģēt ārsta konsultāciju',
    health_journey_consultation_type: 'Konsultācijas veids:',
    health_journey_timeline_consultation_type: 'Veids:',
    mycare_consultation_type_label: 'Konsultācijas veids',
    mycare_consultation_provider_label: 'Veselības aprūpes sniedzēja vārds',
    mycare_consultation_speciality_label: 'Specialitāte',
    my_care_journal_notes: 'Piezīmes',
    my_care_journal_description_placeholder: 'Pierakstiet piezīmes par visu, ko vēlaties...',
    content_carousel_no_content_moderator_title: 'Izveidojiet saturu savai istabai',
    content_carousel_no_content_member_title: 'Esiet sazinā ar savu istabu',
    content_carousel_no_content_member_subtitle: 'Šeit jūs atradīsiet jaunākās ziņas, paziņojumus un resursus.',
    ai_context_label_room: 'Pašreizējais konteksts ir Istaba:',
    companions_block_user: 'Bloķēt',
    companions_unblock_user: 'Atbloķēt',
    companion_request_block: 'Bloķēt',
    companion_request_unblock: 'Atbloķēt',
    companion_report: 'Ziņot',
    companion_block_message: 'Vai tiešām vēlaties bloķēt {{memberDisplayName}} un noņemt kā pavadoni?',
    companion_unblock_message: 'Vai tiešām vēlaties atbloķēt {{memberDisplayName}}?',
    companion_report_message: 'Vai tiešām vēlaties ziņot par {{memberDisplayName}}?',
    companions_block_successfully: 'Jūs veiksmīgi bloķējāt pavadoni!',
    companions_unblock_successfully: 'Jūs veiksmīgi atbloķējāt pavadoni!',
    companions_cannot_block_administrator: 'Jūs nevarat bloķēt administratoru!',
    health_journey_select_date: 'Izvēlieties datumu',
  },
  lt: {
    ai_context_mode_all_rooms: 'Automatiškai',
    like: 'Patinka',
    likes: 'Patinka',
    community_post_like: 'Patinka',
    community_post_likes: 'Patinka',
    reaction_like_label: 'Patinka',
    story_allow_likes: 'Leisti patinka',
    comment: 'Komentaras',
    comments: 'Komentarai',
    reaction_care_label: 'Man rūpi',
    reaction_helpful_label: 'Naudinga',
    story_draft: 'Juodraštis',
    stories_drafts: 'Juodraščiai',
    checker: 'Tikrintuvas',
    health_picture_complete: 'Baigti',
    health_picture_diagnosis_type_nonmedical: 'Tikėtina',
    preview: 'Peržiūra',
    journal_name: 'Dienoraštis',
    community: 'Kambariai',
    stories: 'Istorijos',
    profile_widget_journey: 'Keliai',
    browser: 'Naršyklė',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Prisijungti',
    community_room_type_hub: 'Hub',
    subgroups: 'Pogrupiai',
    max: 'maks',
    companions_title: 'Palydovai',
    health_picture_ongoing: 'Vykstantis',
    poll_period_unlimited: 'Neribota',
    joined_rooms_title: 'Kambariai šiame Hub',
    story_save_as_draft: 'Išsaugoti kaip juodraštį',
    story_saved_draft_snackbar: 'Juodraštis sėkmingai išsaugotas',
    story_save_draft_success_dialog_content: 'Jūsų juodraštis sėkmingai išsaugotas!',
    my_stories_empty_drafts_first_line: 'Neturite jokių istorijų juodraštyje.',
    health_picture_add_journal: 'Pridėti dienoraščio įrašą',
    health_picture_edit_journal: 'Redaguoti dienoraščio įrašą',
    health_picture_journal: 'Mano dienoraščiai',
    health_journey_journal_title: 'Dienoraštis',
    health_journey_journal_date: 'Dienoraščio data',
    delete_journal_confirmation_title: 'Ištrinti dienoraštį',
    delete_journal_confirmation_description: 'Ar tikrai norite ištrinti šį dienoraštį? Šio veiksmo negalima atšaukti.',
    journal_deleted_successfully: 'Dienoraštis sėkmingai ištrintas.',
    error_deleting_journal: 'Trinant dienoraštį įvyko klaida. Bandykite dar kartą.',
    stories_first_tab: 'Visos istorijos',
    stories_third_tab: 'Mano istorijos',
    stories_suggested: 'Siūlomos istorijos',
    stories_my_stories: 'Mano istorijos',
    companions_in_room_title: 'Palydovai kambaryje',
    health_picture_snackbar_adding_ongoing_symptom: 'Jau pridėjote šį simptomą ir jis vis dar vykstantis! Atnaujinkite tą įrašą!',
    health_picture_snackbar_adding_ongoing_diagnosis: 'Jau pridėjote šią diagnozę ir ji vis dar vykstanti! Atnaujinkite tą įrašą!',
    health_picture_snackbar_adding_ongoing_treatment: 'Jau pridėjote šį gydymą ir jis vis dar vykstantis! Atnaujinkite tą įrašą!',
    health_picture_snackbar_adding_ongoing_medication: 'Jau pridėjote šį vaistą ir jis vis dar vykstantis! Atnaujinkite tą įrašą!',
    your_subgroups: 'Jūsų pogrupiai',
    other_subgroups: 'Kiti pogrupiai',
    community_room_type_hub_room: 'Hub kambarys',
    // Room consistency overrides
    rooms_title: 'Kambariai',
    my_rooms_title: 'Mano Kambariai ir Hubai',
    profile_widget_rooms: 'Kambariai',
    bottom_nav_community: 'Kambariai',
    search_rooms_title: 'Kambariai',
    community_first_tab: 'Mano kambariai',
    community_second_tab: 'Visi kambariai',
    community_room_type_private_room: 'Privatus kambarys',
    community_room_type_public_room: 'Viešas kambarys',
    related_rooms: 'Susiję kambariai',
    my_rooms_explore_other_rooms: 'Atraskite kitus kambarius',
    rooms_autocomplete_caption: 'Atraskite kambarius:',
    rooms_no_result_found: 'Kambarių nerasta. Pabandykite kitą paiešką.',
    rooms_view_all_button: 'Peržiūrėti visus kambarius',
    rooms_view_rooms_button: 'Peržiūrėti kambarius',
    rooms_leave_room: 'Palikti kambarį',
    rooms_related_rooms_loading: 'Kraunami susiję kambariai...',
    rooms_related_rooms_end: 'Daugiau susijusių kambarių nėra',
    rooms_by_topic_view_more_button: 'Peržiūrėti daugiau kambarių',
    share_story_to_rooms_no_rooms_button: 'Atraskite kambarius',
    room_join_button: 'Prisijungti prie kambario',
    room_join_button_pending: 'Kambario užklausa laukia',
    room_leave_confirmation_title: 'Palikti kambarį',
    no_related_rooms: 'Susijusių kambarių nerasta.',
    poll_rooms_selection: 'Dalintis apklausa kambariuose:',
    my_rooms_no_member_rooms: 'Jūs nesate jokio kambario narys. Kodėl ne <0>atrasti?<0>',
    suggest_new_rooms_title: 'Nerandate reikiamo kambario?',
    latest_posts_your_topics: 'Naujausi įrašai jūsų kambariuose',
    people_in_the_room: 'Žmonės kambaryje',
    people_in_the_room_no_people: 'Kodėl nebūti pirmuoju, kuris prisijungia prie kambario ir sukuria įrašą.',
    people_in_the_room_no_companions_and_people: 'Sveikiname! Jūs esate pirmas žmogus šiame kambaryje',
    no_moderators_in_the_room: 'Šiame kambaryje nėra moderatorių',
    select_rooms_for_post_modal_title: 'Pasirinkite kambarius, kuriuose norite dalintis savo įrašu:',
    rooms_create_post_dialog_placeholder_nonmember: 'Turite prisijungti prie kambario, kad galėtumėte skelbti',
    rooms_blocked_member: 'Deja, jūsų prieiga prie šio kambario buvo atšaukta',
    community_not_member_of_room: 'Atsiprašome, jūs negalite pasiekti šio kambario',
    community_room_library_no_files: 'Prie šio kambario nepridėta jokių failų.',
    community_room_library_details: 'Išsamiau',
    community_room_library_download: 'Atsisiųsti',
    community_room_library_summarise: 'Apibendrinti',
    community_room_library_translate: 'Versti',
    community_room_library_delete: 'Ištrinti',
    community_room_library_update: 'Atnaujinti',
    profile_settings_ai_settings_title: 'AI nustatymai',
    profile_settings_ai_tokens_allowed: 'Leidžiami žetonai per dieną:',
    profile_settings_ai_tokens_used: 'Šiandien panaudoti žetonai:',
    featured_content_delete_file_warning_title: 'Įspėjimas',
    featured_content_delete_file_warning_message: 'Kai ištrinsite failą čia, jis taip pat bus ištrintas iš bibliotekos',
    share_story_no_rooms_for_member: 'Atrodo, kad nesate jokio kambario narys. Kodėl neatradus ir neprisijungus?',
    community_room_library_file_type: 'Failo tipas',
    community_room_library_language: 'Kalba',
    community_room_library_valid_until: 'Galioja iki',
    community_room_library_date_created: 'Sukūrimo data',
    snackbar_community_member_not_in_room: 'Turite prisijungti prie kambario {{roomName}}, kad galėtumėte atsakyti arba pamėgti.',
    snackbar_community_member_not_in_room_poll: 'Turite prisijungti prie kambario {{roomName}}, kad galėtumėte sukurti apklausą.',
    snackbar_community_moderator_remove_member_success: 'Narys pašalintas iš kambario!',
    snackbar_community_cannot_join_room: 'Negalite prisijungti prie kambario.',
    snackbar_community_cannot_join_room_blocked: 'Jūs buvote užblokuotas šiame kambaryje.',
    snackbar_community_cannot_leave_room: 'Negalite palikti kambario.',
    snackbar_community_join_room_success: 'Jūs prisijungėte prie kambario!',
    snackbar_community_leave_room_success: 'Jūs palikote kambarį!',
    polls_widget_no_polls: 'Šiame kambaryje dar nėra apklausų.',
    poll_proposal_additional_info: 'Kambario moderatorius susisieks dėl papildomos informacijos apie apklausą.',
    room_leave_confirmation_description: 'Ar tikrai norite palikti šį kambarį?',
    story_choose_rooms_subtitle: 'Galite dalintis šia istorija šiuose kambariuose:',
    story_published_modal_title: 'Istorija sėkmingai paskelbta! Ar norite ja dalintis savo kambariuose?',
    story_published_modal_title_mobile: 'Ačiū, kad paskelbėte savo istoriją. Ar norite ja dalintis savo kambariuose?',
    moderator_pin_post_dialog_description: 'Ar tikrai norite prisegti šį įrašą kambaryje?',
    moderator_remove_member_dialog_description: 'Ar tikrai norite pašalinti šį narį iš kambario?',
    errors_posts_not_allowed_in_room: 'Įrašai šiame kambaryje neleidžiami.',
    errors_no_posts_found_room_member: 'Būkite pirmas, kuris paskelbia šiame kambaryje!',
    errors_no_posts_found_room_not_member: 'Prisijunkite prie kambario ir paskelbkite ką nors!',
    errors_no_posts_found_room: 'Būkite pirmas, kuris paskelbia šiame kambaryje!',
    notifications_settings_someone_publishes_a_story_label: 'Nauja istorija Mano Kambariuose',
    share_story_to_rooms_second_title: 'Ar norite ja dalintis savo kambariuose?',
    private_room_code_widget_title: 'Ar turite kodą privačiam Kambariui ar Hubui?',
    private_room_shim_name_title_dialog: 'Sveiki atvykę į šį privatų kambarį!',
    private_room_shim_name_subtitle_dialog: 'Naudokite vardą, kuriuo jus pažįsta žmonės šiame kambaryje.',
    private_room_shim_name_subtitle_dialog_2: 'Liksite anonimiškas už šio kambario ribų.',
    companions_description_no_companion_in_room: 'Šiame kambaryje dar neturite palydovų. Dalyvaukite pokalbiuose ir raskite narių, su kuriais galėtumėte susisiekti.',
    companions_description_no_companion_in_community: 'Dar neturite palydovų. Kodėl neatradus kambarių ir neradus narių, su kuriais galėtumėte susisiekti?',
    companions_no_companion: 'Dar neturite palydovų. Aplankykite naujų narių kambarį, kad rastumėte carecircle narius ir užmegztumėte ryšį',
    personal_private_room_widget_button: 'Sukurti privatų kambarį',
    personal_private_room_dialog_title: 'Sukurkite savo privatų kambarį',
    // Body part Level 1 overrides
    body_part_first_level_head: 'Galva',
    body_part_first_level_chest: 'Krūtinė',
    body_part_first_level_abdomen: 'Pilvas',
    body_part_first_level_pelvis: 'Dubuo',
    body_part_first_level_arms: 'Rankos',
    body_part_first_level_legs: 'Kojos',
    body_part_first_level_back: 'Nugara',
    body_part_first_level_buttocks: 'Sėdmenys',
    // Onboarding carousel overrides
    carousel_community_description_bottom: "Yra 'kambariai', kuriuos galite aplankyti anonimiškai ir prisijungti prie diskusijų, jei norite. Galite skelbti komentarus ar klausimus, arba rasti palydovus, kurie palaikys jus jūsų kelionėje.",
    conversations: 'Pokalbiai',
    // Featured content overrides
    stories_carousel_title: 'Rekomenduojami pasakojimai',
    content_carousel_title: 'Rekomenduojamas turinys',
    content_carousel_add_new_post_button: 'Pridėti naują Rekomenduojamą turinį',
    content_carousel_no_content_moderator_subtitle: 'Praneškite visiems šiame kambaryje apie naujausias naujienas, skelbimus ir išteklius. Rekomenduojamas turinys šiuo metu gali būti kuriamas tik iš žiniatinklio programos.',
    room_content_type_featured_posts_and_library_option: 'Rekomenduojami įrašai ir biblioteka',
    room_content_type_featured_posts_option: 'Rekomenduojami įrašai',
    room_create_content_success: 'Rekomenduojamo turinio elementas sėkmingai sukurtas',
    room_update_content_success: 'Rekomenduojamo turinio elementas sėkmingai atnaujintas',
    room_delete_content_success: 'Rekomenduojamo turinio elementas sėkmingai ištrintas',
    room_delete_content_error: 'Trinant rekomenduojamo turinio elementą įvyko klaida',
    room_confirm_delete_featured_item_title: 'Ištrinti rekomenduojamą turinį',
    // Wellness tracker greeting overrides
    wellness_tracker_how_are_you_feeling: 'Kaip jaučiatės',
    wellness_tracker_morning: 'šį rytą',
    wellness_tracker_afternoon: 'šią popietę',
    wellness_tracker_evening: 'šį vakarą',
    // Navigation "Previous" overrides
    previous: 'Ankstesnis',
    health_picture_symptom_checker_previous: 'Ankstesnis',
    errors_to_previous_link: 'ankstesnis puslapis',
    errors_to_previous_mobile: 'Nukreipk mane į <0>ankstesnį ekraną</0>.',
    errors_to_previous_link_mobile: 'ankstesnį ekraną',
    stories_carousel_previous_story: 'Ankstesnė istorija',
    // Input placeholder overrides
    story_comment_input_placeholder: 'Parašykite komentarą...',
    chat_input_placeholder: 'Parašykite žinutę...',
    ai_chatbot_widget_input_placeholder: 'Parašykite ką nors...',
    create_post_input_placeholder: 'Parašykite žinutę...',
    // Health Journey & Health Picture idiom overrides
    health_journey_placeholder: 'Sveikatos kelias',
    health_picture_name: 'Sveikatos vaizdas',
    ai_context_mode_health_journey: 'Sveikatos kelias',
    health_journey_second_tab: 'Sveikatos vaizdas',
    health_journey_add_new: 'Sukurti sveikatos kelią',
    health_journey_rename_journey: 'Keisti sveikatos kelią',
    wellness_tracker_no_journey_for_himself_create_health_journey: 'Sukurti sveikatos kelią',
    hj_already_create_for_myself_4: 'Peržiūrėti sveikatos kelią',
    health_journey_privacy_dialog_health_journey_link: 'Sveikatos kelias',
    health_picture_view_health_journey: 'Peržiūrėti sveikatos kelią',
    health_picture_symptom_checker_save_to_health_journey: 'Išsaugoti sveikatos kelyje',
    health_journey_my_picture: 'Mano sveikatos vaizdas',
    health_journey_picture_not_created_title: 'Pradėkite sekti savo sveikatos kelią',
    health_journey_start_health_picture_title: 'Ženkite pirmą žingsnį sukurdami sveikatos vaizdą',
    verification_successful_subtitle: 'Netrukus būsite nukreipti.\nPradėkite savo sveikatos kelią šiandien.',
    rooms_redirect_to_health_picture: 'užpildykite savo sveikatos vaizdą',
    health_picture_no_symptoms_info: 'Užregistruokite savo simptomus, kad turėtumėte pilną sveikatos vaizdą',
    ai_context_label_health_journey: 'Dabartinis kontekstas yra sveikatos kelias:',
    wellness_tracker_no_journey_for_himself_subtitle: 'Sukurkite dabar savo sveikatos kelią, kad turėtumėte gerovės apžvalgą ir galėtumėte ištirti kitas įdomias funkcijas.',
    // Create Health Journey form overrides
    new_health_journey_give_name: 'Suteikite savo keliui pavadinimą',
    new_health_journey_placeholder: 'Galite turėti kelis kelius sau arba kam nors kitam.',
    new_health_journey_validation_required_name: 'Įveskite savo kelio pavadinimą',
    new_health_journey_validation_length_name: 'Sveikatos kelio pavadinimas negali viršyti 30 simbolių',
    hj_already_create_for_myself_1: 'Atrodo, kad jūs',
    hj_already_create_for_myself_2: 'jau sukūrėte sveikatos kelią',
    hj_already_create_for_myself_3: 'sau.',
    hj_multiple_dialog_title: 'Kam kuriate sveikatos kelią?',
    hj_multiple_for_myself: 'Noriu sukurti šį kelią sau',
    hj_multiple_for_someone_else: 'Noriu sukurti šį kelią kam nors kitam',
    conversation_hide: 'Slėpti',
    consultation_name: 'Gydytojo konsultacija',
    health_picture_consultations: 'Mano gydytojo konsultacijos',
    health_picture_add_consultation: 'Pridėti gydytojo konsultaciją',
    health_picture_edit_consultation: 'Redaguoti gydytojo konsultaciją',
    health_journey_consultation_type: 'Konsultacijos tipas:',
    health_journey_timeline_consultation_type: 'Tipas:',
    mycare_consultation_type_label: 'Konsultacijos tipas',
    mycare_consultation_provider_label: 'Sveikatos priežiūros paslaugų teikėjo vardas',
    mycare_consultation_speciality_label: 'Specializacija',
    my_care_journal_notes: 'Užrašai',
    my_care_journal_description_placeholder: 'Užsirašykite bet ką, ką norite...',
    content_carousel_no_content_moderator_title: 'Kurkite turinį savo kambariui',
    content_carousel_no_content_member_title: 'Palaikykite ryšį su savo kambariu',
    content_carousel_no_content_member_subtitle: 'Čia rasite naujausias naujienas, skelbimus ir išteklius.',
    ai_context_label_room: 'Dabartinis kontekstas yra Kambarys:',
    companions_block_user: 'Blokuoti',
    companions_unblock_user: 'Atblokuoti',
    companion_request_block: 'Blokuoti',
    companion_request_unblock: 'Atblokuoti',
    companion_report: 'Pranešti',
    companion_block_message: 'Ar tikrai norite užblokuoti {{memberDisplayName}} ir pašalinti kaip palydovą?',
    companion_unblock_message: 'Ar tikrai norite atblokuoti {{memberDisplayName}}?',
    companion_report_message: 'Ar tikrai norite pranešti apie {{memberDisplayName}}?',
    companions_block_successfully: 'Sėkmingai užblokavote palydovą!',
    companions_unblock_successfully: 'Sėkmingai atblokavote palydovą!',
    companions_cannot_block_administrator: 'Negalite blokuoti administratoriaus!',
    health_journey_select_date: 'Pasirinkite datą',
  },
  // Czech — starter overrides for literal-sensitive terms (expand with full QA pass)
  cs: {
    ai_context_mode_all_rooms: 'Automaticky',
    like: 'Líbí se mi',
    likes: 'Líbí se mi',
    community_post_like: 'Líbí se mi',
    community_post_likes: 'Líbí se mi',
    reaction_like_label: 'Líbí se mi',
    story_allow_likes: 'Povolit lajky',
    comment: 'Komentář',
    comments: 'Komentáře',
    reaction_care_label: 'Péče',
    reaction_helpful_label: 'Užitečné',
    story_draft: 'Koncept',
    stories_drafts: 'Koncepty',
    checker: 'Kontrola',
    health_picture_complete: 'Dokončit',
    health_picture_diagnosis_type_nonmedical: 'Pravděpodobné',
    preview: 'Náhled',
    journal_name: 'Deník',
    community: 'Místnosti',
    stories: 'Příběhy',
    profile_widget_journey: 'Cesty',
    browser: 'Prohlížeč',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Propojit',
    community_room_type_hub: 'Hub',
    subgroups: 'Podskupiny',
    max: 'max',
    companions_title: 'Společníci',
    health_picture_ongoing: 'Probíhající',
    poll_period_unlimited: 'Neomezené',
    joined_rooms_title: 'Místnosti v tomto Hubu',
    rooms_title: 'Místnosti',
    my_rooms_title: 'Moje místnosti a Huby',
    private_room_code_widget_title: 'Máte kód pro soukromou místnost nebo Hub?',
    profile_widget_rooms: 'Místnosti',
    bottom_nav_community: 'Místnosti',
    search_rooms_title: 'Místnosti',
    health_journey_placeholder: 'Zdravotní cesta',
    health_picture_name: 'Zdravotní obraz',
    ai_context_mode_health_journey: 'Zdravotní cesta',
    health_journey_second_tab: 'Zdravotní obraz',
    health_picture_symptom_checker_save_to_health_journey: 'Uložit do zdravotní cesty',
    conversation_hide: 'Skrýt',
  },
  // Slovak — starter overrides for literal-sensitive terms (expand with full QA pass)
  sk: {
    ai_context_mode_all_rooms: 'Automaticky',
    like: 'Páči sa mi',
    likes: 'Páči sa mi',
    community_post_like: 'Páči sa mi',
    community_post_likes: 'Páči sa mi',
    reaction_like_label: 'Páči sa mi',
    story_allow_likes: 'Povoliť lajky',
    comment: 'Komentár',
    comments: 'Komentáre',
    reaction_care_label: 'Starostlivosť',
    reaction_helpful_label: 'Užitočné',
    story_draft: 'Koncept',
    stories_drafts: 'Koncepty',
    checker: 'Kontrola',
    health_picture_complete: 'Dokončiť',
    health_picture_diagnosis_type_nonmedical: 'Pravdepodobné',
    preview: 'Náhľad',
    journal_name: 'Denník',
    community: 'Miestnosti',
    stories: 'Príbehy',
    profile_widget_journey: 'Cesty',
    browser: 'Prehliadač',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Prepojiť',
    community_room_type_hub: 'Hub',
    subgroups: 'Podskupiny',
    max: 'max',
    companions_title: 'Spoločníci',
    health_picture_ongoing: 'Prebiehajúce',
    poll_period_unlimited: 'Neobmedzené',
    joined_rooms_title: 'Miestnosti v tomto Hube',
    rooms_title: 'Miestnosti',
    my_rooms_title: 'Moje miestnosti a Huby',
    private_room_code_widget_title: 'Máte kód pre súkromnú miestnosť alebo Hub?',
    profile_widget_rooms: 'Miestnosti',
    bottom_nav_community: 'Miestnosti',
    search_rooms_title: 'Miestnosti',
    health_journey_placeholder: 'Zdravotná cesta',
    health_picture_name: 'Zdravotný obraz',
    ai_context_mode_health_journey: 'Zdravotná cesta',
    health_journey_second_tab: 'Zdravotný obraz',
    health_picture_symptom_checker_save_to_health_journey: 'Uložiť do zdravotnej cesty',
    conversation_hide: 'Skryť',
  },
  // Estonian — starter overrides for literal-sensitive terms (expand with full QA pass)
  et: {
    ai_context_mode_all_rooms: 'Automaatne',
    like: 'Meeldib',
    likes: 'Meeldib',
    community_post_like: 'Meeldib',
    community_post_likes: 'Meeldib',
    reaction_like_label: 'Meeldib',
    story_allow_likes: 'Luba meeldimised',
    comment: 'Kommentaar',
    comments: 'Kommentaarid',
    reaction_care_label: 'Hooliv',
    reaction_helpful_label: 'Kasulik',
    story_draft: 'Mustand',
    stories_drafts: 'Mustandid',
    checker: 'Kontroll',
    health_picture_complete: 'Lõpeta',
    health_picture_diagnosis_type_nonmedical: 'Tõenäoline',
    preview: 'Eelvaade',
    journal_name: 'Päevik',
    community: 'Toad',
    stories: 'Lood',
    profile_widget_journey: 'Teekonnad',
    browser: 'Brauser',
    icare: 'i.care',
    my_care: 'MyCare',
    landing_features_connect: 'Ühendu',
    community_room_type_hub: 'Hub',
    subgroups: 'Alarühmad',
    max: 'max',
    companions_title: 'Kaaslased',
    health_picture_ongoing: 'Kestev',
    poll_period_unlimited: 'Piiramatu',
    joined_rooms_title: 'Toad selles Hubis',
    rooms_title: 'Toad',
    my_rooms_title: 'Minu toad ja Hubid',
    private_room_code_widget_title: 'Kas teil on kood privaatse toa või Hubi jaoks?',
    profile_widget_rooms: 'Toad',
    bottom_nav_community: 'Toad',
    search_rooms_title: 'Toad',
    health_journey_placeholder: 'Terviserada',
    health_picture_name: 'Tervisepilt',
    ai_context_mode_health_journey: 'Terviserada',
    health_journey_second_tab: 'Tervisepilt',
    health_picture_symptom_checker_save_to_health_journey: 'Salvesta terviserajale',
    conversation_hide: 'Peida',
  },
};

const AI_CHAT_ACTIVITY_LABELS: Record<string, string> = {
  en: 'Completed {{count}} task', cy: "Tasgau wedi'u cwblhau: {{count}}",
  fr: 'Tâches terminées : {{count}}', de: 'Abgeschlossene Aufgaben: {{count}}',
  ro: 'Sarcini finalizate: {{count}}', ka: 'შესრულებული დავალებები: {{count}}',
  nl: 'Voltooide taken: {{count}}', it: 'Attività completate: {{count}}',
  el: 'Ολοκληρωμένες εργασίες: {{count}}', es: 'Tareas completadas: {{count}}',
  pt: 'Tarefas concluídas: {{count}}', fi: 'Valmiit tehtävät: {{count}}',
  nb: 'Fullførte oppgaver: {{count}}', sv: 'Slutförda uppgifter: {{count}}',
  da: 'Fuldførte opgaver: {{count}}', pl: 'Ukończone zadania: {{count}}',
  lv: 'Pabeigtie uzdevumi: {{count}}', lt: 'Atliktos užduotys: {{count}}',
  cs: 'Dokončené úkoly: {{count}}', sk: 'Dokončené úlohy: {{count}}', et: 'Lõpetatud ülesanded: {{count}}',
};

const AI_CHAT_ACTIVITY_DURATION_LABELS: Record<string, string> = {
  en: '{{tasks}} in {{minutes}}m {{seconds}}s',
  cy: '{{tasks}} mewn {{minutes}}m {{seconds}}e',
  fr: '{{tasks}} en {{minutes}} min {{seconds}} s',
  de: '{{tasks}} in {{minutes}} Min. {{seconds}} Sek.',
  ro: '{{tasks}} în {{minutes}} min {{seconds}} s',
  ka: '{{tasks}} {{minutes}} წთ {{seconds}} წმ-ში',
  nl: '{{tasks}} in {{minutes}}m {{seconds}}s',
  it: '{{tasks}} in {{minutes}} min {{seconds}} s',
  el: '{{tasks}} σε {{minutes}} λ {{seconds}} δ',
  es: '{{tasks}} en {{minutes}} min {{seconds}} s',
  pt: '{{tasks}} em {{minutes}} min {{seconds}} s',
  fi: '{{tasks}} ajassa {{minutes}} min {{seconds}} s',
  nb: '{{tasks}} på {{minutes}}m {{seconds}}s',
  sv: '{{tasks}} på {{minutes}}m {{seconds}}s',
  da: '{{tasks}} på {{minutes}}m {{seconds}}s',
  pl: '{{tasks}} w {{minutes}} min {{seconds}} s',
  lv: '{{tasks}} {{minutes}} min {{seconds}} s laikā',
  lt: '{{tasks}} per {{minutes}} min {{seconds}} s',
  cs: '{{tasks}} za {{minutes}} min {{seconds}} s',
  sk: '{{tasks}} za {{minutes}} min {{seconds}} s',
  et: '{{tasks}} ajaga {{minutes}} min {{seconds}} s',
};

const AI_CHAT_STREAM_CONTROL_LABELS: Record<string, readonly [string, string]> = {
  en: ['Stop streaming', 'Streaming stopped. Waiting for the server to finish.'],
  cy: ['Atal ffrydio', "Mae ffrydio wedi stopio. Yn aros i'r gweinydd orffen."],
  fr: ['Arrêter la diffusion', 'Diffusion arrêtée. En attente de la fin du traitement du serveur.'],
  de: ['Übertragung stoppen', 'Übertragung gestoppt. Warten auf den Abschluss des Servers.'],
  ro: ['Oprește transmiterea', 'Transmiterea a fost oprită. Se așteaptă finalizarea pe server.'],
  ka: ['სტრიმინგის შეჩერება', 'სტრიმინგი შეჩერებულია. ველოდებით სერვერის დასრულებას.'],
  nl: ['Streaming stoppen', 'Streaming gestopt. Wachten tot de server klaar is.'],
  it: ['Interrompi la trasmissione', 'Trasmissione interrotta. In attesa che il server termini.'],
  el: ['Διακοπή ροής', 'Η ροή διακόπηκε. Αναμονή ολοκλήρωσης του διακομιστή.'],
  es: ['Detener transmisión', 'Transmisión detenida. Esperando a que termine el servidor.'],
  pt: ['Parar transmissão', 'Transmissão parada. A aguardar que o servidor termine.'],
  fi: ['Pysäytä suoratoisto', 'Suoratoisto pysäytetty. Odotetaan palvelimen valmistumista.'],
  nb: ['Stopp strømming', 'Strømming stoppet. Venter på at serveren skal bli ferdig.'],
  sv: ['Stoppa strömning', 'Strömning stoppad. Väntar på att servern ska bli klar.'],
  da: ['Stop streaming', 'Streaming stoppet. Venter på, at serveren bliver færdig.'],
  pl: ['Zatrzymaj strumieniowanie', 'Strumieniowanie zatrzymane. Oczekiwanie na zakończenie pracy serwera.'],
  lv: ['Apturēt straumēšanu', 'Straumēšana apturēta. Gaida servera darba pabeigšanu.'],
  lt: ['Stabdyti srautinį perdavimą', 'Srautinis perdavimas sustabdytas. Laukiama, kol serveris baigs darbą.'],
  cs: ['Zastavit streamování', 'Streamování zastaveno. Čeká se na dokončení serveru.'],
  sk: ['Zastaviť streamovanie', 'Streamovanie zastavené. Čaká sa na dokončenie servera.'],
  et: ['Peata voogedastus', 'Voogedastus peatatud. Oodatakse serveri töö lõppu.'],
};

/**
 * Returns the override map for a short language code or regional locale,
 * or undefined if no overrides exist for that language.
 */
export const getTranslationOverrides = (
  languageCode: string
): Record<string, string> | undefined => {
  const shortCode = languageCode.trim().toLowerCase().split(/[-_]/)[0];
  if (ADDITIONAL_TRANSLATION_OVERRIDES[shortCode]) {
    return { ...ADDITIONAL_TRANSLATION_OVERRIDES[shortCode] };
  }
  const overrides = TRANSLATION_OVERRIDES[shortCode];
  const retiredSurveyStatus = RETIRED_SURVEY_STATUS_OVERRIDES[shortCode];
  const nativeSurveyUi = NATIVE_SURVEY_UI_OVERRIDES[shortCode];
  const chatProgress = AI_CHAT_PROGRESS_OVERRIDES[shortCode];
  const surveyButtons = SURVEY_BUTTON_OVERRIDES[shortCode];
  const surveyDuration = NATIVE_SURVEY_DURATION_SENTENCES[shortCode];
  const searchSliderLabels = ROOM_SEARCH_SLIDER_LABELS[shortCode];
  const backgroundLabels = ROOM_AI_BACKGROUND_LABELS[shortCode];
  const citationCountLabel = AI_CHAT_CITATION_COUNT_LABELS[shortCode];
  const streamControlLabels = AI_CHAT_STREAM_CONTROL_LABELS[shortCode];
  if (!overrides && !retiredSurveyStatus && !nativeSurveyUi && !chatProgress && !surveyButtons && !surveyDuration && !searchSliderLabels && !backgroundLabels) return undefined;

  return {
    ...(overrides ?? {}),
    ...(AI_CHAT_ACTIVITY_LABELS[shortCode] ? { ai_chatbot_activity_history: AI_CHAT_ACTIVITY_LABELS[shortCode],
      ai_chatbot_activity_history_duration: AI_CHAT_ACTIVITY_DURATION_LABELS[shortCode] } : {}),
    ...(shortCode === 'en' ? { ai_chatbot_activity_history_plural: 'Completed {{count}} tasks' } : {}),
    ...(streamControlLabels ? { ai_chatbot_stop_streaming: streamControlLabels[0],
      ai_chatbot_streaming_stopped: streamControlLabels[1] } : {}),
    ...(citationCountLabel ? { ai_chatbot_citations_found_retained: citationCountLabel } : {}),
    ...(AI_CHAT_SOURCE_CITED_LABELS[shortCode] ? { ai_chatbot_source_cited: AI_CHAT_SOURCE_CITED_LABELS[shortCode] } : {}),
    ...(LEGAL_ENGLISH_ONLY_NOTICES[shortCode] ? { legal_english_only_notice: LEGAL_ENGLISH_ONLY_NOTICES[shortCode] } : {}),
    ...(LEGAL_TRANSLATION_NOTICES[shortCode] ? { legal_translation_notice: LEGAL_TRANSLATION_NOTICES[shortCode] } : {}),
    ...(backgroundLabels ? { 'roomAI.backgroundPattern': backgroundLabels[0], 'roomAI.backgroundVisibility': backgroundLabels[1] } : {}),
    ...(searchSliderLabels ? Object.fromEntries(ROOM_SEARCH_SLIDER_KEYS.map((key, index) =>
      [`roomAI.search.slider.${key}`, searchSliderLabels[index]])) : {}),
    ...(nativeSurveyUi ?? {}),
    ...(chatProgress ?? {}),
    ...(surveyButtons ? {
      native_survey_back: surveyButtons.back,
      native_survey_submit: surveyButtons.submit,
      native_survey_continue: surveyButtons.continue,
      native_survey_help: surveyButtons.help,
      native_survey_skip: surveyButtons.quit,
      native_survey_finish: surveyButtons.close,
      poll_preview_back: surveyButtons.back,
      poll_proposal_submit: surveyButtons.submit,
      enhanced_survey_back: surveyButtons.back,
      enhanced_survey_continue: surveyButtons.continue,
      enhanced_survey_submit_responses: surveyButtons.submit,
    } : {}),
    ...(surveyDuration
      ? { native_survey_duration_sentence: surveyDuration }
      : {}),
    ...(retiredSurveyStatus
      ? { enhanced_survey_status_retired: retiredSurveyStatus }
      : {}),
  };
};
